<!-- app-subdomain-creators\src\layouts\SitePageEditorLayout.vue -->

<template>
  <div :class="editorRootClasses">
    <!-- Top chrome reveal area -->
    <div
      class="fixed inset-x-0 top-0 z-[58] h-3"
      @mouseenter="revealEditorChrome"
      @mouseleave="scheduleEditorChromeHide"
    />

    <!-- Overlay Top Bar -->
    <div
      class="fixed inset-x-0 top-0 z-[60] h-12 bg-zinc-700/95 backdrop-blur-sm px-3 py-1.5 flex items-center justify-between shadow-md transition-[transform,opacity] duration-200 ease-out"
      :class="showEditorChrome ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'"
      @mouseenter="revealEditorChrome"
      @mouseleave="scheduleEditorChromeHide"
    >
      <div class="flex items-center gap-2">
        <img
          class="h-8 w-auto"
          src="@/assets/media/images/platformalogostack.png"
          alt="Platforma Logo"
        >

        <!-- Exit / Back Button -->
        <button
          v-if="embedded"
          type="button"
          class="styled-tooltip flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-600 text-white hover:bg-zinc-800 transition cursor-pointer"
          :aria-label="embeddedBackLabel"
          :data-tooltip="embeddedBackLabel"
          @click="exitEmbeddedEditor"
        >
          <ArrowUturnLeftIcon class="w-4 h-4" />
        </button>
        <router-link
          v-else
          :to="{ name: 'PageEditor' }"
          class="styled-tooltip flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-600 text-white hover:bg-zinc-800 transition cursor-pointer"
          aria-label="Back to Page List"
          data-tooltip="Back to Page List"
        >
          <ArrowUturnLeftIcon class="w-4 h-4" />
        </router-link>

        <!-- Settings button -->
        <button
          class="styled-tooltip flex items-center justify-center gap-2 bg-zinc-600 text-white hover:bg-zinc-800 w-8 h-8 rounded-lg"
          aria-label="Page Settings"
          data-tooltip="Page Settings"
          @click="openSettings"
        >
          <Cog6ToothIcon class="w-4 h-4" />
        </button>
        <button
          class="styled-tooltip flex items-center justify-center gap-2 bg-zinc-600 text-white hover:bg-zinc-800 w-8 h-8 rounded-lg"
          aria-label="Page Structure"
          data-tooltip="Page Structure"
          @click="openStructureMenu"
        >
          <ViewColumnsIcon class="w-4 h-4" />
        </button>
        <div class="mx-0.5 h-6 w-px bg-white/15" aria-hidden="true" />
        <button
          type="button"
          class="styled-tooltip flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-600 text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Undo last page change"
          data-tooltip="Undo last change · Ctrl+Z"
          :disabled="!canUndoPageChange"
          @click="undoPageChange"
        >
          <ArrowUturnLeftIcon class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="styled-tooltip flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-600 text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Redo page change"
          data-tooltip="Redo change · Ctrl+Shift+Z"
          :disabled="!canRedoPageChange"
          @click="redoPageChange"
        >
          <ArrowUturnRightIcon class="h-4 w-4" />
        </button>
      </div>

      <!-- Right-side buttons -->
      <div class="flex items-center gap-2 pr-1">
        <button
          v-for="action in featureActions"
          :key="action.key"
          type="button"
          class="flex h-8 items-center justify-center rounded-lg px-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-95"
          :style="{ backgroundColor: action.color || '#ff009d' }"
          @click="emit('feature-action', action.key)"
        >
          {{ action.label }}
        </button>
        <div class="flex items-center gap-1 rounded-lg bg-zinc-800/80 p-1">
          <button
            v-for="option in editorViewportOptions"
            :key="option.key"
            type="button"
            class="styled-tooltip inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-medium transition"
            :class="editorViewportMode === option.key
              ? 'bg-[#2D8CFF] text-white'
              : 'bg-zinc-700 text-zinc-100 hover:bg-zinc-600'"
            :aria-pressed="editorViewportMode === option.key"
            :aria-label="`Switch canvas to ${option.label} view`"
            :data-tooltip="`Switch canvas to ${option.label} view`"
            @click="setEditorViewportMode(option.key)"
          >
            <component
              :is="option.icon"
              class="h-4 w-4"
            />
            <span class="hidden xl:inline">{{ option.label }}</span>
          </button>
        </div>
        <button
          v-if="editorViewportMode !== 'desktop'"
          type="button"
          class="styled-tooltip inline-flex h-8 items-center justify-center gap-1 rounded-lg bg-zinc-600 px-2 text-xs font-medium text-white transition hover:bg-zinc-800"
          :aria-label="`Remove ${editorViewportMode} overrides and inherit from the larger layout`"
          :data-tooltip="`Remove ${editorViewportMode} overrides and inherit from the larger layout`"
          @click="resetCurrentViewportOverrides"
        >
          <ArrowPathIcon class="h-4 w-4" />
          <span class="hidden 2xl:inline">Reset view</span>
        </button>

        <!-- Saved chip (appears for 5s after a successful Save) -->
        <div
          class="flex items-center gap-1 text-green-500 text-sm mr-2 transition-opacity duration-500"
          :class="justSaved ? 'opacity-100' : 'opacity-0 pointer-events-none'"
          aria-live="polite"
        >
          <CheckCircleIcon class="w-6 h-6" />
          <span>Saved To Drafts</span>
        </div>

        <button
          class="flex items-center justify-center gap-2 bg-zinc-600 text-white hover:bg-zinc-800 w-24 h-8 rounded-lg text-sm disabled:cursor-wait disabled:opacity-60"
          :disabled="previewing"
          @click="preview"
        >
          <EyeIcon class="w-4 h-4" />
          <span>{{ previewing ? 'Opening…' : 'Preview' }}</span>
        </button>

        <div
          v-if="autosave"
          class="flex h-8 min-w-32 items-center justify-center gap-2 rounded-lg border px-3 text-xs font-semibold transition-colors"
          :class="autosaveError
            ? 'border-red-400/30 bg-red-500/15 text-red-100'
            : (saving
            ? 'border-blue-400/30 bg-blue-500/15 text-blue-100'
            : (hasUnsavedChanges
              ? 'border-amber-400/30 bg-amber-500/15 text-amber-100'
              : 'border-emerald-400/30 bg-emerald-500/15 text-emerald-100'))"
          aria-live="polite"
        >
          <ArrowPathIcon v-if="saving" class="h-4 w-4 animate-spin" />
          <ExclamationTriangleIcon v-else-if="autosaveError" class="h-4 w-4" />
          <ServerStackIcon v-else-if="hasUnsavedChanges" class="h-4 w-4" />
          <CheckCircleIcon v-else class="h-4 w-4" />
          <span>{{ saving ? 'Saving changes…' : (autosaveError ? 'Autosave interrupted' : (hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved')) }}</span>
        </div>
        <button
          v-else
          class="flex items-center justify-center gap-2 bg-zinc-600 text-white hover:bg-zinc-800 w-24 h-8 rounded-lg text-sm disabled:opacity-60"
          :disabled="saving"
          @click="saveDraft"
        >
          <ServerStackIcon class="w-4 h-4" />
          <span>{{ saving ? 'Saving...' : 'Save' }}</span>
        </button>

        <button
          class="flex items-center justify-center gap-2 bg-[#2D8CFF] text-white hover:bg-[#1b6ad1] w-24 h-8 rounded-lg text-sm disabled:opacity-60"
          :disabled="saving || publishing || externalPublishing"
          @click="publishNow"
        >
          <GlobeAmericasIcon class="w-4 h-4" />
          <span>{{ publishing || externalPublishing ? 'Publishing...' : 'Publish' }}</span>
        </button>
      </div>
    </div>

    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="-translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-2 opacity-0"
    >
      <div
        v-if="autosave && !showEditorChrome && (saving || hasUnsavedChanges || justSaved || autosaveError)"
        class="pointer-events-none fixed right-3 top-3 z-[59] inline-flex min-h-9 items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold shadow-lg backdrop-blur-md"
        :class="autosaveError
          ? 'border-red-200/80 bg-red-50/95 text-red-700'
          : (saving
            ? 'border-blue-200/80 bg-blue-50/95 text-blue-700'
            : (hasUnsavedChanges
              ? 'border-amber-200/80 bg-amber-50/95 text-amber-700'
              : 'border-emerald-200/80 bg-emerald-50/95 text-emerald-700'))"
        aria-live="polite"
      >
        <ArrowPathIcon v-if="saving" class="h-4 w-4 animate-spin" />
        <ExclamationTriangleIcon v-else-if="autosaveError" class="h-4 w-4" />
        <ServerStackIcon v-else-if="hasUnsavedChanges" class="h-4 w-4" />
        <CheckCircleIcon v-else class="h-4 w-4" />
        <span>{{ saving ? 'Saving changes…' : (autosaveError ? 'Autosave interrupted' : (hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved')) }}</span>
      </div>
    </transition>


    <!-- Publish success banner (fixed, no layout shift) -->
    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="justPublished"
          class="fixed left-0 right-0 top-2 flex justify-center px-4 sm:px-6 pointer-events-none"
          :style="{ zIndex: editorOverlayZ(isModalCanvasPresentation ? 1390 : 90, 4) }"
          aria-live="polite"
        >
          <!-- Card wrapper can receive clicks (close button), rest stays click-through -->
          <div class="pointer-events-auto w-full max-w-3xl">
            <div class="rounded-md bg-green-500/20 p-4 outline outline-1 outline-green-500/30 shadow-md backdrop-blur-sm">
              <div class="flex items-start">
                <div class="shrink-0">
                  <CheckCircleIconSolid
                    class="w-5 h-5 text-green-500"
                    aria-hidden="true"
                  />
                </div>
                <div class="ml-3">
                  <p class="text-sm font-medium text-green-500">
                    Published to live
                  </p>
                  <p class="text-xs text-green-500/80 mt-0.5">
                    Your latest changes are now visible on the live page.
                  </p>
                </div>
                <div class="ml-auto pl-3 -mr-1.5 -mt-1">
                  <button
                    type="button"
                    class="inline-flex rounded-md p-1.5 text-green-500 hover:bg-green-500/10 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-1 focus-visible:ring-offset-green-900 focus-visible:outline-hidden"
                    @click="justPublished = false"
                  >
                    <span class="sr-only">Dismiss</span>
                    <XMarkIcon
                      class="w-5 h-5"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <Teleport to="body">
      <div v-if="reusableSaveOpen" class="fixed inset-0 z-[1450] flex items-center justify-center bg-zinc-950/70 p-4 backdrop-blur-sm" @click.self="reusableSaveOpen = false">
        <section class="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" role="dialog" aria-modal="true" aria-label="Save reusable page item">
          <p class="text-[10px] font-extrabold uppercase tracking-[.16em] text-violet-600">Reusable library</p>
          <h2 class="mt-2 text-xl font-extrabold text-zinc-950">Save this {{ selectedReusableType }}</h2>
          <p class="mt-1 text-sm leading-6 text-zinc-500">All content, media references, spacing, responsive settings, colors, and actions will be preserved.</p>
          <label class="mt-5 block"><span class="mb-1 block text-sm font-bold text-zinc-700">Name</span><input v-model="reusableSaveName" maxlength="180" class="w-full rounded-xl border border-zinc-300 px-3 py-2.5 text-sm focus:border-[#2D8CFF] focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]" placeholder="e.g. Pink testimonial row" @keyup.enter="saveReusableSelection"></label>
          <p v-if="reusableSaveError" class="mt-3 text-sm font-medium text-red-600">{{ reusableSaveError }}</p>
          <div class="mt-6 flex justify-end gap-3"><button type="button" class="rounded-xl px-4 py-2.5 text-sm font-bold text-zinc-500 hover:bg-zinc-100" :disabled="savingReusableItem" @click="reusableSaveOpen = false">Cancel</button><button type="button" class="rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-extrabold text-white hover:bg-violet-700 disabled:opacity-60" :disabled="savingReusableItem" @click="saveReusableSelection">{{ savingReusableItem ? 'Saving…' : 'Save to library' }}</button></div>
        </section>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="savedElementLibraryOpen"
        class="fixed inset-0 z-[1460] flex items-center justify-center bg-zinc-950/75 p-4 backdrop-blur-sm"
        @click.self="savedElementLibraryOpen = false"
      >
        <section
          class="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="saved-element-library-title"
        >
          <header class="flex items-start justify-between gap-4 border-b border-zinc-200 px-5 py-4 sm:px-6">
            <div>
              <p class="text-[10px] font-extrabold uppercase tracking-[.16em] text-violet-600">Reusable library</p>
              <h2 id="saved-element-library-title" class="mt-1 text-xl font-extrabold text-zinc-950">Saved Elements</h2>
              <p class="mt-1 text-sm text-zinc-500">Choose a saved element to add with all of its content and styling intact.</p>
            </div>
            <button
              type="button"
              class="rounded-xl p-2 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
              aria-label="Close saved elements"
              @click="savedElementLibraryOpen = false"
            >
              <XMarkIcon class="h-5 w-5" aria-hidden="true" />
            </button>
          </header>

          <div class="app-scroll flex-1 overflow-y-auto bg-zinc-50 p-5 sm:p-6">
            <div v-if="savedLibraryLoading" class="flex min-h-64 items-center justify-center text-sm font-semibold text-zinc-500">
              Loading saved elements…
            </div>
            <div
              v-else-if="!savedLibraryByType.element.length"
              class="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 bg-white px-6 text-center"
            >
              <BookmarkSquareIcon class="h-10 w-10 text-violet-400" aria-hidden="true" />
              <h3 class="mt-3 text-base font-extrabold text-zinc-900">No saved elements yet</h3>
              <p class="mt-1 max-w-md text-sm leading-6 text-zinc-500">Select an element on the page, open its settings, and use the bookmark button to save it for reuse.</p>
            </div>
            <div v-else class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <article
                v-for="item in savedLibraryByType.element"
                :key="`saved-element-card-${item.id}`"
                class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-lg"
              >
                <div class="saved-element-preview-frame">
                  <div class="saved-element-preview-canvas" aria-hidden="true">
                    <PageRenderer
                      :doc="buildSavedElementPreviewDoc(item)"
                      :selected-index="-1"
                      viewport-mode="desktop"
                      class="pointer-events-none select-none"
                    />
                  </div>
                </div>
                <div class="p-4">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <h3 class="truncate text-sm font-extrabold text-zinc-900">{{ item.name }}</h3>
                      <p class="mt-0.5 text-[10px] font-extrabold uppercase tracking-[.12em] text-violet-500">{{ getEditorElementLabel(item.content) }}</p>
                    </div>
                    <BookmarkSquareIcon class="h-5 w-5 shrink-0 text-violet-500" aria-hidden="true" />
                  </div>
                  <button
                    type="button"
                    class="mt-4 w-full rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-extrabold text-white transition hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                    @click="useSavedElement(item)"
                  >
                    Use element
                  </button>
                </div>
              </article>
            </div>
          </div>
        </section>
      </div>
    </Teleport>

    <div :class="editorCanvasClasses">
      <div
        :class="editorCanvasInnerClasses"
        :style="editorCanvasInnerStyle"
      >
        <button
          v-if="isModalCanvasPresentation && embedded"
          type="button"
          class="absolute right-4 top-4 z-[100] rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100"
          aria-label="Close result editor"
          title="Close result editor"
          @click="emit('exit-embedded')"
        >
          <XMarkIcon class="h-6 w-6" aria-hidden="true" />
        </button>
        <div :class="editorViewportFrameClasses">
          <div
            v-if="showViewportPhoneNotch"
            class="pointer-events-none absolute left-1/2 top-2 z-10 h-5 w-28 -translate-x-1/2 rounded-b-2xl border border-zinc-700 bg-zinc-900/95 shadow"
          />
          <div :class="editorViewportScreenClasses">
            <!-- Render the actual page -->
            <div
              v-if="editorDoc"
              class="space-y-0"
            >
              <!-- Real WYSIWYG render with selectable sections -->
              <PageRenderer
                :doc="editorDoc"
                :page-slug="currentEditorPageSlug"
                :default-video-url="defaultVideoUrl"
                :viewport-mode="editorViewportMode"
                :selected-index="selectedIdx"
                :selected-row-context="selectedRowContext"
                :selected-column-context="selectedColumnContext"
                :selected-element-context="selectedElementContext"
                :system-element-types="requiredSystemElementTypes"
                :hidden-element-types="hiddenSystemElementTypes"
                :meet-scheduler-settings="meetSchedulerSettings"
                @select="onSelect"
                @select-row="onSelectRow"
                @select-row-column="onSelectRowColumn"
                @select-row-element="onSelectRowElement"
                @clear-selection="onClearCanvasSelection"
                @add-after="openAddPaletteAfter"
                @add-end="openAddPaletteEnd"
                @add-row-after="onAddRowAfter"
                @add-row-element-after="onAddRowElementAfter"
                @duplicate="duplicateBlock"
                @duplicate-row="duplicateRow"
                @duplicate-row-element="duplicateRowElement"
                @reorder="onReorderBlocks"
                @reorder-row="onReorderRows"
                @reorder-row-element="onReorderRowElements"
                @open-settings-menu="onOpenSettingsMenu"
                @insert-from-drop="onInsertFromDrop"
                @insert-row="onInsertRowFromDrop"
                @insert-row-element="onInsertRowElementFromDrop"
                @nest-row-into-row="onNestRowIntoRow"
                @delete="(index) => requestDeleteSelection('section', { index })"
                @delete-row="(payload) => requestDeleteSelection('row', payload)"
                @move-row-column="moveRowColumn"
                @delete-row-column="(payload) => requestDeleteSelection('column', payload)"
                @delete-row-element="(payload) => requestDeleteSelection('element', payload)"
              />
            </div>


            <div
              v-else
              class="text-zinc-500"
            >
              No template loaded yet.
            </div>
          </div>
        </div>

        <!-- Inspector Drawer (no layout shift) -->
        <!-- Drawer -->
        <Teleport to="body">
          <div
            v-if="drawerOpen && !showAddPalette"
            class="fixed inset-0 bg-black/0"
            :style="{ zIndex: editorOverlayZ(isModalCanvasPresentation ? 1190 : 79) }"
            @click="closeDrawer"
          />
          <transition
            enter-active-class="transition-transform duration-200 ease-out"
            enter-from-class="translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform duration-150 ease-in"
            leave-from-class="translate-x-0"
            leave-to-class="translate-x-full"
          >
            <aside
              v-if="drawerOpen"
              class="fixed inset-y-0 right-0 w-[360px] max-w-[86vw] bg-white/50 backdrop-blur-sm border-l border-zinc-200"
              :style="{ zIndex: editorOverlayZ(isModalCanvasPresentation ? 1191 : 80, 1) }"
              @click.stop
            >
              <div class="h-16 px-4 border-b flex items-center justify-between">
                <div class="font-semibold">
                  <template v-if="showSettings">
                    Page Settings
                  </template>
                  <template v-else-if="showStructureMenu">
                    Page Structure
                  </template>
                  <template v-else-if="showAddPalette">
                    Add Block
                  </template>
                  <template v-else-if="selectedElement">
                    Edit: <span class="font-mono">{{ getEditorElementLabel(selectedElement) }}</span>
                  </template>
                  <template v-else-if="selectedColumn">
                    Edit: <span class="font-mono">column</span>
                  </template>
                  <template v-else-if="selectedRow">
                    Edit: <span class="font-mono">row</span>
                  </template>
                  <template v-else>
                    Edit: <span class="font-mono">{{ selectedBlock.type }}</span>
                  </template>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    v-if="canSaveReusableSelection"
                    type="button"
                    class="styled-tooltip inline-flex h-9 w-9 items-center justify-center rounded-lg border border-violet-100 bg-violet-50 text-violet-600 transition hover:border-violet-200 hover:bg-violet-100"
                    :aria-label="`Save selected ${selectedReusableType}`"
                    :data-tooltip="`Save ${selectedReusableType} to library`"
                    @click="openReusableSaveDialog"
                  >
                    <BookmarkSquareIcon class="h-4 w-4" />
                  </button>
                  <button
                    v-if="canDeleteDrawerSelection"
                    type="button"
                    class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-600 transition hover:border-red-200 hover:bg-red-100"
                    aria-label="Delete selected element"
                    title="Delete selected element"
                    @click="deleteDrawerSelection"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </button>
                  <button
                    class="px-2 py-1 rounded bg-zinc-100 hover:bg-zinc-200"
                    @click="closeDrawer"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div class="p-4 space-y-5 overflow-auto app-scroll h-[calc(100%-4rem)]">
                <div
                  v-if="!showSettings && !showStructureMenu && !showAddPalette"
                  class="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs leading-5 text-blue-800"
                >
                  Editing the <strong class="capitalize">{{ editorViewportMode }}</strong> version. Layout and style changes made here are saved only for this device view.
                </div>
                <!-- SETTINGS PANEL -->
                <template v-if="showSettings">
                  <!-- <label class="block text-sm font-medium mb-1">Page Title</label>
        <input
            v-model="editorDoc.title"
            class="w-full border rounded px-3 py-2"
            placeholder="Page Title"
        /> -->

                  <!-- SEO group -->
                  <div class="mt-2">
                    <label class="block text-sm font-medium mb-1">SEO Page Title</label>
                    <input
                      v-model="editorDoc.seo.title"
                      class="w-full border rounded px-3 py-2"
                      placeholder="SEO title (recommended <= 60 chars)"
                    >

                    <div class="flex gap-2 mt-1 text-sm text-zinc-600">
                      <div class="font-medium">
                        Page ID
                      </div>
                      <div class="font-mono">
                        {{ pageIdRef }}
                      </div>
                    </div>

                    <label class="block text-sm font-medium mt-4 mb-1">SEO Page Description</label>
                    <textarea
                      v-model="editorDoc.seo.description"
                      class="w-full border rounded px-3 py-2 h-24 resize-none"
                      placeholder="SEO description (recommended 50-160 chars)"
                    />

                    <div class="mt-2 text-[11px] text-zinc-500">
                      <span>Title: {{ (editorDoc.seo.title || '').length }} chars</span>
                      <span class="mx-2">|</span>
                      <span>Description: {{ (editorDoc.seo.description || '').length }} chars</span>
                    </div>
                  </div>
                </template>

                <!-- NEW: ADD PALETTE -->
                <template v-else-if="showAddPalette">
                  <div class="text-sm text-zinc-600 mb-3">
                    Choose a block to insert
                  </div>

                  <div class="grid grid-cols-4 gap-2 mb-4">
                    <button
                      type="button"
                      class="rounded-md px-2 py-1.5 text-xs font-semibold border transition"
                      :class="addPaletteCategory === 'sections' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'"
                      @click="addPaletteCategory = 'sections'"
                    >
                      Sections
                    </button>
                    <button
                      type="button"
                      class="rounded-md px-2 py-1.5 text-xs font-semibold border transition"
                      :class="addPaletteCategory === 'rows' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'"
                      @click="addPaletteCategory = 'rows'"
                    >
                      Rows
                    </button>
                    <button
                      type="button"
                      class="rounded-md px-2 py-1.5 text-xs font-semibold border transition"
                      :class="addPaletteCategory === 'columns' ? 'bg-zinc-200 text-zinc-700 border-zinc-300' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'"
                      @click="addPaletteCategory = 'columns'"
                    >
                      Columns
                    </button>
                    <button
                      type="button"
                      class="rounded-md px-2 py-1.5 text-xs font-semibold border transition"
                      :class="addPaletteCategory === 'elements' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'"
                      @click="addPaletteCategory = 'elements'"
                    >
                      Elements
                    </button>
                  </div>

                  <div
                    v-if="addPaletteCategory === 'sections'"
                    class="grid grid-cols-2 gap-3 auto-rows-fr"
                  >
                    <button
                      type="button"
                      class="group flex h-full min-h-[104px] flex-col items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-2 py-3 text-center hover:border-[#ff009dce] hover:bg-purple-50 transition-colors"
                      @click="insertBlock('section')"
                    >
                      <RectangleStackIcon class="w-6 h-6 text-zinc-500 group-hover:text-[#ff009dce] shrink-0" />
                      <span class="text-xs sm:text-sm font-medium text-zinc-600 group-hover:text-[#ff009dce] leading-tight">
                        Section
                      </span>
                    </button>
                    <button
                      v-for="item in savedLibraryByType.section"
                      :key="`saved-section-${item.id}`"
                      type="button"
                      class="group relative flex h-full min-h-[104px] flex-col items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-2 py-3 text-center transition hover:border-violet-400 hover:bg-violet-100"
                      @click="insertSavedLibraryItem(item)"
                    >
                      <BookmarkSquareIcon class="h-6 w-6 text-violet-500" />
                      <span class="text-xs font-bold leading-tight text-violet-700">{{ item.name }}</span>
                      <span class="text-[9px] font-extrabold uppercase tracking-wide text-violet-400">Saved section</span>
                    </button>
                  </div>

                  <div
                    v-else-if="addPaletteCategory === 'rows'"
                    class="space-y-3"
                  >
                    <div
                      v-if="!canInsertRows"
                      class="rounded-md border border-dashed border-zinc-300 px-3 py-2 text-xs text-zinc-500"
                    >
                      Drag a row tile into any section, or select a section first and click a row tile.
                    </div>
                    <div class="grid grid-cols-2 gap-3 auto-rows-fr">
                      <button
                        v-for="count in rowColumnOptions"
                        :key="`row-${count}`"
                        type="button"
                        draggable="true"
                        class="group flex h-full min-h-[104px] flex-col items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-2 py-3 text-center transition-colors"
                        :class="canInsertRows ? 'hover:border-[#2D8CFF] hover:bg-blue-50 cursor-grab active:cursor-grabbing' : 'hover:border-[#2D8CFF] hover:bg-blue-50 cursor-grab active:cursor-grabbing'"
                        @click="insertRowFromPalette(count)"
                        @dragstart="onRowPaletteDragStart(count, $event)"
                      >
                        <div
                          class="grid w-10 gap-0.5"
                          :style="{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }"
                        >
                          <span
                            v-for="n in count"
                            :key="`row-col-${count}-${n}`"
                            class="h-3 rounded-sm border border-blue-200 bg-blue-100"
                          />
                        </div>
                        <span class="text-xs sm:text-sm font-medium text-zinc-600 group-hover:text-[#2D8CFF] leading-tight">
                          {{ count }} Column{{ count > 1 ? 's' : '' }}
                        </span>
                      </button>
                      <button
                        v-for="item in savedLibraryByType.row"
                        :key="`saved-row-${item.id}`"
                        type="button"
                        class="group flex h-full min-h-[104px] flex-col items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-2 py-3 text-center transition hover:border-violet-400 hover:bg-violet-100"
                        @click="insertSavedLibraryItem(item)"
                      >
                        <BookmarkSquareIcon class="h-6 w-6 text-violet-500" />
                        <span class="text-xs font-bold leading-tight text-violet-700">{{ item.name }}</span>
                        <span class="text-[9px] font-extrabold uppercase tracking-wide text-violet-400">Saved row</span>
                      </button>
                    </div>
                  </div>

                  <div
                    v-else-if="addPaletteCategory === 'columns'"
                    class="space-y-2"
                  >
                    <div v-if="!savedLibraryByType.column.length" class="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-3 text-xs leading-5 text-zinc-500">Select a column on the canvas and use the bookmark button in its settings to save it here.</div>
                    <button v-for="item in savedLibraryByType.column" :key="`saved-column-${item.id}`" type="button" class="flex w-full items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 p-3 text-left transition hover:border-violet-400 hover:bg-violet-100" @click="insertSavedLibraryItem(item)"><BookmarkSquareIcon class="h-5 w-5 shrink-0 text-violet-500" /><span><strong class="block text-sm text-violet-800">{{ item.name }}</strong><small class="text-violet-500">Insert into the selected row with all saved contents</small></span></button>
                  </div>

                  <div
                    v-else
                    class="space-y-3"
                  >
                    <div
                      v-if="!canInsertElements"
                      class="rounded-md border border-dashed border-zinc-300 px-3 py-2 text-xs text-zinc-500"
                    >
                      Select a row first, or drag an element tile directly into any row or section.
                    </div>
                    <div class="grid grid-cols-2 gap-3 auto-rows-fr">
                      <button
                        v-for="option in contentElementOptions"
                        :key="`drawer-element-${option.key}`"
                        type="button"
                        draggable="true"
                        class="group flex h-full min-h-[104px] flex-col items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-2 py-3 text-center transition-colors hover:border-[#F59E0B] hover:bg-amber-50 cursor-grab active:cursor-grabbing"
                        @click="onSelectContentElement(option.key)"
                        @dragstart="onContentElementDragStart(option.key, $event)"
                        @dragend="onContentElementDragEnd"
                      >
                        <component
                          :is="option.icon"
                          class="w-6 h-6 text-zinc-500 group-hover:text-[#D97706] shrink-0"
                        />
                        <span class="text-xs sm:text-sm font-medium text-zinc-600 group-hover:text-[#D97706] leading-tight">
                          {{ option.label }}
                        </span>
                      </button>
                      <button
                        type="button"
                        class="group relative flex h-full min-h-[104px] flex-col items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-2 py-3 text-center transition hover:border-violet-400 hover:bg-violet-100"
                        @click="openSavedElementLibrary"
                      >
                        <BookmarkSquareIcon class="h-6 w-6 text-violet-500" />
                        <span class="text-xs sm:text-sm font-bold leading-tight text-violet-700">Saved Element</span>
                        <span class="text-[9px] font-extrabold uppercase tracking-wide text-violet-400">Choose from library</span>
                        <span
                          v-if="savedLibraryByType.element.length"
                          class="absolute right-2 top-2 inline-flex min-w-5 items-center justify-center rounded-full bg-violet-600 px-1.5 py-0.5 text-[9px] font-extrabold text-white"
                        >
                          {{ savedLibraryByType.element.length }}
                        </span>
                      </button>
                    </div>
                  </div>
                </template>

                <!-- EXISTING INSPECTORS -->
                <template v-else-if="selectedBlock && selectedBlock.type === 'hero'">
                  <label class="block text-sm font-medium mb-1">Headline</label>
                  <input
                    v-model="selectedBlock.props.headline"
                    class="w-full border rounded px-3 py-2"
                  >
                  <label class="block text-sm font-medium mt-4 mb-1">Sub-headline</label>
                  <input
                    v-model="selectedBlock.props.sub"
                    class="w-full border rounded px-3 py-2"
                  >
                </template>

                <template v-else-if="selectedBlock && selectedBlock.type === 'features'">
                  <div class="flex items-center justify-between">
                    <div class="text-sm font-medium">
                      Items
                    </div>
                    <button
                      class="text-xs px-2 py-1 rounded border hover:bg-zinc-50"
                      @click="selectedBlock.props.items.push({ title: 'New feature' })"
                    >
                      + Add
                    </button>
                  </div>
                  <div class="space-y-2">
                    <div
                      v-for="(it, j) in selectedBlock.props.items"
                      :key="j"
                      class="flex items-center gap-2"
                    >
                      <input
                        v-model="it.title"
                        class="flex-1 border rounded px-3 py-2"
                      >
                      <button
                        class="px-2 py-1 rounded border"
                        @click="selectedBlock.props.items.splice(j,1)"
                      >
                        X
                      </button>
                    </div>
                  </div>
                </template>

                <!-- NEW: TEXT inspector -->
                <template v-else-if="selectedBlock && selectedBlock.type === 'text'">
                  <label class="block text-sm font-medium mb-1">Content</label>
                  <textarea
                    v-model="selectedBlock.props.content"
                    class="w-full border rounded px-3 py-2 h-40 resize-y"
                    placeholder="Write your text..."
                  />
                </template>

                <!-- inside the HEADER inspector -->
                <template v-else-if="selectedBlock && selectedBlock.type === 'header'">
                  <label class="block text-sm font-medium mb-1">Text</label>
                  <input
                    v-model="selectedBlock.props.text"
                    class="w-full border rounded px-3 py-2"
                    placeholder="Section heading"
                  >

                  <label class="block text-sm font-medium mt-4 mb-1">Level</label>
                  <select
                    v-model="selectedBlock.props.level"
                    class="w-full border rounded px-3 py-2"
                  >
                    <option value="h1">
                      H1
                    </option>
                    <option value="h2">
                      H2
                    </option>
                    <option value="h3">
                      H3
                    </option>
                  </select>

                  <!-- NEW: Size slider -->
                  <div class="mt-4">
                    <div class="flex items-center justify-between">
                      <label class="block text-sm font-medium">Size</label>
                      <div class="text-xs text-zinc-500">
                        {{ Number(selectedBlock.props.size || 32) }} px
                      </div>
                    </div>

                    <div class="mt-2 flex items-center gap-3">
                      <!-- - button -->
                      <button
                        type="button"
                        class="h-9 w-9 shrink-0 rounded-lg border bg-white hover:bg-zinc-50 grid place-items-center"
                        aria-label="Decrease size"
                        @click="selectedBlock.props.size = Math.max(16, Number(selectedBlock.props.size || 32) - 2)"
                      >
                        -
                      </button>

                      <!-- Modern range -->
                      <input
                        v-model.number="selectedBlock.props.size"
                        type="range"
                        min="16"
                        max="96"
                        step="1"
                        class="range-modern w-full"
                        :style="headerSizeFillStyle"
                        aria-label="Header size"
                      >

                      <!-- + button -->
                      <button
                        type="button"
                        class="h-9 w-9 shrink-0 rounded-lg border bg-white hover:bg-zinc-50 grid place-items-center"
                        aria-label="Increase size"
                        @click="selectedBlock.props.size = Math.min(96, Number(selectedBlock.props.size || 32) + 2)"
                      >
                        +
                      </button>
                    </div>

                    <div class="mt-2 flex justify-between text-[11px] text-zinc-500">
                      <span>16</span><span>96</span>
                    </div>
                  </div>

                  <p class="mt-2 text-[11px] text-zinc-500">
                    Tip: Use one H1 per page. Use size to fine-tune visuals without breaking hierarchy.
                  </p>
                </template>

                <template v-else-if="showStructureMenu">
                  <div class="space-y-4">
                    <div class="text-xs text-zinc-600">
                      Select any item to open its settings
                    </div>
                    <div
                      v-if="!pageStructureSections.length"
                      class="rounded-md border border-dashed border-zinc-300 px-3 py-2 text-xs text-zinc-500"
                    >
                      No sections yet.
                    </div>
                    <div
                      v-for="sectionNode in pageStructureSections"
                      :key="`structure-section-${sectionNode.sectionIndex}`"
                      class="rounded-md border border-zinc-200 bg-white/70 p-2"
                    >
                      <button
                        type="button"
                        class="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-left text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                        @click="selectStructureSection(sectionNode.sectionIndex)"
                      >
                        Section {{ sectionNode.sectionIndex + 1 }}
                      </button>

                      <div
                        v-if="isSectionStructureBlock(sectionNode.section)"
                        class="mt-2 ml-2 space-y-2"
                      >
                        <div
                          v-for="rowNode in sectionNode.rows"
                          :key="`structure-row-${sectionNode.sectionIndex}-${rowNode.rowIndex}`"
                          class="rounded-md border border-zinc-200 bg-zinc-50 p-2"
                        >
                          <button
                            type="button"
                            class="w-full rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-left text-xs font-semibold text-blue-700 hover:bg-blue-100"
                            @click="selectStructureRow(sectionNode.sectionIndex, rowNode.rowIndex)"
                          >
                            {{ rowNode.columnCount }} Col. Row
                          </button>

                          <div class="mt-2 ml-2 space-y-2">
                            <div
                              v-for="columnNode in rowNode.columns"
                              :key="`structure-col-${sectionNode.sectionIndex}-${rowNode.rowIndex}-${columnNode.columnIndex}`"
                              class="rounded-md border border-zinc-200 bg-white p-2"
                            >
                              <button
                                type="button"
                                class="w-full rounded border border-blue-200 bg-white px-2 py-1 text-left text-[11px] font-medium text-blue-700 hover:bg-blue-50"
                                @click="selectStructureColumn(sectionNode.sectionIndex, rowNode.rowIndex, columnNode.columnIndex, rowNode.nestedRowElementIndex)"
                              >
                                Column {{ columnNode.columnIndex + 1 }}
                              </button>

                              <div class="mt-1 ml-2 space-y-1">
                                <template
                                  v-for="elementNode in columnNode.elements"
                                  :key="`structure-element-${sectionNode.sectionIndex}-${rowNode.rowIndex}-${columnNode.columnIndex}-${elementNode.elementIndex}`"
                                >
                                  <button
                                    type="button"
                                    class="w-full rounded border border-amber-200 bg-amber-50 px-2 py-1 text-left text-[11px] font-medium text-amber-700 hover:bg-amber-100"
                                    @click="selectStructureElement(sectionNode.sectionIndex, rowNode.rowIndex, elementNode.elementIndex, rowNode.nestedRowElementIndex)"
                                  >
                                    {{ elementNode.label }}
                                  </button>

                                  <div
                                    v-if="elementNode.nestedRow"
                                    class="ml-2 mt-1 rounded border border-blue-200 bg-blue-50/60 p-2 space-y-2"
                                  >
                                    <div class="text-[11px] font-semibold text-blue-700">
                                      Nested {{ elementNode.nestedRow.columnCount }} Col. Row
                                    </div>
                                    <div
                                      v-for="nestedColumnNode in elementNode.nestedRow.columns"
                                      :key="`structure-nested-col-${sectionNode.sectionIndex}-${rowNode.rowIndex}-${elementNode.elementIndex}-${nestedColumnNode.columnIndex}`"
                                      class="rounded border border-zinc-200 bg-white p-2"
                                    >
                                      <button
                                        type="button"
                                        class="w-full rounded border border-blue-200 bg-white px-2 py-1 text-left text-[11px] font-medium text-blue-700 hover:bg-blue-50"
                                        @click="selectStructureColumn(sectionNode.sectionIndex, rowNode.rowIndex, nestedColumnNode.columnIndex, elementNode.elementIndex)"
                                      >
                                        Nested Column {{ nestedColumnNode.columnIndex + 1 }}
                                      </button>
                                      <div class="mt-1 ml-2 space-y-1">
                                        <button
                                          v-for="nestedElementNode in nestedColumnNode.elements"
                                          :key="`structure-nested-element-${sectionNode.sectionIndex}-${rowNode.rowIndex}-${elementNode.elementIndex}-${nestedColumnNode.columnIndex}-${nestedElementNode.elementIndex}`"
                                          type="button"
                                          class="w-full rounded border border-amber-200 bg-amber-50 px-2 py-1 text-left text-[11px] font-medium text-amber-700 hover:bg-amber-100"
                                          @click="selectStructureElement(sectionNode.sectionIndex, rowNode.rowIndex, nestedElementNode.elementIndex, elementNode.elementIndex)"
                                        >
                                          {{ nestedElementNode.label }}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </template>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else-if="selectedElement">
                  <div class="space-y-4">
                    <div class="text-xs text-zinc-600">
                      Element settings
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'row'"
                      class="space-y-3"
                    >
                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Columns</label>
                        <div class="flex items-center gap-3">
                          <input
                            v-model.number="selectedElement.props.rowColumnCount"
                            type="range"
                            min="1"
                            max="6"
                            step="1"
                            class="range-modern flex-1"
                            :style="buildRangeFillStyle(selectedElement.props.rowColumnCount ?? 1, 1, 6, '#2D8CFF', '#3f3f46')"
                          >
                          <input
                            v-model.number="selectedElement.props.rowColumnCount"
                            type="number"
                            min="1"
                            max="6"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                          >
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Column Gutter</label>
                        <div class="flex items-center gap-3">
                          <input
                            v-model.number="selectedElement.props.rowColumnGutter"
                            type="range"
                            min="0"
                            max="120"
                            step="1"
                            class="range-modern flex-1"
                            :style="buildRangeFillStyle(selectedElement.props.rowColumnGutter ?? 12, 0, 120, '#2D8CFF', '#3f3f46')"
                          >
                          <input
                            v-model.number="selectedElement.props.rowColumnGutter"
                            type="number"
                            min="0"
                            max="120"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                          >
                          <span class="text-sm text-zinc-500">px</span>
                        </div>
                      </div>
                      <label
                        v-if="Number(selectedElement.props.rowColumnCount || 1) > 1"
                        class="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-700"
                      >
                        <span>Stack columns on {{ editorViewportMode }}</span>
                        <input v-model="selectedElement.props.stackColumns" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600">
                      </label>
                      <div class="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-3">
                        <p class="text-[11px] font-bold uppercase tracking-wide text-zinc-500">Row frame</p>
                        <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Border</label><PlatformaColorField v-model="selectedElement.props.rowBorderColor" :enable-opacity="true" /></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Border width</label><input v-model.number="selectedElement.props.rowBorderWidth" type="number" min="0" max="8" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div></div>
                        <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Corners</label><input v-model.number="selectedElement.props.rowBorderRadius" type="number" min="0" max="120" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Shadow</label><PlatformaSelect v-model="selectedElement.props.rowShadow" :options="rowShadowOptions" /></div></div>
                      </div>
                    </div>

                    <div
                      v-if="['text', 'header'].includes(String(selectedElement.type || '').toLowerCase())"
                      class="space-y-3"
                    >
                      <div v-if="String(selectedElement.type || '').toLowerCase() === 'header'">
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Header Text</label>
                        <input
                          v-model="selectedElement.props.text"
                          type="text"
                          class="w-full border rounded px-3 py-2 text-sm"
                          placeholder="Section heading"
                        >
                      </div>

                      <div v-if="String(selectedElement.type || '').toLowerCase() === 'header'">
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Header Level</label>
                        <select
                          v-model="selectedElement.props.level"
                          class="w-full border rounded px-3 py-2 text-sm"
                        >
                          <option value="h1">
                            H1
                          </option>
                          <option value="h2">
                            H2
                          </option>
                          <option value="h3">
                            H3
                          </option>
                          <option value="h4">
                            H4
                          </option>
                          <option value="h5">
                            H5
                          </option>
                          <option value="h6">
                            H6
                          </option>
                        </select>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Text Size</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeTextSettings.fontSize"
                            type="range"
                            min="10"
                            max="96"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedElementTextSizeFillStyle"
                            @input="onUpdateTextSettings({ fontSize: $event?.target?.value })"
                          >
                          <input
                            :value="activeTextSettings.fontSize"
                            type="number"
                            min="10"
                            max="96"
                            step="1"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateTextSettings({ fontSize: $event?.target?.value })"
                          >
                          <span class="text-sm text-zinc-500">px</span>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Font Weight</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeTextSettings.fontWeight"
                            type="range"
                            min="100"
                            max="900"
                            step="100"
                            class="range-modern flex-1"
                            :style="selectedElementTextWeightFillStyle"
                            @input="onUpdateTextSettings({ fontWeight: $event?.target?.value })"
                          >
                          <input
                            :value="activeTextSettings.fontWeight"
                            type="number"
                            min="100"
                            max="900"
                            step="100"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateTextSettings({ fontWeight: $event?.target?.value })"
                          >
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Line Height</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeTextSettings.lineHeight"
                            type="range"
                            min="0.8"
                            max="3"
                            step="0.05"
                            class="range-modern flex-1"
                            :style="selectedElementTextLineHeightFillStyle"
                            @input="onUpdateTextSettings({ lineHeight: $event?.target?.value })"
                          >
                          <input
                            :value="activeTextSettings.lineHeight"
                            type="number"
                            min="0.8"
                            max="3"
                            step="0.05"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateTextSettings({ lineHeight: $event?.target?.value })"
                          >
                          <span class="text-sm text-zinc-500">x</span>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Text Color</label>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                            :style="{ backgroundColor: selectedElementTextColorModel }"
                            @click="showElementTextColorPicker = !showElementTextColorPicker"
                          />
                          <input
                            :value="selectedElementTextColorModel"
                            type="text"
                            class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                            @input="onElementTextColorTextInput"
                            @blur="onElementTextColorTextBlur"
                          >
                          <button
                            type="button"
                            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                            @click="showElementTextColorPicker = !showElementTextColorPicker"
                          >
                            Pick
                          </button>
                        </div>
                        <div
                          v-if="showElementTextColorPicker"
                          class="mt-3"
                        >
                          <ModernColorPicker v-model="selectedElementTextColorModel" />
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Link Address</label>
                        <input
                          :value="activeTextSettings.linkAddress"
                          type="text"
                          class="w-full border rounded px-3 py-2 text-sm"
                          placeholder="https://example.com"
                          @input="onUpdateTextSettings({ linkAddress: $event?.target?.value })"
                        >
                      </div>

                      <label class="flex items-center justify-between gap-3">
                        <span class="text-sm text-zinc-700 font-medium">Open In New Tab</span>
                        <input
                          :checked="activeTextSettings.openInNewTab !== false"
                          type="checkbox"
                          class="h-4 w-4 rounded border-zinc-300 text-[#F59E0B] focus:ring-[#F59E0B]"
                          @change="onUpdateTextSettings({ openInNewTab: $event?.target?.checked })"
                        >
                      </label>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'bullet-list'"
                      class="space-y-4"
                    >
                      <div>
                        <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Bullet icon</label>
                        <PlatformaSelect v-model="selectedElement.props.bulletIcon" :options="bulletIconOptions" />
                      </div>
                      <div class="space-y-2">
                        <div v-for="(item, itemIndex) in selectedElement.props.items" :key="`bullet-setting-${itemIndex}`" class="flex items-center gap-2">
                          <input v-model="item.text" type="text" class="min-w-0 flex-1 rounded-md border border-zinc-300 px-3 py-2 text-sm" :placeholder="`Bullet ${itemIndex + 1}`">
                          <button type="button" class="rounded-md border border-red-200 px-2 py-2 text-xs font-semibold text-red-600 hover:bg-red-50" :disabled="selectedElement.props.items.length <= 1" @click="removeBulletItem(itemIndex)">Remove</button>
                        </div>
                        <button type="button" class="w-full rounded-md border border-dashed border-amber-400 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50" @click="addBulletItem">+ Add bullet</button>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <label class="text-xs font-semibold text-zinc-600">Text size<input v-model.number="selectedElement.props.bulletFontSize" type="number" min="10" max="72" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></label>
                        <label class="text-xs font-semibold text-zinc-600">Item spacing<input v-model.number="selectedElement.props.bulletGap" type="number" min="0" max="80" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></label>
                        <label class="text-xs font-semibold text-zinc-600">Icon color<input v-model="selectedElement.props.bulletIconColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                        <label class="text-xs font-semibold text-zinc-600">Text color<input v-model="selectedElement.props.bulletTextColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'share'"
                      class="space-y-4"
                    >
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Label<input v-model="selectedElement.props.shareLabel" type="text" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm normal-case tracking-normal text-zinc-800"></label>
                      <label class="flex items-center justify-between gap-3 text-sm font-medium text-zinc-700"><span>Show label</span><input v-model="selectedElement.props.shareShowLabel" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-amber-500"></label>
                      <div>
                        <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Share services</p>
                        <div class="grid grid-cols-2 gap-2">
                          <label v-for="network in shareNetworkOptions" :key="network.value" class="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-700">
                            <input type="checkbox" :checked="selectedElement.props.shareNetworks.includes(network.value)" class="h-4 w-4 rounded border-zinc-300 text-amber-500" @change="toggleShareNetwork(network.value, $event?.target?.checked)">
                            <span>{{ network.label }}</span>
                          </label>
                        </div>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <label class="text-xs font-semibold text-zinc-600">Alignment<select v-model="selectedElement.props.shareAlignment" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
                        <label class="text-xs font-semibold text-zinc-600">Shape<select v-model="selectedElement.props.shareButtonStyle" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"><option value="circle">Circle</option><option value="square">Rounded square</option></select></label>
                        <label class="text-xs font-semibold text-zinc-600">Button size<input v-model.number="selectedElement.props.shareButtonSize" type="number" min="28" max="72" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></label>
                        <label class="text-xs font-semibold text-zinc-600">Button gap<input v-model.number="selectedElement.props.shareButtonGap" type="number" min="0" max="48" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></label>
                        <label class="text-xs font-semibold text-zinc-600">Button color<input v-model="selectedElement.props.shareButtonColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                        <label class="text-xs font-semibold text-zinc-600">Icon color<input v-model="selectedElement.props.shareIconColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'optin-form'"
                      class="space-y-4"
                    >
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Headline<input v-model="selectedElement.props.optinHeadline" type="text" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm normal-case tracking-normal text-zinc-800"></label>
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Description<textarea v-model="selectedElement.props.optinDescription" rows="3" class="mt-1 w-full resize-y rounded-md border border-zinc-300 px-3 py-2 text-sm normal-case tracking-normal text-zinc-800" /></label>
                      <label class="group flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm font-semibold text-zinc-700">
                        <input v-model="selectedElement.props.optinShowLabels" type="checkbox" class="peer sr-only">
                        <span class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-zinc-300 bg-white shadow-sm transition peer-checked:border-[#2D8CFF] peer-checked:bg-[#2D8CFF] peer-focus-visible:ring-2 peer-focus-visible:ring-[#2D8CFF]/30 peer-focus-visible:ring-offset-2"><CheckIcon class="h-3.5 w-3.5 text-white" /></span>
                        <span>Show field labels</span>
                      </label>
                      <div>
                        <div class="mb-2 flex items-center justify-between"><p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Fields</p><button type="button" class="text-xs font-semibold text-blue-600 hover:text-blue-700" @click="addOptinField">+ Add field</button></div>
                        <div class="space-y-3">
                          <div v-for="(field, fieldIndex) in selectedElement.props.optinFields" :key="`optin-field-${fieldIndex}`" class="rounded-lg border border-zinc-200 p-3">
                            <div class="grid grid-cols-2 gap-2">
                              <PlatformaSelect v-model="field.key" class="min-w-0" :options="optinFieldTypeOptions" menu-placement="auto" @update:model-value="syncOptinFieldType(field)" />
                              <input v-model="field.label" type="text" class="rounded-md border border-zinc-300 px-2 py-2 text-xs" placeholder="Label">
                              <input v-model="field.placeholder" type="text" class="col-span-2 rounded-md border border-zinc-300 px-2 py-2 text-xs" placeholder="Placeholder">
                            </div>
                            <div class="mt-2 flex items-center justify-between gap-3">
                              <div class="flex flex-wrap items-center gap-3">
                                <label class="group flex cursor-pointer items-center gap-2 text-xs font-semibold text-zinc-600">
                                  <input v-model="field.showLabel" type="checkbox" class="peer sr-only">
                                  <span class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-zinc-300 bg-white shadow-sm transition peer-checked:border-[#2D8CFF] peer-checked:bg-[#2D8CFF] peer-focus-visible:ring-2 peer-focus-visible:ring-[#2D8CFF]/30 peer-focus-visible:ring-offset-2"><CheckIcon class="h-3.5 w-3.5 text-white" /></span>
                                  <span>Label</span>
                                </label>
                                <label class="group flex cursor-pointer items-center gap-2 text-xs font-semibold text-zinc-600" :class="field.key === 'email' ? 'cursor-default opacity-70' : ''">
                                  <input v-model="field.required" type="checkbox" :disabled="field.key === 'email'" class="peer sr-only">
                                  <span class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-zinc-300 bg-white shadow-sm transition peer-checked:border-[#2D8CFF] peer-checked:bg-[#2D8CFF] peer-focus-visible:ring-2 peer-focus-visible:ring-[#2D8CFF]/30 peer-focus-visible:ring-offset-2"><CheckIcon class="h-3.5 w-3.5 text-white" /></span>
                                  <span>Required</span>
                                </label>
                              </div>
                              <button v-if="field.key !== 'email'" type="button" class="text-xs font-semibold text-red-600" @click="removeOptinField(fieldIndex)">Remove</button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <label class="text-xs font-semibold text-zinc-600">Button label<input v-model="selectedElement.props.optinButtonLabel" type="text" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></label>
                        <label class="text-xs font-semibold text-zinc-600">Field spacing<input v-model.number="selectedElement.props.optinFieldGap" type="number" min="0" max="80" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></label>
                        <label class="text-xs font-semibold text-zinc-600">Input height<input v-model.number="selectedElement.props.optinInputHeight" type="number" min="36" max="88" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></label>
                        <label class="text-xs font-semibold text-zinc-600">Corners<input v-model.number="selectedElement.props.optinCornerRadius" type="number" min="0" max="48" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></label>
                      </div>
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Success message<input v-model="selectedElement.props.optinSuccessMessage" type="text" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm normal-case tracking-normal text-zinc-800"></label>
                      <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Redirect after opt-in<input v-model="selectedElement.props.optinRedirectUrl" type="text" placeholder="/thank-you or https://example.com" class="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm normal-case tracking-normal text-zinc-800"></label>
                      <label class="flex items-center justify-between gap-3 text-sm font-medium text-zinc-700"><span>Open redirect in a new tab</span><input v-model="selectedElement.props.optinRedirectNewTab" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-500"></label>
                      <div class="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <label class="flex items-center justify-between gap-3 text-sm font-semibold text-blue-800"><span>Send lead to email integration</span><input v-model="selectedElement.props.optinSendToIntegration" type="checkbox" :disabled="!selectedEmailAudienceProvider" class="h-4 w-4 rounded border-blue-300 text-blue-600"></label>
                        <p class="mt-1 text-xs text-blue-700">{{ selectedEmailAudienceProvider ? `Connected automation: ${selectedEmailAudienceProviderLabel}` : 'Connect and select an email integration in Creator Integrations to enable delivery.' }}</p>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <label class="text-xs font-semibold text-zinc-600">Label color<input v-model="selectedElement.props.optinLabelColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                        <label class="text-xs font-semibold text-zinc-600">Input background<input v-model="selectedElement.props.optinInputBackgroundColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                        <label class="text-xs font-semibold text-zinc-600">Input text<input v-model="selectedElement.props.optinInputTextColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                        <label class="text-xs font-semibold text-zinc-600">Input border<input v-model="selectedElement.props.optinInputBorderColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                        <label class="text-xs font-semibold text-zinc-600">Button background<input v-model="selectedElement.props.optinButtonBackgroundColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                        <label class="text-xs font-semibold text-zinc-600">Button text<input v-model="selectedElement.props.optinButtonTextColor" type="color" class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white p-1"></label>
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'image'"
                      class="space-y-3"
                    >
                      <div>
                        <p class="text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">
                          Preview
                        </p>
                        <div class="rounded-lg overflow-hidden border border-zinc-200 bg-zinc-100">
                          <img
                            v-if="resolveImageUrl(selectedElement?.props?.imageFileId || selectedElement?.props?.image_file_id || selectedElement?.props?.fileId || selectedElement?.props?.file_id, selectedElement?.props?.imageUrl || selectedElement?.props?.image_url, '')"
                            :src="resolveImageUrl(selectedElement?.props?.imageFileId || selectedElement?.props?.image_file_id || selectedElement?.props?.fileId || selectedElement?.props?.file_id, selectedElement?.props?.imageUrl || selectedElement?.props?.image_url, '')"
                            alt="Image preview"
                            class="w-full h-auto object-cover"
                          >
                          <div
                            v-else
                            class="h-28 flex items-center justify-center text-xs text-zinc-500"
                          >
                            No image selected
                          </div>
                        </div>
                      </div>

                      <div>
                        <p class="text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">
                          Image Source
                        </p>
                        <div class="space-y-2">
                          <button
                            type="button"
                            class="w-full rounded-md bg-[#2D8CFF] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1f77e6]"
                            @click="openImageMediaPicker"
                          >
                            Select From Media Content
                          </button>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Link Address</label>
                        <input
                          :value="activeImageSettings.imageLinkAddress"
                          type="text"
                          class="w-full border rounded px-3 py-2 text-sm"
                          placeholder="https://example.com"
                          @input="onUpdateImageSettings({ imageLinkAddress: $event?.target?.value })"
                        >
                      </div>

                      <label class="flex items-center justify-between gap-3">
                        <span class="text-sm text-zinc-700 font-medium">Open In New Tab</span>
                        <input
                          :checked="activeImageSettings.imageOpenInNewTab === true"
                          type="checkbox"
                          class="h-4 w-4 rounded border-zinc-300 text-[#F59E0B] focus:ring-[#F59E0B]"
                          @change="onUpdateImageSettings({ imageOpenInNewTab: $event?.target?.checked })"
                        >
                      </label>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">Alignment</label>
                        <div class="grid grid-cols-3 gap-2">
                          <button
                            v-for="alignOption in ['left', 'center', 'right']"
                            :key="`image-align-inline-${alignOption}`"
                            type="button"
                            class="rounded-md border px-2 py-1.5 text-sm capitalize transition-colors"
                            :class="activeImageSettings.imageAlign === alignOption
                              ? 'border-[#F59E0B] bg-[#F59E0B1F] text-[#B45309]'
                              : 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'"
                            @click="onUpdateImageSettings({ imageAlign: alignOption })"
                          >
                            {{ alignOption }}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Image Width</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeImageSettings.imageWidth"
                            type="range"
                            min="20"
                            max="100"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedElementImageWidthFillStyle"
                            @input="onUpdateImageSettings({ imageWidth: $event?.target?.value })"
                          >
                          <input
                            :value="activeImageSettings.imageWidth"
                            type="number"
                            min="20"
                            max="100"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateImageSettings({ imageWidth: $event?.target?.value })"
                          >
                          <span class="text-sm text-zinc-500">%</span>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Rounded Corners</label>
                        <div class="flex items-center gap-3">
                          <input
                            v-model.number="selectedElement.props.imageBorderRadius"
                            type="range"
                            min="0"
                            max="120"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedElementImageRadiusFillStyle"
                          >
                          <input
                            v-model.number="selectedElement.props.imageBorderRadius"
                            type="number"
                            min="0"
                            max="120"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                          >
                          <span class="text-sm text-zinc-500">px</span>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Shadow</label>
                        <div class="flex items-center gap-3">
                          <input
                            v-model.number="selectedElement.props.imageShadowStrength"
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedElementImageShadowFillStyle"
                          >
                          <input
                            v-model.number="selectedElement.props.imageShadowStrength"
                            type="number"
                            min="0"
                            max="100"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                          >
                          <span class="text-sm text-zinc-500">%</span>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'video'"
                      class="space-y-5"
                    >
                      <div>
                        <p class="text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">
                          Video Type
                        </p>
                        <PlatformaSelect
                          v-model="selectedVideoTypeModel"
                          :options="videoTypeOptions"
                          placeholder="Select video type"
                        />
                      </div>

                      <div>
                        <p class="text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">
                          Video Preview
                        </p>
                        <div class="rounded-lg overflow-hidden bg-zinc-100 aspect-video flex items-center justify-center border border-zinc-200">
                          <div
                            v-if="selectedVideoPreviewEmbedHtml"
                            class="w-full h-full [&_iframe]:w-full [&_iframe]:h-full"
                            v-html="selectedVideoPreviewEmbedHtml"
                          />
                          <iframe
                            v-else-if="selectedVideoPreviewIframeUrl"
                            :src="selectedVideoPreviewIframeUrl"
                            class="w-full h-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowfullscreen
                            loading="lazy"
                          />
                          <img
                            v-else-if="selectedVideoResolvedPreviewUrl"
                            :src="selectedVideoResolvedPreviewUrl"
                            alt="Video preview"
                            class="w-full h-full object-cover"
                            @error="onVideoPreviewImageError"
                          >
                          <p
                            v-else
                            class="text-xs text-zinc-500 px-3 text-center"
                          >
                            No video uploaded yet.
                          </p>
                        </div>
                        <div
                          v-if="selectedVideoShowReadySpinner"
                          class="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2"
                        >
                          <div class="flex items-center gap-2">
                            <span class="inline-block h-4 w-4 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin" />
                            <p class="text-xs font-medium text-blue-700">
                              {{ selectedVideoSpinnerMessage }}
                            </p>
                          </div>
                          <p
                            v-if="selectedVideoShowNavigateAwayMessage"
                            class="mt-2 text-[11px] leading-tight font-semibold text-red-500"
                          >
                            You can navigate away and continue working. Your video will display when completed.
                          </p>
                        </div>
                      </div>

                      <div>
                        <p class="text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">
                          Video Source
                        </p>
                        <div
                          v-if="selectedVideoType === 'custom_embed'"
                          class="space-y-2"
                        >
                          <textarea
                            :value="selectedVideoEmbedCodeModel"
                            rows="5"
                            class="w-full border rounded px-3 py-2 text-sm font-mono"
                            placeholder="<iframe src=&quot;...&quot;></iframe>"
                            @input="onUpdateVideoSettings({ videoEmbedCode: $event?.target?.value })"
                          />
                        </div>
                        <div
                          v-else-if="isVideoExternalProviderType(selectedVideoType)"
                          class="space-y-2"
                        >
                          <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                            {{ selectedVideoExternalUrlLabel }}
                          </label>
                          <input
                            :value="selectedVideoExternalUrlModel"
                            type="text"
                            class="w-full border rounded px-3 py-2 text-sm"
                            :placeholder="selectedVideoExternalUrlPlaceholder"
                            @input="onUpdateVideoSettings({ videoExternalUrl: $event?.target?.value })"
                          >
                        </div>
                        <div
                          v-else
                          class="space-y-2"
                        >
                          <button
                            type="button"
                            class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                            :disabled="activeVideoUploadActionsDisabled"
                            @click="openVideoMediaPicker(selectedVideoType)"
                          >
                            {{ selectedVideoMediaButtonLabel }}
                          </button>
                          <p class="text-xs text-zinc-500">
                            Choose an existing video or upload a new one in Media Content.
                          </p>
                        </div>
                      </div>

                      <div v-if="selectedVideoType === 'upload' || selectedVideoType === 'platforma_media'">
                        <p class="text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">
                          Thumbnail
                        </p>
                        <div class="rounded-lg overflow-hidden bg-zinc-100 aspect-video flex items-center justify-center mb-2 border border-zinc-200">
                          <img
                            v-if="selectedVideoResolvedThumbnailPreviewUrl"
                            :src="selectedVideoResolvedThumbnailPreviewUrl"
                            alt="Thumbnail preview"
                            class="w-full h-full object-cover"
                            @error="onVideoThumbnailPreviewImageError"
                          >
                          <p
                            v-else
                            class="text-xs text-zinc-500 px-3 text-center"
                          >
                            No thumbnail selected yet.
                          </p>
                        </div>
                        <button
                          type="button"
                          class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                          :disabled="activeVideoUploadActionsDisabled"
                          @click="onVideoThumbnailUploadRequest"
                        >
                          {{ selectedVideoThumbnailUploadButtonLabel }}
                        </button>
                      </div>

                      <div>
                        <p class="text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">
                          Player Control Color
                        </p>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                            :style="{ backgroundColor: selectedVideoControlColorModel }"
                            @click="showVideoControlColorPicker = !showVideoControlColorPicker"
                          />
                          <input
                            :value="selectedVideoControlColorModel"
                            type="text"
                            maxlength="7"
                            class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                            @input="onVideoControlColorTextInput"
                            @blur="onVideoControlColorTextBlur"
                          >
                          <button
                            type="button"
                            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                            @click="showVideoControlColorPicker = !showVideoControlColorPicker"
                          >
                            HEX
                          </button>
                        </div>
                        <div
                          v-if="showVideoControlColorPicker"
                          class="mt-3"
                        >
                          <ModernColorPicker v-model="selectedVideoControlColorModel" />
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Video Corner Radius</label>
                        <div class="flex items-center gap-3">
                          <input
                            v-model.number="selectedVideoBorderRadiusModel"
                            type="range"
                            min="0"
                            max="120"
                            step="1"
                            class="range-modern flex-1"
                          >
                          <input
                            v-model.number="selectedVideoBorderRadiusModel"
                            type="number"
                            min="0"
                            max="120"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                          >
                          <span class="text-sm text-zinc-500">px</span>
                        </div>
                      </div>

                      <div
                        v-if="activeVideoElementHasSource && (selectedVideoType === 'upload' || selectedVideoType === 'platforma_media')"
                        class="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2"
                      >
                        <p class="text-sm font-medium text-zinc-700 break-words [overflow-wrap:anywhere]">
                          {{ selectedVideoFileName || 'Uploaded video' }}
                        </p>
                        <p
                          v-if="selectedVideoFileMimeType"
                          class="text-xs text-zinc-500 mt-0.5"
                        >
                          {{ selectedVideoFileMimeType }}
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'button'"
                      class="space-y-3"
                    >
                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">On Click</label>
                        <select
                          :value="activeButtonSettings.buttonActionType"
                          class="w-full border rounded px-3 py-2 text-sm bg-white"
                          @change="onUpdateButtonSettings({ buttonActionType: $event?.target?.value })"
                        >
                          <option
                            v-for="option in buttonActionTypeOptions"
                            :key="`btn-action-${option.value}`"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </div>

                      <div v-if="activeButtonSettings.buttonActionType === 'go_to'">
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Destination</label>
                        <select
                          :value="activeButtonSettings.buttonGoToType"
                          class="w-full border rounded px-3 py-2 text-sm bg-white"
                          @change="onUpdateButtonSettings({ buttonGoToType: $event?.target?.value })"
                        >
                          <option
                            v-for="option in buttonGoToTypeOptions"
                            :key="`btn-goto-${option.value}`"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </div>

                      <div v-if="activeButtonSettings.buttonActionType === 'go_to' && activeButtonSettings.buttonGoToType === 'custom_link'">
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Link Address</label>
                        <input
                          :value="activeButtonSettings.buttonLinkAddress"
                          type="text"
                          class="w-full border rounded px-3 py-2 text-sm"
                          placeholder="https://example.com"
                          @input="onUpdateButtonSettings({ buttonLinkAddress: $event?.target?.value })"
                        >
                      </div>

                      <div v-if="activeButtonSettings.buttonActionType === 'go_to' && activeButtonSettings.buttonGoToType === 'feature_instance'">
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Feature Instance</label>
                        <select
                          :value="activeButtonSettings.buttonFeatureSlug || ''"
                          class="w-full border rounded px-3 py-2 text-sm bg-white"
                          @change="onUpdateButtonSettings({ buttonFeatureSlug: $event?.target?.value })"
                        >
                          <option value="">
                            Select feature instance
                          </option>
                          <option
                            v-for="option in buttonActionFeatureOptions"
                            :key="`btn-feature-${option.value}`"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </div>

                      <div
                        v-if="activeButtonSettings.buttonActionType === 'file_access'"
                        class="space-y-3"
                      >
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">File Access</label>
                        <button
                          type="button"
                          class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                          @click="onButtonFileUploadRequest"
                        >
                          Upload File (PDF, TXT, CSV)
                        </button>
                        <label class="block">
                          <span class="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Member Access</span>
                          <select
                            :value="activeButtonSettings.buttonFileAccessMode"
                            class="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-700"
                            @change="onUpdateButtonSettings({ buttonFileAccessMode: $event?.target?.value })"
                          >
                            <option value="view">View in Platforma only</option>
                            <option value="download">Allow file download</option>
                          </select>
                        </label>
                        <div
                          v-if="activeButtonFilePreview.fileId || activeButtonFilePreview.fileName || activeButtonFilePreview.fileUrl"
                          class="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2"
                        >
                          <p class="text-sm font-medium text-zinc-700 break-words [overflow-wrap:anywhere]">
                            {{ activeButtonFilePreview.fileName || 'Uploaded file' }}
                          </p>
                          <p
                            v-if="activeButtonFilePreview.fileMimeType"
                            class="text-xs text-zinc-500 mt-0.5"
                          >
                            {{ activeButtonFilePreview.fileMimeType }}
                          </p>
                          <a
                            v-if="activeButtonFilePreview.fileUrl"
                            :href="activeButtonFilePreview.fileUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex mt-2 text-xs font-semibold text-[#D97706] hover:text-[#B45309]"
                          >
                            Download File
                          </a>
                        </div>
                      </div>

                      <label
                        v-if="activeButtonSettings.buttonActionType === 'go_to'"
                        class="flex items-center justify-between gap-3"
                      >
                        <span class="text-sm text-zinc-700 font-medium">Open In New Tab</span>
                        <input
                          :checked="activeButtonSettings.buttonOpenInNewTab !== false"
                          type="checkbox"
                          class="h-4 w-4 rounded border-zinc-300 text-[#F59E0B] focus:ring-[#F59E0B]"
                          @change="onUpdateButtonSettings({ buttonOpenInNewTab: $event?.target?.checked })"
                        >
                      </label>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Button Background Color</label>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                            :style="{ backgroundColor: selectedButtonBackgroundColorModel }"
                            @click="showButtonBackgroundColorPicker = !showButtonBackgroundColorPicker"
                          />
                          <input
                            :value="selectedButtonBackgroundColorModel"
                            type="text"
                            class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                            @input="onButtonBackgroundColorTextInput"
                            @blur="onButtonBackgroundColorTextBlur"
                          >
                          <button
                            type="button"
                            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                            @click="showButtonBackgroundColorPicker = !showButtonBackgroundColorPicker"
                          >
                            Pick
                          </button>
                        </div>
                        <div
                          v-if="showButtonBackgroundColorPicker"
                          class="mt-3"
                        >
                          <ModernColorPicker v-model="selectedButtonBackgroundColorModel" />
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Button Text Color</label>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                            :style="{ backgroundColor: selectedButtonTextColorModel }"
                            @click="showButtonTextColorPicker = !showButtonTextColorPicker"
                          />
                          <input
                            :value="selectedButtonTextColorModel"
                            type="text"
                            class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                            @input="onButtonTextColorTextInput"
                            @blur="onButtonTextColorTextBlur"
                          >
                          <button
                            type="button"
                            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                            @click="showButtonTextColorPicker = !showButtonTextColorPicker"
                          >
                            Pick
                          </button>
                        </div>
                        <div
                          v-if="showButtonTextColorPicker"
                          class="mt-3"
                        >
                          <ModernColorPicker v-model="selectedButtonTextColorModel" />
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Text Size</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeButtonSettings.buttonTextSize"
                            type="range"
                            min="60"
                            max="160"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedButtonTextSizeFillStyle"
                            @input="onUpdateButtonSettings({ buttonTextSize: $event?.target?.value })"
                          >
                          <input
                            :value="activeButtonSettings.buttonTextSize"
                            type="number"
                            min="60"
                            max="160"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateButtonSettings({ buttonTextSize: $event?.target?.value })"
                          >
                          <span class="text-sm text-zinc-500">%</span>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Text Font Size</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeButtonSettings.buttonFontSize"
                            type="range"
                            min="10"
                            max="56"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedButtonFontSizeFillStyle"
                            @input="onUpdateButtonSettings({ buttonFontSize: $event?.target?.value })"
                          >
                          <input
                            :value="activeButtonSettings.buttonFontSize"
                            type="number"
                            min="10"
                            max="56"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateButtonSettings({ buttonFontSize: $event?.target?.value })"
                          >
                          <span class="text-sm text-zinc-500">px</span>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Button Width</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeButtonSettings.buttonWidth"
                            type="range"
                            min="20"
                            max="100"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedButtonWidthFillStyle"
                            @input="onUpdateButtonSettings({ buttonWidth: $event?.target?.value })"
                          >
                          <input
                            :value="activeButtonSettings.buttonWidth"
                            type="number"
                            min="20"
                            max="100"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateButtonSettings({ buttonWidth: $event?.target?.value })"
                          >
                          <span class="text-sm text-zinc-500">%</span>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Button Padding X</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeButtonSettings.buttonPaddingX"
                            type="range"
                            min="4"
                            max="80"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedButtonPaddingXFillStyle"
                            @input="onUpdateButtonSettings({ buttonPaddingX: $event?.target?.value })"
                          >
                          <input
                            :value="activeButtonSettings.buttonPaddingX"
                            type="number"
                            min="4"
                            max="80"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateButtonSettings({ buttonPaddingX: $event?.target?.value })"
                          >
                          <span class="text-sm text-zinc-500">px</span>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Button Padding Y</label>
                        <div class="flex items-center gap-3">
                          <input
                            :value="activeButtonSettings.buttonPaddingY"
                            type="range"
                            min="4"
                            max="56"
                            step="1"
                            class="range-modern flex-1"
                            :style="selectedButtonPaddingYFillStyle"
                            @input="onUpdateButtonSettings({ buttonPaddingY: $event?.target?.value })"
                          >
                          <input
                            :value="activeButtonSettings.buttonPaddingY"
                            type="number"
                            min="4"
                            max="56"
                            class="w-20 border rounded px-2 py-1.5 text-sm"
                            @input="onUpdateButtonSettings({ buttonPaddingY: $event?.target?.value })"
                          >
                          <span class="text-sm text-zinc-500">px</span>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="isMemberAuthFormType(selectedElement?.type)"
                      class="space-y-3"
                    >
                      <div class="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                        {{ isMemberCreateFreeAccountFormType(selectedElement?.type)
                          ? 'Create Free Account form behavior is locked for this template. You can position the wrapper and set submit button styles.'
                          : 'Login form behavior is locked for this template. You can position the wrapper and set accent colors.' }}
                      </div>

                      <div>
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Submit Button Color</label>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                            :style="{ backgroundColor: selectedLoginSubmitButtonColorModel }"
                            @click="showLoginSubmitButtonColorPicker = !showLoginSubmitButtonColorPicker"
                          />
                          <input
                            :value="selectedLoginSubmitButtonColorModel"
                            type="text"
                            class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                            @input="onLoginSubmitButtonColorTextInput"
                            @blur="onLoginSubmitButtonColorTextBlur"
                          >
                          <button
                            type="button"
                            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                            @click="showLoginSubmitButtonColorPicker = !showLoginSubmitButtonColorPicker"
                          >
                            Pick
                          </button>
                        </div>
                        <div
                          v-if="showLoginSubmitButtonColorPicker"
                          class="mt-3"
                        >
                          <ModernColorPicker v-model="selectedLoginSubmitButtonColorModel" />
                        </div>
                      </div>

                      <div v-if="isMemberLoginFormType(selectedElement?.type)">
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Forgot Password Link Color</label>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                            :style="{ backgroundColor: selectedLoginForgotPasswordColorModel }"
                            @click="showLoginForgotPasswordColorPicker = !showLoginForgotPasswordColorPicker"
                          />
                          <input
                            :value="selectedLoginForgotPasswordColorModel"
                            type="text"
                            class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                            @input="onLoginForgotPasswordColorTextInput"
                            @blur="onLoginForgotPasswordColorTextBlur"
                          >
                          <button
                            type="button"
                            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                            @click="showLoginForgotPasswordColorPicker = !showLoginForgotPasswordColorPicker"
                          >
                            Pick
                          </button>
                        </div>
                        <div
                          v-if="showLoginForgotPasswordColorPicker"
                          class="mt-3"
                        >
                          <ModernColorPicker v-model="selectedLoginForgotPasswordColorModel" />
                        </div>
                      </div>

                      <div v-if="isMemberLoginFormType(selectedElement?.type)">
                        <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Remember Checkbox Color</label>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                            :style="{ backgroundColor: selectedLoginRememberCheckboxColorModel }"
                            @click="showLoginRememberCheckboxColorPicker = !showLoginRememberCheckboxColorPicker"
                          />
                          <input
                            :value="selectedLoginRememberCheckboxColorModel"
                            type="text"
                            class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                            @input="onLoginRememberCheckboxColorTextInput"
                            @blur="onLoginRememberCheckboxColorTextBlur"
                          >
                          <button
                            type="button"
                            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                            @click="showLoginRememberCheckboxColorPicker = !showLoginRememberCheckboxColorPicker"
                          >
                            Pick
                          </button>
                        </div>
                        <div
                          v-if="showLoginRememberCheckboxColorPicker"
                          class="mt-3"
                        >
                          <ModernColorPicker v-model="selectedLoginRememberCheckboxColorModel" />
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'member-pricing-plans'"
                      class="space-y-3"
                    >
                      <div class="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                        Membership pricing cards are dynamic from Membership Plan Tiers. You can position the wrapper, but plan content is not editable here.
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'member-get-access-form'"
                      class="space-y-3"
                    >
                      <div class="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                        Get Access form behavior is locked for this template. You can position the wrapper, but form fields and checkout logic are not editable here.
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'checkout-order-form'"
                      class="space-y-3"
                    >
                      <div class="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-800">
                        This Order Form stays securely connected to the cart product. Its flow, customer fields, shipping, consent, and optional order bump are controlled here.
                      </div>
                      <details open class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Form card</summary>
                        <div class="mt-3 space-y-3">
                          <div class="grid grid-cols-2 gap-2">
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Background</label><PlatformaColorField :model-value="selectedElement.props.cardTransparent === true ? 'transparent' : selectedElement.props.cardBackgroundColor" :enable-opacity="true" @update:model-value="onCheckoutCardBackgroundColorChange" /></div>
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Border</label><PlatformaColorField v-model="selectedElement.props.cardBorderColor" :enable-opacity="true" /></div>
                          </div>
                          <div class="grid grid-cols-2 gap-2">
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Border width</label><input v-model.number="selectedElement.props.cardBorderWidth" type="number" min="0" max="8" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div>
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Corner radius</label><input v-model.number="selectedElement.props.cardCornerRadius" type="number" min="0" max="48" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div>
                          </div>
                          <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Shadow</label><PlatformaSelect v-model="selectedElement.props.cardShadow" :options="checkoutCardShadowOptions" /></div>
                          <div class="grid grid-cols-2 gap-2">
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Horizontal padding</label><input v-model.number="selectedElement.props.cardPaddingX" type="number" min="0" max="96" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div>
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Vertical padding</label><input v-model.number="selectedElement.props.cardPaddingY" type="number" min="0" max="96" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div>
                          </div>
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Show trust badges</span><input v-model="selectedElement.props.showTrustBadges" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                        </div>
                      </details>
                      <details open class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Flow and copy</summary>
                        <div class="mt-3 space-y-3">
                          <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Order flow</label><PlatformaSelect :model-value="selectedElement.props.checkoutMode" :options="checkoutOrderFlowOptions" @update:model-value="onCheckoutOrderFlowChange" /></div>
                          <div v-if="selectedElement.props.checkoutMode === 'two_step'"><label class="mb-1 block text-xs font-semibold text-zinc-600">Editor preview</label><PlatformaSelect v-model="selectedElement.props.editorPreviewStep" :options="checkoutPreviewStepOptions" /></div>
                          <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Form heading</label><input v-model="selectedElement.props.heading" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div>
                          <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Contact step</label><input v-model="selectedElement.props.contactStepLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Payment step</label><input v-model="selectedElement.props.paymentStepLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div></div>
                          <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Step 1 button</label><input v-model="selectedElement.props.continueButtonLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Purchase button</label><input v-model="selectedElement.props.buttonLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div></div>
                        </div>
                      </details>

                      <details open class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Product selector</summary>
                        <div class="mt-3 space-y-3">
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Show product or payment options</span><input v-model="selectedElement.props.productSelectorEnabled" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          <template v-if="selectedElement.props.productSelectorEnabled">
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Selector heading</label><input v-model="selectedElement.props.productSelectorHeading" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Select a payment plan"></div>
                            <div v-for="(option, optionIndex) in selectedElement.props.productSelectorOptions" :key="option.id" class="space-y-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                              <div class="flex items-center justify-between gap-3"><strong class="text-xs text-zinc-700">Option {{ optionIndex + 1 }}</strong><button v-if="selectedElement.props.productSelectorOptions.length > 1" type="button" class="rounded-md px-2 py-1 text-xs font-bold text-red-600 hover:bg-red-50" @click="removeCheckoutProductOption(optionIndex)">Remove</button></div>
                              <div v-if="optionIndex === 0" class="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5"><p class="text-[10px] font-extrabold uppercase tracking-wide text-emerald-700">This checkout product</p><strong class="mt-1 block text-sm text-zinc-900">{{ option.title || checkoutCurrentProduct?.productName || 'Current product' }}</strong><span class="mt-1 block text-xs text-zinc-500">{{ formatCheckoutProductPrice(option) }}</span></div>
                              <div v-else><label class="mb-1 block text-[11px] font-semibold text-zinc-600">Existing cart product</label><PlatformaSelect :model-value="option.productPublicId" :options="checkoutProductSelectorCatalogOptions" placeholder="Choose a product" @update:model-value="updateCheckoutProductOption(option, $event)" /></div>
                            </div>
                            <button type="button" class="w-full rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2 text-xs font-extrabold text-blue-700 hover:bg-blue-100" @click="addCheckoutProductOption">+ Add another option</button>
                            <p class="text-[11px] leading-5 text-zinc-500">The first option is always the product this checkout belongs to. Additional options use the selected cart product’s saved name, billing cadence, price, processor, and fulfillment.</p>
                          </template>
                        </div>
                      </details>

                      <details open class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Customer fields</summary>
                        <div class="mt-3 space-y-3">
                          <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">First name label</label><input v-model="selectedElement.props.firstNameLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Last name label</label><input v-model="selectedElement.props.lastNameLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div></div>
                          <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Email label</label><input v-model="selectedElement.props.emailLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div>
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Ask for company</span><input v-model="selectedElement.props.collectCompany" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          <input v-if="selectedElement.props.collectCompany" v-model="selectedElement.props.companyLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Company label">
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Ask for phone number</span><input v-model="selectedElement.props.collectPhone" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          <template v-if="selectedElement.props.collectPhone">
                            <input v-model="selectedElement.props.phoneLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Phone label">
                            <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Require phone number</span><input v-model="selectedElement.props.phoneRequired" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                            <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Show SMS/Twilio consent</span><input v-model="selectedElement.props.smsConsentEnabled" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                            <textarea v-if="selectedElement.props.smsConsentEnabled" v-model="selectedElement.props.smsConsentLabel" rows="3" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
                          </template>
                          <div class="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                            <p class="mb-3 text-[11px] font-bold uppercase tracking-wide text-zinc-500">Field styling</p>
                            <div class="grid grid-cols-2 gap-2">
                              <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Background</label><PlatformaColorField v-model="selectedElement.props.inputBackgroundColor" :enable-opacity="true" /></div>
                              <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Text</label><PlatformaColorField v-model="selectedElement.props.inputTextColor" /></div>
                              <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Border</label><PlatformaColorField v-model="selectedElement.props.inputBorderColor" :enable-opacity="true" /></div>
                              <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Labels</label><PlatformaColorField v-model="selectedElement.props.inputLabelColor" /></div>
                            </div>
                            <div class="mt-3 grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Field height</label><input v-model.number="selectedElement.props.inputHeight" type="number" min="38" max="72" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Corner radius</label><input v-model.number="selectedElement.props.formCornerRadius" type="number" min="0" max="32" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div></div>
                            <label class="mt-3 flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Field shadow</span><input v-model="selectedElement.props.inputShadow" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          </div>
                        </div>
                      </details>

                      <details class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Shipping address</summary>
                        <div class="mt-3 space-y-3">
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Collect shipping address</span><input v-model="selectedElement.props.collectShippingAddress" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          <template v-if="selectedElement.props.collectShippingAddress">
                            <input v-model="selectedElement.props.shippingHeading" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Shipping heading">
                            <input v-model="selectedElement.props.addressLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Street address label">
                            <div class="grid grid-cols-2 gap-2"><input v-model="selectedElement.props.cityLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="City"><input v-model="selectedElement.props.stateLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="State / province"></div>
                            <div class="grid grid-cols-2 gap-2"><input v-model="selectedElement.props.postalCodeLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Postal code"><input v-model="selectedElement.props.countryLabel" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Country"></div>
                          </template>
                        </div>
                      </details>

                      <details class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Secure badge</summary>
                        <div class="mt-3 space-y-3">
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Show secure badge</span><input v-model="selectedElement.props.showSecureBadge" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          <template v-if="selectedElement.props.showSecureBadge">
                            <input v-model="selectedElement.props.secureBadgeText" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Secure checkout">
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Badge icon</label><PlatformaSelect v-model="selectedElement.props.secureBadgeIcon" :options="checkoutSecureBadgeIconOptions" /></div>
                            <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Badge color</label><PlatformaColorField v-model="selectedElement.props.secureBadgeTextColor" /></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Badge background</label><PlatformaColorField v-model="selectedElement.props.secureBadgeBackgroundColor" :enable-opacity="true" /></div></div>
                          </template>
                        </div>
                      </details>

                      <details class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Purchase button</summary>
                        <div class="mt-3 space-y-3">
                          <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Background</label><PlatformaColorField v-model="selectedElement.props.buttonBackgroundColor" :enable-opacity="true" /></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Text</label><PlatformaColorField v-model="selectedElement.props.buttonTextColor" /></div></div>
                          <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Font weight</label><PlatformaSelect v-model="selectedElement.props.buttonFontWeight" :options="checkoutButtonWeightOptions" /></div>
                          <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Text size</label><input v-model.number="selectedElement.props.buttonFontSize" type="number" min="11" max="24" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Corner radius</label><input v-model.number="selectedElement.props.buttonCornerRadius" type="number" min="0" max="32" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div></div>
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Button shadow</span><input v-model="selectedElement.props.buttonShadow" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          <input v-model="selectedElement.props.buttonSubtext" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Optional text below the button">
                        </div>
                      </details>

                      <details class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Order bump</summary>
                        <div class="mt-3 space-y-3">
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Enable order bump</span><input :checked="selectedElement.props.orderBumpEnabled" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600" :disabled="checkoutCurrentProduct && !['one_time', 'free_forever'].includes(checkoutCurrentProduct.paymentType)" @change="onOrderBumpToggle"></label>
                          <p v-if="checkoutCurrentProduct && !['one_time', 'free_forever'].includes(checkoutCurrentProduct.paymentType)" class="rounded-md bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">Order bumps are available for one-time and free main products. Subscription add-ons need their own recurring pricing flow.</p>
                          <template v-if="selectedElement.props.orderBumpEnabled">
                            <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Add-on product</label><PlatformaSelect v-model="selectedElement.props.orderBumpProductPublicId" :options="checkoutBumpProductOptions" placeholder="Select a one-time product" allow-empty /><p v-if="checkoutBumpProductsLoading" class="mt-1 text-[11px] text-zinc-400">Loading products…</p></div>
                            <input v-model="selectedElement.props.orderBumpHeadline" type="text" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Yes, add this to my order">
                            <textarea v-model="selectedElement.props.orderBumpDescription" rows="2" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" placeholder="Short add-on description" />
                            <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Selected by default</span><input v-model="selectedElement.props.orderBumpSelectedByDefault" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          </template>
                        </div>
                      </details>

                      <details class="rounded-lg border border-zinc-200 bg-white p-3">
                        <summary class="cursor-pointer text-xs font-bold uppercase tracking-wide text-zinc-700">Terms and style</summary>
                        <div class="mt-3 space-y-3">
                          <label class="flex items-center justify-between gap-3 text-sm font-semibold text-zinc-700"><span>Require terms acceptance</span><input v-model="selectedElement.props.termsEnabled" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600"></label>
                          <textarea v-if="selectedElement.props.termsEnabled" v-model="selectedElement.props.termsLabel" rows="2" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm" />
                          <div><label class="mb-1 block text-xs font-semibold text-zinc-600">Accent color</label><PlatformaColorField v-model="selectedElement.props.accentColor" /></div>
                        </div>
                      </details>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'meet-scheduler'"
                      class="space-y-3"
                    >
                      <div class="rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-xs leading-5 text-blue-800">
                        This element uses the Meet branding, availability, duration, and payment settings that members see.
                      </div>
                      <button
                        type="button"
                        class="flex w-full items-center justify-center gap-2 rounded-lg bg-[#2D8CFF] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1b6ad1]"
                        @click="emit('feature-action', 'edit-scheduler')"
                      >
                        <CalendarDaysIcon class="h-4 w-4" />
                        Open Scheduler Designer
                      </button>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'member-approval-form'"
                      class="space-y-3"
                    >
                      <div class="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                        Member approval request behavior is locked for this template. You can position the wrapper, but form fields and submission behavior are not editable here.
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'member-get-access-success'"
                      class="space-y-3"
                    >
                      <div class="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                        Get Access Success behavior is locked for this template. Countdown, auto-login fallback, and redirect logic are not editable here.
                      </div>
                    </div>

                    <div
                      v-if="String(selectedElement.type || '').toLowerCase() === 'quiz-result-history'"
                      class="space-y-3"
                    >
                      <div class="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs leading-5 text-zinc-600">
                        This is the dynamic member Quiz results list. Move and size the wrapper as one element; each member's individual result rows are generated automatically.
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Color</label>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                          :style="{ backgroundColor: elementBackgroundColorModel }"
                          @click="showElementBackgroundColorPicker = !showElementBackgroundColorPicker"
                        />
                        <input
                          :value="elementBackgroundColorModel"
                          type="text"
                          class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                          @input="onElementBackgroundColorTextInput"
                          @blur="onElementBackgroundColorTextBlur"
                        >
                        <button
                          type="button"
                          class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                          @click="showElementBackgroundColorPicker = !showElementBackgroundColorPicker"
                        >
                          Pick
                        </button>
                      </div>
                      <div
                        v-if="showElementBackgroundColorPicker"
                        class="mt-3"
                      >
                        <ModernColorPicker
                          v-model="elementBackgroundColorModel"
                          :allow-transparent="true"
                          :enable-opacity="true"
                        />
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Width</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedElement.props.elementWidthPercent"
                          type="range"
                          min="10"
                          max="100"
                          step="1"
                          class="range-modern flex-1"
                          :style="elementWidthFillStyle"
                        >
                        <input
                          v-model.number="selectedElement.props.elementWidthPercent"
                          type="number"
                          min="10"
                          max="100"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">%</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Height</label>
                      <div class="mb-2 inline-flex rounded-md border border-zinc-300 p-0.5">
                        <button
                          type="button"
                          class="rounded px-2 py-1 text-xs font-medium transition"
                          :class="(selectedElement.props.elementHeightUnit || 'px') === 'px' ? 'bg-amber-50 text-[#B45309] border border-[#F59E0B]' : 'text-zinc-600 hover:bg-zinc-50 border border-transparent'"
                          @click="setElementHeightUnit('px')"
                        >
                          PX
                        </button>
                        <button
                          type="button"
                          class="rounded px-2 py-1 text-xs font-medium transition"
                          :class="(selectedElement.props.elementHeightUnit || 'px') === 'vh' ? 'bg-amber-50 text-[#B45309] border border-[#F59E0B]' : 'text-zinc-600 hover:bg-zinc-50 border border-transparent'"
                          @click="setElementHeightUnit('vh')"
                        >
                          VH
                        </button>
                      </div>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedElement.props.elementHeight"
                          type="range"
                          :min="elementHeightRange.min"
                          :max="elementHeightRange.max"
                          step="1"
                          class="range-modern flex-1"
                          :style="elementHeightFillStyle"
                        >
                        <input
                          v-model.number="selectedElement.props.elementHeight"
                          type="number"
                          :min="elementHeightRange.min"
                          :max="elementHeightRange.max"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">{{ (selectedElement.props.elementHeightUnit || 'px').toLowerCase() }}</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Padding X</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedElement.props.elementPaddingX"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="elementPaddingXFillStyle"
                        >
                        <input
                          v-model.number="selectedElement.props.elementPaddingX"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Padding Y</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedElement.props.elementPaddingY"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="elementPaddingYFillStyle"
                        >
                        <input
                          v-model.number="selectedElement.props.elementPaddingY"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Margin Y</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedElement.props.elementMargin"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="elementMarginFillStyle"
                        >
                        <input
                          v-model.number="selectedElement.props.elementMargin"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">Alignment</label>
                      <div class="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="getSelectedElementAlignment() === 'left' ? 'border-[#F59E0B] bg-amber-50 text-[#B45309]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="setSelectedElementAlignment('left')"
                        >
                          Left
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="getSelectedElementAlignment() === 'center' ? 'border-[#F59E0B] bg-amber-50 text-[#B45309]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="setSelectedElementAlignment('center')"
                        >
                          Center
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="getSelectedElementAlignment() === 'right' ? 'border-[#F59E0B] bg-amber-50 text-[#B45309]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="setSelectedElementAlignment('right')"
                        >
                          Right
                        </button>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else-if="selectedColumn">
                  <div class="space-y-4">
                    <div class="text-xs text-zinc-600">
                      Column settings
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Column Width</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedColumnWidthModel"
                          type="range"
                          min="5"
                          max="100"
                          step="1"
                          class="range-modern flex-1"
                          :style="selectedColumnWidthFillStyle"
                        >
                        <input
                          v-model.number="selectedColumnWidthModel"
                          type="number"
                          min="5"
                          max="100"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">%</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Color</label>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                          :style="{ backgroundColor: columnBackgroundColorModel }"
                          @click="showColumnBackgroundColorPicker = !showColumnBackgroundColorPicker"
                        />
                        <input
                          :value="columnBackgroundColorModel"
                          type="text"
                          class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                          @input="onColumnBackgroundColorTextInput"
                          @blur="onColumnBackgroundColorTextBlur"
                        >
                        <button
                          type="button"
                          class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                          @click="showColumnBackgroundColorPicker = !showColumnBackgroundColorPicker"
                        >
                          Pick
                        </button>
                      </div>
                      <div
                        v-if="showColumnBackgroundColorPicker"
                        class="mt-3"
                      >
                        <ModernColorPicker
                          v-model="columnBackgroundColorModel"
                          :allow-transparent="true"
                          :enable-opacity="true"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Image</label>
                      <div class="rounded-lg overflow-hidden border border-zinc-200 bg-zinc-100">
                        <img
                          v-if="selectedColumnBackgroundPreview"
                          :src="selectedColumnBackgroundPreview"
                          alt="Column background"
                          class="w-full h-28 object-cover"
                        >
                        <div
                          v-else
                          class="h-28 flex items-center justify-center text-xs text-zinc-500"
                        >
                          No image selected
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="flex-1 rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                          @click="onColumnBackgroundUploadRequest"
                        >
                          Upload Image
                        </button>
                        <button
                          type="button"
                          class="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50"
                          @click="onClearColumnBackground"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Fit</label>
                      <select
                        v-model="selectedColumn.styleEntry.backgroundSize"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="cover">
                          Cover
                        </option>
                        <option value="contain">
                          Contain
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Position</label>
                      <select
                        v-model="selectedColumn.styleEntry.backgroundPosition"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="center">
                          Center
                        </option>
                        <option value="top">
                          Top
                        </option>
                        <option value="bottom">
                          Bottom
                        </option>
                        <option value="left">
                          Left
                        </option>
                        <option value="right">
                          Right
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Repeat</label>
                      <select
                        v-model="selectedColumn.styleEntry.backgroundRepeat"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="no-repeat">
                          No Repeat
                        </option>
                        <option value="repeat">
                          Repeat
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Height</label>
                      <div class="mb-2 inline-flex rounded-md border border-zinc-300 p-0.5">
                        <button
                          type="button"
                          class="rounded px-2 py-1 text-xs font-medium transition"
                          :class="(selectedColumn.styleEntry.heightUnit || 'px') === 'px' ? 'bg-blue-50 text-[#1D4ED8] border border-[#2D8CFF]' : 'text-zinc-600 hover:bg-zinc-50 border border-transparent'"
                          @click="setColumnHeightUnit('px')"
                        >
                          PX
                        </button>
                        <button
                          type="button"
                          class="rounded px-2 py-1 text-xs font-medium transition"
                          :class="(selectedColumn.styleEntry.heightUnit || 'px') === 'vh' ? 'bg-blue-50 text-[#1D4ED8] border border-[#2D8CFF]' : 'text-zinc-600 hover:bg-zinc-50 border border-transparent'"
                          @click="setColumnHeightUnit('vh')"
                        >
                          VH
                        </button>
                      </div>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedColumn.styleEntry.height"
                          type="range"
                          :min="columnHeightRange.min"
                          :max="columnHeightRange.max"
                          step="1"
                          class="range-modern flex-1"
                          :style="columnHeightFillStyle"
                        >
                        <input
                          v-model.number="selectedColumn.styleEntry.height"
                          type="number"
                          :min="columnHeightRange.min"
                          :max="columnHeightRange.max"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">{{ (selectedColumn.styleEntry.heightUnit || 'px').toLowerCase() }}</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Padding X</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedColumn.styleEntry.paddingX"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="buildRangeFillStyle(selectedColumn.styleEntry.paddingX ?? 0, 0, 240, '#2D8CFF', '#3f3f46')"
                        >
                        <input
                          v-model.number="selectedColumn.styleEntry.paddingX"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Padding Y</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedColumn.styleEntry.paddingY"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="buildRangeFillStyle(selectedColumn.styleEntry.paddingY ?? 0, 0, 240, '#2D8CFF', '#3f3f46')"
                        >
                        <input
                          v-model.number="selectedColumn.styleEntry.paddingY"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Content Horizontal</label>
                      <div class="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="(selectedColumn.styleEntry.contentAlign || 'start') === 'start' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedColumn.styleEntry.contentAlign = 'start'"
                        >
                          Left
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="(selectedColumn.styleEntry.contentAlign || 'start') === 'center' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedColumn.styleEntry.contentAlign = 'center'"
                        >
                          Center
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="(selectedColumn.styleEntry.contentAlign || 'start') === 'end' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedColumn.styleEntry.contentAlign = 'end'"
                        >
                          Right
                        </button>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Content Vertical</label>
                      <div class="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="(selectedColumn.styleEntry.contentJustify || 'start') === 'start' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedColumn.styleEntry.contentJustify = 'start'"
                        >
                          Top
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="(selectedColumn.styleEntry.contentJustify || 'start') === 'center' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedColumn.styleEntry.contentJustify = 'center'"
                        >
                          Center
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="(selectedColumn.styleEntry.contentJustify || 'start') === 'end' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedColumn.styleEntry.contentJustify = 'end'"
                        >
                          Bottom
                        </button>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else-if="selectedRow">
                  <div class="space-y-4">
                    <div class="text-xs text-zinc-600">
                      Row settings
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Columns</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedRow.props.rowColumnCount"
                          type="range"
                          min="1"
                          max="6"
                          step="1"
                          class="range-modern flex-1"
                          :style="rowColumnsFillStyle"
                        >
                        <input
                          v-model.number="selectedRow.props.rowColumnCount"
                          type="number"
                          min="1"
                          max="6"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Column Gutter</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedRow.props.rowColumnGutter"
                          type="range"
                          min="0"
                          max="120"
                          step="1"
                          class="range-modern flex-1"
                          :style="rowGutterFillStyle"
                        >
                        <input
                          v-model.number="selectedRow.props.rowColumnGutter"
                          type="number"
                          min="0"
                          max="120"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>
                    <label
                      v-if="Number(selectedRow.props.rowColumnCount || 1) > 1"
                      class="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-700"
                    >
                      <span>Stack columns on {{ editorViewportMode }}</span>
                      <input v-model="selectedRow.props.stackColumns" type="checkbox" class="h-4 w-4 rounded border-zinc-300 text-blue-600">
                    </label>
                    <div class="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-3">
                      <p class="text-[11px] font-bold uppercase tracking-wide text-zinc-500">Row frame</p>
                      <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Border</label><PlatformaColorField v-model="selectedRow.props.rowBorderColor" :enable-opacity="true" /></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Border width</label><input v-model.number="selectedRow.props.rowBorderWidth" type="number" min="0" max="8" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div></div>
                      <div class="grid grid-cols-2 gap-2"><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Corners</label><input v-model.number="selectedRow.props.rowBorderRadius" type="number" min="0" max="120" class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"></div><div><label class="mb-1 block text-xs font-semibold text-zinc-600">Shadow</label><PlatformaSelect v-model="selectedRow.props.rowShadow" :options="rowShadowOptions" /></div></div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Color</label>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                          :style="{ backgroundColor: rowBackgroundColorModel }"
                          @click="showRowBackgroundColorPicker = !showRowBackgroundColorPicker"
                        />
                        <input
                          :value="rowBackgroundColorModel"
                          type="text"
                          class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                          @input="onRowBackgroundColorTextInput"
                          @blur="onRowBackgroundColorTextBlur"
                        >
                        <button
                          type="button"
                          class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                          @click="showRowBackgroundColorPicker = !showRowBackgroundColorPicker"
                        >
                          Pick
                        </button>
                      </div>
                      <div
                        v-if="showRowBackgroundColorPicker"
                        class="mt-3"
                      >
                        <ModernColorPicker
                          v-model="rowBackgroundColorModel"
                          :allow-transparent="true"
                          :enable-opacity="true"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Image</label>
                      <div class="rounded-lg overflow-hidden border border-zinc-200 bg-zinc-100">
                        <img
                          v-if="selectedRowBackgroundPreview"
                          :src="selectedRowBackgroundPreview"
                          alt="Row background"
                          class="w-full h-28 object-cover"
                        >
                        <div
                          v-else
                          class="h-28 flex items-center justify-center text-xs text-zinc-500"
                        >
                          No image selected
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="flex-1 rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                          @click="onRowBackgroundUploadRequest"
                        >
                          Upload Image
                        </button>
                        <button
                          type="button"
                          class="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50"
                          @click="onClearRowBackground"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Fit</label>
                      <select
                        v-model="selectedRow.props.rowBackgroundSize"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="cover">
                          Cover
                        </option>
                        <option value="contain">
                          Contain
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Position</label>
                      <select
                        v-model="selectedRow.props.rowBackgroundPosition"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="center">
                          Center
                        </option>
                        <option value="top">
                          Top
                        </option>
                        <option value="bottom">
                          Bottom
                        </option>
                        <option value="left">
                          Left
                        </option>
                        <option value="right">
                          Right
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Repeat</label>
                      <select
                        v-model="selectedRow.props.rowBackgroundRepeat"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="no-repeat">
                          No Repeat
                        </option>
                        <option value="repeat">
                          Repeat
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Width</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedRow.props.rowWidthPercent"
                          type="range"
                          min="10"
                          max="100"
                          step="1"
                          class="range-modern flex-1"
                          :style="rowWidthFillStyle"
                        >
                        <input
                          v-model.number="selectedRow.props.rowWidthPercent"
                          type="number"
                          min="10"
                          max="100"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">%</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Height</label>
                      <div class="mb-2 inline-flex rounded-md border border-zinc-300 p-0.5">
                        <button
                          type="button"
                          class="rounded px-2 py-1 text-xs font-medium transition"
                          :class="(selectedRow.props.rowHeightUnit || 'px') === 'px' ? 'bg-blue-50 text-[#1D4ED8] border border-[#2D8CFF]' : 'text-zinc-600 hover:bg-zinc-50 border border-transparent'"
                          @click="setRowHeightUnit('px')"
                        >
                          PX
                        </button>
                        <button
                          type="button"
                          class="rounded px-2 py-1 text-xs font-medium transition"
                          :class="(selectedRow.props.rowHeightUnit || 'px') === 'vh' ? 'bg-blue-50 text-[#1D4ED8] border border-[#2D8CFF]' : 'text-zinc-600 hover:bg-zinc-50 border border-transparent'"
                          @click="setRowHeightUnit('vh')"
                        >
                          VH
                        </button>
                      </div>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedRow.props.rowHeight"
                          type="range"
                          :min="rowHeightRange.min"
                          :max="rowHeightRange.max"
                          step="1"
                          class="range-modern flex-1"
                          :style="rowHeightFillStyle"
                        >
                        <input
                          v-model.number="selectedRow.props.rowHeight"
                          type="number"
                          :min="rowHeightRange.min"
                          :max="rowHeightRange.max"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">{{ (selectedRow.props.rowHeightUnit || 'px').toLowerCase() }}</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Padding X</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedRow.props.rowPaddingX"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="rowPaddingXFillStyle"
                        >
                        <input
                          v-model.number="selectedRow.props.rowPaddingX"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Padding Y</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedRow.props.rowPaddingY"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="rowPaddingYFillStyle"
                        >
                        <input
                          v-model.number="selectedRow.props.rowPaddingY"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Margin Y</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedRow.props.rowMargin"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="rowMarginFillStyle"
                        >
                        <input
                          v-model.number="selectedRow.props.rowMargin"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">Alignment</label>
                      <div class="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="selectedRow.props.rowAlign === 'left' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedRow.props.rowAlign = 'left'"
                        >
                          Left
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="selectedRow.props.rowAlign === 'center' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedRow.props.rowAlign = 'center'"
                        >
                          Center
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="selectedRow.props.rowAlign === 'right' ? 'border-[#2D8CFF] bg-blue-50 text-[#1D4ED8]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedRow.props.rowAlign = 'right'"
                        >
                          Right
                        </button>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else-if="selectedBlock && selectedBlock.type === 'section'">
                  <div class="space-y-4">
                    <div class="text-xs text-zinc-600">
                      Section settings
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Color</label>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
                          :style="{ backgroundColor: sectionBackgroundColorModel }"
                          @click="showSectionBackgroundColorPicker = !showSectionBackgroundColorPicker"
                        />
                        <input
                          :value="sectionBackgroundColorModel"
                          type="text"
                          class="flex-1 border rounded px-3 py-2 text-sm font-mono uppercase"
                          @input="onSectionBackgroundColorTextInput"
                          @blur="onSectionBackgroundColorTextBlur"
                        >
                        <button
                          type="button"
                          class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50"
                          @click="showSectionBackgroundColorPicker = !showSectionBackgroundColorPicker"
                        >
                          Pick
                        </button>
                      </div>
                      <div
                        v-if="showSectionBackgroundColorPicker"
                        class="mt-3"
                      >
                        <ModernColorPicker
                          v-model="sectionBackgroundColorModel"
                          :allow-transparent="true"
                          :enable-opacity="true"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Image</label>
                      <div class="rounded-lg overflow-hidden border border-zinc-200 bg-zinc-100">
                        <img
                          v-if="selectedSectionBackgroundPreview"
                          :src="selectedSectionBackgroundPreview"
                          alt="Section background"
                          class="w-full h-28 object-cover"
                        >
                        <div
                          v-else
                          class="h-28 flex items-center justify-center text-xs text-zinc-500"
                        >
                          No image selected
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="flex-1 rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                          @click="onSectionBackgroundUploadRequest"
                        >
                          Upload Image
                        </button>
                        <button
                          type="button"
                          class="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50"
                          @click="onClearSectionBackground"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Fit</label>
                      <select
                        v-model="selectedBlock.props.sectionBackgroundSize"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="cover">
                          Cover
                        </option>
                        <option value="contain">
                          Contain
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Position</label>
                      <select
                        v-model="selectedBlock.props.sectionBackgroundPosition"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="center">
                          Center
                        </option>
                        <option value="top">
                          Top
                        </option>
                        <option value="bottom">
                          Bottom
                        </option>
                        <option value="left">
                          Left
                        </option>
                        <option value="right">
                          Right
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Background Repeat</label>
                      <select
                        v-model="selectedBlock.props.sectionBackgroundRepeat"
                        class="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="no-repeat">
                          No Repeat
                        </option>
                        <option value="repeat">
                          Repeat
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Width</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedBlock.props.sectionWidthPercent"
                          type="range"
                          min="10"
                          max="100"
                          step="1"
                          class="range-modern flex-1"
                          :style="sectionWidthFillStyle"
                        >
                        <input
                          v-model.number="selectedBlock.props.sectionWidthPercent"
                          type="number"
                          min="10"
                          max="100"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">%</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Height</label>
                      <div class="mb-2 inline-flex rounded-md border border-zinc-300 p-0.5">
                        <button
                          type="button"
                          class="rounded px-2 py-1 text-xs font-medium transition"
                          :class="(selectedBlock.props.sectionHeightUnit || 'px') === 'px' ? 'bg-[#ccfbf1] text-[#0f766e] border border-[#14b8a6]' : 'text-zinc-600 hover:bg-zinc-50 border border-transparent'"
                          @click="setSectionHeightUnit('px')"
                        >
                          PX
                        </button>
                        <button
                          type="button"
                          class="rounded px-2 py-1 text-xs font-medium transition"
                          :class="(selectedBlock.props.sectionHeightUnit || 'px') === 'vh' ? 'bg-[#ccfbf1] text-[#0f766e] border border-[#14b8a6]' : 'text-zinc-600 hover:bg-zinc-50 border border-transparent'"
                          @click="setSectionHeightUnit('vh')"
                        >
                          VH
                        </button>
                      </div>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedBlock.props.sectionHeight"
                          type="range"
                          :min="sectionHeightRange.min"
                          :max="sectionHeightRange.max"
                          step="1"
                          class="range-modern flex-1"
                          :style="sectionHeightFillStyle"
                        >
                        <input
                          v-model.number="selectedBlock.props.sectionHeight"
                          type="number"
                          :min="sectionHeightRange.min"
                          :max="sectionHeightRange.max"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">{{ (selectedBlock.props.sectionHeightUnit || 'px').toLowerCase() }}</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Padding X</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedBlock.props.sectionPaddingX"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="sectionPaddingXFillStyle"
                        >
                        <input
                          v-model.number="selectedBlock.props.sectionPaddingX"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Padding Y</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedBlock.props.sectionPaddingY"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="sectionPaddingYFillStyle"
                        >
                        <input
                          v-model.number="selectedBlock.props.sectionPaddingY"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-1">Margin Y</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-model.number="selectedBlock.props.sectionMargin"
                          type="range"
                          min="0"
                          max="240"
                          step="1"
                          class="range-modern flex-1"
                          :style="sectionMarginFillStyle"
                        >
                        <input
                          v-model.number="selectedBlock.props.sectionMargin"
                          type="number"
                          min="0"
                          max="240"
                          class="w-20 border rounded px-2 py-1.5 text-sm"
                        >
                        <span class="text-sm text-zinc-500">px</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">Alignment</label>
                      <div class="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="selectedBlock.props.sectionAlign === 'left' ? 'border-[#14b8a6] bg-[#ccfbf1] text-[#0f766e]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedBlock.props.sectionAlign = 'left'"
                        >
                          Left
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="selectedBlock.props.sectionAlign === 'center' ? 'border-[#14b8a6] bg-[#ccfbf1] text-[#0f766e]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedBlock.props.sectionAlign = 'center'"
                        >
                          Center
                        </button>
                        <button
                          type="button"
                          class="rounded-md border px-3 py-1.5 text-sm transition"
                          :class="selectedBlock.props.sectionAlign === 'right' ? 'border-[#14b8a6] bg-[#ccfbf1] text-[#0f766e]' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'"
                          @click="selectedBlock.props.sectionAlign = 'right'"
                        >
                          Right
                        </button>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="text-sm text-zinc-500">
                    No inspector yet.
                  </div>
                </template>
              </div>
            </aside>
          </transition>
        </Teleport>

        <Teleport to="body">
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="floatingMenusEnabled && contentSelectorMenuOpen"
              ref="contentSelectorMenuRef"
              class="fixed rounded-2xl border border-[#ff009d49] bg-white/10 shadow-2xl p-3 sm:p-4 overflow-y-auto"
              :style="editorOverlayStyle(contentSelectorMenuStyle, 1250, 2)"
              @click.stop
            >
              <div class="grid grid-cols-2 gap-3 auto-rows-fr">
                <button
                  v-for="option in contentElementOptions"
                  :key="option.key"
                  type="button"
                  :draggable="canDragContentElement(option.key)"
                  class="group flex h-full min-h-[104px] flex-col items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-2 py-3 text-center hover:border-[#ff009dce] hover:bg-purple-50 transition-colors"
                  :class="canDragContentElement(option.key) ? 'cursor-grab active:cursor-grabbing' : ''"
                  @click="onSelectContentElement(option.key)"
                  @dragstart="onContentElementDragStart(option.key, $event)"
                  @dragend="onContentElementDragEnd"
                >
                  <component
                    :is="option.icon"
                    class="w-6 h-6 text-zinc-500 group-hover:text-[#ff009dce] shrink-0"
                  />
                  <span class="text-xs sm:text-sm font-medium text-zinc-600 group-hover:text-[#ff009dce] leading-tight">
                    {{ option.label }}
                  </span>
                </button>
              </div>
            </div>
          </transition>
        </Teleport>

        <Teleport to="body">
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <CourseModalTextSettingsMenu
              v-if="floatingMenusEnabled && textSettingsMenuOpen"
              ref="textSettingsMenuRef"
              :open="textSettingsMenuOpen"
              :menu-style="editorOverlayStyle(textSettingsMenuStyle, 1260, 2)"
              :settings="activeTextSettings"
              @close="closeElementSettingsMenus"
              @update:settings="onUpdateTextSettings"
            />
          </transition>
        </Teleport>

        <Teleport to="body">
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <CourseModalImageSettingsMenu
              v-if="floatingMenusEnabled && imageSettingsMenuOpen"
              ref="imageSettingsMenuRef"
              :open="imageSettingsMenuOpen"
              :menu-style="editorOverlayStyle(imageSettingsMenuStyle, 1260, 2)"
              :settings="activeImageSettings"
              :preview-url="activeImagePreviewUrl"
              @close="closeElementSettingsMenus"
              @update:settings="onUpdateImageSettings"
              @media-request="openImageMediaPicker"
            />
          </transition>
        </Teleport>

        <Teleport to="body">
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <CourseModalButtonSettingsMenu
              v-if="floatingMenusEnabled && buttonSettingsMenuOpen"
              ref="buttonSettingsMenuRef"
              :open="buttonSettingsMenuOpen"
              :menu-style="editorOverlayStyle(buttonSettingsMenuStyle, 1260, 2)"
              :settings="activeButtonSettings"
              :feature-options="buttonActionFeatureOptions"
              :file-preview="activeButtonFilePreview"
              @close="closeElementSettingsMenus"
              @update:settings="onUpdateButtonSettings"
              @upload-request="onButtonFileUploadRequest"
            />
          </transition>
        </Teleport>

        <Teleport to="body">
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <CourseModalVideoSettingsMenu
              v-if="floatingMenusEnabled && videoSettingsMenuOpen"
              ref="videoSettingsMenuRef"
              :open="videoSettingsMenuOpen"
              :menu-style="editorOverlayStyle(videoSettingsMenuStyle, 1260, 2)"
              :settings="activeVideoSettings"
              :video-preview-url="activeVideoPreviewUrl"
              :thumbnail-preview-url="activeVideoThumbnailPreviewUrl"
              :is-uploading="activeVideoElementUploading"
              :is-processing="activeVideoElementProcessing"
              :is-ready="activeVideoElementReady"
              :upload-progress="activeVideoElementUploadProgress"
              @close="closeElementSettingsMenus"
              @update:settings="onUpdateVideoSettings"
              @upload-request="onVideoUploadRequest"
              @thumbnail-upload-request="onVideoThumbnailUploadRequest"
            />
          </transition>
        </Teleport>

        <input
          ref="pageButtonFileUploadInputRef"
          type="file"
          class="hidden"
          accept=".pdf,.txt,.csv,application/pdf,text/plain,text/csv"
          @change="onPageButtonFileUploadSelected"
        >
        <input
          ref="pageVideoUploadInputRef"
          type="file"
          class="hidden"
          accept="video/*"
          @change="onPageVideoUploadSelected"
        >
        <input
          ref="pageVideoThumbnailUploadInputRef"
          type="file"
          class="hidden"
          accept="image/*"
          @change="onPageVideoThumbnailUploadSelected"
        >
        <input
          ref="pageSectionBackgroundUploadInputRef"
          type="file"
          class="hidden"
          accept="image/*"
          @change="onSectionBackgroundUploadSelected"
        >
        <input
          ref="pageRowBackgroundUploadInputRef"
          type="file"
          class="hidden"
          accept="image/*"
          @change="onRowBackgroundUploadSelected"
        >
        <input
          ref="pageColumnBackgroundUploadInputRef"
          type="file"
          class="hidden"
          accept="image/*"
          @change="onColumnBackgroundUploadSelected"
        >

        <PageEditorVideoElementModal
          :show="videoMediaPickerOpen"
          :z-index="editorOverlayZ(1700, 10)"
          :api-proxy="apiProxy"
          :requested-type="videoMediaRequestedType"
          :initial-folder-id="videoMediaCurrentFolderId"
          @close="closeVideoMediaPicker"
          @folder-change="onVideoMediaFolderChanged"
          @select="onVideoMediaSelected"
        />
        <Teleport to="body">
          <transition name="fade">
            <div
              v-if="deleteConfirmation"
              class="fixed inset-0 flex items-center justify-center bg-zinc-950/65 p-4 backdrop-blur-sm"
              :style="{ zIndex: editorOverlayZ(2100, 20) }"
              @click.self="deleteConfirmation = null"
            >
              <div class="w-full max-w-md overflow-hidden rounded-[24px] bg-white shadow-2xl">
                <div class="p-6 sm:p-7">
                  <div
                    class="flex h-12 w-12 items-center justify-center rounded-2xl"
                    :class="deleteConfirmation.blocked ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'"
                  >
                    <TrashIcon class="h-6 w-6" />
                  </div>
                  <p
                    class="mt-5 text-[10px] font-extrabold uppercase tracking-[.16em]"
                    :class="deleteConfirmation.blocked ? 'text-amber-600' : 'text-red-600'"
                  >
                    {{ deleteConfirmation.blocked ? 'Protected element' : (deleteConfirmation.viewportOnly ? `Remove from ${editorViewportMode}` : 'Delete from page') }}
                  </p>
                  <h3 class="mt-2 text-xl font-extrabold text-zinc-950">
                    {{ deleteConfirmation.title }}
                  </h3>
                  <p class="mt-2 text-sm leading-6 text-zinc-500">
                    {{ deleteConfirmation.message }}
                  </p>
                  <div
                    v-if="!deleteConfirmation.blocked && deleteConfirmation.viewportOnly"
                    class="mt-5 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs font-semibold leading-5 text-zinc-600"
                  >
                    This removes it from the current {{ editorViewportMode }} view. It remains available in any other device views where it is enabled.
                  </div>
                </div>
                <div class="flex justify-end gap-3 border-t border-zinc-200 bg-zinc-50 px-6 py-4">
                  <button
                    type="button"
                    class="rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-sm font-bold text-zinc-700"
                    @click="deleteConfirmation = null"
                  >
                    {{ deleteConfirmation.blocked ? 'Close' : 'Cancel' }}
                  </button>
                  <button
                    v-if="!deleteConfirmation.blocked"
                    type="button"
                    class="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-extrabold text-white"
                    @click="confirmDeleteSelection"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </Teleport>
        <PageEditorVideoElementModal
          :show="imageMediaPickerOpen"
          :z-index="editorOverlayZ(1700, 10)"
          media-type="image"
          :api-proxy="apiProxy"
          requested-type="platforma_media"
          :initial-folder-id="imageMediaCurrentFolderId"
          @close="closeImageMediaPicker"
          @folder-change="onImageMediaFolderChanged"
          @select="onImageMediaSelected"
        />
      </div>
    </div>

  </div>
</template>

<script setup>
import { onMounted, ref, onUnmounted, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ServerStackIcon,
  EyeIcon,
  CheckCircleIcon,
  GlobeAmericasIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  PhotoIcon,
  CursorArrowRaysIcon,
  MusicalNoteIcon,
  CodeBracketIcon,
  VideoCameraIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  RectangleStackIcon,
  ViewColumnsIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ComputerDesktopIcon,
  ArrowPathIcon,
  TrashIcon,
  ListBulletIcon,
  ShareIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  BookmarkSquareIcon,
} from '@heroicons/vue/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid, XMarkIcon } from '@heroicons/vue/24/solid'
import PageRenderer from '@/Components/PageRenderer.vue'
import PageEditorVideoElementModal from '@/Components/PageEditorVideoElementModal.vue'
import PlatformaSelect from '@/Components/PlatformaSelect.vue'
import PlatformaColorField from '@/Components/PlatformaColorField.vue'
import ModernColorPicker from '@/Components/ModernColorPicker.vue'
import CourseModalTextSettingsMenu from '@/Components/CourseModalTextSettingsMenu.vue'
import CourseModalImageSettingsMenu from '@/Components/CourseModalImageSettingsMenu.vue'
import CourseModalButtonSettingsMenu from '@/Components/CourseModalButtonSettingsMenu.vue'
import CourseModalVideoSettingsMenu from '@/Components/CourseModalVideoSettingsMenu.vue'
import { useMediaUploadStore } from '@/stores/mediaUploadStore'
import { sanitizeVideoEmbedHtml } from '@/utils/videoEmbedSanitizer'
import chatVideoProcessingPoster from '@/assets/media/images/chat-video-thumb.gif'
import loginBgDefault from '@/assets/media/images/bg_hold_reg.gif'
import loginLeftLogoDefault from '@/assets/media/images/platforma-logo-white.svg'
import loginRightLogoDefault from '@/assets/media/images/platforma-logo-in-color.svg'
import onboardingLogoDefault from '@/assets/media/images/platforma-creator-icons-onboarding-page.png'
import homeBusinessGroupDefault from '@/assets/media/images/business-group.jpg'
import homeBusinessmanDefault from '@/assets/media/images/platforma-businessman-rv.jpeg'
import pageTemplateCourseDefault from '@/assets/media/images/online-learning-courses-video-thumbnail.jpg'
import pageTemplateWebinarDefault from '@/assets/media/images/webinar.png'
import pageTemplateSkincareDefault from '@/assets/media/images/skin-care-2-default.png'
import pageTemplateBusinessHeroDefault from '@/assets/media/images/business-group-hero.jpeg'
import pageTemplateBusinessHeroAltDefault from '@/assets/media/images/business-group-hero2.png'
import pageTemplateBusinessPortraitDefault from '@/assets/media/images/businessman2.jpg'
import pageTemplateCourseDashboardDefault from '@/assets/media/images/online-course.png'
import pageTemplateSkincareAltDefault from '@/assets/media/images/skin-care-1-default.png'
import cartTemplateFounderStudio from '@/assets/media/images/cart-templates/founder-studio.png'
import cartTemplateFitnessFocus from '@/assets/media/images/cart-templates/fitness-focus.png'
import cartTemplateBotanicalLuxe from '@/assets/media/images/cart-templates/botanical-luxe.png'

const props = defineProps({
  pageId: {
    type: [String, Number],
    default: null,
  },
  cartProductId: {
    type: [String, Number],
    default: null,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
  embeddedBackLabel: {
    type: String,
    default: 'Back to Feature',
  },
  defaultVideoUrl: {
    type: String,
    default: '',
  },
  featureActions: {
    type: Array,
    default: () => [],
  },
  enableQuizAction: {
    type: Boolean,
    default: false,
  },
  enableMeetAction: {
    type: Boolean,
    default: false,
  },
  meetSchedulerSettings: {
    type: Object,
    default: () => ({}),
  },
  canvasPresentation: {
    type: String,
    default: 'page',
  },
  requiredSystemElements: {
    type: Array,
    default: () => [],
  },
  hiddenSystemElementTypes: {
    type: Array,
    default: () => [],
  },
  autosave: {
    type: Boolean,
    default: true,
  },
  externalPublish: {
    type: Boolean,
    default: false,
  },
  externalPublishing: {
    type: Boolean,
    default: false,
  },
  overlayZBase: {
    type: Number,
    default: 0,
  },
})
const emit = defineEmits(['exit-embedded', 'feature-action', 'draft-saved', 'publish-request'])

const route = useRoute()
const router = useRouter()
const mediaUploadStore = useMediaUploadStore()
const requiredSystemElementTypes = computed(() =>
  (Array.isArray(props.requiredSystemElements) ? props.requiredSystemElements : [])
    .map((element) => String(element?.type || '').trim().toLowerCase())
    .filter(Boolean)
)
const normalizeApiBaseUrl = (value) => {
  const raw = String(value ?? '').trim()
  if (!raw || raw.toLowerCase() === 'undefined' || raw.toLowerCase() === 'null') return ''
  return raw.replace(/\/+$/, '')
}
const resolveEditorPageId = () => {
  const fromProp = String(props.pageId ?? '').trim()
  if (fromProp && fromProp.toLowerCase() !== 'undefined' && fromProp.toLowerCase() !== 'null') {
    return fromProp
  }
  return String(route.params.pageId ?? '').trim()
}
const apiProxy = normalizeApiBaseUrl(process.env.VUE_APP_API_PROXY_URL)
const pageIdRef = ref(resolveEditorPageId()) // track current id as it may change after auto-create
const currentEditorPageSlug = ref('')
const editorDoc = ref(null)
const deleteConfirmation = ref(null)
const saving = ref(false)
const publishing = ref(false)
const previewing = ref(false)
const justSaved = ref(false)          
const hasUnsavedChanges = ref(false)
const autosaveError = ref('')
let savedTimer = null
let autosaveTimer = null
let autosaveReady = false
let autosaveQueued = false
let lastSavedDocSignature = ''
const editorHistory = ref([])
const editorHistoryIndex = ref(-1)
const canUndoPageChange = computed(() => editorHistoryIndex.value > 0)
const canRedoPageChange = computed(() => editorHistoryIndex.value >= 0 && editorHistoryIndex.value < editorHistory.value.length - 1)
const MAX_EDITOR_HISTORY_SNAPSHOTS = 60
let editorHistoryReady = false
let editorHistoryApplying = false
let editorHistoryTimer = null

const justPublished = ref(false)
let publishedTimer = null
const EDITOR_VIEWPORT_STORAGE_KEY = 'platforma-site-editor-viewport-mode'
const editorViewportMode = ref('desktop')
const editorViewportOptions = [
  { key: 'desktop', label: 'Desktop', icon: ComputerDesktopIcon },
  { key: 'tablet', label: 'Tablet', icon: DeviceTabletIcon },
  { key: 'mobile', label: 'Mobile', icon: DevicePhoneMobileIcon },
]
const normalizeViewportMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'mobile' || normalized === 'tablet' || normalized === 'desktop') return normalized
  return 'desktop'
}
const normalizeDeviceVisibility = (value) => {
  const source = value && typeof value === 'object' ? value : {}
  return {
    mobile: source.mobile !== false,
    tablet: source.tablet !== false,
    desktop: source.desktop !== false,
  }
}
const ensureNodeVisibility = (node) => {
  if (!node || typeof node !== 'object') return { mobile: true, tablet: true, desktop: true }
  node.visibility = normalizeDeviceVisibility(node.visibility)
  return node.visibility
}
const RESPONSIVE_STRUCTURAL_PROP_KEYS = new Set(['rows', 'elements'])
const RESPONSIVE_PROPS_VERSION = 2
const RESPONSIVE_STYLE_PROP_PATTERN = /(width|height|padding|margin|align|font|color|background|border|radius|corner|shadow|spacing|gap|gutter|stack|column|opacity|position|size|lineheight|letterspacing|display|direction|wrap|order|fit|aspect|layout)/i
const cloneResponsiveValue = (value) => {
  if (value === undefined) return undefined
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (_error) {
    return value
  }
}
const getNodeOwnProps = (node) => {
  const source = node?.props && typeof node.props === 'object' ? node.props : {}
  return Object.keys(source).reduce((result, key) => {
    if (!RESPONSIVE_STRUCTURAL_PROP_KEYS.has(key)) result[key] = cloneResponsiveValue(source[key])
    return result
  }, {})
}
const isResponsivePlainObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value))
const isResponsiveStyleProp = (key) => key === 'textSettings' || key === 'rowColumnStyles' || RESPONSIVE_STYLE_PROP_PATTERN.test(String(key || ''))
const isResponsiveValueEqual = (left, right) => {
  if (left === right) return true
  try {
    return JSON.stringify(left) === JSON.stringify(right)
  } catch (_error) {
    return false
  }
}
const mergeResponsiveValues = (base, override) => {
  if (!isResponsivePlainObject(base) || !isResponsivePlainObject(override)) {
    return override === undefined ? cloneResponsiveValue(base) : cloneResponsiveValue(override)
  }
  const next = cloneResponsiveValue(base) || {}
  Object.keys(override).forEach((key) => {
    next[key] = isResponsivePlainObject(next[key]) && isResponsivePlainObject(override[key])
      ? mergeResponsiveValues(next[key], override[key])
      : cloneResponsiveValue(override[key])
  })
  return next
}
const diffResponsiveValues = (current, inherited) => {
  if (!isResponsivePlainObject(current) || !isResponsivePlainObject(inherited)) {
    return isResponsiveValueEqual(current, inherited) ? undefined : cloneResponsiveValue(current)
  }
  const diff = {}
  Object.keys(current).forEach((key) => {
    const valueDiff = diffResponsiveValues(current[key], inherited[key])
    if (valueDiff !== undefined) diff[key] = valueDiff
  })
  return Object.keys(diff).length ? diff : undefined
}
const getResponsiveStyleProps = (props) => {
  const source = isResponsivePlainObject(props) ? props : {}
  return Object.keys(source).reduce((result, key) => {
    if (!isResponsiveStyleProp(key)) return result
    if (key === 'textSettings' && isResponsivePlainObject(source[key])) {
      const settings = Object.keys(source[key]).reduce((textResult, settingKey) => {
        if (RESPONSIVE_STYLE_PROP_PATTERN.test(settingKey)) {
          textResult[settingKey] = cloneResponsiveValue(source[key][settingKey])
        }
        return textResult
      }, {})
      if (Object.keys(settings).length) result[key] = settings
      return result
    }
    result[key] = cloneResponsiveValue(source[key])
    return result
  }, {})
}
const syncGlobalPropsToDesktop = (currentProps, desktopProps) => {
  const current = isResponsivePlainObject(currentProps) ? currentProps : {}
  const desktop = isResponsivePlainObject(desktopProps) ? cloneResponsiveValue(desktopProps) : {}
  Object.keys(current).forEach((key) => {
    if (!isResponsiveStyleProp(key)) {
      desktop[key] = cloneResponsiveValue(current[key])
      return
    }
    if (key === 'textSettings' && isResponsivePlainObject(current[key])) {
      const desktopSettings = isResponsivePlainObject(desktop[key]) ? cloneResponsiveValue(desktop[key]) : {}
      Object.keys(current[key]).forEach((settingKey) => {
        if (!RESPONSIVE_STYLE_PROP_PATTERN.test(settingKey)) {
          desktopSettings[settingKey] = cloneResponsiveValue(current[key][settingKey])
        }
      })
      desktop[key] = desktopSettings
    }
  })
  return desktop
}
const clampResponsiveSpacing = (value, max) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return value
  return Math.max(0, Math.min(max, parsed))
}
const createDefaultResponsiveSnapshot = (node, viewport, desktopSnapshot) => {
  const next = cloneResponsiveValue(desktopSnapshot) || {}
  const type = String(node?.type || '').trim().toLowerCase()
  const isMobile = viewport === 'mobile'
  const isTablet = viewport === 'tablet'
  const paddingLimit = isMobile ? 20 : 48

  if (type === 'section') {
    next.sectionWidthPercent = 100
    next.sectionPaddingX = clampResponsiveSpacing(next.sectionPaddingX ?? next.sectionPadding, paddingLimit)
    next.sectionMargin = clampResponsiveSpacing(next.sectionMargin, isMobile ? 12 : 24)
  }

  if (type === 'row') {
    next.rowWidthPercent = 100
    next.rowPaddingX = clampResponsiveSpacing(next.rowPaddingX ?? next.rowPadding, paddingLimit)
    next.rowMargin = clampResponsiveSpacing(next.rowMargin, isMobile ? 12 : 24)
    next.rowColumnGutter = clampResponsiveSpacing(next.rowColumnGutter, isMobile ? 16 : 28)
    next.stackColumns = isMobile && Number(next.rowColumnCount ?? 1) > 1
    if (isMobile) {
      next.rowHeight = 0
      next.rowHeightUnit = 'px'
    }
    if (Array.isArray(next.rowColumnStyles)) {
      next.rowColumnStyles = next.rowColumnStyles.map((entry) => ({
        ...(entry && typeof entry === 'object' ? entry : {}),
        ...(isMobile ? {
          height: entry?.backgroundImageUrl || entry?.backgroundImageFileId ? 360 : 0,
          heightUnit: 'px',
        } : {}),
        paddingX: clampResponsiveSpacing(entry?.paddingX, paddingLimit),
        paddingY: clampResponsiveSpacing(entry?.paddingY, isMobile ? 28 : 48),
      }))
    }
  }

  if (type !== 'section' && type !== 'row') {
    if (isMobile) next.elementWidthPercent = 100
    next.elementPaddingX = clampResponsiveSpacing(next.elementPaddingX ?? next.elementPadding, isMobile ? 16 : 32)
    next.elementPaddingY = clampResponsiveSpacing(next.elementPaddingY ?? next.elementPadding, isMobile ? 16 : 32)
    next.elementMargin = clampResponsiveSpacing(next.elementMargin, isMobile ? 12 : 24)
    if (isMobile && next.textSettings && typeof next.textSettings === 'object') {
      next.textSettings.fontSize = Math.min(36, Number(next.textSettings.fontSize) || 16)
    }
    if (isMobile && type === 'header') next.size = Math.min(40, Number(next.size) || 32)
    if (type === 'checkout-order-form') {
      next.cardPaddingX = clampResponsiveSpacing(next.cardPaddingX ?? 36, isMobile ? 20 : 32)
      next.cardPaddingY = clampResponsiveSpacing(next.cardPaddingY ?? 36, isMobile ? 24 : 36)
    }
  }

  if (isTablet && type === 'row' && typeof next.stackColumns !== 'boolean') next.stackColumns = false
  return next
}
const visitEditorNodeTree = (nodes, visitor) => {
  const stack = Array.isArray(nodes) ? [...nodes] : []
  while (stack.length) {
    const node = stack.pop()
    if (!node || typeof node !== 'object') continue
    visitor(node)
    const rows = Array.isArray(node?.props?.rows) ? node.props.rows : []
    const elements = Array.isArray(node?.props?.elements) ? node.props.elements : []
    if (rows.length) stack.push(...rows)
    if (elements.length) stack.push(...elements)
  }
}
const ensureNodeResponsiveProps = (node) => {
  if (!node || typeof node !== 'object') return {}
  const existing = isResponsivePlainObject(node.responsiveProps) ? node.responsiveProps : {}
  if (Number(existing.version) !== RESPONSIVE_PROPS_VERSION) {
    // Legacy documents stored three complete, independent page designs. Migrate
    // them to the saved desktop design as the canonical source, intentionally
    // dropping the duplicated tablet/mobile snapshots so existing production
    // pages inherit their proven desktop content and structure.
    node.responsiveProps = {
      version: RESPONSIVE_PROPS_VERSION,
      strategy: 'desktop-cascade',
      desktop: cloneResponsiveValue(isResponsivePlainObject(existing.desktop) ? existing.desktop : getNodeOwnProps(node)) || {},
      tabletOverrides: {},
      mobileOverrides: {},
    }
  } else {
    node.responsiveProps = {
      version: RESPONSIVE_PROPS_VERSION,
      strategy: 'desktop-cascade',
      desktop: cloneResponsiveValue(isResponsivePlainObject(existing.desktop) ? existing.desktop : getNodeOwnProps(node)) || {},
      tabletOverrides: cloneResponsiveValue(
        isResponsivePlainObject(existing.tabletOverrides)
          ? existing.tabletOverrides
          : (existing.strategy === 'desktop-cascade' ? {} : existing.tablet),
      ) || {},
      mobileOverrides: cloneResponsiveValue(
        isResponsivePlainObject(existing.mobileOverrides)
          ? existing.mobileOverrides
          : (existing.strategy === 'desktop-cascade' ? {} : existing.mobile),
      ) || {},
    }
  }
  // Keep complete compatibility snapshots for a rolling deployment. Older
  // member renderers still read tablet/mobile as full documents, while the new
  // editor and renderer use only the sparse *Overrides fields.
  const desktop = cloneResponsiveValue(node.responsiveProps.desktop) || {}
  const tabletInherited = createDefaultResponsiveSnapshot(node, 'tablet', desktop)
  const tabletResolved = mergeResponsiveValues(tabletInherited, node.responsiveProps.tabletOverrides)
  const mobileInherited = createDefaultResponsiveSnapshot(node, 'mobile', tabletResolved)
  node.responsiveProps.tablet = cloneResponsiveValue(tabletResolved) || {}
  node.responsiveProps.mobile = mergeResponsiveValues(mobileInherited, node.responsiveProps.mobileOverrides)
  return node.responsiveProps
}
const resolveNodeViewportProps = (node, mode = editorViewportMode.value) => {
  const viewport = normalizeViewportMode(mode)
  const responsiveProps = ensureNodeResponsiveProps(node)
  const desktop = cloneResponsiveValue(responsiveProps.desktop) || {}
  if (viewport === 'desktop') return desktop
  const tabletInherited = createDefaultResponsiveSnapshot(node, 'tablet', desktop)
  const tabletResolved = mergeResponsiveValues(tabletInherited, responsiveProps.tabletOverrides)
  if (viewport === 'tablet') return tabletResolved
  const mobileInherited = createDefaultResponsiveSnapshot(node, 'mobile', tabletResolved)
  return mergeResponsiveValues(mobileInherited, responsiveProps.mobileOverrides)
}
const captureNodeViewportProps = (node, mode) => {
  const viewport = normalizeViewportMode(mode)
  const responsiveProps = ensureNodeResponsiveProps(node)
  const current = getNodeOwnProps(node)
  if (viewport === 'desktop') {
    responsiveProps.desktop = current
    return
  }
  responsiveProps.desktop = syncGlobalPropsToDesktop(current, responsiveProps.desktop)
  const desktop = cloneResponsiveValue(responsiveProps.desktop) || {}
  const tabletInherited = createDefaultResponsiveSnapshot(node, 'tablet', desktop)
  if (viewport === 'tablet') {
    responsiveProps.tabletOverrides = diffResponsiveValues(
      getResponsiveStyleProps(current),
      getResponsiveStyleProps(tabletInherited),
    ) || {}
    return
  }
  const tabletResolved = mergeResponsiveValues(tabletInherited, responsiveProps.tabletOverrides)
  const mobileInherited = createDefaultResponsiveSnapshot(node, 'mobile', tabletResolved)
  responsiveProps.mobileOverrides = diffResponsiveValues(
    getResponsiveStyleProps(current),
    getResponsiveStyleProps(mobileInherited),
  ) || {}
}
const captureDocumentViewportProps = (doc, mode = editorViewportMode.value) => {
  const viewport = normalizeViewportMode(mode)
  visitEditorNodeTree(doc?.sections, (node) => {
    captureNodeViewportProps(node, viewport)
  })
  return doc
}
const applyDocumentViewportProps = (doc, mode = editorViewportMode.value) => {
  const viewport = normalizeViewportMode(mode)
  visitEditorNodeTree(doc?.sections, (node) => {
    const rows = Array.isArray(node?.props?.rows) ? node.props.rows : undefined
    const elements = Array.isArray(node?.props?.elements) ? node.props.elements : undefined
    node.props = resolveNodeViewportProps(node, viewport)
    if (rows !== undefined) node.props.rows = rows
    if (elements !== undefined) node.props.elements = elements
  })
  return doc
}
const prepareResponsiveEditorDocument = (doc) => {
  visitEditorNodeTree(doc?.sections, (node) => ensureNodeResponsiveProps(node))
  return applyDocumentViewportProps(doc, editorViewportMode.value)
}
const getViewportScopedVisibility = () => {
  // Content structure is shared across breakpoints. Creators can still use the
  // existing visibility controls to hide an item on a specific device, but a
  // newly added section/row/element should not require being rebuilt elsewhere.
  return { mobile: true, tablet: true, desktop: true }
}
const applyViewportVisibilityToNewNode = (node) => {
  if (!node || typeof node !== 'object') return node
  node.visibility = getViewportScopedVisibility()
  ensureNodeResponsiveProps(node)
  const rows = Array.isArray(node?.props?.rows) ? node.props.rows : undefined
  const elements = Array.isArray(node?.props?.elements) ? node.props.elements : undefined
  node.props = resolveNodeViewportProps(node, editorViewportMode.value)
  if (rows !== undefined) node.props.rows = rows
  if (elements !== undefined) node.props.elements = elements
  return node
}
const scopeNodeTreeToCurrentViewport = (node) => {
  if (!node || typeof node !== 'object') return node
  applyViewportVisibilityToNewNode(node)
  const normalizedType = String(node?.type || '').trim().toLowerCase()
  if (normalizedType === 'section') {
    const rows = Array.isArray(node?.props?.rows) ? node.props.rows : []
    rows.forEach((row) => scopeNodeTreeToCurrentViewport(row))
    return node
  }
  if (normalizedType === 'row') {
    const elements = Array.isArray(node?.props?.elements) ? node.props.elements : []
    elements.forEach((element) => scopeNodeTreeToCurrentViewport(element))
    return node
  }
  return node
}
const getViewportVisibilityKey = (mode = editorViewportMode.value) => {
  const normalizedMode = normalizeViewportMode(mode)
  if (normalizedMode === 'mobile') return 'mobile'
  if (normalizedMode === 'tablet') return 'tablet'
  return 'desktop'
}
const isNodeVisibleForViewport = (node, mode = editorViewportMode.value) => {
  const visibility = ensureNodeVisibility(node)
  return Boolean(visibility[getViewportVisibilityKey(mode)])
}
const hideNodeInCurrentViewport = (node) => {
  if (!node || typeof node !== 'object') {
    return { didChange: false, shouldRemoveCompletely: false }
  }
  const visibility = ensureNodeVisibility(node)
  const key = getViewportVisibilityKey()
  if (visibility[key] === false) {
    return {
      didChange: false,
      shouldRemoveCompletely: !visibility.mobile && !visibility.tablet && !visibility.desktop,
    }
  }
  const nextVisibility = {
    ...visibility,
    [key]: false,
  }
  node.visibility = nextVisibility
  return {
    didChange: true,
    shouldRemoveCompletely: !nextVisibility.mobile && !nextVisibility.tablet && !nextVisibility.desktop,
  }
}
const setEditorViewportMode = (mode) => {
  if (!editorViewportOptions.some(option => option.key === mode)) return
  if (mode === editorViewportMode.value) return
  if (editorDoc.value) captureDocumentViewportProps(editorDoc.value, editorViewportMode.value)
  editorViewportMode.value = mode
  if (editorDoc.value) applyDocumentViewportProps(editorDoc.value, mode)
  try {
    localStorage.setItem(EDITOR_VIEWPORT_STORAGE_KEY, mode)
  } catch (err) {
    console.debug('[SitePageEditor] Failed to persist viewport mode', err)
  }
}
const resetCurrentViewportOverrides = () => {
  const viewport = normalizeViewportMode(editorViewportMode.value)
  if (viewport === 'desktop' || !editorDoc.value) return
  visitEditorNodeTree(editorDoc.value?.sections, (node) => {
    const responsiveProps = ensureNodeResponsiveProps(node)
    responsiveProps[`${viewport}Overrides`] = {}
  })
  applyDocumentViewportProps(editorDoc.value, viewport)
}
const selectedIdx = ref(-1)
const selectedRowContext = ref({ sectionIndex: -1, rowIndex: -1 })
const selectedColumnContext = ref({ sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 })
const selectedElementContext = ref({ sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 })
const selectedMemberLoginPart = ref('')
const showSectionBackgroundColorPicker = ref(false)
const showRowBackgroundColorPicker = ref(false)
const showColumnBackgroundColorPicker = ref(false)
const showElementBackgroundColorPicker = ref(false)
const showElementTextColorPicker = ref(false)
const showButtonBackgroundColorPicker = ref(false)
const showButtonTextColorPicker = ref(false)
const showVideoControlColorPicker = ref(false)
const showLoginSubmitButtonColorPicker = ref(false)
const showLoginForgotPasswordColorPicker = ref(false)
const showLoginRememberCheckboxColorPicker = ref(false)
const showSettings = ref(false)
const floatingMenusEnabled = ref(false)
const showStructureMenu = ref(false)
const checkoutBumpProducts = ref([])
const checkoutCatalogProducts = ref([])
const checkoutCurrentProduct = ref(null)
const checkoutBumpProductsLoading = ref(false)
const emailAudience = ref({ selectedProvider: '', providers: [] })
const selectedEmailAudienceProvider = computed(() => String(emailAudience.value?.selectedProvider || '').trim())
const selectedEmailAudienceProviderLabel = computed(() => {
  const selected = selectedEmailAudienceProvider.value
  if (!selected) return ''
  const provider = (Array.isArray(emailAudience.value?.providers) ? emailAudience.value.providers : [])
    .find((entry) => String(entry?.id || '').trim() === selected)
  return String(provider?.label || selected)
})

const showAddPalette = ref(false)
const addPaletteCategory = ref('elements')
const savedLibraryItems = ref([])
const savedLibraryLoading = ref(false)
const reusableSaveOpen = ref(false)
const savedElementLibraryOpen = ref(false)
const reusableSaveName = ref('')
const reusableSaveError = ref('')
const savingReusableItem = ref(false)
const pendingInsertIndex = ref(null) // number | null
const contentSelectorMenuOpen = ref(false)
const contentSelectorMenuRef = ref(null)
const contentSelectorMenuStyle = ref({ top: '0px', left: '0px', width: '320px', height: '320px' })
const contentSelectorAnchorRect = ref(null)
const contentSelectorMenuTargetIndex = ref(null)
const contentSelectorRowTarget = ref({ sectionIndex: -1, rowIndex: -1, elementIndex: -1 })
const pageButtonFileUploadInputRef = ref(null)
const pageVideoUploadInputRef = ref(null)
const pageVideoThumbnailUploadInputRef = ref(null)
const pageSectionBackgroundUploadInputRef = ref(null)
const pageRowBackgroundUploadInputRef = ref(null)
const pageColumnBackgroundUploadInputRef = ref(null)
const videoMediaPickerOpen = ref(false)
const videoMediaRequestedType = ref('platforma_media')
const videoMediaCurrentFolderId = ref('')
const imageMediaPickerOpen = ref(false)
const imageMediaCurrentFolderId = ref('')
// Keep the exact target stable while a native upload or teleported media picker is open.
// Relying only on the current inspector selection can lose the target during async uploads.
const pendingImageSourceTarget = ref(null)
const textSettingsMenuRef = ref(null)
const imageSettingsMenuRef = ref(null)
const buttonSettingsMenuRef = ref(null)
const videoSettingsMenuRef = ref(null)
const textSettingsMenuOpen = ref(false)
const imageSettingsMenuOpen = ref(false)
const buttonSettingsMenuOpen = ref(false)
const videoSettingsMenuOpen = ref(false)
const textSettingsMenuStyle = ref({ top: '0px', left: '0px', width: '320px', height: '360px' })
const imageSettingsMenuStyle = ref({ top: '0px', left: '0px', width: '320px', height: '420px' })
const buttonSettingsMenuStyle = ref({ top: '0px', left: '0px', width: '320px', height: '560px' })
const videoSettingsMenuStyle = ref({ top: '0px', left: '0px', width: '320px', height: '560px' })
const activeSettingsContext = ref({
  scope: '',
  index: -1,
  rowIndex: -1,
  columnIndex: -1,
  elementIndex: -1,
  nestedRowElementIndex: -1,
  partKey: '',
  settingsType: '',
})
const PAGE_ROW_INSERT_MIME = 'application/x-platforma-row-insert'
const PAGE_ROW_INSERT_TEXT_PREFIX = 'platforma-row-insert:'
const PAGE_ROW_ELEMENT_INSERT_MIME = 'application/x-platforma-row-element-insert'
const PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX = 'platforma-row-element-insert:'
const rowColumnOptions = [1, 2, 3, 4, 5, 6]
const contentElementOptions = computed(() => [
  { key: 'text', label: 'Text', icon: DocumentTextIcon },
  { key: 'header', label: 'Header', icon: DocumentTextIcon },
  { key: 'bullet-list', label: 'Bullet List', icon: ListBulletIcon },
  { key: 'image', label: 'Image', icon: PhotoIcon },
  { key: 'button', label: 'Button', icon: CursorArrowRaysIcon },
  { key: 'share', label: 'Share', icon: ShareIcon },
  { key: 'optin-form', label: 'Opt-in Form', icon: EnvelopeIcon },
  { key: 'mp3', label: 'MP3 Player', icon: MusicalNoteIcon },
  { key: 'embed', label: 'Embed', icon: CodeBracketIcon },
  { key: 'video', label: 'Video', icon: VideoCameraIcon },
  { key: 'checkout-order-form', label: 'Checkout Order Form', icon: CreditCardIcon },
  ...(props.enableMeetAction ? [{ key: 'meet-scheduler', label: 'Meet Scheduler', icon: CalendarDaysIcon }] : []),
])
const bulletIconOptions = [
  { value: 'disc', label: 'Standard bullet' },
  { value: 'arrow', label: 'Arrow' },
  { value: 'check', label: 'Checkmark' },
  { value: 'check-circle', label: 'Check in circle' },
  { value: 'star', label: 'Star' },
]
const shareNetworkOptions = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'x', label: 'X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'copy', label: 'Copy link' },
]
const optinFieldTypeOptions = [
  { value: 'firstName', label: 'First name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'company', label: 'Company' },
  { value: 'custom', label: 'Custom' },
]
const createBulletListProps = () => ({
  items: [
    { text: 'Add your first benefit here' },
    { text: 'Explain another important point' },
    { text: 'Finish with a clear takeaway' },
  ],
  bulletIcon: 'check',
  bulletIconColor: '#22C55E',
  bulletTextColor: '#18181B',
  bulletFontSize: 18,
  bulletGap: 12,
})
const createShareProps = () => ({
  shareNetworks: ['facebook', 'x', 'linkedin', 'email', 'copy'],
  shareLabel: 'Share this page',
  shareShowLabel: true,
  shareAlignment: 'left',
  shareButtonStyle: 'circle',
  shareButtonColor: '#18181B',
  shareIconColor: '#FFFFFF',
  shareButtonSize: 42,
  shareButtonGap: 10,
})
const createOptinFormProps = () => ({
  optinId: `optin_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
  optinHeadline: 'Get updates delivered to your inbox',
  optinDescription: 'Enter your details below and we’ll keep you in the loop.',
  optinShowLabels: true,
  optinFields: [
    { key: 'firstName', type: 'text', label: 'First name', placeholder: 'Your first name', showLabel: true, required: false },
    { key: 'email', type: 'email', label: 'Email address', placeholder: 'you@example.com', showLabel: true, required: true },
  ],
  optinButtonLabel: 'Subscribe',
  optinSuccessMessage: 'Thanks! You’re on the list.',
  optinRedirectUrl: '',
  optinRedirectNewTab: false,
  optinSendToIntegration: true,
  optinFieldGap: 12,
  optinInputHeight: 48,
  optinCornerRadius: 10,
  optinLabelColor: '#3F3F46',
  optinInputBackgroundColor: '#FFFFFF',
  optinInputTextColor: '#18181B',
  optinInputBorderColor: '#D4D4D8',
  optinButtonBackgroundColor: '#2D8CFF',
  optinButtonTextColor: '#FFFFFF',
})
const checkoutOrderFlowOptions = [
  { value: 'one_step', label: 'One-step order form' },
  { value: 'two_step', label: 'Two-step order form' },
]
const checkoutPreviewStepOptions = [
  { value: 'contact', label: 'Step 1 · Contact' },
  { value: 'payment', label: 'Step 2 · Payment' },
]
const checkoutSecureBadgeIconOptions = [
  { value: 'lock', label: 'Lock icon' },
  { value: 'shield', label: 'Shield icon' },
  { value: 'check', label: 'Check icon' },
  { value: 'none', label: 'Text only' },
]
const checkoutButtonWeightOptions = [
  { value: 500, label: 'Medium' },
  { value: 600, label: 'Semibold' },
  { value: 700, label: 'Bold' },
  { value: 800, label: 'Extra bold' },
]
const checkoutCardShadowOptions = [
  { value: 'none', label: 'No shadow' },
  { value: 'soft', label: 'Soft shadow' },
  { value: 'medium', label: 'Medium shadow' },
  { value: 'strong', label: 'Strong shadow' },
]
const rowShadowOptions = [
  { value: 'none', label: 'No shadow' },
  { value: '0 8px 24px rgba(15, 23, 42, 0.07)', label: 'Soft shadow' },
  { value: '0 25px 50px -12px rgba(148, 163, 184, 0.5)', label: 'Medium shadow' },
  { value: '0 32px 70px rgba(15, 23, 42, 0.22)', label: 'Strong shadow' },
]
const checkoutBumpProductOptions = computed(() => checkoutBumpProducts.value.map((product) => ({
  value: product.publicId,
  label: `${product.name} · ${formatCheckoutProductPrice(product)}`,
})))
const checkoutProductSelectorCatalogOptions = computed(() => checkoutCatalogProducts.value.map((product) => ({
  value: product.publicId,
  label: `${product.name} · ${formatCheckoutProductPrice(product)}`,
})))
const MEMBER_LOGIN_TEMPLATE_SECTION = 'member-login-template'
const MEMBER_LOGIN_TEMPLATE_ROW = 'member-login-template'
const MEMBER_LOGIN_TEMPLATE_VERSION = 4
const MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE = 'member-create-free-account-form'
const MEMBER_HOME_TEMPLATE_SECTION = 'member-home-template'
const MEMBER_HOME_TEMPLATE_VERSION = 9
const MEMBER_PRICING_TEMPLATE_SECTION = 'member-pricing-template'
const MEMBER_PRICING_TEMPLATE_VERSION = 1
const MEMBER_GET_ACCESS_TEMPLATE_SECTION = 'member-get-access-template'
const MEMBER_GET_ACCESS_TEMPLATE_VERSION = 4
const MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_SECTION = 'member-get-access-success-template'
const MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_VERSION = 2
const MEMBER_APPROVAL_TEMPLATE_SECTION = 'member-approval-template'
const MEMBER_APPROVAL_TEMPLATE_VERSION = 1
const MEMBER_LOGIN_DEFAULTS = Object.freeze({
  leftPanelHeadline: 'Login to my awesome members area.',
  rightHeading: 'Sign in to your account',
  joinPromptText: 'Not a member yet?',
  joinLinkText: 'Get started today',
  joinLinkHref: 'join',
  forgotPasswordText: 'Forgot password?',
  forgotPasswordHref: 'password-recover',
  submitButtonText: 'Sign in',
  submitButtonColor: '#2D8CFF',
  forgotPasswordColor: '#2D8CFF',
  rememberCheckboxColor: '#2D8CFF',
  leftLogoUrl: '@/assets/media/images/platforma-logo-white.svg',
  rightLogoUrl: '@/assets/media/images/platforma-logo-in-color.svg',
  backgroundImageUrl: '@/assets/media/images/bg_hold_reg.gif',
})
const MEMBER_HOME_DEFAULTS = Object.freeze({
  navHomeText: 'Home',
  navPricingText: 'Pricing',
  navLoginText: 'Log in ->',
  heroHeadline: 'Join the Leveraging Business Community-DO IT NOW',
  heroSub: 'Access all our training programs and exclusive online community.',
  heroButtonText: 'Yes, I Want to Join Today',
  heroBackgroundImageUrl: '',
  whatWeDoHeading: 'WHAT WE DO',
  whatWeDoBody: 'It\'s not about time sheets, or quarterly reports, or calling shots. A good leader gets results-and that starts by empowering and inspiring the people around you.',
  whatWeDoButtonText: 'Join To Watch',
  aboutMeHeading: 'ABOUT ME',
  aboutMeBody: 'I\'m an expert brand strategist, consultant, and photographer, passionate about helping others find their passion and audience to create a living doing what they love.',
  aboutMeButtonText: 'Get started',
  pricingHeading: 'Simple no-tricks pricing',
  pricingPlanHeading: 'Lifetime membership',
  pricingIncludedHeading: 'What\'s included',
  pricingFeatureOne: 'Private forum access',
  pricingFeatureTwo: 'Member resources',
  pricingFeatureThree: 'Entry to annual conference',
  pricingFeatureFour: 'Official member t-shirt',
  pricingCardTagline: 'Pay once, own it forever',
  pricingCardPrice: '$349',
  pricingCardCurrency: 'USD',
  pricingCardButtonText: 'Get access',
  pricingCardFinePrint: 'Invoices and receipts available for easy company reimbursement',
  footerSupportText: 'support@youremail.com',
  logoUrl: '@/assets/media/images/platforma-creator-icons-onboarding-page.png',
  whatWeDoImageUrl: '@/assets/media/images/business-group.jpg',
  aboutMeImageUrl: '@/assets/media/images/platforma-businessman-rv.jpeg',
})
const MEMBER_PRICING_DEFAULTS = Object.freeze({
  navHomeText: 'Home',
  navPricingText: 'Pricing',
  navLoginText: 'Log in ->',
  heroHeading: 'Ready To Take Your Business To The Next Level?',
  heroSub: 'Join over 20,000 other entrepreneurs and get access to all my tips and tricks to help your business bloom.',
  dynamicPlansHint: 'Pricing cards are rendered dynamically from your Membership Plan Tiers.',
  footerSupportText: 'support@youremail.com',
  logoUrl: '@/assets/media/images/platforma-creator-icons-onboarding-page.png',
})
const MEMBER_GET_ACCESS_DEFAULTS = Object.freeze({
  navHomeText: 'Home',
  navPricingText: 'Pricing',
  navLoginText: 'Log in',
  progressCreateAccount: '01  Create Account',
  progressAccountConfirmation: '02  Account Confirmation',
  progressInstantAccess: '03  Instant Access',
  signupHeading: 'Create Your Account Below\nTo Get Started !',
  selectedPlanText: 'Selected plan: Loser Tier',
  detailsHeading: 'Your Details',
  decisionHeading: 'You\'ve Made a Great Decision!',
  decisionBody: 'This Program is designed to walk anyone through the step-by-step. Everything is given to you. You\'ll have all the tools you need.\n\nIf you do this program, take the action, and complete the action based guarantee requirements, and you do not get results.\nThen I will give you a full immediate refund.',
  helpHeading: 'Need Help With Anything?',
  helpBody: 'Our Office is open 9AM-5PM and real life people are standing by and ready to help you right now.',
  helpEmailText: 'info@robinvolk.com',
  summaryHeading: 'Summary',
  summaryPlanName: 'Loser Tier',
  summaryPlanPrice: '$9.99 /mth',
  summaryIncludesHeading: 'What\'s included',
  summaryFeatureOne: 'Private forum access',
  summaryFeatureTwo: 'Member resources',
  summaryFeatureThree: 'Entry to annual conference',
  summaryFeatureFour: 'Official member t-shirt',
  dynamicFormHint: 'Get Access signup form is locked for editing and is rendered from the member onboarding flow.',
  footerSupportText: 'support@youremail.com',
  logoUrl: '@/assets/media/images/platforma-creator-icons-onboarding-page.png',
  summaryImageUrl: '@/assets/media/images/businessman2.jpg',
})
const MEMBER_GET_ACCESS_SUCCESS_DEFAULTS = Object.freeze({
  navHomeText: 'Home',
  navPricingText: 'Pricing',
  navLoginText: 'Log in',
  successHelperText: 'Get Access success auto-login/redirect is locked and rendered from member onboarding flow.',
  footerSupportText: 'support@youremail.com',
  logoUrl: '@/assets/media/images/platforma-creator-icons-onboarding-page.png',
})
const MEMBER_APPROVAL_DEFAULTS = Object.freeze({
  formHelperText: 'Member approval request form is locked for behavior and lead capture.',
  submitButtonText: 'Send Request',
})
const normalizeMemberPageSlug = (value) => {
  const raw = String(value || '').trim()
  if (!raw || raw === '/') return '/'
  return `/${raw.replace(/^\/+/, '').replace(/\/+$/, '').toLowerCase()}`
}
const normalizeElementType = (value) => String(value || '').trim().toLowerCase()
const isMemberLoginFormType = (value) => normalizeElementType(value) === 'member-login-form'
const isMemberCreateFreeAccountFormType = (value) =>
  normalizeElementType(value) === MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE
const isMemberAuthFormType = (value) =>
  isMemberLoginFormType(value) || isMemberCreateFreeAccountFormType(value)
const isJoinPageSlug = (value) => normalizeMemberPageSlug(value) === '/join'
const isJoinPageContext = computed(() => isJoinPageSlug(currentEditorPageSlug.value))
const buttonActionTypeOptions = computed(() => [
  { value: 'go_to', label: 'Go To...' },
  ...(props.enableQuizAction ? [{ value: 'start_quiz', label: 'Start Quiz' }] : []),
  ...(props.enableMeetAction ? [{ value: 'open_meet_scheduler', label: 'Open Meet Scheduler' }] : []),
  { value: 'open_pay_click_pop', label: 'Open Pay Click-Pop' },
  { value: 'file_access', label: 'File Access' },
])
const buttonGoToTypeOptions = [
  { value: 'custom_link', label: 'Custom Action Link' },
  { value: 'feature_instance', label: 'Membership Feature Instance' },
]
const videoTypeOptions = [
  { value: 'custom_embed', label: 'Custom Embed' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'wistia', label: 'Wistia' },
  { value: 'media_content', label: 'Media Content' },
]

/* helper to get/set selected block safely */
const selectedBlock = computed(() =>
  editorDoc.value && selectedIdx.value > -1 ? editorDoc.value.sections[selectedIdx.value] : null
)
const selectedRow = computed(() => {
  const sectionIndex = Number(selectedRowContext.value?.sectionIndex)
  const rowIndex = Number(selectedRowContext.value?.rowIndex)
  if (!Number.isFinite(sectionIndex) || sectionIndex < 0) return null
  if (!Number.isFinite(rowIndex) || rowIndex < 0) return null
  const section = editorDoc.value?.sections?.[sectionIndex]
  if (!section || String(section.type || '') !== 'section') return null
  const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
  return rows[rowIndex] || null
})
const selectedColumn = computed(() => {
  const sectionIndex = Number(selectedColumnContext.value?.sectionIndex)
  const rowIndex = Number(selectedColumnContext.value?.rowIndex)
  const columnIndex = Number(selectedColumnContext.value?.columnIndex)
  const nestedRowElementIndex = Number(selectedColumnContext.value?.nestedRowElementIndex ?? -1)
  if (!Number.isFinite(sectionIndex) || sectionIndex < 0) return null
  if (!Number.isFinite(rowIndex) || rowIndex < 0) return null
  if (!Number.isFinite(columnIndex) || columnIndex < 0) return null
  const section = editorDoc.value?.sections?.[sectionIndex]
  if (!section || String(section.type || '') !== 'section') return null
  const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
  const hostRow = rows[rowIndex]
  if (!hostRow || String(hostRow.type || '') !== 'row') return null
  let row = hostRow
  if (Number.isFinite(nestedRowElementIndex) && nestedRowElementIndex >= 0) {
    const nestedRow = hostRow?.props?.elements?.[nestedRowElementIndex]
    if (!nestedRow || String(nestedRow?.type || '') !== 'row') return null
    ensureRowElementBlockProps(nestedRow)
    row = nestedRow
  }
  const rowColumnCount = Math.max(1, Math.min(6, Number(row?.props?.rowColumnCount ?? 1) || 1))
  if (columnIndex >= rowColumnCount) return null
  const styleEntry = ensureRowColumnStyleEntry(row, columnIndex)
  return {
    sectionIndex,
    rowIndex,
    columnIndex,
    nestedRowElementIndex: (Number.isFinite(nestedRowElementIndex) && nestedRowElementIndex >= 0) ? nestedRowElementIndex : -1,
    row,
    styleEntry,
  }
})
const selectedElement = computed(() => {
  const sectionIndex = Number(selectedElementContext.value?.sectionIndex)
  const rowIndex = Number(selectedElementContext.value?.rowIndex)
  const elementIndex = Number(selectedElementContext.value?.elementIndex)
  const nestedRowElementIndex = Number(selectedElementContext.value?.nestedRowElementIndex ?? -1)
  if (!Number.isFinite(sectionIndex) || sectionIndex < 0) return null
  if (!Number.isFinite(rowIndex) || rowIndex < 0) return null
  if (!Number.isFinite(elementIndex) || elementIndex < 0) return null
  const section = editorDoc.value?.sections?.[sectionIndex]
  if (!section || String(section.type || '') !== 'section') return null
  const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
  const row = rows[rowIndex]
  if (!row || String(row.type || '') !== 'row') return null
  const elements = Array.isArray(row?.props?.elements) ? row.props.elements : []
  if (Number.isFinite(nestedRowElementIndex) && nestedRowElementIndex >= 0) {
    const nestedRow = elements[nestedRowElementIndex]
    if (!nestedRow || String(nestedRow?.type || '') !== 'row') return null
    const nestedElements = Array.isArray(nestedRow?.props?.elements) ? nestedRow.props.elements : []
    return nestedElements[elementIndex] || null
  }
  return elements[elementIndex] || null
})
const canInsertRows = computed(() => {
  if (selectedRow.value) return true
  return String(selectedBlock.value?.type || '') === 'section'
})
const canInsertElements = computed(() => {
  if (selectedElement.value) return true
  return Boolean(selectedRow.value)
})
const savedLibraryByType = computed(() => {
  const grouped = { page: [], section: [], row: [], column: [], element: [] }
  savedLibraryItems.value.forEach((item) => {
    const type = String(item?.itemType || '').trim().toLowerCase()
    if (grouped[type]) grouped[type].push(item)
  })
  return grouped
})
const selectedReusableType = computed(() => {
  if (selectedElement.value) return 'element'
  if (selectedColumn.value) return 'column'
  if (selectedRow.value) return 'row'
  if (String(selectedBlock.value?.type || '') === 'section') return 'section'
  return ''
})
const canSaveReusableSelection = computed(() => {
  if (!selectedReusableType.value || showSettings.value || showStructureMenu.value || showAddPalette.value) return false
  if (selectedReusableType.value === 'column') {
    return !rowColumnContainsRequiredSystemElement(selectedColumn.value?.row, selectedColumn.value?.columnIndex)
  }
  const node = selectedElement.value || selectedRow.value || selectedBlock.value
  return Boolean(node) && !nodeContainsRequiredSystemElement(node)
})
watch(
  () => selectedBlock.value?.type,
  (type) => {
    if (type !== 'member-login') {
      selectedMemberLoginPart.value = ''
    }
    if (type !== 'section') {
      selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
      selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
      selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
      showRowBackgroundColorPicker.value = false
      showColumnBackgroundColorPicker.value = false
      showElementBackgroundColorPicker.value = false
      showElementTextColorPicker.value = false
    }
  }
)
watch(
  () => Boolean(selectedElement.value),
  (hasSelectedElement) => {
    const selectedType = String(selectedElement.value?.type || '').trim().toLowerCase()
    if (!hasSelectedElement) {
      showElementBackgroundColorPicker.value = false
      showElementTextColorPicker.value = false
      showLoginSubmitButtonColorPicker.value = false
      showLoginForgotPasswordColorPicker.value = false
      showLoginRememberCheckboxColorPicker.value = false
    }
    if (!hasSelectedElement || selectedType !== 'video') {
      showVideoControlColorPicker.value = false
      closeVideoMediaPicker()
    }
    if (!hasSelectedElement || selectedType !== 'image') {
      closeImageMediaPicker()
    }
  }
)

const drawerOpen = computed(() => showSettings.value || showStructureMenu.value || showAddPalette.value || selectedIdx.value > -1)
const editorChromeHover = ref(false)
const initialChromeVisible = ref(true)
const getEditorWindowSize = () => ({
  width: typeof window === 'undefined' ? 1440 : Math.max(320, Number(window.innerWidth) || 1440),
  height: typeof window === 'undefined' ? 900 : Math.max(480, Number(window.innerHeight) || 900),
})
const editorWindowSize = ref(getEditorWindowSize())
const syncEditorWindowSize = () => {
  editorWindowSize.value = getEditorWindowSize()
}
let initialChromeTimer = null
let editorChromeHideTimer = null
const showEditorChrome = computed(() => {
  return initialChromeVisible.value || editorChromeHover.value
})
const clearEditorChromeHideTimer = () => {
  if (!editorChromeHideTimer) return
  clearTimeout(editorChromeHideTimer)
  editorChromeHideTimer = null
}
const revealEditorChrome = () => {
  clearEditorChromeHideTimer()
  editorChromeHover.value = true
}
const scheduleEditorChromeHide = () => {
  initialChromeVisible.value = false
  if (initialChromeTimer) {
    clearTimeout(initialChromeTimer)
    initialChromeTimer = null
  }
  clearEditorChromeHideTimer()
  editorChromeHideTimer = setTimeout(() => {
    editorChromeHover.value = false
    editorChromeHideTimer = null
  }, 110)
}
const isSingleMemberLoginEditorPage = computed(() => {
  const sections = Array.isArray(editorDoc.value?.sections) ? editorDoc.value.sections : []
  return sections.length === 1 && String(sections[0]?.type || '') === 'member-login'
})
const isModalCanvasPresentation = computed(() => String(props.canvasPresentation || '').toLowerCase() === 'modal')
const editorOverlayZBase = computed(() => {
  const value = Number(props.overlayZBase)
  return Number.isFinite(value) && value > 0 ? value : 0
})
const editorOverlayZ = (fallback, offset = 0) => (
  editorOverlayZBase.value ? editorOverlayZBase.value + offset : fallback
)
const editorOverlayStyle = (style, fallback, offset = 0) => ({
  ...(style || {}),
  zIndex: editorOverlayZ(fallback, offset),
})
const editorRootClasses = computed(() => isModalCanvasPresentation.value
  ? 'h-screen overflow-hidden flex flex-col bg-transparent'
  : 'h-screen overflow-hidden flex flex-col bg-white')
const editorCanvasClasses = computed(() => {
  if (isModalCanvasPresentation.value) {
    return 'relative flex flex-1 items-center justify-center overflow-hidden bg-black/65 p-4 backdrop-blur-[2px] sm:p-8'
  }
  if (editorViewportMode.value === 'mobile' || editorViewportMode.value === 'tablet') {
    return 'relative flex flex-1 items-center justify-center overflow-hidden bg-slate-100/80 p-3 sm:p-4'
  }
  return isSingleMemberLoginEditorPage.value
    ? 'p-0 flex-1 overflow-hidden'
    : 'p-0 flex-1 overflow-y-auto overflow-x-hidden app-scroll no-inline-gutter'
})
const editorCanvasInnerClasses = computed(() => {
  const base = isModalCanvasPresentation.value
    ? 'relative z-10 transition-all duration-200'
    : 'relative transition-all duration-200'
  if (editorViewportMode.value === 'mobile') {
    return 'relative z-10 mx-auto min-h-0 max-h-full max-w-full shrink-0 transition-all duration-200'
  }
  if (editorViewportMode.value === 'tablet') {
    return 'relative z-10 mx-auto min-h-0 max-h-full max-w-full shrink-0 transition-all duration-200'
  }
  if (isModalCanvasPresentation.value) {
    return `${base} mx-auto w-full max-w-[720px]`
  }
  return `${base} w-full max-w-full`
})
const editorCanvasInnerStyle = computed(() => {
  const verticalInset = isModalCanvasPresentation.value ? 64 : 32
  const horizontalInset = isModalCanvasPresentation.value ? 64 : 24
  const availableWidth = Math.max(240, editorWindowSize.value.width - horizontalInset)
  const availableHeight = Math.max(360, editorWindowSize.value.height - verticalInset)
  const fittedDeviceStyle = (deviceWidth, deviceHeight) => {
    const scale = Math.min(1, availableWidth / deviceWidth, availableHeight / deviceHeight)
    return {
      width: `${Math.round(deviceWidth * scale)}px`,
      height: `${Math.round(deviceHeight * scale)}px`,
      aspectRatio: `${deviceWidth} / ${deviceHeight}`,
    }
  }
  if (editorViewportMode.value === 'mobile') {
    return fittedDeviceStyle(430, 932)
  }
  if (editorViewportMode.value === 'tablet') {
    return fittedDeviceStyle(834, 1112)
  }
  return undefined
})
const showViewportPhoneNotch = computed(() => editorViewportMode.value === 'mobile')
const editorViewportFrameClasses = computed(() => {
  if (editorViewportMode.value === 'mobile') {
    return 'relative flex h-full min-h-0 flex-col overflow-hidden rounded-[2rem] border border-zinc-700/90 bg-zinc-900 p-2.5 shadow-[0_28px_70px_-30px_rgba(0,0,0,0.85)]'
  }
  if (editorViewportMode.value === 'tablet') {
    return 'relative flex h-full min-h-0 flex-col overflow-hidden rounded-[1.4rem] border border-zinc-300 bg-zinc-900/90 p-2 shadow-[0_18px_48px_-28px_rgba(0,0,0,0.7)]'
  }
  if (isModalCanvasPresentation.value) {
    return 'relative max-h-[calc(100vh-8rem)] overflow-y-auto rounded-[1.5rem] border border-zinc-300 bg-white shadow-[0_28px_80px_-24px_rgba(0,0,0,0.8)] app-scroll'
  }
  return ''
})
const editorViewportScreenClasses = computed(() => {
  if (editorViewportMode.value === 'mobile') {
    return 'app-scroll min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain rounded-[1.55rem] bg-white'
  }
  if (editorViewportMode.value === 'tablet') {
    return 'app-scroll min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain rounded-[1.05rem] bg-white'
  }
  if (isModalCanvasPresentation.value) {
    return 'overflow-hidden rounded-[1.45rem] bg-white'
  }
  return ''
})

const brandColor = ref('#c342ff') // or pull from your theme
const buildRangeFillStyle = (rawValue, min, max, fillColor = '#14b8a6', trackColor = '#e5e7eb') => {
  const value = Number(rawValue)
  const safeValue = Number.isFinite(value) ? value : min
  const pct = Math.min(100, Math.max(0, ((safeValue - min) / (max - min)) * 100))
  return {
    background: `linear-gradient(to right, ${fillColor} 0%, ${fillColor} ${pct}%, ${trackColor} ${pct}%, ${trackColor} 100%)`
  }
}
const normalizeHeightUnit = (value, fallback = 'px') => {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'vh') return 'vh'
  return fallback === 'vh' ? 'vh' : 'px'
}
const getHeightRangeByUnit = (unit) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  if (normalizedUnit === 'vh') {
    return { min: 5, max: 300 }
  }
  return { min: 50, max: 2400 }
}
const getRowHeightRangeByUnit = (unit) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  if (normalizedUnit === 'vh') {
    return { min: 0, max: 300 }
  }
  return { min: 0, max: 2400 }
}
const getColumnHeightRangeByUnit = (unit) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  if (normalizedUnit === 'vh') {
    return { min: 0, max: 300 }
  }
  return { min: 0, max: 2400 }
}
const getElementHeightRange = (element) => {
  const normalizedUnit = normalizeHeightUnit(element?.props?.elementHeightUnit, 'px')
  if (normalizedUnit === 'vh') {
    return { min: 5, max: 300 }
  }
  const type = String(element?.type || '').trim().toLowerCase()
  if (type === 'text') {
    return { min: 24, max: 2400 }
  }
  return { min: 50, max: 2400 }
}
const clampElementHeightByType = (value, element, fallback) => {
  const range = getElementHeightRange(element)
  const parsed = Number(value)
  const next = Number.isFinite(parsed) ? parsed : fallback
  return Math.max(range.min, Math.min(range.max, Math.round(next)))
}
const clampHeightByUnit = (value, unit, fallback) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  const range = getHeightRangeByUnit(normalizedUnit)
  const parsed = Number(value)
  const next = Number.isFinite(parsed) ? parsed : fallback
  return Math.max(range.min, Math.min(range.max, Math.round(next)))
}
const clampRowHeightByUnit = (value, unit, fallback) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  const range = getRowHeightRangeByUnit(normalizedUnit)
  const parsed = Number(value)
  const next = Number.isFinite(parsed) ? parsed : fallback
  return Math.max(range.min, Math.min(range.max, Math.round(next)))
}
const clampColumnHeightByUnit = (value, unit, fallback) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  const range = getColumnHeightRangeByUnit(normalizedUnit)
  const parsed = Number(value)
  const next = Number.isFinite(parsed) ? parsed : fallback
  return Math.max(range.min, Math.min(range.max, Math.round(next)))
}
const setSectionHeightUnit = (unit) => {
  if (!selectedBlock.value || String(selectedBlock.value?.type || '') !== 'section') return
  if (!selectedBlock.value.props || typeof selectedBlock.value.props !== 'object') selectedBlock.value.props = {}
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  selectedBlock.value.props.sectionHeightUnit = normalizedUnit
  selectedBlock.value.props.sectionHeight = clampHeightByUnit(selectedBlock.value.props.sectionHeight, normalizedUnit, 100)
}
const setRowHeightUnit = (unit) => {
  if (!selectedRow.value) return
  if (!selectedRow.value.props || typeof selectedRow.value.props !== 'object') selectedRow.value.props = {}
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  selectedRow.value.props.rowHeightUnit = normalizedUnit
  selectedRow.value.props.rowHeight = clampRowHeightByUnit(selectedRow.value.props.rowHeight, normalizedUnit, 120)
}
const setColumnHeightUnit = (unit) => {
  if (!selectedColumn.value?.row) return
  const style = ensureRowColumnStyleEntry(selectedColumn.value.row, selectedColumn.value.columnIndex)
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  style.heightUnit = normalizedUnit
  style.height = clampColumnHeightByUnit(style.height, normalizedUnit, 120)
}
const setElementHeightUnit = (unit) => {
  if (!selectedElement.value) return
  if (!selectedElement.value.props || typeof selectedElement.value.props !== 'object') selectedElement.value.props = {}
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  selectedElement.value.props.elementHeightUnit = normalizedUnit
  selectedElement.value.props.elementHeight = clampElementHeightByType(
    selectedElement.value.props.elementHeight,
    selectedElement.value,
    String(selectedElement.value?.type || '').trim().toLowerCase() === 'text' ? 28 : 120
  )
}

const headerSizeFillStyle = computed(() => {
  return buildRangeFillStyle(selectedBlock.value?.props?.size ?? 32, 16, 96, brandColor.value)
})
const sectionWidthFillStyle = computed(() => {
  return buildRangeFillStyle(selectedBlock.value?.props?.sectionWidthPercent ?? 100, 10, 100, '#14b8a6', '#3f3f46')
})
const sectionHeightFillStyle = computed(() => {
  const range = getHeightRangeByUnit(selectedBlock.value?.props?.sectionHeightUnit || 'px')
  return buildRangeFillStyle(selectedBlock.value?.props?.sectionHeight ?? 100, range.min, range.max, '#14b8a6', '#3f3f46')
})
const sectionHeightRange = computed(() => getHeightRangeByUnit(selectedBlock.value?.props?.sectionHeightUnit || 'px'))
const sectionPaddingXFillStyle = computed(() => {
  return buildRangeFillStyle(
    selectedBlock.value?.props?.sectionPaddingX ?? selectedBlock.value?.props?.sectionPadding ?? 0,
    0,
    240,
    '#14b8a6',
    '#3f3f46'
  )
})
const sectionPaddingYFillStyle = computed(() => {
  return buildRangeFillStyle(
    selectedBlock.value?.props?.sectionPaddingY ?? selectedBlock.value?.props?.sectionPadding ?? 0,
    0,
    240,
    '#14b8a6',
    '#3f3f46'
  )
})
const sectionMarginFillStyle = computed(() => {
  return buildRangeFillStyle(selectedBlock.value?.props?.sectionMargin ?? 0, 0, 240, '#14b8a6', '#3f3f46')
})
const rowColumnsFillStyle = computed(() => {
  return buildRangeFillStyle(selectedRow.value?.props?.rowColumnCount ?? 1, 1, 6, '#2D8CFF', '#3f3f46')
})
const rowGutterFillStyle = computed(() => {
  return buildRangeFillStyle(selectedRow.value?.props?.rowColumnGutter ?? 12, 0, 120, '#2D8CFF', '#3f3f46')
})
const rowWidthFillStyle = computed(() => {
  return buildRangeFillStyle(selectedRow.value?.props?.rowWidthPercent ?? 100, 10, 100, '#2D8CFF', '#3f3f46')
})
const rowHeightFillStyle = computed(() => {
  const range = getRowHeightRangeByUnit(selectedRow.value?.props?.rowHeightUnit || 'px')
  return buildRangeFillStyle(selectedRow.value?.props?.rowHeight ?? 120, range.min, range.max, '#2D8CFF', '#3f3f46')
})
const rowHeightRange = computed(() => getRowHeightRangeByUnit(selectedRow.value?.props?.rowHeightUnit || 'px'))
const rowPaddingXFillStyle = computed(() => {
  return buildRangeFillStyle(selectedRow.value?.props?.rowPaddingX ?? selectedRow.value?.props?.rowPadding ?? 16, 0, 240, '#2D8CFF', '#3f3f46')
})
const rowPaddingYFillStyle = computed(() => {
  return buildRangeFillStyle(selectedRow.value?.props?.rowPaddingY ?? selectedRow.value?.props?.rowPadding ?? 16, 0, 240, '#2D8CFF', '#3f3f46')
})
const rowMarginFillStyle = computed(() => {
  return buildRangeFillStyle(selectedRow.value?.props?.rowMargin ?? 5, 0, 240, '#2D8CFF', '#3f3f46')
})
const columnHeightFillStyle = computed(() => {
  const unit = selectedColumn.value?.styleEntry?.heightUnit || 'px'
  const range = getColumnHeightRangeByUnit(unit)
  return buildRangeFillStyle(selectedColumn.value?.styleEntry?.height ?? 120, range.min, range.max, '#2D8CFF', '#3f3f46')
})
const columnHeightRange = computed(() => {
  const unit = selectedColumn.value?.styleEntry?.heightUnit || 'px'
  return getColumnHeightRangeByUnit(unit)
})
const elementWidthFillStyle = computed(() => {
  return buildRangeFillStyle(selectedElement.value?.props?.elementWidthPercent ?? 100, 10, 100, '#F59E0B', '#3f3f46')
})
const elementHeightFillStyle = computed(() => {
  const range = getElementHeightRange(selectedElement.value)
  return buildRangeFillStyle(selectedElement.value?.props?.elementHeight ?? 120, range.min, range.max, '#F59E0B', '#3f3f46')
})
const elementHeightRange = computed(() => getElementHeightRange(selectedElement.value))
const elementPaddingXFillStyle = computed(() => {
  return buildRangeFillStyle(selectedElement.value?.props?.elementPaddingX ?? selectedElement.value?.props?.elementPadding ?? 12, 0, 240, '#F59E0B', '#3f3f46')
})
const elementPaddingYFillStyle = computed(() => {
  return buildRangeFillStyle(selectedElement.value?.props?.elementPaddingY ?? selectedElement.value?.props?.elementPadding ?? 12, 0, 240, '#F59E0B', '#3f3f46')
})
const elementMarginFillStyle = computed(() => {
  return buildRangeFillStyle(selectedElement.value?.props?.elementMargin ?? 0, 0, 240, '#F59E0B', '#3f3f46')
})
const selectedElementTextSizeFillStyle = computed(() => {
  return buildRangeFillStyle(activeTextSettings.value?.fontSize ?? 16, 10, 96, '#F59E0B', '#3f3f46')
})
const selectedElementTextWeightFillStyle = computed(() => {
  return buildRangeFillStyle(activeTextSettings.value?.fontWeight ?? 400, 100, 900, '#F59E0B', '#3f3f46')
})
const selectedElementTextLineHeightFillStyle = computed(() => {
  return buildRangeFillStyle(activeTextSettings.value?.lineHeight ?? 1.6, 0.8, 3, '#F59E0B', '#3f3f46')
})
const selectedColumnWidthFillStyle = computed(() => {
  return buildRangeFillStyle(selectedColumn.value?.styleEntry?.widthPercent ?? 100, 5, 100, '#2D8CFF', '#3f3f46')
})
const selectedColumnWidthModel = computed({
  get: () => {
    return Math.max(5, Math.min(100, toFiniteNumber(selectedColumn.value?.styleEntry?.widthPercent, 100)))
  },
  set: (value) => {
    if (!selectedColumn.value?.row) return
    const style = ensureRowColumnStyleEntry(selectedColumn.value.row, selectedColumn.value.columnIndex)
    style.widthPercent = Math.max(5, Math.min(100, toFiniteNumber(value, 100)))
  },
})
const selectedElementImageWidthFillStyle = computed(() => {
  return buildRangeFillStyle(selectedElement.value?.props?.imageWidth ?? 80, 20, 100, '#F59E0B', '#3f3f46')
})
const selectedElementImageRadiusFillStyle = computed(() => {
  return buildRangeFillStyle(selectedElement.value?.props?.imageBorderRadius ?? 0, 0, 120, '#F59E0B', '#3f3f46')
})
const selectedElementImageShadowFillStyle = computed(() => {
  return buildRangeFillStyle(selectedElement.value?.props?.imageShadowStrength ?? 0, 0, 100, '#F59E0B', '#3f3f46')
})
const selectedButtonTextSizeFillStyle = computed(() => {
  return buildRangeFillStyle(activeButtonSettings.value?.buttonTextSize ?? 100, 60, 160, '#F59E0B', '#3f3f46')
})
const selectedButtonFontSizeFillStyle = computed(() => {
  return buildRangeFillStyle(activeButtonSettings.value?.buttonFontSize ?? 18, 10, 56, '#F59E0B', '#3f3f46')
})
const selectedButtonWidthFillStyle = computed(() => {
  return buildRangeFillStyle(activeButtonSettings.value?.buttonWidth ?? 36, 20, 100, '#F59E0B', '#3f3f46')
})
const selectedButtonPaddingXFillStyle = computed(() => {
  return buildRangeFillStyle(activeButtonSettings.value?.buttonPaddingX ?? 24, 4, 80, '#F59E0B', '#3f3f46')
})
const selectedButtonPaddingYFillStyle = computed(() => {
  return buildRangeFillStyle(activeButtonSettings.value?.buttonPaddingY ?? 14, 4, 56, '#F59E0B', '#3f3f46')
})
const sectionBackgroundColorModel = computed({
  get: () => normalizeBackgroundColor(selectedBlock.value?.props?.sectionBackgroundColor, '#FFFFFF'),
  set: (value) => {
    if (!selectedBlock.value) return
    if (!selectedBlock.value.props || typeof selectedBlock.value.props !== 'object') selectedBlock.value.props = {}
    selectedBlock.value.props.sectionBackgroundColor = normalizeBackgroundColor(value, '#FFFFFF')
  },
})
const rowBackgroundColorModel = computed({
  get: () => normalizeBackgroundColor(selectedRow.value?.props?.rowBackgroundColor, '#FFFFFF'),
  set: (value) => {
    if (!selectedRow.value) return
    if (!selectedRow.value.props || typeof selectedRow.value.props !== 'object') selectedRow.value.props = {}
    selectedRow.value.props.rowBackgroundColor = normalizeBackgroundColor(value, '#FFFFFF')
  },
})
const columnBackgroundColorModel = computed({
  get: () => normalizeBackgroundColor(selectedColumn.value?.styleEntry?.backgroundColor, 'transparent'),
  set: (value) => {
    if (!selectedColumn.value?.row) return
    const style = ensureRowColumnStyleEntry(selectedColumn.value.row, selectedColumn.value.columnIndex)
    style.backgroundColor = normalizeBackgroundColor(value, 'transparent')
  },
})
const selectedSectionBackgroundPreview = computed(() => {
  if (!selectedBlock.value || String(selectedBlock.value?.type || '') !== 'section') return ''
  return resolveImageUrl(
    selectedBlock.value?.props?.sectionBackgroundImageFileId,
    selectedBlock.value?.props?.sectionBackgroundImageUrl,
    ''
  )
})
const selectedRowBackgroundPreview = computed(() => {
  if (!selectedRow.value) return ''
  return resolveImageUrl(
    selectedRow.value?.props?.rowBackgroundImageFileId,
    selectedRow.value?.props?.rowBackgroundImageUrl,
    ''
  )
})
const selectedColumnBackgroundPreview = computed(() => {
  if (!selectedColumn.value?.row) return ''
  const style = getRowColumnStyleEntry(selectedColumn.value.row, selectedColumn.value.columnIndex)
  return resolveImageUrl(
    style?.backgroundImageFileId || style?.background_image_file_id,
    style?.backgroundImageUrl || style?.background_image_url,
    ''
  )
})
const elementBackgroundColorModel = computed({
  get: () => normalizeBackgroundColor(selectedElement.value?.props?.elementBackgroundColor, 'transparent'),
  set: (value) => {
    if (!selectedElement.value) return
    if (!selectedElement.value.props || typeof selectedElement.value.props !== 'object') selectedElement.value.props = {}
    selectedElement.value.props.elementBackgroundColor = normalizeBackgroundColor(value, 'transparent')
  },
})

const onSectionBackgroundUploadRequest = () => {
  if (!selectedBlock.value || String(selectedBlock.value?.type || '') !== 'section') return
  pageSectionBackgroundUploadInputRef.value?.click?.()
}
const onRowBackgroundUploadRequest = () => {
  if (!selectedRow.value) return
  pageRowBackgroundUploadInputRef.value?.click?.()
}
const onColumnBackgroundUploadRequest = () => {
  if (!selectedColumn.value?.row) return
  pageColumnBackgroundUploadInputRef.value?.click?.()
}
const onClearSectionBackground = () => {
  if (!selectedBlock.value || String(selectedBlock.value?.type || '') !== 'section') return
  if (!selectedBlock.value.props || typeof selectedBlock.value.props !== 'object') selectedBlock.value.props = {}
  selectedBlock.value.props.sectionBackgroundImageFileId = null
  selectedBlock.value.props.sectionBackgroundImageUrl = ''
}
const onClearRowBackground = () => {
  if (!selectedRow.value) return
  if (!selectedRow.value.props || typeof selectedRow.value.props !== 'object') selectedRow.value.props = {}
  selectedRow.value.props.rowBackgroundImageFileId = null
  selectedRow.value.props.rowBackgroundImageUrl = ''
}
const onClearColumnBackground = () => {
  if (!selectedColumn.value?.row) return
  const style = ensureRowColumnStyleEntry(selectedColumn.value.row, selectedColumn.value.columnIndex)
  style.backgroundImageFileId = null
  style.backgroundImageUrl = ''
}
const selectedElementTextColorModel = computed({
  get: () => normalizeHexColor(activeTextSettings.value?.textColor, '#18181B'),
  set: (value) => {
    if (!selectedElement.value) return
    const type = String(selectedElement.value?.type || '').trim().toLowerCase()
    if (type !== 'text' && type !== 'header') return
    onUpdateTextSettings({ textColor: value })
  },
})
const selectedButtonBackgroundColorModel = computed({
  get: () => normalizeHexColor(activeButtonSettings.value?.buttonBackgroundColor, '#2D8BFF'),
  set: (value) => {
    if (!selectedElement.value || String(selectedElement.value?.type || '').trim().toLowerCase() !== 'button') return
    onUpdateButtonSettings({ buttonBackgroundColor: value })
  },
})
const selectedButtonTextColorModel = computed({
  get: () => normalizeHexColor(activeButtonSettings.value?.buttonTextColor, '#FFFFFF'),
  set: (value) => {
    if (!selectedElement.value || String(selectedElement.value?.type || '').trim().toLowerCase() !== 'button') return
    onUpdateButtonSettings({ buttonTextColor: value })
  },
})
const selectedLoginSubmitButtonColorModel = computed({
  get: () => normalizeHexColor(selectedElement.value?.props?.loginSubmitButtonColor, '#2D8CFF'),
  set: (value) => {
    if (!selectedElement.value || !isMemberAuthFormType(selectedElement.value?.type)) return
    if (!selectedElement.value.props || typeof selectedElement.value.props !== 'object') selectedElement.value.props = {}
    const normalized = normalizeHexColor(value, '#2D8CFF')
    selectedElement.value.props.loginSubmitButtonColor = normalized
    selectedElement.value.props.submitButtonColor = normalized
  },
})
const selectedLoginForgotPasswordColorModel = computed({
  get: () => normalizeHexColor(selectedElement.value?.props?.loginForgotPasswordColor, '#2D8CFF'),
  set: (value) => {
    if (!selectedElement.value || !isMemberLoginFormType(selectedElement.value?.type)) return
    if (!selectedElement.value.props || typeof selectedElement.value.props !== 'object') selectedElement.value.props = {}
    const normalized = normalizeHexColor(value, '#2D8CFF')
    selectedElement.value.props.loginForgotPasswordColor = normalized
    selectedElement.value.props.forgotPasswordColor = normalized
  },
})
const selectedLoginRememberCheckboxColorModel = computed({
  get: () => normalizeHexColor(selectedElement.value?.props?.loginRememberCheckboxColor, '#2D8CFF'),
  set: (value) => {
    if (!selectedElement.value || !isMemberLoginFormType(selectedElement.value?.type)) return
    if (!selectedElement.value.props || typeof selectedElement.value.props !== 'object') selectedElement.value.props = {}
    const normalized = normalizeHexColor(value, '#2D8CFF')
    selectedElement.value.props.loginRememberCheckboxColor = normalized
    selectedElement.value.props.rememberCheckboxColor = normalized
  },
})

const getSelectedElementAlignment = () => {
  const element = selectedElement.value
  if (!element || !element.props) return 'left'
  const type = String(element.type || '').trim().toLowerCase()
  if (type === 'text' || type === 'header') {
    return normalizeTextAlign(activeTextSettings.value?.textAlign, 'left')
  }
  if (type === 'image') {
    return normalizeTextAlign(activeImageSettings.value?.imageAlign, 'center')
  }
  if (type === 'button') {
    return normalizeTextAlign(activeButtonSettings.value?.buttonAlign, 'center')
  }
  return normalizeRowAlign(element.props.elementAlign)
}

const setSelectedElementAlignment = (value) => {
  const element = selectedElement.value
  if (!element || !element.props) return
  const align = normalizeTextAlign(value, 'left')
  const type = String(element.type || '').trim().toLowerCase()
  if (type === 'text' || type === 'header') {
    onUpdateTextSettings({ textAlign: align })
    element.props.elementAlign = align
    return
  }
  if (type === 'image') {
    onUpdateImageSettings({ imageAlign: align })
    element.props.elementAlign = align
    return
  }
  if (type === 'button') {
    onUpdateButtonSettings({ buttonAlign: align })
    element.props.elementAlign = align
    return
  }
  element.props.elementAlign = normalizeRowAlign(align)
}

const normalizeSectionAlign = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'left' || normalized === 'center' || normalized === 'right') return normalized
  return 'left'
}
const normalizeRowAlign = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'left' || normalized === 'center' || normalized === 'right') return normalized
  return 'left'
}

const toFiniteNumber = (value, fallback) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const deepClone = (value) => {
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (_) {
    return value
  }
}

const getRowColumnStyleEntry = (row, columnIndex = 0) => {
  const styles = Array.isArray(row?.props?.rowColumnStyles) ? row.props.rowColumnStyles : []
  const idx = Math.max(0, Number(columnIndex) || 0)
  const existing = styles[idx]
  return existing && typeof existing === 'object' ? existing : {}
}

const ensureRowColumnStyleEntry = (row, columnIndex = 0) => {
  if (!row || String(row.type || '') !== 'row') return {}
  if (!row.props || typeof row.props !== 'object') row.props = {}
  if (!Array.isArray(row.props.rowColumnStyles)) row.props.rowColumnStyles = []
  const idx = Math.max(0, Number(columnIndex) || 0)
  if (!row.props.rowColumnStyles[idx] || typeof row.props.rowColumnStyles[idx] !== 'object') {
    row.props.rowColumnStyles[idx] = {}
  }
  return row.props.rowColumnStyles[idx]
}

const loadSavedPageLibrary = async () => {
  savedLibraryLoading.value = true
  try {
    const response = await fetch(`${apiProxy}/api/pages/library`, { credentials: 'include' })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data?.success) throw new Error(data?.message || 'Unable to load saved page items.')
    savedLibraryItems.value = Array.isArray(data.items) ? data.items : []
  } catch (error) {
    console.error('[SitePageEditor] Failed to load reusable library', error)
  } finally {
    savedLibraryLoading.value = false
  }
}

const buildSelectedReusableContent = () => {
  const type = selectedReusableType.value
  if (type === 'section') return deepClone(selectedBlock.value)
  if (type === 'row') return deepClone(selectedRow.value)
  if (type === 'element') return deepClone(selectedElement.value)
  if (type === 'column' && selectedColumn.value?.row) {
    const row = selectedColumn.value.row
    const columnIndex = Number(selectedColumn.value.columnIndex)
    const elements = (Array.isArray(row?.props?.elements) ? row.props.elements : [])
      .filter(element => getEditorRowElementColumnIndex(element, row) === columnIndex)
      .map(element => {
        const clone = deepClone(element)
        if (!clone.props || typeof clone.props !== 'object') clone.props = {}
        clone.props.rowElementColumnIndex = 0
        return clone
      })
    return {
      type: 'column',
      style: deepClone(selectedColumn.value.styleEntry || {}),
      elements,
    }
  }
  return null
}

const openReusableSaveDialog = () => {
  if (!canSaveReusableSelection.value) return
  const label = selectedReusableType.value === 'element'
    ? getEditorElementLabel(selectedElement.value)
    : selectedReusableType.value
  reusableSaveName.value = `Saved ${label}`
  reusableSaveError.value = ''
  reusableSaveOpen.value = true
}

const saveReusableSelection = async () => {
  const itemType = selectedReusableType.value
  const content = buildSelectedReusableContent()
  if (!itemType || !content) {
    reusableSaveError.value = 'Select a section, row, column, or element first.'
    return
  }
  if (!reusableSaveName.value.trim()) {
    reusableSaveError.value = 'Enter a name for this saved item.'
    return
  }
  savingReusableItem.value = true
  reusableSaveError.value = ''
  try {
    const response = await fetch(`${apiProxy}/api/pages/library`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ itemType, name: reusableSaveName.value.trim(), content }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data?.success) throw new Error(data?.message || 'Unable to save this item.')
    savedLibraryItems.value = [data.item, ...savedLibraryItems.value.filter(item => Number(item.id) !== Number(data.item?.id))]
    reusableSaveOpen.value = false
  } catch (error) {
    reusableSaveError.value = error.message || 'Unable to save this item.'
  } finally {
    savingReusableItem.value = false
  }
}

const closeSavedLibraryPalette = () => {
  showAddPalette.value = false
  contentSelectorMenuOpen.value = false
  contentSelectorMenuTargetIndex.value = null
  pendingInsertIndex.value = null
}

const openSavedElementLibrary = () => {
  savedElementLibraryOpen.value = true
}

const buildSavedElementPreviewDoc = (item) => {
  const element = deepClone(item?.content)
  if (!element || typeof element !== 'object' || Array.isArray(element)) {
    return { version: 1, title: '', seo: {}, sections: [] }
  }

  if (!element.props || typeof element.props !== 'object') element.props = {}
  element.props.rowElementColumnIndex = 0

  return {
    version: 1,
    title: String(item?.name || 'Saved Element'),
    seo: { title: String(item?.name || ''), description: '' },
    sections: [{
      type: 'section',
      props: {
        sectionWidthPercent: 100,
        sectionHeight: 300,
        sectionHeightUnit: 'px',
        sectionPadding: 0,
        sectionPaddingX: 18,
        sectionPaddingY: 18,
        sectionMargin: 0,
        sectionAlign: 'left',
        sectionBackgroundColor: '#FFFFFF',
        sectionBackgroundImageFileId: null,
        sectionBackgroundImageUrl: '',
        sectionBackgroundSize: 'cover',
        sectionBackgroundPosition: 'center',
        sectionBackgroundRepeat: 'no-repeat',
        rows: [{
          type: 'row',
          props: {
            rowColumnCount: 1,
            rowColumnGutter: 0,
            rowWidthPercent: 100,
            rowHeight: 264,
            rowHeightUnit: 'px',
            rowPadding: 12,
            rowPaddingX: 12,
            rowPaddingY: 12,
            rowMargin: 0,
            rowAlign: 'left',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [{
              widthPercent: 100,
              backgroundColor: 'transparent',
              backgroundImageFileId: null,
              backgroundImageUrl: '',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              height: 264,
              heightUnit: 'px',
              paddingX: 0,
              paddingY: 0,
            }],
            elements: [element],
          },
        }],
      },
    }],
  }
}

const useSavedElement = (item) => {
  insertSavedLibraryItem(item)
  savedElementLibraryOpen.value = false
}

const insertSavedLibraryItem = (item) => {
  if (!editorDoc.value || !item?.content) return
  const itemType = String(item.itemType || '').trim().toLowerCase()
  const content = deepClone(item.content)

  if (itemType === 'section' && String(content?.type || '') === 'section') {
    if (!Array.isArray(editorDoc.value.sections)) editorDoc.value.sections = []
    const requested = Number.isFinite(Number(pendingInsertIndex.value))
      ? Math.max(0, Number(pendingInsertIndex.value) + 1)
      : (selectedIdx.value >= 0 ? selectedIdx.value + 1 : editorDoc.value.sections.length)
    const at = Math.max(0, Math.min(editorDoc.value.sections.length, requested))
    editorDoc.value.sections.splice(at, 0, content)
    selectedIdx.value = at
    selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
    selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
    selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
    closeSavedLibraryPalette()
    return
  }

  if (itemType === 'row' && String(content?.type || '') === 'row') {
    const sectionIndex = getActiveSectionIndexForRows()
    const section = editorDoc.value?.sections?.[sectionIndex]
    if (!section || String(section.type || '') !== 'section') return
    ensureSectionBlockProps(section)
    const rows = section.props.rows
    const at = selectedRow.value ? Number(selectedRowContext.value.rowIndex) + 1 : rows.length
    rows.splice(Math.max(0, Math.min(rows.length, at)), 0, content)
    onSelectRow({ sectionIndex, rowIndex: Math.max(0, Math.min(rows.length - 1, at)) })
    closeSavedLibraryPalette()
    return
  }

  if (itemType === 'element' && content?.type) {
    const target = getActiveRowContextForElements()
    const rowTarget = getRowTargetForElements(target.sectionIndex, target.rowIndex, -1)
    if (!rowTarget?.targetRow) return
    const elements = rowTarget.targetRow.props.elements
    const selectedColumnIndex = selectedColumn.value?.columnIndex
    const fallbackColumnIndex = selectedElement.value
      ? getEditorRowElementColumnIndex(selectedElement.value, rowTarget.targetRow)
      : 0
    if (!content.props || typeof content.props !== 'object') content.props = {}
    content.props.rowElementColumnIndex = Number.isFinite(Number(selectedColumnIndex)) ? Number(selectedColumnIndex) : fallbackColumnIndex
    const at = Number.isFinite(Number(target.elementIndex)) && Number(target.elementIndex) >= 0
      ? Math.min(elements.length, Number(target.elementIndex) + 1)
      : elements.length
    elements.splice(at, 0, content)
    onSelectRowElement({ sectionIndex: target.sectionIndex, rowIndex: target.rowIndex, elementIndex: at })
    closeSavedLibraryPalette()
    return
  }

  if (itemType === 'column' && (selectedColumn.value?.row || selectedRow.value) && content?.type === 'column') {
    const row = selectedColumn.value?.row || selectedRow.value
    ensureRowBlockProps(row)
    const count = Math.max(1, Math.min(6, Number(row?.props?.rowColumnCount || 1)))
    if (count >= 6) {
      window.alert('A row can contain up to six columns. Select another row or remove a column first.')
      return
    }
    const newColumnIndex = count
    row.props.rowColumnCount = count + 1
    if (!Array.isArray(row.props.rowColumnStyles)) row.props.rowColumnStyles = []
    row.props.rowColumnStyles.push(deepClone(content.style || {}))
    ;(Array.isArray(content.elements) ? content.elements : []).forEach(element => {
      const clone = deepClone(element)
      if (!clone.props || typeof clone.props !== 'object') clone.props = {}
      clone.props.rowElementColumnIndex = newColumnIndex
      row.props.elements.push(clone)
    })
    onSelectRowColumn({
      sectionIndex: selectedRowContext.value.sectionIndex,
      rowIndex: selectedRowContext.value.rowIndex,
      columnIndex: newColumnIndex,
      nestedRowElementIndex: selectedColumn.value?.nestedRowElementIndex ?? -1,
    })
    closeSavedLibraryPalette()
  }
}

const normalizeApiImagePath = (value) => {
  const raw = resolveStoredMediaUrl(value)
  if (!raw) return ''
  const marker = '/api/image/'
  const index = raw.toLowerCase().indexOf(marker)
  if (index >= 0) return raw.slice(index)
  return raw
}

const createLoginTemplateElement = (type, role, columnIndex, props = {}) => {
  const normalizedType = String(type || 'text').trim().toLowerCase()
  const base = {
    type: normalizedType,
    props: {
      elementWidthPercent: 100,
      elementHeight: 120,
      elementHeightUnit: 'px',
      elementMargin: 0,
      elementPadding: 12,
      elementPaddingX: 12,
      elementPaddingY: 12,
      elementAlign: 'left',
      elementBackgroundColor: 'transparent',
      rowElementColumnIndex: Math.max(0, toRoundedNumber(columnIndex, 0)),
      templateRole: String(role || '').trim(),
      ...props,
    },
  }
  return base
}

const getTemplateElementByRole = (row, role) => {
  const normalizedRole = String(role || '').trim()
  const walk = (elements) => {
    const list = Array.isArray(elements) ? elements : []
    for (const element of list) {
      if (String(element?.props?.templateRole || '').trim() === normalizedRole) return element
      if (String(element?.type || '').trim().toLowerCase() === 'row') {
        const nested = walk(element?.props?.elements)
        if (nested) return nested
      }
    }
    return null
  }
  return walk(row?.props?.elements)
}

const isCurrentMemberLoginTemplateSection = (section) => {
  if (!section || String(section?.type || '') !== 'section') return false
  if (String(section?.props?.sectionTemplateType || '') !== MEMBER_LOGIN_TEMPLATE_SECTION) return false
  return Number(section?.props?.sectionTemplateVersion || 0) >= MEMBER_LOGIN_TEMPLATE_VERSION
}

const buildMemberLoginTemplateSection = (legacyBlock = {}) => {
  const legacyProps = (legacyBlock?.props && typeof legacyBlock.props === 'object')
    ? { ...MEMBER_LOGIN_DEFAULTS, ...legacyBlock.props }
    : { ...MEMBER_LOGIN_DEFAULTS }
  const partSettings = (legacyProps?.loginPartSettings && typeof legacyProps.loginPartSettings === 'object')
    ? legacyProps.loginPartSettings
    : {}
  const leftPanelSettings = (partSettings.left_panel && typeof partSettings.left_panel === 'object') ? partSettings.left_panel : {}
  const leftLogoSettings = (partSettings.left_logo && typeof partSettings.left_logo === 'object') ? partSettings.left_logo : {}
  const rightLogoSettings = (partSettings.right_logo && typeof partSettings.right_logo === 'object') ? partSettings.right_logo : {}
  const leftHeadlineSettings = (partSettings.left_headline && typeof partSettings.left_headline === 'object') ? partSettings.left_headline : {}
  const rightHeadingSettings = (partSettings.right_heading && typeof partSettings.right_heading === 'object') ? partSettings.right_heading : {}
  const joinPromptSettings = (partSettings.join_prompt && typeof partSettings.join_prompt === 'object') ? partSettings.join_prompt : {}
  const joinInlineRow = {
    type: 'row',
    props: {
      templateRole: 'join_inline_row',
      rowColumnCount: 2,
      rowColumnGutter: 0,
      rowWidthPercent: 100,
      rowHeight: 24,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 0,
      rowPaddingY: 0,
      rowMargin: 0,
      rowAlign: 'left',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        {
          widthPercent: 50,
          backgroundColor: 'transparent',
          backgroundImageFileId: null,
          backgroundImageUrl: '',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          height: 24,
          heightUnit: 'px',
          paddingX: 0,
          paddingY: 0,
          contentJustify: 'center',
          contentAlign: 'end',
        },
        {
          widthPercent: 50,
          backgroundColor: 'transparent',
          backgroundImageFileId: null,
          backgroundImageUrl: '',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          height: 24,
          heightUnit: 'px',
          paddingX: 0,
          paddingY: 0,
          contentJustify: 'center',
          contentAlign: 'start',
        },
      ],
      elements: [
        createLoginTemplateElement('text', 'join_prompt', 0, {
          content: String(legacyProps.joinPromptText || MEMBER_LOGIN_DEFAULTS.joinPromptText),
          elementAlign: 'right',
          elementWidthPercent: 100,
          elementHeight: 24,
          elementPaddingX: 0,
          elementPaddingY: 0,
          elementMargin: 0,
          textSettings: {
            fontSize: Math.max(10, toRoundedNumber(joinPromptSettings.fontSize, 14)),
            fontWeight: Math.max(100, Math.min(900, Math.round(toRoundedNumber(joinPromptSettings.fontWeight, 400) / 100) * 100)),
            lineHeight: Math.max(0.8, Math.min(3, Number.isFinite(Number(joinPromptSettings.lineHeight)) ? Number(joinPromptSettings.lineHeight) : 1.5)),
            textColor: normalizeHexColor(joinPromptSettings.textColor, '#6b7280'),
            textAlign: normalizeTextAlign(joinPromptSettings.textAlign, 'right'),
          },
        }),
        createLoginTemplateElement('text', 'join_link', 1, {
          content: String(legacyProps.joinLinkText || MEMBER_LOGIN_DEFAULTS.joinLinkText),
          elementAlign: 'left',
          elementWidthPercent: 100,
          elementHeight: 24,
          elementPaddingX: 0,
          elementPaddingY: 0,
          elementMargin: 0,
          textSettings: {
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.5,
            textColor: '#2D8CFF',
            textAlign: 'left',
            linkAddress: (() => {
              const raw = String(legacyProps.joinLinkHref || MEMBER_LOGIN_DEFAULTS.joinLinkHref || 'join').trim()
              if (!raw) return '/join'
              if (/^(https?:)?\/\//i.test(raw)) return raw
              return raw.startsWith('/') ? raw : `/${raw.replace(/^\/+/, '')}`
            })(),
            openInNewTab: false,
          },
        }),
      ],
      elementWidthPercent: 74,
      elementHeight: 24,
      elementHeightUnit: 'px',
      elementPadding: 0,
      elementPaddingX: 0,
      elementPaddingY: 0,
      elementMargin: 0,
      elementAlign: 'center',
      elementBackgroundColor: 'transparent',
      rowElementColumnIndex: 1,
    },
  }
  const shouldUseCreateFreeAccountWidget = (() => {
    const joinTarget = normalizeMemberPageSlug(legacyProps?.joinLinkHref || '')
    const headingText = String(legacyProps?.rightHeading || '').toLowerCase()
    const submitText = String(legacyProps?.submitButtonText || '').toLowerCase()
    return (
      isJoinPageContext.value
      || joinTarget === '/login'
      || headingText.includes('create your account')
      || submitText.includes('sign up')
      || submitText.includes('create account')
    )
  })()
  const loginFormElementType = shouldUseCreateFreeAccountWidget
    ? MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE
    : 'member-login-form'

  return {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_LOGIN_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_LOGIN_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 100,
      sectionHeightUnit: 'vh',
      sectionPadding: 0,
      sectionPaddingX: 0,
      sectionPaddingY: 0,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#FFFFFF',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [{
        type: 'row',
        props: {
          rowTemplateType: MEMBER_LOGIN_TEMPLATE_ROW,
          rowColumnCount: 2,
          rowColumnGutter: 0,
          rowWidthPercent: 100,
          rowHeight: 100,
          rowHeightUnit: 'vh',
          rowPadding: 0,
          rowPaddingX: 0,
          rowPaddingY: 0,
          rowMargin: 0,
          rowAlign: 'left',
          rowBackgroundColor: 'transparent',
          rowBackgroundImageFileId: null,
          rowBackgroundImageUrl: '',
          rowBackgroundSize: 'cover',
          rowBackgroundPosition: 'center',
          rowBackgroundRepeat: 'no-repeat',
          rowColumnStyles: [
            {
              widthPercent: 50,
              backgroundColor: '#111827',
              backgroundImageFileId: sanitizeMediaFileId(leftPanelSettings.imageFileId || leftPanelSettings.image_file_id) || null,
              backgroundImageUrl: resolveStoredMediaUrl(legacyProps.backgroundImageUrl),
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              height: 100,
              heightUnit: 'vh',
              paddingX: 0,
              paddingY: 0,
              contentJustify: 'center',
              contentAlign: 'center',
            },
            {
              widthPercent: 50,
              backgroundColor: '#FFFFFF',
              backgroundImageFileId: null,
              backgroundImageUrl: '',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              height: 100,
              heightUnit: 'vh',
              paddingX: 80,
              paddingY: 0,
              contentJustify: 'center',
              contentAlign: 'center',
            },
          ],
          elements: [
            createLoginTemplateElement('image', 'left_logo', 0, {
              imageFileId: sanitizeMediaFileId(leftLogoSettings.imageFileId || leftLogoSettings.image_file_id) || null,
              imageUrl: resolveStoredMediaUrl(legacyProps.leftLogoUrl),
              imageWidth: 44,
              imageAlign: 'center',
              imageBorderRadius: 0,
              imageShadowStrength: 0,
              elementAlign: 'center',
              elementWidthPercent: 74,
              elementHeight: 70,
              elementPaddingX: 0,
              elementPaddingY: 0,
              elementMargin: 4,
            }),
            createLoginTemplateElement('text', 'left_headline', 0, {
              content: String(legacyProps.leftPanelHeadline || MEMBER_LOGIN_DEFAULTS.leftPanelHeadline),
              elementAlign: 'center',
              elementWidthPercent: 74,
              elementHeight: 36,
              elementPaddingX: 0,
              elementPaddingY: 0,
              elementMargin: 0,
              textSettings: {
                fontSize: Math.max(10, toRoundedNumber(leftHeadlineSettings.fontSize, 20)),
                fontWeight: Math.max(100, Math.min(900, Math.round(toRoundedNumber(leftHeadlineSettings.fontWeight, 400) / 100) * 100)),
                lineHeight: Math.max(0.8, Math.min(3, Number.isFinite(Number(leftHeadlineSettings.lineHeight)) ? Number(leftHeadlineSettings.lineHeight) : 1.4)),
                textColor: normalizeHexColor(leftHeadlineSettings.textColor, '#FFFFFF'),
                textAlign: normalizeTextAlign(leftHeadlineSettings.textAlign, 'center'),
              },
            }),
            createLoginTemplateElement('image', 'right_logo', 1, {
              imageFileId: sanitizeMediaFileId(rightLogoSettings.imageFileId || rightLogoSettings.image_file_id) || null,
              imageUrl: resolveStoredMediaUrl(legacyProps.rightLogoUrl),
              imageWidth: 41,
              imageAlign: 'left',
              imageBorderRadius: 0,
              imageShadowStrength: 0,
              elementAlign: 'center',
              elementWidthPercent: 74,
              elementHeight: 56,
              elementPaddingX: 0,
              elementPaddingY: 0,
              elementMargin: 0,
            }),
            createLoginTemplateElement('text', 'right_heading', 1, {
              content: String(legacyProps.rightHeading || MEMBER_LOGIN_DEFAULTS.rightHeading),
              elementAlign: 'center',
              elementWidthPercent: 74,
              elementHeight: 48,
              elementPaddingX: 0,
              elementPaddingY: 0,
              elementMargin: 4,
              textSettings: {
                fontSize: Math.max(10, toRoundedNumber(rightHeadingSettings.fontSize, 24)),
                fontWeight: Math.max(100, Math.min(900, Math.round(toRoundedNumber(rightHeadingSettings.fontWeight, 700) / 100) * 100)),
                lineHeight: Math.max(0.8, Math.min(3, Number.isFinite(Number(rightHeadingSettings.lineHeight)) ? Number(rightHeadingSettings.lineHeight) : 1.2)),
                textColor: normalizeHexColor(rightHeadingSettings.textColor, '#111827'),
                textAlign: normalizeTextAlign(rightHeadingSettings.textAlign, 'left'),
              },
            }),
            joinInlineRow,
            createLoginTemplateElement(loginFormElementType, 'login_form', 1, {
              elementAlign: 'center',
              elementWidthPercent: 74,
              elementHeight: 260,
              elementPaddingX: 0,
              elementPaddingY: 0,
              elementMargin: 10,
              formLocked: true,
              forgotPasswordText: String(legacyProps.forgotPasswordText || MEMBER_LOGIN_DEFAULTS.forgotPasswordText),
              forgotPasswordHref: String(legacyProps.forgotPasswordHref || MEMBER_LOGIN_DEFAULTS.forgotPasswordHref),
              submitButtonText: String(legacyProps.submitButtonText || MEMBER_LOGIN_DEFAULTS.submitButtonText),
              joinLinkHref: String(legacyProps.joinLinkHref || MEMBER_LOGIN_DEFAULTS.joinLinkHref),
              loginSubmitButtonColor: normalizeHexColor(
                legacyProps.loginSubmitButtonColor || legacyProps.submitButtonColor,
                MEMBER_LOGIN_DEFAULTS.submitButtonColor
              ),
              loginForgotPasswordColor: normalizeHexColor(
                legacyProps.loginForgotPasswordColor || legacyProps.forgotPasswordColor,
                MEMBER_LOGIN_DEFAULTS.forgotPasswordColor
              ),
              loginRememberCheckboxColor: normalizeHexColor(
                legacyProps.loginRememberCheckboxColor || legacyProps.rememberCheckboxColor,
                MEMBER_LOGIN_DEFAULTS.rememberCheckboxColor
              ),
            }),
          ],
        },
      }],
    },
  }
}

const buildLegacyMemberLoginBlockFromTemplateSection = (section = {}) => {
  const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
  const row = rows.find((item) => String(item?.type || '') === 'row') || null
  const leftLogo = getTemplateElementByRole(row, 'left_logo')
  const leftHeadline = getTemplateElementByRole(row, 'left_headline')
  const rightLogo = getTemplateElementByRole(row, 'right_logo')
  const rightHeading = getTemplateElementByRole(row, 'right_heading')
  const joinPrompt = getTemplateElementByRole(row, 'join_prompt')
  const joinLink = getTemplateElementByRole(row, 'join_link')
  const loginForm = getTemplateElementByRole(row, 'login_form')
  const leftPanelStyle = getRowColumnStyleEntry(row, 0)
  const leftHeadlineSettings = (leftHeadline?.props?.textSettings && typeof leftHeadline.props.textSettings === 'object') ? leftHeadline.props.textSettings : {}
  const rightHeadingSettings = (rightHeading?.props?.textSettings && typeof rightHeading.props.textSettings === 'object') ? rightHeading.props.textSettings : {}
  const joinPromptSettings = (joinPrompt?.props?.textSettings && typeof joinPrompt.props.textSettings === 'object') ? joinPrompt.props.textSettings : {}

  return {
    type: 'member-login',
    props: {
      pageEditorTemplateSection: deepClone(section),
      leftPanelHeadline: String(leftHeadline?.props?.content || MEMBER_LOGIN_DEFAULTS.leftPanelHeadline),
      rightHeading: String(rightHeading?.props?.content || MEMBER_LOGIN_DEFAULTS.rightHeading),
      joinPromptText: String(joinPrompt?.props?.content || MEMBER_LOGIN_DEFAULTS.joinPromptText),
      joinLinkText: String(joinLink?.props?.content || MEMBER_LOGIN_DEFAULTS.joinLinkText),
      joinLinkHref: String(loginForm?.props?.joinLinkHref || MEMBER_LOGIN_DEFAULTS.joinLinkHref),
      forgotPasswordText: String(loginForm?.props?.forgotPasswordText || MEMBER_LOGIN_DEFAULTS.forgotPasswordText),
      forgotPasswordHref: String(loginForm?.props?.forgotPasswordHref || MEMBER_LOGIN_DEFAULTS.forgotPasswordHref),
      submitButtonText: String(loginForm?.props?.submitButtonText || MEMBER_LOGIN_DEFAULTS.submitButtonText),
      submitButtonColor: normalizeHexColor(
        loginForm?.props?.loginSubmitButtonColor || loginForm?.props?.submitButtonColor,
        MEMBER_LOGIN_DEFAULTS.submitButtonColor
      ),
      forgotPasswordColor: normalizeHexColor(
        loginForm?.props?.loginForgotPasswordColor || loginForm?.props?.forgotPasswordColor,
        MEMBER_LOGIN_DEFAULTS.forgotPasswordColor
      ),
      rememberCheckboxColor: normalizeHexColor(
        loginForm?.props?.loginRememberCheckboxColor || loginForm?.props?.rememberCheckboxColor,
        MEMBER_LOGIN_DEFAULTS.rememberCheckboxColor
      ),
      leftLogoUrl: normalizeApiImagePath(leftLogo?.props?.imageUrl || MEMBER_LOGIN_DEFAULTS.leftLogoUrl),
      rightLogoUrl: normalizeApiImagePath(rightLogo?.props?.imageUrl || MEMBER_LOGIN_DEFAULTS.rightLogoUrl),
      backgroundImageUrl: normalizeApiImagePath(leftPanelStyle?.backgroundImageUrl || MEMBER_LOGIN_DEFAULTS.backgroundImageUrl),
      loginPartSettings: {
        left_panel: {
          imageFileId: sanitizeMediaFileId(leftPanelStyle?.backgroundImageFileId) || null,
        },
        left_logo: {
          imageFileId: sanitizeMediaFileId(leftLogo?.props?.imageFileId) || null,
        },
        right_logo: {
          imageFileId: sanitizeMediaFileId(rightLogo?.props?.imageFileId) || null,
        },
        left_headline: {
          fontSize: Math.max(10, toRoundedNumber(leftHeadlineSettings.fontSize, 20)),
          fontWeight: Math.max(100, Math.min(900, Math.round(toRoundedNumber(leftHeadlineSettings.fontWeight, 400) / 100) * 100)),
          lineHeight: Math.max(0.8, Math.min(3, Number.isFinite(Number(leftHeadlineSettings.lineHeight)) ? Number(leftHeadlineSettings.lineHeight) : 1.4)),
          textColor: normalizeHexColor(leftHeadlineSettings.textColor, '#FFFFFF'),
          textAlign: normalizeTextAlign(leftHeadlineSettings.textAlign, 'center'),
        },
        right_heading: {
          fontSize: Math.max(10, toRoundedNumber(rightHeadingSettings.fontSize, 24)),
          fontWeight: Math.max(100, Math.min(900, Math.round(toRoundedNumber(rightHeadingSettings.fontWeight, 700) / 100) * 100)),
          lineHeight: Math.max(0.8, Math.min(3, Number.isFinite(Number(rightHeadingSettings.lineHeight)) ? Number(rightHeadingSettings.lineHeight) : 1.2)),
          textColor: normalizeHexColor(rightHeadingSettings.textColor, '#111827'),
          textAlign: normalizeTextAlign(rightHeadingSettings.textAlign, 'left'),
        },
        join_prompt: {
          fontSize: Math.max(10, toRoundedNumber(joinPromptSettings.fontSize, 14)),
          fontWeight: Math.max(100, Math.min(900, Math.round(toRoundedNumber(joinPromptSettings.fontWeight, 400) / 100) * 100)),
          lineHeight: Math.max(0.8, Math.min(3, Number.isFinite(Number(joinPromptSettings.lineHeight)) ? Number(joinPromptSettings.lineHeight) : 1.5)),
          textColor: normalizeHexColor(joinPromptSettings.textColor, '#6b7280'),
          textAlign: normalizeTextAlign(joinPromptSettings.textAlign, 'left'),
        },
      },
    },
  }
}

const shouldMigrateLegacyPricingTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  if (sections.some((section) => String(section?.type || '') === 'section')) return false
  const titleHaystack = `${String(doc?.title || '')} ${String(doc?.seo?.title || '')}`.toLowerCase()
  const hasPricingMarker = /\bpricing\b/.test(titleHaystack)
  const hasHomeMarker = /\bhome\b/.test(titleHaystack)
  if (!hasPricingMarker || hasHomeMarker) return false
  return true
}

const shouldMigrateLegacyGetAccessSuccessTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  if (sections.some((section) => String(section?.type || '') === 'section')) return false
  const titleHaystack = `${String(doc?.title || '')} ${String(doc?.seo?.title || '')}`.toLowerCase()
  const hasSuccessMarker =
    titleHaystack.includes('get access success') ||
    titleHaystack.includes('signup success') ||
    titleHaystack.includes('account confirmed') ||
    titleHaystack.includes('confirmation success')
  const hasPricingMarker = /\bpricing\b/.test(titleHaystack)
  const hasHomeMarker = /\bhome\b/.test(titleHaystack)
  const hasLoginMarker = /\blogin\b/.test(titleHaystack)
  if (!hasSuccessMarker || hasPricingMarker || hasHomeMarker || hasLoginMarker) return false
  return true
}

const shouldRefreshDefaultMemberGetAccessSuccessTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  const successTemplateSections = sections.filter((section) =>
    String(section?.type || '') === 'section' &&
    String(section?.props?.sectionTemplateType || '') === MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_SECTION
  )
  if (!successTemplateSections.length || successTemplateSections.length !== sections.length) return false
  const isOutdated = successTemplateSections.some((section) =>
    Number(section?.props?.sectionTemplateVersion || 0) < MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_VERSION
  )
  return isOutdated
}

const shouldMigrateLegacyMemberApprovalTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false

  const types = sections.map((section) => String(section?.type || '').trim().toLowerCase())
  if (types.includes('member-approval-form')) return true
  if (sections.some((section) => String(section?.type || '') === 'section')) return false

  const titleHaystack = `${String(doc?.title || '')} ${String(doc?.seo?.title || '')}`.toLowerCase()
  const hasApprovalMarker = titleHaystack.includes('approval')
  if (!hasApprovalMarker) return false
  const hasKnownNonApprovalMarker =
    titleHaystack.includes('login') ||
    titleHaystack.includes('get access') ||
    titleHaystack.includes('pricing') ||
    titleHaystack.includes('home')
  if (hasKnownNonApprovalMarker) return false
  return types.includes('header') && types.includes('text')
}

const shouldRefreshDefaultMemberApprovalTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  const approvalTemplateSections = sections.filter((section) =>
    String(section?.type || '') === 'section' &&
    String(section?.props?.sectionTemplateType || '') === MEMBER_APPROVAL_TEMPLATE_SECTION
  )
  if (!approvalTemplateSections.length || approvalTemplateSections.length !== sections.length) return false
  return approvalTemplateSections.some((section) =>
    Number(section?.props?.sectionTemplateVersion || 0) < MEMBER_APPROVAL_TEMPLATE_VERSION
  )
}

const buildMemberApprovalTemplateSections = (legacyDoc = {}) => {
  const legacySections = Array.isArray(legacyDoc?.sections) ? legacyDoc.sections : []
  const legacyHeader = legacySections.find((section) => String(section?.type || '').trim().toLowerCase() === 'header') || null
  const legacyText = legacySections.find((section) => String(section?.type || '').trim().toLowerCase() === 'text') || null
  const legacyForm = legacySections.find((section) => String(section?.type || '').trim().toLowerCase() === 'member-approval-form') || null

  const headingText = String(legacyHeader?.props?.text || 'Request Membership Approval').trim() || 'Request Membership Approval'
  const helperText = String(
    legacyText?.props?.content || 'Submit your details and the creator will contact you directly.'
  ).trim() || 'Submit your details and the creator will contact you directly.'
  const formHelperText = String(
    legacyForm?.props?.formHelperText || 'This form sends the request to the creator and stores it in Leads.'
  ).trim() || 'This form sends the request to the creator and stores it in Leads.'
  const submitButtonText = String(
    legacyForm?.props?.submitButtonText || MEMBER_APPROVAL_DEFAULTS.submitButtonText
  ).trim() || MEMBER_APPROVAL_DEFAULTS.submitButtonText

  return [
    {
      type: 'section',
      props: {
        sectionTemplateType: MEMBER_APPROVAL_TEMPLATE_SECTION,
        sectionTemplateVersion: MEMBER_APPROVAL_TEMPLATE_VERSION,
        sectionWidthPercent: 100,
        sectionHeight: 640,
        sectionHeightUnit: 'px',
        sectionPadding: 0,
        sectionPaddingX: 24,
        sectionPaddingY: 48,
        sectionMargin: 0,
        sectionAlign: 'left',
        sectionBackgroundColor: '#FFFFFF',
        sectionBackgroundImageFileId: null,
        sectionBackgroundImageUrl: '',
        sectionBackgroundSize: 'cover',
        sectionBackgroundPosition: 'center',
        sectionBackgroundRepeat: 'no-repeat',
        rows: [
          {
            type: 'row',
            props: {
              rowColumnCount: 1,
              rowColumnGutter: 0,
              rowWidthPercent: 100,
              rowHeight: 520,
              rowHeightUnit: 'px',
              rowPadding: 0,
              rowPaddingX: 0,
              rowPaddingY: 0,
              rowMargin: 0,
              rowAlign: 'center',
              rowBackgroundColor: 'transparent',
              rowBackgroundImageFileId: null,
              rowBackgroundImageUrl: '',
              rowBackgroundSize: 'cover',
              rowBackgroundPosition: 'center',
              rowBackgroundRepeat: 'no-repeat',
              rowColumnStyles: [
                {
                  widthPercent: 100,
                  backgroundColor: 'transparent',
                  backgroundImageFileId: null,
                  backgroundImageUrl: '',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  height: 520,
                  heightUnit: 'px',
                  paddingX: 0,
                  paddingY: 0,
                  contentJustify: 'center',
                  contentAlign: 'center',
                },
              ],
              elements: [
                {
                  type: 'header',
                  props: {
                    text: headingText,
                    level: 'h1',
                    size: 38,
                    elementWidthPercent: 100,
                    elementHeight: 76,
                    elementHeightUnit: 'px',
                    elementAlign: 'center',
                    elementPadding: 0,
                    elementPaddingX: 0,
                    elementPaddingY: 0,
                    elementMargin: 0,
                    elementBackgroundColor: 'transparent',
                    rowElementColumnIndex: 0,
                  },
                },
                {
                  type: 'text',
                  props: {
                    content: helperText,
                    elementWidthPercent: 100,
                    elementHeight: 64,
                    elementHeightUnit: 'px',
                    elementAlign: 'center',
                    elementPadding: 0,
                    elementPaddingX: 0,
                    elementPaddingY: 0,
                    elementMargin: 0,
                    elementBackgroundColor: 'transparent',
                    rowElementColumnIndex: 0,
                  },
                },
                {
                  type: 'member-approval-form',
                  props: {
                    formLocked: true,
                    formMode: 'member-approval-default',
                    formHelperText,
                    submitButtonText,
                    elementWidthPercent: 100,
                    elementHeight: 360,
                    elementHeightUnit: 'px',
                    elementAlign: 'center',
                    elementPadding: 0,
                    elementPaddingX: 0,
                    elementPaddingY: 0,
                    elementMargin: 8,
                    elementBackgroundColor: 'transparent',
                    rowElementColumnIndex: 0,
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ]
}

const shouldMigrateLegacyGetAccessTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  if (sections.some((section) => String(section?.type || '') === 'section')) return false
  const titleHaystack = `${String(doc?.title || '')} ${String(doc?.seo?.title || '')}`.toLowerCase()
  const hasGetAccessMarker =
    titleHaystack.includes('get access') ||
    titleHaystack.includes('memberapp signup') ||
    titleHaystack.includes('signup')
  const hasPricingMarker = /\bpricing\b/.test(titleHaystack)
  const hasHomeMarker = /\bhome\b/.test(titleHaystack)
  const hasLoginMarker = /\blogin\b/.test(titleHaystack)
  if (!hasGetAccessMarker || hasPricingMarker || hasHomeMarker || hasLoginMarker) return false
  return true
}

const shouldRefreshDefaultMemberGetAccessTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  const getAccessTemplateSections = sections.filter((section) =>
    String(section?.type || '') === 'section' &&
    String(section?.props?.sectionTemplateType || '') === MEMBER_GET_ACCESS_TEMPLATE_SECTION
  )
  if (!getAccessTemplateSections.length || getAccessTemplateSections.length !== sections.length) return false
  const isOutdated = getAccessTemplateSections.some((section) =>
    Number(section?.props?.sectionTemplateVersion || 0) < MEMBER_GET_ACCESS_TEMPLATE_VERSION
  )
  return isOutdated
}

const shouldRefreshDefaultMemberPricingTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  const titleHaystack = `${String(doc?.title || '')} ${String(doc?.seo?.title || '')}`.toLowerCase()
  const hasPricingMarker = /\bpricing\b/.test(titleHaystack)
  const hasHomeMarker = /\bhome\b/.test(titleHaystack)
  if (!hasPricingMarker || hasHomeMarker) return false
  const pricingTemplateSections = sections.filter((section) =>
    String(section?.type || '') === 'section' &&
    String(section?.props?.sectionTemplateType || '') === MEMBER_PRICING_TEMPLATE_SECTION
  )
  if (!pricingTemplateSections.length || pricingTemplateSections.length !== sections.length) return false
  const isOutdated = pricingTemplateSections.some((section) =>
    Number(section?.props?.sectionTemplateVersion || 0) < MEMBER_PRICING_TEMPLATE_VERSION
  )
  return isOutdated
}

const shouldMigrateLegacyHomeTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  if (sections.some((section) => String(section?.type || '') === 'section')) return false
  const titleHaystack = `${String(doc?.title || '')} ${String(doc?.seo?.title || '')}`.toLowerCase()
  const hasHomeMarker =
    titleHaystack.includes('home/pricing') ||
    /\bhome\b/.test(titleHaystack) ||
    titleHaystack.includes('memberapp onboarding')
  if (!hasHomeMarker) return false
  const types = sections.map((section) => String(section?.type || '').trim().toLowerCase())
  if (types.includes('hero') || types.includes('features')) return true

  const hasLegacyDefaultCopy = sections.some((section) => {
    if (String(section?.type || '').trim().toLowerCase() !== 'text') return false
    const content = String(section?.props?.content || '').toLowerCase()
    return content.includes('default home') && content.includes('site page editor')
  })

  if (hasLegacyDefaultCopy) return true
  return types.includes('header') && types.includes('text')
}

const shouldRefreshDefaultMemberHomeTemplateDoc = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  if (!sections.length) return false
  const titleHaystack = `${String(doc?.title || '')} ${String(doc?.seo?.title || '')}`.toLowerCase()
  const hasHomeMarker =
    titleHaystack.includes('home/pricing') ||
    /\bhome\b/.test(titleHaystack) ||
    titleHaystack.includes('memberapp onboarding')
  if (!hasHomeMarker) return false
  const homeTemplateSections = sections.filter((section) =>
    String(section?.type || '') === 'section' &&
    String(section?.props?.sectionTemplateType || '') === MEMBER_HOME_TEMPLATE_SECTION
  )
  if (!homeTemplateSections.length || homeTemplateSections.length !== sections.length) return false
  const isOutdated = homeTemplateSections.some((section) =>
    Number(section?.props?.sectionTemplateVersion || 0) < MEMBER_HOME_TEMPLATE_VERSION
  )
  if (!isOutdated) return false

  return true
}

const createHomeTemplateElement = (type, columnIndex, props = {}) => {
  const normalizedType = String(type || 'text').trim().toLowerCase()
  return {
    type: normalizedType,
    props: {
      elementWidthPercent: 100,
      elementHeight: 120,
      elementHeightUnit: 'px',
      elementMargin: 0,
      elementPadding: 0,
      elementPaddingX: 0,
      elementPaddingY: 0,
      elementAlign: 'left',
      elementBackgroundColor: 'transparent',
      rowElementColumnIndex: Math.max(0, toRoundedNumber(columnIndex, 0)),
      ...props,
    },
  }
}

const buildMemberHomeTemplateSections = (legacyDoc = {}) => {
  const legacySections = Array.isArray(legacyDoc?.sections) ? legacyDoc.sections : []
  const legacyHero = legacySections.find((section) => String(section?.type || '').trim().toLowerCase() === 'hero') || null

  const heroHeadline = String(legacyHero?.props?.headline || MEMBER_HOME_DEFAULTS.heroHeadline)
  const heroSub = String(legacyHero?.props?.sub || MEMBER_HOME_DEFAULTS.heroSub)
  const year = new Date().getFullYear()

  const createHomeTextSettings = (overrides = {}) => ({
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    textColor: '#18181B',
    textAlign: 'left',
    linkAddress: '',
    openInNewTab: false,
    ...overrides,
  })

  const createHomeButtonProps = (text, linkAddress = '') => ({
    buttonText: String(text || '').trim() || 'Button Element',
    button_text: String(text || '').trim() || 'Button Element',
    content: String(text || '').trim() || 'Button Element',
    buttonBackgroundColor: '#2D8CFF',
    buttonTextColor: '#FFFFFF',
    buttonTextSize: 100,
    buttonFontSize: 14,
    buttonActionType: 'go_to',
    buttonGoToType: 'custom_link',
    buttonFeatureSlug: '',
    buttonLinkAddress: String(linkAddress || '').trim(),
    buttonOpenInNewTab: false,
    buttonFileId: null,
    buttonFileUrl: '',
    buttonFileName: '',
    buttonFileMimeType: '',
    buttonWidth: 22,
    buttonPaddingX: 14,
    buttonPaddingY: 10,
    buttonBorderRadius: 6,
    buttonAlign: 'center',
  })

  const navRow = {
    type: 'row',
    props: {
      rowColumnCount: 3,
      rowColumnGutter: 0,
      rowWidthPercent: 100,
      rowHeight: 96,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 24,
      rowPaddingY: 24,
      rowMargin: 0,
      rowAlign: 'center',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        { widthPercent: 42, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
        { widthPercent: 16, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
        { widthPercent: 42, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'right' },
      ],
      elements: [
        createHomeTemplateElement('image', 0, {
          imageFileId: null,
          imageUrl: MEMBER_HOME_DEFAULTS.logoUrl,
          imageWidth: 100,
          imageAlign: 'left',
          imageBorderRadius: 0,
          imageShadowStrength: 0,
          elementWidthPercent: 100,
          elementHeight: 48,
          elementAlign: 'left',
        }),
        createHomeTemplateElement('row', 1, {
          rowColumnCount: 2,
          rowColumnGutter: 48,
          rowWidthPercent: 100,
          rowHeight: 24,
          rowHeightUnit: 'px',
          rowPadding: 0,
          rowPaddingX: 0,
          rowPaddingY: 0,
          rowMargin: 0,
          rowAlign: 'center',
          rowBackgroundColor: 'transparent',
          rowBackgroundImageFileId: null,
          rowBackgroundImageUrl: '',
          rowBackgroundSize: 'cover',
          rowBackgroundPosition: 'center',
          rowBackgroundRepeat: 'no-repeat',
          rowColumnStyles: [
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 24, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 24, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
          ],
          elements: [
            createHomeTemplateElement('text', 0, {
              content: MEMBER_HOME_DEFAULTS.navHomeText,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'center',
              textSettings: createHomeTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'center',
                linkAddress: '/',
                openInNewTab: false,
              }),
            }),
            createHomeTemplateElement('text', 1, {
              content: MEMBER_HOME_DEFAULTS.navPricingText,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'center',
              textSettings: createHomeTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'center',
                linkAddress: '/pricing',
                openInNewTab: false,
              }),
            }),
          ],
        }),
        createHomeTemplateElement('text', 2, {
          content: MEMBER_HOME_DEFAULTS.navLoginText,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'right',
          textSettings: createHomeTextSettings({
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.4,
            textColor: '#111827',
            textAlign: 'right',
            linkAddress: '/login',
            openInNewTab: false,
          }),
        }),
      ],
    },
  }

  const heroRow = {
    type: 'row',
    props: {
      rowColumnCount: 1,
      rowColumnGutter: 0,
      rowWidthPercent: 100,
      rowHeight: 760,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 32,
      rowPaddingY: 0,
      rowMargin: 0,
      rowAlign: 'left',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 760, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
      ],
      elements: [
        createHomeTemplateElement('header', 0, {
          text: heroHeadline,
          level: 'h1',
          size: 60,
          elementWidthPercent: 100,
          elementHeight: 180,
          elementAlign: 'center',
          textSettings: createHomeTextSettings({
            fontSize: 60,
            fontWeight: 700,
            textColor: '#111827',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('text', 0, {
          content: heroSub,
          elementWidthPercent: 100,
          elementHeight: 44,
          elementAlign: 'center',
          textSettings: createHomeTextSettings({
            fontSize: 18,
            fontWeight: 400,
            lineHeight: 1.5,
            textColor: '#4b5563',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('button', 0, {
          ...createHomeButtonProps(MEMBER_HOME_DEFAULTS.heroButtonText, '/join'),
          elementWidthPercent: 100,
          elementHeight: 44,
          elementAlign: 'center',
          buttonWidth: 20,
          buttonFontSize: 14,
          buttonPaddingX: 14,
          buttonPaddingY: 10,
          buttonBorderRadius: 6,
        }),
      ],
    },
  }

  const aboutSectionOne = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_HOME_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_HOME_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 700,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 32,
      sectionPaddingY: 96,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#FFFFFF',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        {
          type: 'row',
          props: {
            rowColumnCount: 2,
            rowColumnGutter: 32,
            rowWidthPercent: 100,
            rowHeight: 500,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 500, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
              { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 500, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('header', 0, {
                text: MEMBER_HOME_DEFAULTS.whatWeDoHeading,
                level: 'h2',
                size: 36,
                elementWidthPercent: 100,
                elementHeight: 72,
                elementAlign: 'left',
                textSettings: createHomeTextSettings({
                  fontWeight: 700,
                  textColor: '#111827',
                  textAlign: 'left',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: MEMBER_HOME_DEFAULTS.whatWeDoBody,
                elementWidthPercent: 100,
                elementHeight: 170,
                elementAlign: 'left',
                textSettings: createHomeTextSettings({
                  fontSize: 18,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textColor: '#4b5563',
                  textAlign: 'left',
                }),
              }),
              createHomeTemplateElement('button', 0, {
                ...createHomeButtonProps(MEMBER_HOME_DEFAULTS.whatWeDoButtonText, '/join'),
                elementWidthPercent: 100,
                elementHeight: 44,
                elementAlign: 'left',
                buttonWidth: 22,
                buttonBorderRadius: 6,
              }),
              createHomeTemplateElement('image', 1, {
                imageFileId: null,
                imageUrl: MEMBER_HOME_DEFAULTS.whatWeDoImageUrl,
                imageWidth: 100,
                imageAlign: 'center',
                imageBorderRadius: 12,
                imageShadowStrength: 20,
                elementWidthPercent: 100,
                elementHeight: 480,
                elementAlign: 'center',
              }),
            ],
          },
        },
      ],
    },
  }

  const aboutSectionTwo = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_HOME_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_HOME_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 700,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 32,
      sectionPaddingY: 96,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#FFFFFF',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        {
          type: 'row',
          props: {
            rowColumnCount: 2,
            rowColumnGutter: 32,
            rowWidthPercent: 100,
            rowHeight: 500,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 500, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
              { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 500, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
            ],
            elements: [
              createHomeTemplateElement('image', 0, {
                imageFileId: null,
                imageUrl: MEMBER_HOME_DEFAULTS.aboutMeImageUrl,
                imageWidth: 100,
                imageAlign: 'center',
                imageBorderRadius: 12,
                imageShadowStrength: 20,
                elementWidthPercent: 100,
                elementHeight: 480,
                elementAlign: 'center',
              }),
              createHomeTemplateElement('header', 1, {
                text: MEMBER_HOME_DEFAULTS.aboutMeHeading,
                level: 'h2',
                size: 36,
                elementWidthPercent: 100,
                elementHeight: 72,
                elementAlign: 'left',
                textSettings: createHomeTextSettings({
                  fontWeight: 700,
                  textColor: '#111827',
                  textAlign: 'left',
                }),
              }),
              createHomeTemplateElement('text', 1, {
                content: MEMBER_HOME_DEFAULTS.aboutMeBody,
                elementWidthPercent: 100,
                elementHeight: 170,
                elementAlign: 'left',
                textSettings: createHomeTextSettings({
                  fontSize: 18,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textColor: '#4b5563',
                  textAlign: 'left',
                }),
              }),
              createHomeTemplateElement('button', 1, {
                ...createHomeButtonProps(MEMBER_HOME_DEFAULTS.aboutMeButtonText, '/get-access'),
                elementWidthPercent: 100,
                elementHeight: 44,
                elementAlign: 'left',
                buttonWidth: 22,
                buttonBorderRadius: 6,
              }),
            ],
          },
        },
      ],
    },
  }

  const pricingSection = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_HOME_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_HOME_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 760,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 32,
      sectionPaddingY: 96,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#FFFFFF',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        {
          type: 'row',
          props: {
            rowColumnCount: 1,
            rowColumnGutter: 0,
            rowWidthPercent: 100,
            rowHeight: 108,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 108, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('header', 0, {
                text: MEMBER_HOME_DEFAULTS.pricingHeading,
                level: 'h2',
                size: 36,
                elementWidthPercent: 100,
                elementHeight: 72,
                elementAlign: 'center',
                textSettings: createHomeTextSettings({
                  fontWeight: 700,
                  textColor: '#111827',
                  textAlign: 'center',
                }),
              }),
            ],
          },
        },
        {
          type: 'row',
          props: {
            rowColumnCount: 2,
            rowColumnGutter: 16,
            rowWidthPercent: 100,
            rowHeight: 460,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 67, backgroundColor: '#FFFFFF', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 460, heightUnit: 'px', paddingX: 24, paddingY: 24, contentJustify: 'start', contentAlign: 'left' },
              { widthPercent: 33, backgroundColor: '#f9fafb', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 460, heightUnit: 'px', paddingX: 24, paddingY: 24, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('header', 0, {
                text: MEMBER_HOME_DEFAULTS.pricingPlanHeading,
                level: 'h3',
                size: 30,
                elementWidthPercent: 100,
                elementHeight: 52,
                elementAlign: 'left',
                textSettings: createHomeTextSettings({
                  fontSize: 30,
                  fontWeight: 700,
                  textColor: '#111827',
                  textAlign: 'left',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: MEMBER_HOME_DEFAULTS.pricingIncludedHeading,
                elementWidthPercent: 100,
                elementHeight: 32,
                elementAlign: 'left',
                textSettings: createHomeTextSettings({
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.5,
                  textColor: '#2d8bffce',
                  textAlign: 'left',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: `- ${MEMBER_HOME_DEFAULTS.pricingFeatureOne}\n- ${MEMBER_HOME_DEFAULTS.pricingFeatureTwo}`,
                elementWidthPercent: 48,
                elementHeight: 84,
                elementAlign: 'left',
                textSettings: createHomeTextSettings({
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textColor: '#4b5563',
                  textAlign: 'left',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: `- ${MEMBER_HOME_DEFAULTS.pricingFeatureThree}\n- ${MEMBER_HOME_DEFAULTS.pricingFeatureFour}`,
                elementWidthPercent: 48,
                elementHeight: 84,
                elementAlign: 'right',
                textSettings: createHomeTextSettings({
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textColor: '#4b5563',
                  textAlign: 'left',
                }),
              }),
              createHomeTemplateElement('text', 1, {
                content: MEMBER_HOME_DEFAULTS.pricingCardTagline,
                elementWidthPercent: 100,
                elementHeight: 32,
                elementAlign: 'center',
                textSettings: createHomeTextSettings({
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.5,
                  textColor: '#4b5563',
                  textAlign: 'center',
                }),
              }),
              createHomeTemplateElement('text', 1, {
                content: `${MEMBER_HOME_DEFAULTS.pricingCardPrice} ${MEMBER_HOME_DEFAULTS.pricingCardCurrency}`,
                elementWidthPercent: 100,
                elementHeight: 84,
                elementAlign: 'center',
                textSettings: createHomeTextSettings({
                  fontSize: 48,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  textColor: '#111827',
                  textAlign: 'center',
                }),
              }),
              createHomeTemplateElement('button', 1, {
                ...createHomeButtonProps(MEMBER_HOME_DEFAULTS.pricingCardButtonText, '/get-access'),
                elementWidthPercent: 100,
                elementHeight: 44,
                elementAlign: 'center',
                buttonWidth: 100,
                buttonBorderRadius: 6,
              }),
              createHomeTemplateElement('text', 1, {
                content: MEMBER_HOME_DEFAULTS.pricingCardFinePrint,
                elementWidthPercent: 100,
                elementHeight: 54,
                elementAlign: 'center',
                textSettings: createHomeTextSettings({
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  textColor: '#4b5563',
                  textAlign: 'center',
                }),
              }),
            ],
          },
        },
      ],
    },
  }

  const footerSection = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_HOME_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_HOME_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 220,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 32,
      sectionPaddingY: 96,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#FFFFFF',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        {
          type: 'row',
          props: {
            rowColumnCount: 1,
            rowColumnGutter: 0,
            rowWidthPercent: 100,
            rowHeight: 140,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'left',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 140, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('image', 0, {
                imageFileId: null,
                imageUrl: MEMBER_HOME_DEFAULTS.logoUrl,
                imageWidth: 12,
                imageAlign: 'center',
                imageBorderRadius: 0,
                imageShadowStrength: 0,
                elementWidthPercent: 100,
                elementHeight: 48,
                elementAlign: 'center',
                elementMargin: 4,
              }),
              createHomeTemplateElement('text', 0, {
                content: `(c) ${year} Your Company, Inc. All rights reserved.`,
                elementWidthPercent: 100,
                elementHeight: 24,
                elementAlign: 'center',
                textSettings: createHomeTextSettings({
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.4,
                  textColor: '#6b7280',
                  textAlign: 'center',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: MEMBER_HOME_DEFAULTS.footerSupportText,
                elementWidthPercent: 100,
                elementHeight: 24,
                elementAlign: 'center',
                textSettings: createHomeTextSettings({
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.4,
                  textColor: '#6b7280',
                  textAlign: 'center',
                }),
              }),
            ],
          },
        },
      ],
    },
  }

  return [
    {
      type: 'section',
      props: {
        sectionTemplateType: MEMBER_HOME_TEMPLATE_SECTION,
        sectionTemplateVersion: MEMBER_HOME_TEMPLATE_VERSION,
        sectionWidthPercent: 100,
        sectionHeight: 840,
        sectionHeightUnit: 'px',
        sectionPadding: 0,
        sectionPaddingX: 0,
        sectionPaddingY: 0,
        sectionMargin: 0,
        sectionAlign: 'left',
        sectionBackgroundColor: '#FFFFFF',
        sectionBackgroundImageFileId: null,
        sectionBackgroundImageUrl: '',
        sectionBackgroundSize: 'cover',
        sectionBackgroundPosition: 'center',
        sectionBackgroundRepeat: 'no-repeat',
        rows: [navRow, heroRow],
      },
    },
    aboutSectionOne,
    aboutSectionTwo,
    pricingSection,
    footerSection,
  ]
}

const buildMemberGetAccessTemplateSections = (legacyDoc = {}) => {
  void legacyDoc
  const year = new Date().getFullYear()

  const createGetAccessTextSettings = (overrides = {}) => ({
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    textColor: '#18181B',
    textAlign: 'left',
    linkAddress: '',
    openInNewTab: false,
    ...overrides,
  })

  const navRow = {
    type: 'row',
    props: {
      rowColumnCount: 3,
      rowColumnGutter: 0,
      rowWidthPercent: 100,
      rowHeight: 96,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 32,
      rowPaddingY: 24,
      rowMargin: 0,
      rowAlign: 'center',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        { widthPercent: 42, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
        { widthPercent: 16, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
        { widthPercent: 42, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'right' },
      ],
      elements: [
        createHomeTemplateElement('image', 0, {
          imageFileId: null,
          imageUrl: MEMBER_GET_ACCESS_DEFAULTS.logoUrl,
          imageWidth: 36,
          imageAlign: 'left',
          imageBorderRadius: 0,
          imageShadowStrength: 0,
          elementWidthPercent: 100,
          elementHeight: 48,
          elementAlign: 'left',
        }),
        createHomeTemplateElement('row', 1, {
          rowColumnCount: 2,
          rowColumnGutter: 48,
          rowWidthPercent: 100,
          rowHeight: 24,
          rowHeightUnit: 'px',
          rowPadding: 0,
          rowPaddingX: 0,
          rowPaddingY: 0,
          rowMargin: 0,
          rowAlign: 'center',
          rowBackgroundColor: 'transparent',
          rowBackgroundImageFileId: null,
          rowBackgroundImageUrl: '',
          rowBackgroundSize: 'cover',
          rowBackgroundPosition: 'center',
          rowBackgroundRepeat: 'no-repeat',
          rowColumnStyles: [
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 24, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 24, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
          ],
          elements: [
            createHomeTemplateElement('text', 0, {
              content: MEMBER_GET_ACCESS_DEFAULTS.navHomeText,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'center',
              textSettings: createGetAccessTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'center',
                linkAddress: '/',
                openInNewTab: false,
              }),
            }),
            createHomeTemplateElement('text', 1, {
              content: MEMBER_GET_ACCESS_DEFAULTS.navPricingText,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'center',
              textSettings: createGetAccessTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'center',
                linkAddress: '/pricing',
                openInNewTab: false,
              }),
            }),
          ],
        }),
        createHomeTemplateElement('text', 2, {
          content: MEMBER_GET_ACCESS_DEFAULTS.navLoginText,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'right',
          textSettings: createGetAccessTextSettings({
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.4,
            textColor: '#111827',
            textAlign: 'right',
            linkAddress: '/login',
            openInNewTab: false,
          }),
        }),
      ],
    },
  }

  const progressRow = {
    type: 'row',
    props: {
      rowColumnCount: 1,
      rowColumnGutter: 0,
      rowWidthPercent: 100,
      rowHeight: 72,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 0,
      rowPaddingY: 0,
      rowMargin: 18,
      rowAlign: 'center',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 72, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
      ],
      elements: [
        createHomeTemplateElement('row', 0, {
          rowColumnCount: 3,
          rowColumnGutter: 0,
          rowWidthPercent: 100,
          rowHeight: 52,
          rowHeightUnit: 'px',
          rowPadding: 0,
          rowPaddingX: 0,
          rowPaddingY: 0,
          rowMargin: 0,
          rowAlign: 'center',
          rowBackgroundColor: '#FFFFFF',
          rowBackgroundImageFileId: null,
          rowBackgroundImageUrl: '',
          rowBackgroundSize: 'cover',
          rowBackgroundPosition: 'center',
          rowBackgroundRepeat: 'no-repeat',
          rowColumnStyles: [
            { widthPercent: 33.34, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 52, heightUnit: 'px', paddingX: 20, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
            { widthPercent: 33.33, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 52, heightUnit: 'px', paddingX: 20, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
            { widthPercent: 33.33, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 52, heightUnit: 'px', paddingX: 20, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
          ],
          elements: [
            createHomeTemplateElement('text', 0, {
              content: MEMBER_GET_ACCESS_DEFAULTS.progressCreateAccount,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'left',
              textSettings: createGetAccessTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'left',
              }),
            }),
            createHomeTemplateElement('text', 1, {
              content: MEMBER_GET_ACCESS_DEFAULTS.progressAccountConfirmation,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'left',
              textSettings: createGetAccessTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#2D8CFF',
                textAlign: 'left',
              }),
            }),
            createHomeTemplateElement('text', 2, {
              content: MEMBER_GET_ACCESS_DEFAULTS.progressInstantAccess,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'left',
              textSettings: createGetAccessTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#6B7280',
                textAlign: 'left',
              }),
            }),
          ],
          elementWidthPercent: 100,
          elementHeight: 52,
          elementAlign: 'center',
          elementBackgroundColor: '#FFFFFF',
          elementPaddingX: 16,
          elementPaddingY: 0,
        }),
      ],
    },
  }

  const contentRow = {
    type: 'row',
    props: {
      rowColumnCount: 2,
      rowColumnGutter: 20,
      rowWidthPercent: 100,
      rowHeight: 920,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 0,
      rowPaddingY: 0,
      rowMargin: 20,
      rowAlign: 'center',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        { widthPercent: 63.64, backgroundColor: '#FFFFFF', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 920, heightUnit: 'px', paddingX: 30, paddingY: 30, contentJustify: 'start', contentAlign: 'left' },
        { widthPercent: 36.36, backgroundColor: '#FFFFFF', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 920, heightUnit: 'px', paddingX: 24, paddingY: 30, contentJustify: 'start', contentAlign: 'left' },
      ],
      elements: [
        createHomeTemplateElement('header', 0, {
          text: MEMBER_GET_ACCESS_DEFAULTS.signupHeading,
          level: 'h2',
          size: 36,
          elementWidthPercent: 100,
          elementHeight: 100,
          elementAlign: 'center',
          textSettings: createGetAccessTextSettings({
            fontSize: 36,
            fontWeight: 700,
            lineHeight: 1.2,
            textColor: '#111827',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('text', 0, {
          content: MEMBER_GET_ACCESS_DEFAULTS.selectedPlanText,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'center',
          textSettings: createGetAccessTextSettings({
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.5,
            textColor: '#6B7280',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('header', 0, {
          text: MEMBER_GET_ACCESS_DEFAULTS.detailsHeading,
          level: 'h3',
          size: 30,
          elementWidthPercent: 100,
          elementHeight: 40,
          elementAlign: 'left',
          elementMargin: 8,
          textSettings: createGetAccessTextSettings({
            fontSize: 30,
            fontWeight: 700,
            lineHeight: 1.2,
            textColor: '#111827',
            textAlign: 'left',
          }),
        }),
        createHomeTemplateElement('member-get-access-form', 0, {
          formLocked: true,
          formMode: 'step-one-default',
          formHelperText: MEMBER_GET_ACCESS_DEFAULTS.dynamicFormHint,
          elementWidthPercent: 100,
          elementHeight: 330,
          elementHeightUnit: 'px',
          elementAlign: 'center',
          elementPadding: 0,
          elementPaddingX: 0,
          elementPaddingY: 0,
          elementMargin: 0,
          elementBackgroundColor: 'transparent',
        }),
        createHomeTemplateElement('text', 0, {
          content: MEMBER_GET_ACCESS_DEFAULTS.decisionHeading,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'center',
          elementMargin: 22,
          textSettings: createGetAccessTextSettings({
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.35,
            textColor: '#111827',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('text', 0, {
          content: MEMBER_GET_ACCESS_DEFAULTS.decisionBody,
          elementWidthPercent: 100,
          elementHeight: 140,
          elementAlign: 'center',
          textSettings: createGetAccessTextSettings({
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.75,
            textColor: '#4B5563',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('header', 0, {
          text: MEMBER_GET_ACCESS_DEFAULTS.helpHeading,
          level: 'h3',
          size: 18,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'center',
          elementMargin: 20,
          textSettings: createGetAccessTextSettings({
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.35,
            textColor: '#111827',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('text', 0, {
          content: MEMBER_GET_ACCESS_DEFAULTS.helpBody,
          elementWidthPercent: 100,
          elementHeight: 60,
          elementAlign: 'center',
          textSettings: createGetAccessTextSettings({
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.65,
            textColor: '#4B5563',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('text', 0, {
          content: MEMBER_GET_ACCESS_DEFAULTS.helpEmailText,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'center',
          textSettings: createGetAccessTextSettings({
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.35,
            textColor: '#111827',
            textAlign: 'center',
          }),
        }),
        createHomeTemplateElement('header', 1, {
          text: MEMBER_GET_ACCESS_DEFAULTS.summaryHeading,
          level: 'h3',
          size: 20,
          elementWidthPercent: 100,
          elementHeight: 30,
          elementAlign: 'left',
          textSettings: createGetAccessTextSettings({
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.3,
            textColor: '#111827',
            textAlign: 'left',
          }),
        }),
        createHomeTemplateElement('text', 1, {
          content: MEMBER_GET_ACCESS_DEFAULTS.summaryPlanName,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'left',
          elementMargin: 10,
          textSettings: createGetAccessTextSettings({
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.45,
            textColor: '#4B5563',
            textAlign: 'left',
          }),
        }),
        createHomeTemplateElement('text', 1, {
          content: MEMBER_GET_ACCESS_DEFAULTS.summaryPlanPrice,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'left',
          textSettings: createGetAccessTextSettings({
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.45,
            textColor: '#4B5563',
            textAlign: 'left',
          }),
        }),
        createHomeTemplateElement('text', 1, {
          content: MEMBER_GET_ACCESS_DEFAULTS.summaryIncludesHeading,
          elementWidthPercent: 100,
          elementHeight: 20,
          elementAlign: 'left',
          elementMargin: 14,
          textSettings: createGetAccessTextSettings({
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.3,
            textColor: '#2D8CFF',
            textAlign: 'left',
          }),
        }),
        createHomeTemplateElement('row', 1, {
          rowColumnCount: 2,
          rowColumnGutter: 14,
          rowWidthPercent: 100,
          rowHeight: 104,
          rowHeightUnit: 'px',
          rowPadding: 0,
          rowPaddingX: 0,
          rowPaddingY: 0,
          rowMargin: 8,
          rowAlign: 'left',
          rowBackgroundColor: 'transparent',
          rowBackgroundImageFileId: null,
          rowBackgroundImageUrl: '',
          rowBackgroundSize: 'cover',
          rowBackgroundPosition: 'center',
          rowBackgroundRepeat: 'no-repeat',
          rowColumnStyles: [
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 104, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'start', contentAlign: 'left' },
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 104, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'start', contentAlign: 'left' },
          ],
          elements: [
            createHomeTemplateElement('text', 0, {
              content: `${MEMBER_GET_ACCESS_DEFAULTS.summaryFeatureOne}\n\n${MEMBER_GET_ACCESS_DEFAULTS.summaryFeatureThree}`,
              elementWidthPercent: 100,
              elementHeight: 104,
              elementAlign: 'left',
              textSettings: createGetAccessTextSettings({
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.6,
                textColor: '#4B5563',
                textAlign: 'left',
              }),
            }),
            createHomeTemplateElement('text', 1, {
              content: `${MEMBER_GET_ACCESS_DEFAULTS.summaryFeatureTwo}\n\n${MEMBER_GET_ACCESS_DEFAULTS.summaryFeatureFour}`,
              elementWidthPercent: 100,
              elementHeight: 104,
              elementAlign: 'left',
              textSettings: createGetAccessTextSettings({
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.6,
                textColor: '#4B5563',
                textAlign: 'left',
              }),
            }),
          ],
          elementWidthPercent: 100,
          elementHeight: 104,
          elementAlign: 'left',
          elementBackgroundColor: 'transparent',
        }),
        createHomeTemplateElement('image', 1, {
          imageFileId: null,
          imageUrl: MEMBER_GET_ACCESS_DEFAULTS.summaryImageUrl,
          imageWidth: 100,
          imageAlign: 'center',
          imageBorderRadius: 24,
          imageShadowStrength: 0,
          elementWidthPercent: 100,
          elementHeight: 255,
          elementAlign: 'center',
          elementMargin: 12,
        }),
      ],
    },
  }

  const formSection = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_GET_ACCESS_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_GET_ACCESS_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 1080,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 24,
      sectionPaddingY: 24,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#FAFAFA',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        progressRow,
        contentRow,
      ],
    },
  }

  const footerSection = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_GET_ACCESS_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_GET_ACCESS_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 300,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 24,
      sectionPaddingY: 80,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#F1F5F9',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        {
          type: 'row',
          props: {
            rowColumnCount: 1,
            rowColumnGutter: 0,
            rowWidthPercent: 100,
            rowHeight: 180,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 180, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('row', 0, {
                rowColumnCount: 2,
                rowColumnGutter: 48,
                rowWidthPercent: 100,
                rowHeight: 40,
                rowHeightUnit: 'px',
                rowPadding: 0,
                rowPaddingX: 0,
                rowPaddingY: 0,
                rowMargin: 0,
                rowAlign: 'center',
                rowBackgroundColor: 'transparent',
                rowBackgroundImageFileId: null,
                rowBackgroundImageUrl: '',
                rowBackgroundSize: 'cover',
                rowBackgroundPosition: 'center',
                rowBackgroundRepeat: 'no-repeat',
                rowColumnStyles: [
                  { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 40, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
                  { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 40, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
                ],
                elements: [
                  createHomeTemplateElement('text', 0, {
                    content: MEMBER_GET_ACCESS_DEFAULTS.navHomeText,
                    elementWidthPercent: 100,
                    elementHeight: 24,
                    elementAlign: 'center',
                    textSettings: createGetAccessTextSettings({
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      textColor: '#4B5563',
                      textAlign: 'center',
                      linkAddress: '/',
                      openInNewTab: false,
                    }),
                  }),
                  createHomeTemplateElement('text', 1, {
                    content: MEMBER_GET_ACCESS_DEFAULTS.navPricingText,
                    elementWidthPercent: 100,
                    elementHeight: 24,
                    elementAlign: 'center',
                    textSettings: createGetAccessTextSettings({
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      textColor: '#4B5563',
                      textAlign: 'center',
                      linkAddress: '/pricing',
                      openInNewTab: false,
                    }),
                  }),
                ],
                elementWidthPercent: 100,
                elementHeight: 40,
                elementAlign: 'center',
                elementBackgroundColor: 'transparent',
              }),
              createHomeTemplateElement('image', 0, {
                imageFileId: null,
                imageUrl: MEMBER_GET_ACCESS_DEFAULTS.logoUrl,
                imageWidth: 12,
                imageAlign: 'center',
                imageBorderRadius: 0,
                imageShadowStrength: 0,
                elementWidthPercent: 100,
                elementHeight: 48,
                elementAlign: 'center',
                elementMargin: 6,
              }),
              createHomeTemplateElement('text', 0, {
                content: `(c) ${year} Your Company, Inc. All rights reserved.`,
                elementWidthPercent: 100,
                elementHeight: 24,
                elementAlign: 'center',
                textSettings: createGetAccessTextSettings({
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  textColor: '#6B7280',
                  textAlign: 'center',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: MEMBER_GET_ACCESS_DEFAULTS.footerSupportText,
                elementWidthPercent: 100,
                elementHeight: 24,
                elementAlign: 'center',
                textSettings: createGetAccessTextSettings({
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.5,
                  textColor: '#6B7280',
                  textAlign: 'center',
                }),
              }),
            ],
          },
        },
      ],
    },
  }

  return [
    {
      type: 'section',
      props: {
        sectionTemplateType: MEMBER_GET_ACCESS_TEMPLATE_SECTION,
        sectionTemplateVersion: MEMBER_GET_ACCESS_TEMPLATE_VERSION,
        sectionWidthPercent: 100,
        sectionHeight: 108,
        sectionHeightUnit: 'px',
        sectionPadding: 0,
        sectionPaddingX: 0,
        sectionPaddingY: 0,
        sectionMargin: 0,
        sectionAlign: 'left',
        sectionBackgroundColor: '#F1F5F9',
        sectionBackgroundImageFileId: null,
        sectionBackgroundImageUrl: '',
        sectionBackgroundSize: 'cover',
        sectionBackgroundPosition: 'center',
        sectionBackgroundRepeat: 'no-repeat',
        rows: [navRow],
      },
    },
    formSection,
    footerSection,
  ]
}

const buildMemberGetAccessSuccessTemplateSections = (legacyDoc = {}) => {
  void legacyDoc
  const year = new Date().getFullYear()

  const createSuccessTextSettings = (overrides = {}) => ({
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    textColor: '#18181B',
    textAlign: 'left',
    linkAddress: '',
    openInNewTab: false,
    ...overrides,
  })

  const navRow = {
    type: 'row',
    props: {
      rowColumnCount: 3,
      rowColumnGutter: 0,
      rowWidthPercent: 100,
      rowHeight: 96,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 32,
      rowPaddingY: 24,
      rowMargin: 0,
      rowAlign: 'center',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        { widthPercent: 42, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
        { widthPercent: 16, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
        { widthPercent: 42, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'right' },
      ],
      elements: [
        createHomeTemplateElement('image', 0, {
          imageFileId: null,
          imageUrl: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.logoUrl,
          imageWidth: 36,
          imageAlign: 'left',
          imageBorderRadius: 0,
          imageShadowStrength: 0,
          elementWidthPercent: 100,
          elementHeight: 48,
          elementAlign: 'left',
        }),
        createHomeTemplateElement('row', 1, {
          rowColumnCount: 2,
          rowColumnGutter: 48,
          rowWidthPercent: 100,
          rowHeight: 24,
          rowHeightUnit: 'px',
          rowPadding: 0,
          rowPaddingX: 0,
          rowPaddingY: 0,
          rowMargin: 0,
          rowAlign: 'center',
          rowBackgroundColor: 'transparent',
          rowBackgroundImageFileId: null,
          rowBackgroundImageUrl: '',
          rowBackgroundSize: 'cover',
          rowBackgroundPosition: 'center',
          rowBackgroundRepeat: 'no-repeat',
          rowColumnStyles: [
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 24, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 24, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
          ],
          elements: [
            createHomeTemplateElement('text', 0, {
              content: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.navHomeText,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'center',
              textSettings: createSuccessTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'center',
                linkAddress: '/',
                openInNewTab: false,
              }),
            }),
            createHomeTemplateElement('text', 1, {
              content: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.navPricingText,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'center',
              textSettings: createSuccessTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'center',
                linkAddress: '/pricing',
                openInNewTab: false,
              }),
            }),
          ],
        }),
        createHomeTemplateElement('text', 2, {
          content: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.navLoginText,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'right',
          textSettings: createSuccessTextSettings({
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.4,
            textColor: '#111827',
            textAlign: 'right',
            linkAddress: '/login',
            openInNewTab: false,
          }),
        }),
      ],
    },
  }

  const successRow = {
    type: 'row',
    props: {
      rowColumnCount: 1,
      rowColumnGutter: 0,
      rowWidthPercent: 100,
      rowHeight: 120,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 0,
      rowPaddingY: 0,
      rowMargin: 0,
      rowAlign: 'center',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 120, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
      ],
      elements: [
        createHomeTemplateElement('member-get-access-success', 0, {
          formLocked: true,
          successMode: 'auto-redirect-default',
          successHelperText: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.successHelperText,
          elementWidthPercent: 100,
          elementHeight: 120,
          elementHeightUnit: 'px',
          elementAlign: 'center',
          elementPadding: 0,
          elementPaddingX: 0,
          elementPaddingY: 0,
          elementMargin: 0,
          elementBackgroundColor: 'transparent',
        }),
      ],
    },
  }

  const successSection = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 120,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 0,
      sectionPaddingY: 20,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#FAFAFA',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [successRow],
    },
  }

  const footerSection = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 300,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 24,
      sectionPaddingY: 80,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#F1F5F9',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        {
          type: 'row',
          props: {
            rowColumnCount: 1,
            rowColumnGutter: 0,
            rowWidthPercent: 100,
            rowHeight: 180,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 180, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('row', 0, {
                rowColumnCount: 2,
                rowColumnGutter: 48,
                rowWidthPercent: 100,
                rowHeight: 40,
                rowHeightUnit: 'px',
                rowPadding: 0,
                rowPaddingX: 0,
                rowPaddingY: 0,
                rowMargin: 0,
                rowAlign: 'center',
                rowBackgroundColor: 'transparent',
                rowBackgroundImageFileId: null,
                rowBackgroundImageUrl: '',
                rowBackgroundSize: 'cover',
                rowBackgroundPosition: 'center',
                rowBackgroundRepeat: 'no-repeat',
                rowColumnStyles: [
                  { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 40, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
                  { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 40, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
                ],
                elements: [
                  createHomeTemplateElement('text', 0, {
                    content: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.navHomeText,
                    elementWidthPercent: 100,
                    elementHeight: 24,
                    elementAlign: 'center',
                    textSettings: createSuccessTextSettings({
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      textColor: '#4B5563',
                      textAlign: 'center',
                      linkAddress: '/',
                      openInNewTab: false,
                    }),
                  }),
                  createHomeTemplateElement('text', 1, {
                    content: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.navPricingText,
                    elementWidthPercent: 100,
                    elementHeight: 24,
                    elementAlign: 'center',
                    textSettings: createSuccessTextSettings({
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      textColor: '#4B5563',
                      textAlign: 'center',
                      linkAddress: '/pricing',
                      openInNewTab: false,
                    }),
                  }),
                ],
                elementWidthPercent: 100,
                elementHeight: 40,
                elementAlign: 'center',
                elementBackgroundColor: 'transparent',
              }),
              createHomeTemplateElement('image', 0, {
                imageFileId: null,
                imageUrl: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.logoUrl,
                imageWidth: 12,
                imageAlign: 'center',
                imageBorderRadius: 0,
                imageShadowStrength: 0,
                elementWidthPercent: 100,
                elementHeight: 48,
                elementAlign: 'center',
                elementMargin: 6,
              }),
              createHomeTemplateElement('text', 0, {
                content: `(c) ${year} Your Company, Inc. All rights reserved.`,
                elementWidthPercent: 100,
                elementHeight: 24,
                elementAlign: 'center',
                textSettings: createSuccessTextSettings({
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  textColor: '#6B7280',
                  textAlign: 'center',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.footerSupportText,
                elementWidthPercent: 100,
                elementHeight: 24,
                elementAlign: 'center',
                textSettings: createSuccessTextSettings({
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.5,
                  textColor: '#6B7280',
                  textAlign: 'center',
                }),
              }),
            ],
          },
        },
      ],
    },
  }

  return [
    {
      type: 'section',
      props: {
        sectionTemplateType: MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_SECTION,
        sectionTemplateVersion: MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_VERSION,
        sectionWidthPercent: 100,
        sectionHeight: 108,
        sectionHeightUnit: 'px',
        sectionPadding: 0,
        sectionPaddingX: 0,
        sectionPaddingY: 0,
        sectionMargin: 0,
        sectionAlign: 'left',
        sectionBackgroundColor: '#F1F5F9',
        sectionBackgroundImageFileId: null,
        sectionBackgroundImageUrl: '',
        sectionBackgroundSize: 'cover',
        sectionBackgroundPosition: 'center',
        sectionBackgroundRepeat: 'no-repeat',
        rows: [navRow],
      },
    },
    successSection,
    footerSection,
  ]
}

const buildMemberPricingTemplateSections = (legacyDoc = {}) => {
  void legacyDoc
  const year = new Date().getFullYear()

  const createPricingTextSettings = (overrides = {}) => ({
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    textColor: '#18181B',
    textAlign: 'left',
    linkAddress: '',
    openInNewTab: false,
    ...overrides,
  })

  const navRow = {
    type: 'row',
    props: {
      rowColumnCount: 3,
      rowColumnGutter: 0,
      rowWidthPercent: 100,
      rowHeight: 96,
      rowHeightUnit: 'px',
      rowPadding: 0,
      rowPaddingX: 32,
      rowPaddingY: 24,
      rowMargin: 0,
      rowAlign: 'center',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles: [
        { widthPercent: 42, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'left' },
        { widthPercent: 16, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
        { widthPercent: 42, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 96, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'right' },
      ],
      elements: [
        createHomeTemplateElement('image', 0, {
          imageFileId: null,
          imageUrl: MEMBER_PRICING_DEFAULTS.logoUrl,
          imageWidth: 100,
          imageAlign: 'left',
          imageBorderRadius: 0,
          imageShadowStrength: 0,
          elementWidthPercent: 100,
          elementHeight: 48,
          elementAlign: 'left',
        }),
        createHomeTemplateElement('row', 1, {
          rowColumnCount: 2,
          rowColumnGutter: 48,
          rowWidthPercent: 100,
          rowHeight: 24,
          rowHeightUnit: 'px',
          rowPadding: 0,
          rowPaddingX: 0,
          rowPaddingY: 0,
          rowMargin: 0,
          rowAlign: 'center',
          rowBackgroundColor: 'transparent',
          rowBackgroundImageFileId: null,
          rowBackgroundImageUrl: '',
          rowBackgroundSize: 'cover',
          rowBackgroundPosition: 'center',
          rowBackgroundRepeat: 'no-repeat',
          rowColumnStyles: [
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 24, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 24, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
          ],
          elements: [
            createHomeTemplateElement('text', 0, {
              content: MEMBER_PRICING_DEFAULTS.navHomeText,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'center',
              textSettings: createPricingTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'center',
                linkAddress: '/',
                openInNewTab: false,
              }),
            }),
            createHomeTemplateElement('text', 1, {
              content: MEMBER_PRICING_DEFAULTS.navPricingText,
              elementWidthPercent: 100,
              elementHeight: 24,
              elementAlign: 'center',
              textSettings: createPricingTextSettings({
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                textColor: '#111827',
                textAlign: 'center',
                linkAddress: '/pricing',
                openInNewTab: false,
              }),
            }),
          ],
        }),
        createHomeTemplateElement('text', 2, {
          content: MEMBER_PRICING_DEFAULTS.navLoginText,
          elementWidthPercent: 100,
          elementHeight: 24,
          elementAlign: 'right',
          textSettings: createPricingTextSettings({
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.4,
            textColor: '#111827',
            textAlign: 'right',
            linkAddress: '/login',
            openInNewTab: false,
          }),
        }),
      ],
    },
  }

  const heroAndPlansSection = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_PRICING_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_PRICING_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 980,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 24,
      sectionPaddingY: 96,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#FFFFFF',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        {
          type: 'row',
          props: {
            rowColumnCount: 1,
            rowColumnGutter: 0,
            rowWidthPercent: 100,
            rowHeight: 220,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 220, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('header', 0, {
                text: MEMBER_PRICING_DEFAULTS.heroHeading,
                level: 'h2',
                size: 48,
                elementWidthPercent: 100,
                elementHeight: 96,
                elementAlign: 'center',
                textSettings: createPricingTextSettings({
                  fontSize: 48,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  textColor: '#111827',
                  textAlign: 'center',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: MEMBER_PRICING_DEFAULTS.heroSub,
                elementWidthPercent: 100,
                elementHeight: 64,
                elementAlign: 'center',
                textSettings: createPricingTextSettings({
                  fontSize: 20,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textColor: '#4B5563',
                  textAlign: 'center',
                }),
              }),
            ],
          },
        },
        {
          type: 'row',
          props: {
            rowColumnCount: 1,
            rowColumnGutter: 0,
            rowWidthPercent: 100,
            rowHeight: 620,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 620, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('member-pricing-plans', 0, {
                plansLocked: true,
                plansHelperText: MEMBER_PRICING_DEFAULTS.dynamicPlansHint,
                elementWidthPercent: 100,
                elementHeight: 560,
                elementHeightUnit: 'px',
                elementAlign: 'center',
                elementPadding: 0,
                elementPaddingX: 0,
                elementPaddingY: 0,
                elementMargin: 0,
                elementBackgroundColor: 'transparent',
              }),
            ],
          },
        },
      ],
    },
  }

  const footerSection = {
    type: 'section',
    props: {
      sectionTemplateType: MEMBER_PRICING_TEMPLATE_SECTION,
      sectionTemplateVersion: MEMBER_PRICING_TEMPLATE_VERSION,
      sectionWidthPercent: 100,
      sectionHeight: 300,
      sectionHeightUnit: 'px',
      sectionPadding: 0,
      sectionPaddingX: 24,
      sectionPaddingY: 80,
      sectionMargin: 0,
      sectionAlign: 'left',
      sectionBackgroundColor: '#F1F5F9',
      sectionBackgroundImageFileId: null,
      sectionBackgroundImageUrl: '',
      sectionBackgroundSize: 'cover',
      sectionBackgroundPosition: 'center',
      sectionBackgroundRepeat: 'no-repeat',
      rows: [
        {
          type: 'row',
          props: {
            rowColumnCount: 1,
            rowColumnGutter: 0,
            rowWidthPercent: 100,
            rowHeight: 180,
            rowHeightUnit: 'px',
            rowPadding: 0,
            rowPaddingX: 0,
            rowPaddingY: 0,
            rowMargin: 0,
            rowAlign: 'center',
            rowBackgroundColor: 'transparent',
            rowBackgroundImageFileId: null,
            rowBackgroundImageUrl: '',
            rowBackgroundSize: 'cover',
            rowBackgroundPosition: 'center',
            rowBackgroundRepeat: 'no-repeat',
            rowColumnStyles: [
              { widthPercent: 100, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 180, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
            ],
            elements: [
              createHomeTemplateElement('row', 0, {
                rowColumnCount: 2,
                rowColumnGutter: 48,
                rowWidthPercent: 100,
                rowHeight: 40,
                rowHeightUnit: 'px',
                rowPadding: 0,
                rowPaddingX: 0,
                rowPaddingY: 0,
                rowMargin: 0,
                rowAlign: 'center',
                rowBackgroundColor: 'transparent',
                rowBackgroundImageFileId: null,
                rowBackgroundImageUrl: '',
                rowBackgroundSize: 'cover',
                rowBackgroundPosition: 'center',
                rowBackgroundRepeat: 'no-repeat',
                rowColumnStyles: [
                  { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 40, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
                  { widthPercent: 50, backgroundColor: 'transparent', backgroundImageFileId: null, backgroundImageUrl: '', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: 40, heightUnit: 'px', paddingX: 0, paddingY: 0, contentJustify: 'center', contentAlign: 'center' },
                ],
                elements: [
                  createHomeTemplateElement('text', 0, {
                    content: MEMBER_PRICING_DEFAULTS.navHomeText,
                    elementWidthPercent: 100,
                    elementHeight: 24,
                    elementAlign: 'center',
                    textSettings: createPricingTextSettings({
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      textColor: '#4B5563',
                      textAlign: 'center',
                      linkAddress: '/',
                      openInNewTab: false,
                    }),
                  }),
                  createHomeTemplateElement('text', 1, {
                    content: MEMBER_PRICING_DEFAULTS.navPricingText,
                    elementWidthPercent: 100,
                    elementHeight: 24,
                    elementAlign: 'center',
                    textSettings: createPricingTextSettings({
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      textColor: '#4B5563',
                      textAlign: 'center',
                      linkAddress: '/pricing',
                      openInNewTab: false,
                    }),
                  }),
                ],
                elementWidthPercent: 100,
                elementHeight: 40,
                elementAlign: 'center',
                elementBackgroundColor: 'transparent',
              }),
              createHomeTemplateElement('image', 0, {
                imageFileId: null,
                imageUrl: MEMBER_PRICING_DEFAULTS.logoUrl,
                imageWidth: 12,
                imageAlign: 'center',
                imageBorderRadius: 0,
                imageShadowStrength: 0,
                elementWidthPercent: 100,
                elementHeight: 48,
                elementAlign: 'center',
                elementMargin: 6,
              }),
              createHomeTemplateElement('text', 0, {
                content: `(c) ${year} Your Company, Inc. All rights reserved.`,
                elementWidthPercent: 100,
                elementHeight: 24,
                elementAlign: 'center',
                textSettings: createPricingTextSettings({
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  textColor: '#6B7280',
                  textAlign: 'center',
                }),
              }),
              createHomeTemplateElement('text', 0, {
                content: MEMBER_PRICING_DEFAULTS.footerSupportText,
                elementWidthPercent: 100,
                elementHeight: 24,
                elementAlign: 'center',
                textSettings: createPricingTextSettings({
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.5,
                  textColor: '#6B7280',
                  textAlign: 'center',
                }),
              }),
            ],
          },
        },
      ],
    },
  }

  return [
    {
      type: 'section',
      props: {
        sectionTemplateType: MEMBER_PRICING_TEMPLATE_SECTION,
        sectionTemplateVersion: MEMBER_PRICING_TEMPLATE_VERSION,
        sectionWidthPercent: 100,
        sectionHeight: 108,
        sectionHeightUnit: 'px',
        sectionPadding: 0,
        sectionPaddingX: 0,
        sectionPaddingY: 0,
        sectionMargin: 0,
        sectionAlign: 'left',
        sectionBackgroundColor: '#F1F5F9',
        sectionBackgroundImageFileId: null,
        sectionBackgroundImageUrl: '',
        sectionBackgroundSize: 'cover',
        sectionBackgroundPosition: 'center',
        sectionBackgroundRepeat: 'no-repeat',
        rows: [navRow],
      },
    },
    heroAndPlansSection,
    footerSection,
  ]
}

const mapDocForEditorCanvas = (doc) => {
  const next = deepClone(doc || {})
  if (!next || typeof next !== 'object') return { version: 1, title: 'Untitled Page', sections: [], seo: { title: '', description: '' } }
  if (String(next?.type || '') === 'member-login') {
    const persistedTemplateSection = (next?.props?.pageEditorTemplateSection && typeof next.props.pageEditorTemplateSection === 'object')
      ? deepClone(next.props.pageEditorTemplateSection)
      : null
    const templateSection = isCurrentMemberLoginTemplateSection(persistedTemplateSection)
      ? persistedTemplateSection
      : buildMemberLoginTemplateSection(next)
    return prepareResponsiveEditorDocument({
      version: Number(next?.version) || 1,
      title: String(next?.title || 'Untitled Page'),
      seo: (next?.seo && typeof next.seo === 'object') ? next.seo : { title: '', description: '' },
      sections: [templateSection],
    })
  }
  if (!Array.isArray(next.sections)) {
    if (Array.isArray(next.blocks)) {
      next.sections = next.blocks
    } else if (next.section && typeof next.section === 'object') {
      next.sections = [next.section]
    } else {
      next.sections = []
    }
  }
  next.sections = next.sections.map((block) => {
    if (String(block?.type || '') === 'member-login') {
      const persistedTemplateSection = (block?.props?.pageEditorTemplateSection && typeof block.props.pageEditorTemplateSection === 'object')
        ? deepClone(block.props.pageEditorTemplateSection)
      : null
      if (isCurrentMemberLoginTemplateSection(persistedTemplateSection)) return persistedTemplateSection
      return buildMemberLoginTemplateSection(block)
    }
    return block
  })
  if (shouldMigrateLegacyMemberApprovalTemplateDoc(next) || shouldRefreshDefaultMemberApprovalTemplateDoc(next)) {
    next.sections = buildMemberApprovalTemplateSections(next)
  } else if (shouldMigrateLegacyGetAccessSuccessTemplateDoc(next) || shouldRefreshDefaultMemberGetAccessSuccessTemplateDoc(next)) {
    next.sections = buildMemberGetAccessSuccessTemplateSections(next)
  } else if (shouldMigrateLegacyGetAccessTemplateDoc(next) || shouldRefreshDefaultMemberGetAccessTemplateDoc(next)) {
    next.sections = buildMemberGetAccessTemplateSections(next)
  } else if (shouldMigrateLegacyPricingTemplateDoc(next) || shouldRefreshDefaultMemberPricingTemplateDoc(next)) {
    next.sections = buildMemberPricingTemplateSections(next)
  } else if (shouldMigrateLegacyHomeTemplateDoc(next) || shouldRefreshDefaultMemberHomeTemplateDoc(next)) {
    next.sections = buildMemberHomeTemplateSections(next)
  }
  return prepareResponsiveEditorDocument(next)
}

const mapDocForPersistence = (doc) => {
  const next = deepClone(doc || {})
  if (!next || typeof next !== 'object') return { version: 1, title: 'Untitled Page', sections: [], seo: { title: '', description: '' } }
  if (!Array.isArray(next.sections)) next.sections = []
  captureDocumentViewportProps(next, editorViewportMode.value)
  applyDocumentViewportProps(next, 'desktop')
  next.sections = next.sections.map((block) => {
    if (
      String(block?.type || '') === 'section' &&
      String(block?.props?.sectionTemplateType || '') === MEMBER_LOGIN_TEMPLATE_SECTION
    ) {
      return buildLegacyMemberLoginBlockFromTemplateSection(block)
    }
    return block
  })
  return next
}

const stableDocumentValue = (value) => {
  if (Array.isArray(value)) return value.map(stableDocumentValue)
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        const nextValue = value[key]
        if (typeof nextValue !== 'undefined') result[key] = stableDocumentValue(nextValue)
        return result
      }, {})
  }
  return value
}

const getPersistedDocumentSnapshot = () => {
  const data = mapDocForPersistence(editorDoc.value)
  return {
    data,
    signature: JSON.stringify(stableDocumentValue(data)),
  }
}

const cloneEditorHistoryDocument = () => {
  try {
    return JSON.parse(JSON.stringify(editorDoc.value))
  } catch (_error) {
    return mapDocForEditorCanvas(mapDocForPersistence(editorDoc.value))
  }
}

const captureEditorHistorySnapshot = () => {
  if (!editorDoc.value) return null
  const { signature } = getPersistedDocumentSnapshot()
  return {
    signature,
    document: cloneEditorHistoryDocument(),
    capturedAt: Date.now(),
  }
}

const commitEditorHistorySnapshot = () => {
  if (!editorHistoryReady || editorHistoryApplying || !editorDoc.value) return false
  const snapshot = captureEditorHistorySnapshot()
  if (!snapshot) return false
  const activeSnapshot = editorHistory.value[editorHistoryIndex.value]
  if (activeSnapshot?.signature === snapshot.signature) return false
  if (editorHistoryIndex.value < editorHistory.value.length - 1) {
    editorHistory.value.splice(editorHistoryIndex.value + 1)
  }
  editorHistory.value.push(snapshot)
  if (editorHistory.value.length > MAX_EDITOR_HISTORY_SNAPSHOTS) {
    editorHistory.value.splice(0, editorHistory.value.length - MAX_EDITOR_HISTORY_SNAPSHOTS)
  }
  editorHistoryIndex.value = editorHistory.value.length - 1
  return true
}

const initializeEditorHistory = () => {
  if (editorHistoryTimer) {
    clearTimeout(editorHistoryTimer)
    editorHistoryTimer = null
  }
  editorHistory.value = []
  editorHistoryIndex.value = -1
  editorHistoryReady = true
  const initialSnapshot = captureEditorHistorySnapshot()
  if (initialSnapshot) {
    editorHistory.value.push(initialSnapshot)
    editorHistoryIndex.value = 0
  }
}

const scheduleEditorHistorySnapshot = () => {
  if (!editorHistoryReady || editorHistoryApplying || !editorDoc.value) return
  if (editorHistoryTimer) clearTimeout(editorHistoryTimer)
  editorHistoryTimer = setTimeout(() => {
    editorHistoryTimer = null
    commitEditorHistorySnapshot()
  }, 350)
}

const restoreEditorHistorySnapshot = async (targetIndex) => {
  const snapshot = editorHistory.value[targetIndex]
  if (!snapshot?.document) return
  editorHistoryApplying = true
  editorHistoryIndex.value = targetIndex
  editorDoc.value = mapDocForEditorCanvas(JSON.parse(JSON.stringify(snapshot.document)))
  ensureSeo()
  ensureSections()
  ensureRequiredSystemElements()
  applyDocumentViewportProps(editorDoc.value, editorViewportMode.value)
  selectedIdx.value = -1
  selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  await nextTick()
  setTimeout(() => { editorHistoryApplying = false }, 0)
}

const undoPageChange = async () => {
  if (editorHistoryTimer) {
    clearTimeout(editorHistoryTimer)
    editorHistoryTimer = null
  }
  commitEditorHistorySnapshot()
  if (!canUndoPageChange.value) return
  await restoreEditorHistorySnapshot(editorHistoryIndex.value - 1)
}

const redoPageChange = async () => {
  if (editorHistoryTimer) {
    clearTimeout(editorHistoryTimer)
    editorHistoryTimer = null
  }
  if (!canRedoPageChange.value) return
  await restoreEditorHistorySnapshot(editorHistoryIndex.value + 1)
}

const onEditorHistoryKeydown = (event) => {
  if (!(event?.ctrlKey || event?.metaKey) || event?.altKey) return
  const target = event.target
  const tagName = String(target?.tagName || '').toLowerCase()
  if (target?.isContentEditable || ['input', 'textarea', 'select'].includes(tagName)) return
  const key = String(event.key || '').toLowerCase()
  if (key === 'z' && event.shiftKey) {
    event.preventDefault()
    redoPageChange()
  } else if (key === 'z') {
    event.preventDefault()
    undoPageChange()
  } else if (key === 'y') {
    event.preventDefault()
    redoPageChange()
  }
}

const ensureSectionBlockProps = (block) => {
  if (!block || String(block.type || '') !== 'section') return
  ensureNodeVisibility(block)
  if (!block.props || typeof block.props !== 'object') block.props = {}
  if (String(block?.props?.sectionTemplateType || '') === MEMBER_LOGIN_TEMPLATE_SECTION) {
    block.props.sectionTemplateVersion = MEMBER_LOGIN_TEMPLATE_VERSION
  }
  if (String(block?.props?.sectionTemplateType || '') === MEMBER_HOME_TEMPLATE_SECTION) {
    block.props.sectionTemplateVersion = MEMBER_HOME_TEMPLATE_VERSION
  }
  if (String(block?.props?.sectionTemplateType || '') === MEMBER_PRICING_TEMPLATE_SECTION) {
    block.props.sectionTemplateVersion = MEMBER_PRICING_TEMPLATE_VERSION
  }
  if (String(block?.props?.sectionTemplateType || '') === MEMBER_GET_ACCESS_TEMPLATE_SECTION) {
    block.props.sectionTemplateVersion = MEMBER_GET_ACCESS_TEMPLATE_VERSION
  }
  if (String(block?.props?.sectionTemplateType || '') === MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_SECTION) {
    block.props.sectionTemplateVersion = MEMBER_GET_ACCESS_SUCCESS_TEMPLATE_VERSION
  }
  if (String(block?.props?.sectionTemplateType || '') === MEMBER_APPROVAL_TEMPLATE_SECTION) {
    block.props.sectionTemplateVersion = MEMBER_APPROVAL_TEMPLATE_VERSION
  }

  block.props.sectionWidthPercent = toFiniteNumber(block.props.sectionWidthPercent, 100)
  block.props.sectionHeightUnit = normalizeHeightUnit(block.props.sectionHeightUnit, 'px')
  block.props.sectionHeight = clampHeightByUnit(block.props.sectionHeight, block.props.sectionHeightUnit, 100)
  block.props.sectionMargin = toFiniteNumber(block.props.sectionMargin, 0)
  block.props.sectionPaddingX = toFiniteNumber(block.props.sectionPaddingX, toFiniteNumber(block.props.sectionPadding, 24))
  block.props.sectionPaddingY = toFiniteNumber(block.props.sectionPaddingY, toFiniteNumber(block.props.sectionPadding, 24))
  block.props.sectionPadding = toFiniteNumber(block.props.sectionPadding, block.props.sectionPaddingX)
  block.props.sectionAlign = normalizeSectionAlign(block.props.sectionAlign)
  block.props.sectionBackgroundColor = normalizeBackgroundColor(block.props.sectionBackgroundColor, '#FFFFFF')
  block.props.sectionBackgroundImageFileId = sanitizeMediaFileId(block.props.sectionBackgroundImageFileId || block.props.section_background_image_file_id) || null
  block.props.sectionBackgroundImageUrl = resolveStoredMediaUrl(block.props.sectionBackgroundImageUrl || block.props.section_background_image_url)
  block.props.sectionBackgroundSize = String(block.props.sectionBackgroundSize || 'cover').trim().toLowerCase() === 'contain' ? 'contain' : 'cover'
  block.props.sectionBackgroundPosition = String(block.props.sectionBackgroundPosition || 'center').trim() || 'center'
  block.props.sectionBackgroundRepeat = String(block.props.sectionBackgroundRepeat || 'no-repeat').trim().toLowerCase() === 'repeat' ? 'repeat' : 'no-repeat'
  if (!Array.isArray(block.props.rows)) block.props.rows = []
  block.props.rows.forEach((row) => {
    ensureRowBlockProps(row)
  })
}

const ensureRowBlockProps = (row) => {
  if (!row || String(row.type || '') !== 'row') return
  ensureNodeVisibility(row)
  if (!row.props || typeof row.props !== 'object') row.props = {}
  row.props.rowColumnCount = Math.max(1, Math.min(6, toFiniteNumber(row.props.rowColumnCount, 1)))
  row.props.rowColumnGutter = Math.max(0, Math.min(120, toFiniteNumber(row.props.rowColumnGutter, 12)))
  row.props.rowWidthPercent = Math.max(10, Math.min(100, toFiniteNumber(row.props.rowWidthPercent, 100)))
  const storedRowMaxWidth = toFiniteNumber(row.props.rowMaxWidthPx ?? row.props.row_max_width_px, 0)
  if (storedRowMaxWidth > 0) row.props.rowMaxWidthPx = Math.max(320, Math.min(1920, storedRowMaxWidth))
  row.props.rowHeightUnit = normalizeHeightUnit(row.props.rowHeightUnit, 'px')
  row.props.rowHeight = clampRowHeightByUnit(row.props.rowHeight, row.props.rowHeightUnit, 120)
  row.props.rowMargin = Math.max(0, Math.min(240, toFiniteNumber(row.props.rowMargin, 5)))
  row.props.rowPaddingX = Math.max(0, Math.min(240, toFiniteNumber(row.props.rowPaddingX, toFiniteNumber(row.props.rowPadding, 16))))
  row.props.rowPaddingY = Math.max(0, Math.min(240, toFiniteNumber(row.props.rowPaddingY, toFiniteNumber(row.props.rowPadding, 16))))
  row.props.rowPadding = toFiniteNumber(row.props.rowPadding, row.props.rowPaddingX)
  row.props.rowAlign = normalizeRowAlign(row.props.rowAlign)
  row.props.rowBackgroundColor = normalizeBackgroundColor(row.props.rowBackgroundColor, '#FFFFFF')
  row.props.rowBackgroundImageFileId = sanitizeMediaFileId(row.props.rowBackgroundImageFileId || row.props.row_background_image_file_id) || null
  row.props.rowBackgroundImageUrl = resolveStoredMediaUrl(row.props.rowBackgroundImageUrl || row.props.row_background_image_url)
  row.props.rowBackgroundSize = String(row.props.rowBackgroundSize || 'cover').trim().toLowerCase() === 'contain' ? 'contain' : 'cover'
  row.props.rowBackgroundPosition = String(row.props.rowBackgroundPosition || 'center').trim() || 'center'
  row.props.rowBackgroundRepeat = String(row.props.rowBackgroundRepeat || 'no-repeat').trim().toLowerCase() === 'repeat' ? 'repeat' : 'no-repeat'
  row.props.stackColumns = row.props.stackColumns === true
  row.props.rowBorderColor = normalizeBackgroundColor(row.props.rowBorderColor, 'transparent')
  row.props.rowBorderWidth = Math.max(0, Math.min(8, toFiniteNumber(row.props.rowBorderWidth, row.props.rowBorderColor === 'transparent' ? 0 : 1)))
  row.props.rowBorderRadius = Math.max(0, Math.min(120, toFiniteNumber(row.props.rowBorderRadius, 0)))
  row.props.rowShadow = String(row.props.rowShadow || 'none').trim() || 'none'
  row.props.rowTemplateType = String(row.props.rowTemplateType || '').trim()
  if (!Array.isArray(row.props.rowColumnStyles)) row.props.rowColumnStyles = []
  for (let columnIndex = 0; columnIndex < row.props.rowColumnCount; columnIndex += 1) {
    const style = ensureRowColumnStyleEntry(row, columnIndex)
    style.widthPercent = Math.max(
      5,
      Math.min(100, toFiniteNumber(style.widthPercent, Number((100 / Math.max(1, row.props.rowColumnCount)).toFixed(2))))
    )
    style.backgroundColor = normalizeBackgroundColor(style.backgroundColor, 'transparent')
    style.backgroundImageFileId = sanitizeMediaFileId(style.backgroundImageFileId || style.background_image_file_id) || null
    style.backgroundImageUrl = resolveStoredMediaUrl(style.backgroundImageUrl || style.background_image_url)
    style.backgroundSize = String(style.backgroundSize || 'cover').trim().toLowerCase() === 'contain' ? 'contain' : 'cover'
    style.backgroundPosition = String(style.backgroundPosition || 'center').trim() || 'center'
    style.backgroundRepeat = String(style.backgroundRepeat || 'no-repeat').trim().toLowerCase() === 'repeat' ? 'repeat' : 'no-repeat'
    style.heightUnit = normalizeHeightUnit(style.heightUnit, 'px')
    style.height = clampColumnHeightByUnit(style.height, style.heightUnit, 120)
    style.paddingX = Math.max(0, Math.min(240, toFiniteNumber(style.paddingX, 0)))
    style.paddingY = Math.max(0, Math.min(240, toFiniteNumber(style.paddingY, 0)))
    const justify = String(style.contentJustify || '').trim().toLowerCase()
    style.contentJustify = justify === 'center' || justify === 'end' ? justify : 'start'
    const align = String(style.contentAlign || '').trim().toLowerCase()
    style.contentAlign = align === 'center' || align === 'right' || align === 'end'
      ? align
      : (align === 'left' || align === 'start' ? 'left' : 'start')
  }
  if (row.props.rowColumnStyles.length > row.props.rowColumnCount) {
    row.props.rowColumnStyles = row.props.rowColumnStyles.slice(0, row.props.rowColumnCount)
  }
  if (!Array.isArray(row.props.elements)) row.props.elements = []
  row.props.elements.forEach((element) => {
    ensureRowElementBlockProps(element)
  })
}

const ensureRowElementBlockProps = (element) => {
  if (!element || typeof element !== 'object') return
  ensureNodeVisibility(element)
  if (!element.props || typeof element.props !== 'object') element.props = {}
  const normalizedElementType = String(element.type || '').trim().toLowerCase()
  element.props.elementWidthPercent = Math.max(10, Math.min(100, toFiniteNumber(element.props.elementWidthPercent, 100)))
  delete element.props.elementMaxWidthPx
  delete element.props.element_max_width_px
  element.props.elementHeightUnit = normalizeHeightUnit(element.props.elementHeightUnit, 'px')
  if (normalizedElementType === 'text' && Number(element.props.elementHeight) <= 50) {
    element.props.elementHeight = 28
  }
  element.props.elementHeight = clampElementHeightByType(
    element.props.elementHeight,
    element,
    normalizedElementType === 'text' ? 28 : 120
  )
  element.props.elementMargin = Math.max(0, Math.min(240, toFiniteNumber(element.props.elementMargin, 0)))
  element.props.elementPaddingX = Math.max(0, Math.min(240, toFiniteNumber(element.props.elementPaddingX, toFiniteNumber(element.props.elementPadding, 12))))
  element.props.elementPaddingY = Math.max(0, Math.min(240, toFiniteNumber(element.props.elementPaddingY, toFiniteNumber(element.props.elementPadding, 12))))
  element.props.elementPadding = toFiniteNumber(element.props.elementPadding, element.props.elementPaddingX)
  element.props.elementAlign = normalizeRowAlign(element.props.elementAlign)
  element.props.elementBackgroundColor = normalizeBackgroundColor(element.props.elementBackgroundColor, 'transparent')
  element.props.rowElementColumnIndex = Math.max(0, toRoundedNumber(element.props.rowElementColumnIndex, 0))
  element.props.templateRole = String(element.props.templateRole || '').trim()
  if (normalizedElementType === 'row') {
    ensureRowBlockProps(element)
    element.props.elementBackgroundColor = 'transparent'
  }
  if (normalizedElementType === 'text') {
    const explicitTextAlignRaw = String(element.props.textAlign || element.props.text_align || '').trim()
    const inheritedTextAlign = normalizeTextAlign(
      explicitTextAlignRaw || element.props.elementAlign,
      'left'
    )
    const nextTextSettings = {
      ...(element.props.textSettings && typeof element.props.textSettings === 'object'
        ? element.props.textSettings
        : {}),
    }
    if (explicitTextAlignRaw) {
      nextTextSettings.textAlign = normalizeTextAlign(explicitTextAlignRaw, inheritedTextAlign)
    }
    if (
      !explicitTextAlignRaw
      && inheritedTextAlign === 'center'
      && normalizeTextAlign(nextTextSettings.textAlign, 'left') === 'left'
    ) {
      nextTextSettings.textAlign = 'center'
    }
    element.props.textSettings = normalizeTextSettings(nextTextSettings, {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 1.6,
      textColor: '#18181B',
      textAlign: inheritedTextAlign,
      linkAddress: '',
      openInNewTab: true,
    })
  }
  if (normalizedElementType === 'header') {
    const explicitTextAlignRaw = String(element.props.textAlign || element.props.text_align || '').trim()
    const inheritedTextAlign = normalizeTextAlign(
      explicitTextAlignRaw || element.props.elementAlign,
      'left'
    )
    const nextTextSettings = {
      ...(element.props.textSettings && typeof element.props.textSettings === 'object'
        ? element.props.textSettings
        : {}),
    }
    if (explicitTextAlignRaw) {
      nextTextSettings.textAlign = normalizeTextAlign(explicitTextAlignRaw, inheritedTextAlign)
    }
    if (
      !explicitTextAlignRaw
      && inheritedTextAlign === 'center'
      && normalizeTextAlign(nextTextSettings.textAlign, 'left') === 'left'
    ) {
      nextTextSettings.textAlign = 'center'
    }
    element.props.textSettings = normalizeTextSettings(nextTextSettings, {
      fontSize: Math.max(10, toRoundedNumber(element.props.size, 32)),
      fontWeight: 700,
      lineHeight: 1.2,
      textColor: '#18181B',
      textAlign: inheritedTextAlign,
      linkAddress: '',
      openInNewTab: true,
    })
  }
  if (normalizedElementType === 'bullet-list') {
    const items = Array.isArray(element.props.items) ? element.props.items : createBulletListProps().items
    element.props.items = items.slice(0, 100).map((item, index) => ({
      text: String(item?.text ?? item ?? `Bullet ${index + 1}`).slice(0, 1000),
    }))
    if (!element.props.items.length) element.props.items = [{ text: 'Add a bullet point' }]
    const bulletIcon = String(element.props.bulletIcon || 'disc').trim().toLowerCase()
    element.props.bulletIcon = bulletIconOptions.some((option) => option.value === bulletIcon) ? bulletIcon : 'disc'
    element.props.bulletIconColor = normalizeHexColor(element.props.bulletIconColor, '#22C55E')
    element.props.bulletTextColor = normalizeHexColor(element.props.bulletTextColor, '#18181B')
    element.props.bulletFontSize = Math.max(10, Math.min(72, toRoundedNumber(element.props.bulletFontSize, 18)))
    element.props.bulletGap = Math.max(0, Math.min(80, toRoundedNumber(element.props.bulletGap, 12)))
  }
  if (normalizedElementType === 'share') {
    const allowedNetworks = new Set(shareNetworkOptions.map((option) => option.value))
    const networks = Array.isArray(element.props.shareNetworks) ? element.props.shareNetworks : createShareProps().shareNetworks
    element.props.shareNetworks = [...new Set(networks.map((network) => String(network || '').trim().toLowerCase()))]
      .filter((network) => allowedNetworks.has(network))
    if (!element.props.shareNetworks.length) element.props.shareNetworks = ['copy']
    element.props.shareLabel = String(element.props.shareLabel || 'Share this page').slice(0, 160)
    element.props.shareShowLabel = element.props.shareShowLabel !== false
    element.props.shareAlignment = normalizeTextAlign(element.props.shareAlignment, 'left')
    element.props.shareButtonStyle = String(element.props.shareButtonStyle || '') === 'square' ? 'square' : 'circle'
    element.props.shareButtonColor = normalizeHexColor(element.props.shareButtonColor, '#18181B')
    element.props.shareIconColor = normalizeHexColor(element.props.shareIconColor, '#FFFFFF')
    element.props.shareButtonSize = Math.max(28, Math.min(72, toRoundedNumber(element.props.shareButtonSize, 42)))
    element.props.shareButtonGap = Math.max(0, Math.min(48, toRoundedNumber(element.props.shareButtonGap, 10)))
  }
  if (normalizedElementType === 'optin-form') {
    element.props.optinId = String(element.props.optinId || createOptinFormProps().optinId).slice(0, 80)
    element.props.optinHeadline = String(element.props.optinHeadline || 'Get updates delivered to your inbox').slice(0, 300)
    element.props.optinDescription = String(element.props.optinDescription || '').slice(0, 1200)
    element.props.optinShowLabels = element.props.optinShowLabels !== false
    const allowedFieldTypes = new Set(['text', 'email', 'tel'])
    const allowedFieldKeys = new Set(['firstName', 'lastName', 'email', 'phone', 'company', 'custom'])
    const rawFields = Array.isArray(element.props.optinFields) ? element.props.optinFields : createOptinFormProps().optinFields
    element.props.optinFields = rawFields.slice(0, 12).map((field, index) => {
      const key = allowedFieldKeys.has(String(field?.key || '')) ? String(field.key) : `custom_${index + 1}`
      const type = allowedFieldTypes.has(String(field?.type || '')) ? String(field.type) : (key === 'email' ? 'email' : 'text')
      return {
        key,
        type,
        label: String(field?.label || `Field ${index + 1}`).slice(0, 120),
        placeholder: String(field?.placeholder || '').slice(0, 180),
        showLabel: field?.showLabel !== false,
        required: key === 'email' ? true : field?.required === true,
      }
    })
    if (!element.props.optinFields.some((field) => field.key === 'email')) {
      element.props.optinFields.push({ key: 'email', type: 'email', label: 'Email address', placeholder: 'you@example.com', showLabel: true, required: true })
    }
    element.props.optinButtonLabel = String(element.props.optinButtonLabel || 'Subscribe').slice(0, 120)
    element.props.optinSuccessMessage = String(element.props.optinSuccessMessage || 'Thanks! You’re on the list.').slice(0, 500)
    element.props.optinRedirectUrl = String(element.props.optinRedirectUrl || '').trim().slice(0, 2000)
    element.props.optinRedirectNewTab = element.props.optinRedirectNewTab === true
    element.props.optinSendToIntegration = element.props.optinSendToIntegration !== false
    element.props.optinFieldGap = Math.max(0, Math.min(80, toRoundedNumber(element.props.optinFieldGap, 12)))
    element.props.optinInputHeight = Math.max(36, Math.min(88, toRoundedNumber(element.props.optinInputHeight, 48)))
    element.props.optinCornerRadius = Math.max(0, Math.min(48, toRoundedNumber(element.props.optinCornerRadius, 10)))
    element.props.optinLabelColor = normalizeHexColor(element.props.optinLabelColor, '#3F3F46')
    element.props.optinInputBackgroundColor = normalizeBackgroundColor(element.props.optinInputBackgroundColor, '#FFFFFF')
    element.props.optinInputTextColor = normalizeHexColor(element.props.optinInputTextColor, '#18181B')
    element.props.optinInputBorderColor = normalizeBackgroundColor(element.props.optinInputBorderColor, '#D4D4D8')
    element.props.optinButtonBackgroundColor = normalizeBackgroundColor(element.props.optinButtonBackgroundColor, '#2D8CFF')
    element.props.optinButtonTextColor = normalizeHexColor(element.props.optinButtonTextColor, '#FFFFFF')
  }
  if (normalizedElementType === 'image') {
    const imageFileId = sanitizeMediaFileId(
      element.props.imageFileId ||
      element.props.image_file_id ||
      element.props.fileId ||
      element.props.file_id
    ) || null
    const imageUrl = resolveStoredMediaUrl(element.props.imageUrl || element.props.image_url)
    element.props.imageFileId = imageFileId
    element.props.image_file_id = imageFileId
    element.props.imageUrl = imageUrl
    element.props.image_url = imageUrl
    element.props.imageWidth = Math.max(20, Math.min(100, toFiniteNumber(element.props.imageWidth, 80)))
    element.props.imageAlign = normalizeRowAlign(element.props.imageAlign || 'center')
    element.props.imageBorderRadius = Math.max(0, Math.min(120, toFiniteNumber(element.props.imageBorderRadius, 0)))
    element.props.imageShadowStrength = Math.max(0, Math.min(100, toFiniteNumber(element.props.imageShadowStrength, 0)))
    element.props.imageLinkAddress = String(element.props.imageLinkAddress || element.props.image_link_address || '').trim()
    element.props.imageOpenInNewTab = (element.props.imageOpenInNewTab ?? element.props.image_open_in_new_tab) === true
  }
  if (normalizedElementType === 'video') {
    const nextType = inferVideoTypeFromLegacyProps(element.props)
    const nextVideoUrl = normalizeVideoSourceUrl(element.props.videoUrl || element.props.video_url || '')
    const nextVideoPlaybackUrlRaw = normalizeVideoSourceUrl(element.props.videoPlaybackUrl || element.props.video_playback_url || '')
    const nextVideoFileId = resolveVideoFileIdCandidate(
      element.props.videoFileId,
      element.props.video_file_id,
      nextVideoPlaybackUrlRaw,
      nextVideoUrl,
      element.props.videoPosterUrl,
      element.props.video_poster_url
    ) || null
    const nextVideoThumbnailFileIdRaw = sanitizeMediaFileId(
      element.props.videoThumbnailFileId ||
      element.props.video_thumbnail_file_id ||
      element.props.thumbnailFileId ||
      element.props.thumbnail_file_id
    ) || null
    const nextVideoThumbnailFileId = nextVideoThumbnailFileIdRaw && nextVideoThumbnailFileIdRaw !== nextVideoFileId
      ? nextVideoThumbnailFileIdRaw
      : null
    const nextVideoExternalUrl = String(element.props.videoExternalUrl || element.props.video_external_url || '').trim()
    const nextVideoEmbedCode = String(element.props.videoEmbedCode || element.props.video_embed_code || '').trim()
    const nextVideoPlaybackUrl = nextVideoFileId
      ? buildVideoPlaybackUrlFromFileId(nextVideoFileId)
      : (isLikelyHlsVideoSource(nextVideoPlaybackUrlRaw)
        ? nextVideoPlaybackUrlRaw
        : (isLikelyHlsVideoSource(nextVideoUrl) ? nextVideoUrl : ''))
    const nextVideoAutoPreviewUrl = String(element.props.videoAutoPreviewUrl || element.props.video_auto_preview_url || '').trim()
    const nextVideoThumbnailUrl = String(element.props.videoThumbnailUrl || element.props.video_thumbnail_url || '').trim()
    const nextVideoPosterUrlRaw = normalizeVideoSourceUrl(element.props.videoPosterUrl || element.props.video_poster_url || '')
    const nextVideoPosterUrl = (nextVideoThumbnailFileId ? resolveImageUrl(nextVideoThumbnailFileId, '') : '') ||
      nextVideoThumbnailUrl ||
      nextVideoPosterUrlRaw ||
      nextVideoAutoPreviewUrl ||
      (nextVideoFileId ? buildVideoThumbnailProxyUrlFromFileId(nextVideoFileId) : '')
    const nextVideoBorderRadius = Math.max(0, Math.min(120, toFiniteNumber(element.props.videoBorderRadius ?? element.props.video_border_radius, 16)))
    element.props.videoType = nextType
    element.props.video_type = nextType
    element.props.videoFileId = nextVideoFileId
    element.props.video_file_id = nextVideoFileId
    element.props.videoUrl = nextVideoUrl
    element.props.video_url = element.props.videoUrl
    element.props.videoPlaybackUrl = nextVideoPlaybackUrl
    element.props.video_playback_url = nextVideoPlaybackUrl
    element.props.videoExternalUrl = nextVideoExternalUrl
    element.props.video_external_url = nextVideoExternalUrl
    element.props.videoEmbedCode = nextVideoEmbedCode
    element.props.video_embed_code = nextVideoEmbedCode
    element.props.videoFileName = String(element.props.videoFileName || element.props.video_file_name || '').trim()
    element.props.video_file_name = element.props.videoFileName
    element.props.videoFileMimeType = String(element.props.videoFileMimeType || element.props.video_file_mime_type || '').trim()
    element.props.video_file_mime_type = element.props.videoFileMimeType
    element.props.videoThumbnailFileId = nextVideoThumbnailFileId
    element.props.video_thumbnail_file_id = nextVideoThumbnailFileId
    element.props.thumbnailFileId = nextVideoThumbnailFileId
    element.props.thumbnail_file_id = nextVideoThumbnailFileId
    element.props.videoThumbnailUrl = nextVideoThumbnailUrl
    element.props.video_thumbnail_url = element.props.videoThumbnailUrl
    element.props.thumbnailUrl = element.props.videoThumbnailUrl
    element.props.thumbnail_url = element.props.videoThumbnailUrl
    element.props.videoAutoPreviewUrl = nextVideoAutoPreviewUrl
    element.props.video_auto_preview_url = element.props.videoAutoPreviewUrl
    element.props.videoPosterUrl = nextVideoPosterUrl
    element.props.video_poster_url = nextVideoPosterUrl
    element.props.videoBorderRadius = nextVideoBorderRadius
    element.props.video_border_radius = nextVideoBorderRadius
    const nextControlColor = normalizeHexColor(element.props.videoControlColor || element.props.video_control_color, '#2D8CFF')
    element.props.videoControlColor = nextControlColor
    element.props.video_control_color = nextControlColor
  }
  if (normalizedElementType === 'button') {
    element.props.buttonText = String(element.props.buttonText || element.props.button_text || element.props.content || '').trim() || 'Button Element'
    element.props.button_text = element.props.buttonText
    element.props.content = element.props.buttonText
    element.props.buttonBackgroundColor = normalizeHexColor(element.props.buttonBackgroundColor, '#2D8BFF')
    element.props.buttonTextColor = normalizeHexColor(element.props.buttonTextColor, '#FFFFFF')
    element.props.buttonTextSize = Math.max(60, Math.min(160, toFiniteNumber(element.props.buttonTextSize, 100)))
    element.props.buttonFontSize = Math.max(10, Math.min(56, toFiniteNumber(element.props.buttonFontSize, 18)))
    {
      const normalizedActionType = String(
        element.props.buttonActionType || element.props.button_action_type || element.props.actionType || ''
      ).trim().toLowerCase()
      if (normalizedActionType === 'file_access') {
        element.props.buttonActionType = 'file_access'
      } else if (normalizedActionType === 'start_quiz') {
        element.props.buttonActionType = 'start_quiz'
      } else if (normalizedActionType === 'open_meet_scheduler') {
        element.props.buttonActionType = 'open_meet_scheduler'
      } else if (normalizedActionType === 'open_pay_click_pop') {
        element.props.buttonActionType = 'open_pay_click_pop'
      } else if (String(element.props.templateRole || '').trim() === 'feature_paywall_cta') {
        element.props.buttonActionType = 'open_pay_click_pop'
      } else {
        element.props.buttonActionType = 'go_to'
      }
    }
    element.props.button_action_type = element.props.buttonActionType
    element.props.actionType = element.props.buttonActionType
    element.props.buttonGoToType = String(element.props.buttonGoToType || element.props.button_go_to_type || '').trim().toLowerCase() === 'feature_instance' ? 'feature_instance' : 'custom_link'
    element.props.buttonFeatureSlug = String(element.props.buttonFeatureSlug || element.props.button_feature_slug || '').trim()
    element.props.buttonLinkAddress = String(element.props.buttonLinkAddress || element.props.button_link_address || '').trim()
    element.props.buttonOpenInNewTab = (element.props.buttonOpenInNewTab ?? element.props.button_open_in_new_tab) !== false
    element.props.buttonFileId = element.props.buttonFileId || element.props.button_file_id || null
    element.props.buttonFileUrl = String(element.props.buttonFileUrl || element.props.button_file_url || '').trim()
    element.props.buttonFileName = String(element.props.buttonFileName || element.props.button_file_name || '').trim()
    element.props.buttonFileMimeType = String(element.props.buttonFileMimeType || element.props.button_file_mime_type || '').trim()
    element.props.buttonFileAccessMode = String(element.props.buttonFileAccessMode || element.props.button_file_access_mode || '').trim().toLowerCase() === 'download' ? 'download' : 'view'
    element.props.button_file_access_mode = element.props.buttonFileAccessMode
    element.props.buttonWidth = Math.max(20, Math.min(100, toFiniteNumber(element.props.buttonWidth, 36)))
    element.props.buttonPaddingX = Math.max(4, Math.min(80, toFiniteNumber(element.props.buttonPaddingX, 24)))
    element.props.buttonPaddingY = Math.max(4, Math.min(56, toFiniteNumber(element.props.buttonPaddingY, 14)))
    element.props.buttonAlign = normalizeTextAlign(element.props.buttonAlign, 'center')
  }
  if (isMemberLoginFormType(element.type)) {
    element.props.formLocked = true
    element.props.forgotPasswordText = String(element.props.forgotPasswordText || MEMBER_LOGIN_DEFAULTS.forgotPasswordText)
    element.props.forgotPasswordHref = String(element.props.forgotPasswordHref || MEMBER_LOGIN_DEFAULTS.forgotPasswordHref)
    element.props.submitButtonText = String(element.props.submitButtonText || MEMBER_LOGIN_DEFAULTS.submitButtonText)
    element.props.joinLinkHref = String(element.props.joinLinkHref || MEMBER_LOGIN_DEFAULTS.joinLinkHref)
    const submitButtonColor = normalizeHexColor(
      element.props.loginSubmitButtonColor || element.props.submitButtonColor,
      MEMBER_LOGIN_DEFAULTS.submitButtonColor
    )
    const forgotPasswordColor = normalizeHexColor(
      element.props.loginForgotPasswordColor || element.props.forgotPasswordColor,
      MEMBER_LOGIN_DEFAULTS.forgotPasswordColor
    )
    const rememberCheckboxColor = normalizeHexColor(
      element.props.loginRememberCheckboxColor || element.props.rememberCheckboxColor,
      MEMBER_LOGIN_DEFAULTS.rememberCheckboxColor
    )
    element.props.loginSubmitButtonColor = submitButtonColor
    element.props.submitButtonColor = submitButtonColor
    element.props.loginForgotPasswordColor = forgotPasswordColor
    element.props.forgotPasswordColor = forgotPasswordColor
    element.props.loginRememberCheckboxColor = rememberCheckboxColor
    element.props.rememberCheckboxColor = rememberCheckboxColor
  }
  if (isMemberCreateFreeAccountFormType(element.type)) {
    element.props.formLocked = true
    element.props.submitButtonText = String(element.props.submitButtonText || 'Sign Up')
    element.props.joinLinkHref = String(element.props.joinLinkHref || 'login')
    const submitButtonColor = normalizeHexColor(
      element.props.loginSubmitButtonColor || element.props.submitButtonColor,
      MEMBER_LOGIN_DEFAULTS.submitButtonColor
    )
    element.props.loginSubmitButtonColor = submitButtonColor
    element.props.submitButtonColor = submitButtonColor
  }
  if (String(element.type || '').trim().toLowerCase() === 'member-pricing-plans') {
    element.props.formLocked = true
    element.props.plansHelperText = String(
      element.props.plansHelperText || MEMBER_PRICING_DEFAULTS.dynamicPlansHint
    )
  }
  if (String(element.type || '').trim().toLowerCase() === 'member-get-access-form') {
    element.props.formLocked = true
    element.props.formMode = String(element.props.formMode || 'step-one-default').trim() || 'step-one-default'
    element.props.formHelperText = String(
      element.props.formHelperText || MEMBER_GET_ACCESS_DEFAULTS.dynamicFormHint
    )
  }
  if (String(element.type || '').trim().toLowerCase() === 'checkout-order-form') {
    element.props.systemManaged = true
    element.props.checkoutMode = String(element.props.checkoutMode || 'one_step') === 'two_step' ? 'two_step' : 'one_step'
    element.props.editorPreviewStep = String(element.props.editorPreviewStep || 'contact') === 'payment' ? 'payment' : 'contact'
    element.props.heading = String(element.props.heading || 'Complete your order').trim() || 'Complete your order'
    element.props.contactStepLabel = String(element.props.contactStepLabel || 'Your information').trim() || 'Your information'
    element.props.paymentStepLabel = String(element.props.paymentStepLabel || 'Payment details').trim() || 'Payment details'
    element.props.continueButtonLabel = String(element.props.continueButtonLabel || 'Continue to secure payment').trim() || 'Continue to secure payment'
    element.props.buttonLabel = String(element.props.buttonLabel || 'Complete purchase').trim() || 'Complete purchase'
    element.props.firstNameLabel = String(element.props.firstNameLabel || 'First name').trim() || 'First name'
    element.props.lastNameLabel = String(element.props.lastNameLabel || 'Last name').trim() || 'Last name'
    element.props.emailLabel = String(element.props.emailLabel || 'Email address').trim() || 'Email address'
    element.props.collectCompany = element.props.collectCompany === true
    element.props.companyLabel = String(element.props.companyLabel || 'Company').trim() || 'Company'
    element.props.collectPhone = element.props.collectPhone !== false
    element.props.phoneLabel = String(element.props.phoneLabel || 'Phone number').trim() || 'Phone number'
    element.props.phoneRequired = element.props.phoneRequired === true
    element.props.smsConsentEnabled = element.props.smsConsentEnabled === true
    element.props.smsConsentLabel = String(element.props.smsConsentLabel || 'I agree to receive transactional text messages about this purchase. Message and data rates may apply.').trim()
    element.props.collectShippingAddress = element.props.collectShippingAddress === true
    element.props.shippingHeading = String(element.props.shippingHeading || 'Shipping address').trim() || 'Shipping address'
    element.props.addressLabel = String(element.props.addressLabel || 'Street address').trim() || 'Street address'
    element.props.cityLabel = String(element.props.cityLabel || 'City').trim() || 'City'
    element.props.stateLabel = String(element.props.stateLabel || 'State / province').trim() || 'State / province'
    element.props.postalCodeLabel = String(element.props.postalCodeLabel || 'Postal code').trim() || 'Postal code'
    element.props.countryLabel = String(element.props.countryLabel || 'Country').trim() || 'Country'
    element.props.productSelectorEnabled = element.props.productSelectorEnabled === true
    element.props.productSelectorHeading = String(element.props.productSelectorHeading || 'Select a payment plan').trim() || 'Select a payment plan'
    element.props.productSelectorOptions = (Array.isArray(element.props.productSelectorOptions) ? element.props.productSelectorOptions : [])
      .slice(0, 12)
      .map((option, index) => ({
        id: String(option?.id || `offer-${index + 1}`).trim() || `offer-${index + 1}`,
        productPublicId: String(option?.productPublicId || '').trim(),
        title: String(option?.title || `Offer ${index + 1}`).trim() || `Offer ${index + 1}`,
        subtitle: String(option?.subtitle || '').trim(),
        badge: String(option?.badge || '').trim(),
        paymentType: ['one_time', 'monthly', 'yearly', 'free_forever'].includes(String(option?.paymentType || '')) ? String(option.paymentType) : 'one_time',
        priceCents: Math.max(0, Math.min(100000000, Math.round(Number(option?.priceCents) || 0))),
        currencyCode: String(option?.currencyCode || 'USD').trim().toUpperCase().slice(0, 3) || 'USD',
      }))
    if (!element.props.productSelectorOptions.length) element.props.productSelectorOptions = [{ id: 'primary-offer', productPublicId: '', title: 'Current product', subtitle: '', badge: '', paymentType: 'one_time', priceCents: 0, currencyCode: 'USD' }]
    element.props.orderBumpEnabled = element.props.orderBumpEnabled === true
    if (element.props.orderBumpEnabled) {
      element.props.checkoutMode = 'two_step'
    }
    element.props.orderBumpProductPublicId = String(element.props.orderBumpProductPublicId || '').trim()
    element.props.orderBumpHeadline = String(element.props.orderBumpHeadline || 'Yes, add this to my order').trim() || 'Yes, add this to my order'
    element.props.orderBumpDescription = String(element.props.orderBumpDescription || 'Add this special offer to your purchase.').trim()
    element.props.orderBumpSelectedByDefault = element.props.orderBumpSelectedByDefault === true
    element.props.termsEnabled = element.props.termsEnabled === true
    element.props.termsLabel = String(element.props.termsLabel || 'I agree to the terms of purchase and privacy policy.').trim()
    element.props.accentColor = normalizeHexColor(element.props.accentColor, '#2D8CFF')
    element.props.showSecureBadge = element.props.showSecureBadge !== false
    element.props.secureBadgeText = String(element.props.secureBadgeText || 'Secure checkout').trim() || 'Secure checkout'
    element.props.secureBadgeIcon = ['lock', 'shield', 'check', 'none'].includes(String(element.props.secureBadgeIcon || '')) ? String(element.props.secureBadgeIcon) : 'lock'
    element.props.secureBadgeTextColor = normalizeHexColor(element.props.secureBadgeTextColor, '#047857')
    element.props.secureBadgeBackgroundColor = normalizeBackgroundColor(element.props.secureBadgeBackgroundColor, '#ECFDF5')
    element.props.cardBackgroundColor = normalizeBackgroundColor(
      element.props.cardTransparent === true ? 'transparent' : element.props.cardBackgroundColor,
      '#FFFFFF'
    )
    element.props.cardTransparent = false
    element.props.cardBorderColor = normalizeBackgroundColor(element.props.cardBorderColor, '#E4E4E7')
    element.props.cardBorderWidth = Math.max(0, Math.min(8, toRoundedNumber(element.props.cardBorderWidth, 1)))
    element.props.cardCornerRadius = Math.max(0, Math.min(48, toRoundedNumber(element.props.cardCornerRadius, 16)))
    element.props.cardShadow = ['none', 'soft', 'medium', 'strong'].includes(String(element.props.cardShadow || '')) ? String(element.props.cardShadow) : 'medium'
    element.props.cardPaddingX = Math.max(0, Math.min(96, toRoundedNumber(element.props.cardPaddingX, 36)))
    element.props.cardPaddingY = Math.max(0, Math.min(96, toRoundedNumber(element.props.cardPaddingY, 36)))
    element.props.showTrustBadges = element.props.showTrustBadges !== false
    element.props.inputBackgroundColor = normalizeBackgroundColor(element.props.inputBackgroundColor, '#FFFFFF')
    element.props.inputTextColor = normalizeHexColor(element.props.inputTextColor, '#18181B')
    element.props.inputBorderColor = normalizeBackgroundColor(element.props.inputBorderColor, '#D4D4D8')
    element.props.inputLabelColor = normalizeHexColor(element.props.inputLabelColor, '#3F3F46')
    element.props.inputHeight = Math.max(38, Math.min(72, toRoundedNumber(element.props.inputHeight, 48)))
    element.props.formCornerRadius = Math.max(0, Math.min(32, toRoundedNumber(element.props.formCornerRadius, 12)))
    element.props.inputShadow = element.props.inputShadow === true
    element.props.buttonBackgroundColor = normalizeBackgroundColor(element.props.buttonBackgroundColor, element.props.accentColor)
    element.props.buttonTextColor = normalizeHexColor(element.props.buttonTextColor, '#FFFFFF')
    element.props.buttonFontWeight = [500, 600, 700, 800].includes(Number(element.props.buttonFontWeight)) ? Number(element.props.buttonFontWeight) : 800
    element.props.buttonFontSize = Math.max(11, Math.min(24, toRoundedNumber(element.props.buttonFontSize, 14)))
    element.props.buttonCornerRadius = Math.max(0, Math.min(32, toRoundedNumber(element.props.buttonCornerRadius, 12)))
    element.props.buttonShadow = element.props.buttonShadow !== false
    element.props.buttonSubtext = String(element.props.buttonSubtext || '').trim()
  }
  if (String(element.type || '').trim().toLowerCase() === 'member-approval-form') {
    element.props.formLocked = true
    element.props.formMode = String(element.props.formMode || 'member-approval-default').trim() || 'member-approval-default'
    element.props.formHelperText = String(
      element.props.formHelperText || MEMBER_APPROVAL_DEFAULTS.formHelperText
    )
    element.props.submitButtonText = String(
      element.props.submitButtonText || MEMBER_APPROVAL_DEFAULTS.submitButtonText
    ).trim() || MEMBER_APPROVAL_DEFAULTS.submitButtonText
  }
  if (String(element.type || '').trim().toLowerCase() === 'member-get-access-success') {
    element.props.formLocked = true
    element.props.successMode = String(element.props.successMode || 'auto-redirect-default').trim() || 'auto-redirect-default'
    element.props.successHelperText = String(
      element.props.successHelperText || MEMBER_GET_ACCESS_SUCCESS_DEFAULTS.successHelperText
    )
  }
}

const isSectionStructureBlock = (section) => String(section?.type || '') === 'section'
const getStructureRowColumnCount = (row) => {
  const count = Number(row?.props?.rowColumnCount ?? 1)
  return Math.max(1, Math.min(6, Number.isFinite(count) ? Math.round(count) : 1))
}
const getStructureRowElements = (row) => {
  return Array.isArray(row?.props?.elements) ? row.props.elements : []
}
const getStructureColumnElements = (row, columnIndex) => {
  const elements = getStructureRowElements(row)
  return elements
    .map((element, elementIndex) => ({ element, elementIndex }))
    .filter(({ element }) => {
      const idxRaw = Number(element?.props?.rowElementColumnIndex ?? 0)
      const idx = Number.isFinite(idxRaw) ? Math.max(0, Math.round(idxRaw)) : 0
      return idx === Number(columnIndex)
    })
}
const getStructureElementLabel = (element, elementIndex) => {
  const type = String(element?.type || '').trim().toLowerCase()
  if (type === 'row') {
    return `${getStructureRowColumnCount(element)} Col. Row`
  }
  if (type === 'member-login-form') return isJoinPageContext.value ? `Create Free Account Form` : `Login Form`
  if (type === MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE) return `Create Free Account Form`
  if (type === 'member-pricing-plans') return `Pricing Plans`
  if (type === 'member-get-access-form') return `Get Access Form`
  if (type === 'checkout-order-form') return `Order Form`
  if (type === 'member-approval-form') return `Member Approval Form`
  if (type === 'member-get-access-success') return `Get Access Success`
  if (type === 'text') return `Text`
  if (type === 'image') return `Image`
  if (type === 'button') return `Button`
  if (type === 'video') return `Video`
  if (type === 'mp3') return `MP3`
  if (type === 'embed') return `Embed`
  if (type === 'header') return `Header`
  return `Element ${elementIndex + 1}`
}
const buildStructureRowNode = (row, sectionIndex, rowIndex, nestedRowElementIndex = -1, allowNestedRows = true) => {
  const columnCount = getStructureRowColumnCount(row)
  const columns = Array.from({ length: columnCount }, (_, columnIndex) => {
    const elements = getStructureColumnElements(row, columnIndex).map(({ element, elementIndex }) => {
      const type = String(element?.type || '').trim().toLowerCase()
      const node = {
        element,
        type,
        elementIndex,
        label: getStructureElementLabel(element, elementIndex),
        nestedRow: null,
      }
      if (allowNestedRows && type === 'row') {
        node.nestedRow = buildStructureRowNode(element, sectionIndex, rowIndex, elementIndex, false)
      }
      return node
    })
    return { columnIndex, elements }
  })
  return {
    row,
    sectionIndex,
    rowIndex,
    nestedRowElementIndex: Number.isFinite(Number(nestedRowElementIndex)) ? Number(nestedRowElementIndex) : -1,
    columnCount,
    columns,
  }
}
const pageStructureSections = computed(() => {
  const sections = Array.isArray(editorDoc.value?.sections) ? editorDoc.value.sections : []
  return sections.map((section, sectionIndex) => {
    const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
    return {
      section,
      sectionIndex,
      rows: rows.map((row, rowIndex) => buildStructureRowNode(row, sectionIndex, rowIndex)),
    }
  })
})

// handlers
const onSelect = (i) => {
  showSettings.value = false
  showStructureMenu.value = false
  showAddPalette.value = false
  addPaletteCategory.value = 'elements'
  contentSelectorMenuOpen.value = false
  contentSelectorRowTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  closeElementSettingsMenus()
  showSectionBackgroundColorPicker.value = false
  showRowBackgroundColorPicker.value = false
  showColumnBackgroundColorPicker.value = false
  showElementBackgroundColorPicker.value = false
  showElementTextColorPicker.value = false
  selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  if (editorDoc.value?.sections?.[i] && !editorDoc.value.sections[i].props) {
    editorDoc.value.sections[i].props = {}
  }
  if (editorDoc.value?.sections?.[i] && String(editorDoc.value.sections[i]?.type || '') === 'section') {
    ensureSectionBlockProps(editorDoc.value.sections[i])
  }
  selectedMemberLoginPart.value = ''
  selectedIdx.value = i 
}
const onClearCanvasSelection = () => {
  showStructureMenu.value = false
  selectedIdx.value = -1
  selectedMemberLoginPart.value = ''
  contentSelectorRowTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  showSectionBackgroundColorPicker.value = false
  showRowBackgroundColorPicker.value = false
  showColumnBackgroundColorPicker.value = false
  showElementBackgroundColorPicker.value = false
  showElementTextColorPicker.value = false
  showButtonBackgroundColorPicker.value = false
  showButtonTextColorPicker.value = false
  showLoginSubmitButtonColorPicker.value = false
  showLoginForgotPasswordColorPicker.value = false
  showLoginRememberCheckboxColorPicker.value = false
  selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
}
const onSelectRow = ({ sectionIndex, rowIndex }) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  if (!Number.isFinite(sIdx) || sIdx < 0) return
  if (!Number.isFinite(rIdx) || rIdx < 0) return
  onSelect(sIdx)
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return
  ensureSectionBlockProps(section)
  if (!Array.isArray(section?.props?.rows) || !section.props.rows[rIdx]) return
  selectedRowContext.value = { sectionIndex: sIdx, rowIndex: rIdx }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
}
const onSelectRowColumn = ({ sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1 }) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  const cIdx = Number(columnIndex)
  const nestedIdx = Number(nestedRowElementIndex ?? -1)
  if (!Number.isFinite(sIdx) || sIdx < 0) return
  if (!Number.isFinite(rIdx) || rIdx < 0) return
  if (!Number.isFinite(cIdx) || cIdx < 0) return
  onSelect(sIdx)
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return
  ensureSectionBlockProps(section)
  const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
  const hostRow = rows[rIdx]
  if (!hostRow || String(hostRow.type || '') !== 'row') return
  let row = hostRow
  if (Number.isFinite(nestedIdx) && nestedIdx >= 0) {
    const nestedRow = hostRow?.props?.elements?.[nestedIdx]
    if (!nestedRow || String(nestedRow?.type || '') !== 'row') return
    ensureRowElementBlockProps(nestedRow)
    row = nestedRow
  }
  const rowColumnCount = Math.max(1, Math.min(6, Number(row?.props?.rowColumnCount ?? 1) || 1))
  if (cIdx >= rowColumnCount) return
  ensureRowColumnStyleEntry(row, cIdx)
  selectedRowContext.value = { sectionIndex: sIdx, rowIndex: rIdx }
  selectedColumnContext.value = {
    sectionIndex: sIdx,
    rowIndex: rIdx,
    columnIndex: cIdx,
    nestedRowElementIndex: (Number.isFinite(nestedIdx) && nestedIdx >= 0) ? nestedIdx : -1,
  }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  activeSettingsContext.value = {
    scope: 'row-column',
    index: sIdx,
    rowIndex: rIdx,
    columnIndex: cIdx,
    elementIndex: -1,
    nestedRowElementIndex: (Number.isFinite(nestedIdx) && nestedIdx >= 0) ? nestedIdx : -1,
    partKey: '',
    settingsType: 'column',
  }
}
const onSelectRowElement = ({ sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1 }) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  const eIdx = Number(elementIndex)
  const nestedIdx = Number(nestedRowElementIndex ?? -1)
  if (!Number.isFinite(sIdx) || sIdx < 0) return
  if (!Number.isFinite(rIdx) || rIdx < 0) return
  if (!Number.isFinite(eIdx) || eIdx < 0) return
  onSelect(sIdx)
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return
  ensureSectionBlockProps(section)
  const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
  const row = rows[rIdx]
  if (!row || String(row.type || '') !== 'row') return
  const rowElements = Array.isArray(row?.props?.elements) ? row.props.elements : []
  let element = rowElements[eIdx]
  if (Number.isFinite(nestedIdx) && nestedIdx >= 0) {
    const nestedRow = rowElements[nestedIdx]
    if (!nestedRow || String(nestedRow?.type || '') !== 'row') return
    const nestedElements = Array.isArray(nestedRow?.props?.elements) ? nestedRow.props.elements : []
    element = nestedElements[eIdx]
  }
  if (!element) return
  selectedRowContext.value = { sectionIndex: sIdx, rowIndex: rIdx }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = { sectionIndex: sIdx, rowIndex: rIdx, elementIndex: eIdx, nestedRowElementIndex: (Number.isFinite(nestedIdx) && nestedIdx >= 0) ? nestedIdx : -1 }
  const type = String(element?.type || '').trim().toLowerCase()
  const settingsType = type === 'image'
    ? 'image'
    : type === 'button'
      ? 'button'
      : type === 'video'
        ? 'video'
        : type === 'member-login-form' || type === MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE || type === 'member-pricing-plans' || type === 'member-get-access-form' || type === 'checkout-order-form' || type === 'member-approval-form' || type === 'member-get-access-success' || type === 'row'
          ? 'none'
          : 'text'
  activeSettingsContext.value = {
    scope: 'row-element',
    index: sIdx,
    rowIndex: rIdx,
    columnIndex: -1,
    elementIndex: eIdx,
    nestedRowElementIndex: (Number.isFinite(nestedIdx) && nestedIdx >= 0) ? nestedIdx : -1,
    partKey: '',
    settingsType,
  }
}
const openSettings = () => {
  closeElementSettingsMenus()
  showSectionBackgroundColorPicker.value = false
  showRowBackgroundColorPicker.value = false
  showColumnBackgroundColorPicker.value = false
  showElementBackgroundColorPicker.value = false
  showElementTextColorPicker.value = false
  contentSelectorMenuOpen.value = false
  contentSelectorRowTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  selectedIdx.value = -1
  selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  selectedMemberLoginPart.value = ''
  addPaletteCategory.value = 'elements'
  showStructureMenu.value = false
  showSettings.value = true
  ensureSeo()
}
const openStructureMenu = () => {
  closeElementSettingsMenus()
  showSectionBackgroundColorPicker.value = false
  showRowBackgroundColorPicker.value = false
  showColumnBackgroundColorPicker.value = false
  showElementBackgroundColorPicker.value = false
  showElementTextColorPicker.value = false
  contentSelectorMenuOpen.value = false
  contentSelectorRowTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  showSettings.value = false
  showAddPalette.value = false
  showStructureMenu.value = true
}
const selectStructureSection = (sectionIndex) => {
  showStructureMenu.value = false
  onSelect(Number(sectionIndex))
}
const selectStructureRow = (sectionIndex, rowIndex) => {
  showStructureMenu.value = false
  onSelectRow({ sectionIndex, rowIndex })
}
const selectStructureColumn = (sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) => {
  showStructureMenu.value = false
  onSelectRowColumn({ sectionIndex, rowIndex, columnIndex, nestedRowElementIndex })
}
const selectStructureElement = (sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) => {
  showStructureMenu.value = false
  onSelectRowElement({ sectionIndex, rowIndex, elementIndex, nestedRowElementIndex })
}

// open palette handlers
const openAddPaletteAfter = (payload) => {
  const index = Number(payload?.index)
  const insertAt = Number.isFinite(index) ? index + 1 : null
  openContentSelectorMenu(null, insertAt)
  addPaletteCategory.value = 'elements'
}

const openAddPaletteEnd = () => {
  const hasExisting = Array.isArray(editorDoc.value?.sections) && editorDoc.value.sections.length > 0
  openContentSelectorMenu(null, null)
  addPaletteCategory.value = hasExisting ? 'elements' : 'sections'
}

// insert a block and jump to its inspector
const insertBlock = (type, explicitInsertIndex = null) => {
  if (!editorDoc.value) return
  const makeBlock = (t) => {
    if (t === 'section') {
      return {
        type: 'section',
        props: {
          sectionWidthPercent: 100,
          sectionHeight: 100,
          sectionHeightUnit: 'px',
          sectionPadding: 0,
          sectionPaddingX: 24,
          sectionPaddingY: 24,
          sectionMargin: 0,
          sectionAlign: 'left',
          sectionBackgroundColor: '#FFFFFF',
          sectionBackgroundImageFileId: null,
          sectionBackgroundImageUrl: '',
          sectionBackgroundSize: 'cover',
          sectionBackgroundPosition: 'center',
          sectionBackgroundRepeat: 'no-repeat',
          rows: [],
        },
      }
    }
    if (t === 'text') return { type: 'text', props: { content: 'New text' } }
    if (t === 'header') return { type: 'header', props: { text: 'Section heading', level: 'h2', size: 32 } }
    if (t === 'bullet-list') return { type: 'bullet-list', props: createBulletListProps() }
    if (t === 'image') return { type: 'image', props: { imageFileId: null, imageUrl: '', imageWidth: 80, imageAlign: 'center', imageBorderRadius: 0, imageShadowStrength: 0, imageLinkAddress: '', imageOpenInNewTab: false } }
    if (t === 'button') {
      return {
        type: 'button',
        props: {
          buttonText: 'Button Element',
          button_text: 'Button Element',
          content: 'Button Element',
          buttonBackgroundColor: '#2D8CFF',
          buttonTextColor: '#FFFFFF',
          buttonTextSize: 100,
          buttonFontSize: 18,
          buttonActionType: 'go_to',
          buttonGoToType: 'custom_link',
          buttonFeatureSlug: '',
          buttonLinkAddress: '',
          buttonOpenInNewTab: true,
          buttonFileId: null,
          buttonFileUrl: '',
    buttonFileName: '',
    buttonFileMimeType: '',
    buttonFileAccessMode: 'view',
    buttonWidth: 36,
          buttonPaddingX: 24,
          buttonPaddingY: 14,
          buttonAlign: 'center',
        },
      }
    }
    if (t === 'mp3') return { type: 'mp3', props: { mp3FileId: null, mp3Url: '', mp3FileName: '', mp3FileMimeType: 'audio/mpeg', mp3ControlColor: '#22C55E' } }
    if (t === 'share') return { type: 'share', props: createShareProps() }
    if (t === 'optin-form') return { type: 'optin-form', props: createOptinFormProps() }
    if (t === 'embed') return { type: 'embed', props: { embedCode: '', embedSrc: '', embedProvider: 'unknown', embedWidthPercent: 100, embedAlign: 'center', embedHeight: 352 } }
    if (t === 'video') {
      return {
        type: 'video',
        props: {
          videoType: 'upload',
          videoFileId: null,
          videoUrl: '',
          videoPlaybackUrl: '',
          videoExternalUrl: '',
          videoEmbedCode: '',
          videoFileName: '',
          videoFileMimeType: '',
          videoThumbnailFileId: null,
          videoThumbnailUrl: '',
          videoAutoPreviewUrl: '',
          videoPosterUrl: '',
          videoBorderRadius: 16,
          videoControlColor: '#2D8CFF',
        },
      }
    }
    if (t === 'member-login') {
      return buildMemberLoginTemplateSection({ type: 'member-login', props: { ...MEMBER_LOGIN_DEFAULTS } })
    }

    // future types here...
    return { type: 'text', props: { content: 'New text' } }
  }
  const newBlock = applyViewportVisibilityToNewNode(makeBlock(type))
  if (!Array.isArray(editorDoc.value.sections)) editorDoc.value.sections = []

  const normalizedExplicitIndex = Number.isFinite(Number(explicitInsertIndex))
    ? Math.max(0, Number(explicitInsertIndex))
    : null
  const normalizedMenuIndex = Number.isFinite(Number(contentSelectorMenuTargetIndex.value))
    ? Math.max(0, Number(contentSelectorMenuTargetIndex.value))
    : null
  const normalizedLegacyIndex = Number.isFinite(Number(pendingInsertIndex.value))
    ? Math.max(0, Number(pendingInsertIndex.value + 1))
    : null

  const requestedInsertAt =
    normalizedExplicitIndex != null
      ? normalizedExplicitIndex
      : (normalizedMenuIndex != null ? normalizedMenuIndex : normalizedLegacyIndex)

  let insertAt
  if (requestedInsertAt == null) {
    editorDoc.value.sections.push(newBlock)
    insertAt = editorDoc.value.sections.length - 1
  } else {
    insertAt = Math.min(editorDoc.value.sections.length, requestedInsertAt)
    editorDoc.value.sections.splice(insertAt, 0, newBlock)
  }

  // open inspector for the new block
  showAddPalette.value = false
  contentSelectorMenuOpen.value = false
  contentSelectorMenuTargetIndex.value = null
  selectedMemberLoginPart.value = ''
  pendingInsertIndex.value = null
  selectedIdx.value = insertAt
}

const createRowBlock = (columnCount = 1) => {
  const count = Math.max(1, Math.min(6, toRoundedNumber(columnCount, 1)))
  const defaultColumnWidth = Number((100 / Math.max(1, count)).toFixed(2))
  const rowColumnStyles = Array.from({ length: count }, () => ({
    widthPercent: defaultColumnWidth,
    backgroundColor: 'transparent',
    backgroundImageFileId: null,
    backgroundImageUrl: '',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    height: 120,
    heightUnit: 'px',
    paddingX: 0,
    paddingY: 0,
  }))
  return applyViewportVisibilityToNewNode({
    type: 'row',
    props: {
      rowColumnCount: count,
      rowColumnGutter: 12,
      rowWidthPercent: 100,
      rowHeight: 120,
      rowHeightUnit: 'px',
      rowPadding: 16,
      rowPaddingX: 16,
      rowPaddingY: 16,
      rowMargin: 5,
      rowAlign: 'left',
      rowBackgroundColor: 'transparent',
      rowBackgroundImageFileId: null,
      rowBackgroundImageUrl: '',
      rowBackgroundSize: 'cover',
      rowBackgroundPosition: 'center',
      rowBackgroundRepeat: 'no-repeat',
      rowColumnStyles,
      elements: [],
    },
  })
}

const getActiveSectionIndexForRows = () => {
  if (selectedRow.value) return Number(selectedRowContext.value.sectionIndex)
  if (String(selectedBlock.value?.type || '') === 'section') return Number(selectedIdx.value)
  return -1
}

const isRowElementType = (type) => {
  const normalized = String(type || '').trim().toLowerCase()
  return ['text', 'header', 'bullet-list', 'image', 'button', 'share', 'optin-form', 'mp3', 'embed', 'video', 'checkout-order-form', 'meet-scheduler', 'row'].includes(normalized)
}

const getEditorElementLabel = (element) => {
  const type = String(element?.type || '').trim().toLowerCase()
  if (type === 'quiz-result-history') return 'Member Quiz Results'
  if (type === 'checkout-order-form') return 'Order Form'
  if (!type) return 'Element'
  return type.split(/[-_\s]+/).filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
}

const isRequiredSystemElement = (element) =>
  requiredSystemElementTypes.value.includes(String(element?.type || '').trim().toLowerCase())

const nodeContainsRequiredSystemElement = (node) => {
  if (!node || typeof node !== 'object') return false
  if (isRequiredSystemElement(node)) return true
  const rows = Array.isArray(node?.props?.rows) ? node.props.rows : []
  const elements = Array.isArray(node?.props?.elements) ? node.props.elements : []
  return [...rows, ...elements].some((child) => nodeContainsRequiredSystemElement(child))
}

const getEditorRowElementColumnIndex = (element, row) => {
  const columnCount = Math.max(1, Math.min(6, Number(row?.props?.rowColumnCount ?? 1) || 1))
  const rawIndex = Number(element?.props?.rowElementColumnIndex ?? 0)
  return Math.max(0, Math.min(columnCount - 1, Number.isFinite(rawIndex) ? Math.round(rawIndex) : 0))
}

const rowColumnContainsRequiredSystemElement = (row, columnIndex) => {
  const elements = Array.isArray(row?.props?.elements) ? row.props.elements : []
  return elements.some((element) => (
    getEditorRowElementColumnIndex(element, row) === Number(columnIndex) &&
    nodeContainsRequiredSystemElement(element)
  ))
}

const canDeleteDrawerSelection = computed(() => {
  if (showSettings.value || showStructureMenu.value || showAddPalette.value) return false
  if (selectedElement.value) return !nodeContainsRequiredSystemElement(selectedElement.value)
  if (selectedColumn.value) {
    const columnCount = Math.max(1, Math.min(6, Number(selectedColumn.value.row?.props?.rowColumnCount ?? 1) || 1))
    return columnCount > 1 && !rowColumnContainsRequiredSystemElement(selectedColumn.value.row, selectedColumn.value.columnIndex)
  }
  if (selectedRow.value) return !nodeContainsRequiredSystemElement(selectedRow.value)
  if (selectedBlock.value) return !nodeContainsRequiredSystemElement(selectedBlock.value)
  return false
})

const createRowElementBlock = (type, columnIndex = 0, options = {}) => {
  const normalizedType = String(type || '').trim().toLowerCase()
  let element = { type: 'text', props: { content: 'New text' } }
  if (normalizedType === 'text') element = { type: 'text', props: { content: 'New text' } }
  if (normalizedType === 'header') element = { type: 'header', props: { text: 'Section heading', level: 'h2', size: 32 } }
  if (normalizedType === 'bullet-list') element = { type: 'bullet-list', props: createBulletListProps() }
  if (normalizedType === 'image') element = { type: 'image', props: { imageFileId: null, imageUrl: '', imageWidth: 80, imageAlign: 'center', imageBorderRadius: 0, imageShadowStrength: 0, imageLinkAddress: '', imageOpenInNewTab: false } }
  if (normalizedType === 'button') {
    element = {
      type: 'button',
      props: {
        buttonText: 'Button Element',
        button_text: 'Button Element',
        content: 'Button Element',
        buttonBackgroundColor: '#2D8CFF',
        buttonTextColor: '#FFFFFF',
        buttonTextSize: 100,
        buttonFontSize: 18,
        buttonActionType: 'go_to',
        buttonGoToType: 'custom_link',
        buttonFeatureSlug: '',
        buttonLinkAddress: '',
        buttonOpenInNewTab: true,
        buttonFileId: null,
        buttonFileUrl: '',
          buttonFileName: '',
          buttonFileMimeType: '',
          buttonFileAccessMode: 'view',
          buttonWidth: 36,
        buttonPaddingX: 24,
        buttonPaddingY: 14,
        buttonAlign: 'center',
      },
    }
  }
  if (normalizedType === 'mp3') element = { type: 'mp3', props: { mp3FileId: null, mp3Url: '', mp3FileName: '', mp3FileMimeType: 'audio/mpeg', mp3ControlColor: '#22C55E' } }
  if (normalizedType === 'share') element = { type: 'share', props: createShareProps() }
  if (normalizedType === 'optin-form') element = { type: 'optin-form', props: createOptinFormProps() }
  if (normalizedType === 'embed') element = { type: 'embed', props: { embedCode: '', embedSrc: '', embedProvider: 'unknown', embedWidthPercent: 100, embedAlign: 'center', embedHeight: 352 } }
  if (normalizedType === 'video') {
    element = {
      type: 'video',
      props: {
        videoType: 'upload',
        videoFileId: null,
        videoUrl: '',
        videoPlaybackUrl: '',
        videoExternalUrl: '',
        videoEmbedCode: '',
        videoFileName: '',
        videoFileMimeType: '',
        videoThumbnailFileId: null,
        videoThumbnailUrl: '',
        videoAutoPreviewUrl: '',
        videoPosterUrl: '',
        videoBorderRadius: 16,
        videoControlColor: '#2D8CFF',
      },
    }
  }
  if (normalizedType === 'checkout-order-form') {
    element = {
      type: 'checkout-order-form',
      props: {
        systemManaged: true,
        checkoutMode: 'one_step',
        editorPreviewStep: 'contact',
        heading: 'Complete your order',
        contactStepLabel: 'Your information',
        paymentStepLabel: 'Payment details',
        continueButtonLabel: 'Continue to secure payment',
        buttonLabel: 'Complete purchase',
        firstNameLabel: 'First name',
        lastNameLabel: 'Last name',
        emailLabel: 'Email address',
        collectCompany: false,
        companyLabel: 'Company',
        collectPhone: true,
        phoneLabel: 'Phone number',
        phoneRequired: false,
        smsConsentEnabled: false,
        smsConsentLabel: 'I agree to receive transactional text messages about this purchase. Message and data rates may apply.',
        collectShippingAddress: false,
        shippingHeading: 'Shipping address',
        addressLabel: 'Street address',
        cityLabel: 'City',
        stateLabel: 'State / province',
        postalCodeLabel: 'Postal code',
        countryLabel: 'Country',
        productSelectorEnabled: false,
        productSelectorHeading: 'Select a payment plan',
        productSelectorOptions: [{ id: 'primary-offer', productPublicId: '', title: 'Current product', subtitle: '', badge: '', paymentType: 'one_time', priceCents: 0, currencyCode: 'USD' }],
        orderBumpEnabled: false,
        orderBumpProductPublicId: '',
        orderBumpHeadline: 'Yes, add this to my order',
        orderBumpDescription: 'Add this special offer to your purchase.',
        orderBumpSelectedByDefault: false,
        termsEnabled: false,
        termsLabel: 'I agree to the terms of purchase and privacy policy.',
        showSecureBadge: true,
        secureBadgeText: 'Secure checkout',
        secureBadgeIcon: 'lock',
        secureBadgeTextColor: '#047857',
        secureBadgeBackgroundColor: '#ECFDF5',
        cardBackgroundColor: '#FFFFFF',
        cardTransparent: false,
        cardBorderColor: '#E4E4E7',
        cardBorderWidth: 1,
        cardCornerRadius: 16,
        cardShadow: 'medium',
        cardPaddingX: 36,
        cardPaddingY: 36,
        showTrustBadges: true,
        accentColor: '#2D8CFF',
        inputBackgroundColor: '#FFFFFF',
        inputTextColor: '#18181B',
        inputBorderColor: '#D4D4D8',
        inputLabelColor: '#3F3F46',
        inputHeight: 48,
        formCornerRadius: 12,
        inputShadow: false,
        buttonBackgroundColor: '#2D8CFF',
        buttonTextColor: '#FFFFFF',
        buttonFontWeight: 800,
        buttonFontSize: 14,
        buttonCornerRadius: 12,
        buttonShadow: true,
        buttonSubtext: '',
      },
    }
  }
  if (normalizedType === 'meet-scheduler') {
    element = { type: 'meet-scheduler', props: { elementWidthPercent: 100, elementHeight: 620, elementHeightUnit: 'px', elementAlign: 'center' } }
  }
  if (normalizedType === 'row') element = createRowBlock(options?.columnCount || 1)
  if (!element.props || typeof element.props !== 'object') element.props = {}
  element.props.elementWidthPercent = 100
  element.props.elementHeight = 120
  element.props.elementHeightUnit = 'px'
  element.props.elementPadding = 12
  element.props.elementPaddingX = 12
  element.props.elementPaddingY = 12
  element.props.elementMargin = 0
  element.props.elementAlign = 'left'
  element.props.elementBackgroundColor = 'transparent'
  if (normalizedType === 'checkout-order-form') {
    element.props.elementHeight = 560
    element.props.elementPadding = 0
    element.props.elementPaddingX = 0
    element.props.elementPaddingY = 0
    element.props.elementBackgroundColor = 'transparent'
  }
  if (normalizedType === 'meet-scheduler') {
    element.props.elementHeight = 620
    element.props.elementPadding = 0
    element.props.elementPaddingX = 0
    element.props.elementPaddingY = 0
  }
  element.props.rowElementColumnIndex = Math.max(0, toRoundedNumber(columnIndex, 0))
  ensureRowElementBlockProps(element)
  return applyViewportVisibilityToNewNode(element)
}

const getActiveRowContextForElements = () => {
  const targetSection = Number(contentSelectorRowTarget.value?.sectionIndex)
  const targetRow = Number(contentSelectorRowTarget.value?.rowIndex)
  if (Number.isFinite(targetSection) && targetSection >= 0 && Number.isFinite(targetRow) && targetRow >= 0) {
    return {
      sectionIndex: targetSection,
      rowIndex: targetRow,
      elementIndex: Number(contentSelectorRowTarget.value?.elementIndex),
    }
  }
  if (selectedElement.value) {
    return {
      sectionIndex: Number(selectedElementContext.value.sectionIndex),
      rowIndex: Number(selectedElementContext.value.rowIndex),
      elementIndex: Number(selectedElementContext.value.elementIndex),
    }
  }
  if (selectedRow.value) {
    return {
      sectionIndex: Number(selectedRowContext.value.sectionIndex),
      rowIndex: Number(selectedRowContext.value.rowIndex),
      elementIndex: -1,
    }
  }
  return { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
}

const getRowTargetForElements = (sectionIndex, rowIndex, nestedRowElementIndex = -1) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  if (!Number.isFinite(sIdx) || sIdx < 0) return null
  if (!Number.isFinite(rIdx) || rIdx < 0) return null
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return null
  ensureSectionBlockProps(section)
  const rows = section.props.rows
  const row = rows[rIdx]
  if (!row || String(row.type || '') !== 'row') return null
  const nestedIdx = Number(nestedRowElementIndex)
  if (Number.isFinite(nestedIdx) && nestedIdx >= 0) {
    const nestedRowElement = row?.props?.elements?.[nestedIdx]
    if (nestedRowElement && String(nestedRowElement?.type || '') === 'row') {
      ensureRowElementBlockProps(nestedRowElement)
      if (!Array.isArray(nestedRowElement?.props?.elements)) nestedRowElement.props.elements = []
      return { section, row, targetRow: nestedRowElement, sectionIndex: sIdx, rowIndex: rIdx, nestedRowElementIndex: nestedIdx }
    }
  }
  if (!Array.isArray(row?.props?.elements)) row.props.elements = []
  return { section, row, targetRow: row, sectionIndex: sIdx, rowIndex: rIdx, nestedRowElementIndex: -1 }
}

const ensureSectionRowTargetForElementDrop = (sectionIndex, preferredRowIndex = null, columnCount = 1) => {
  const sIdx = Number(sectionIndex)
  if (!Number.isFinite(sIdx) || sIdx < 0) return -1
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section?.type || '') !== 'section') return -1
  ensureSectionBlockProps(section)
  const rows = section.props.rows
  let desiredIndex = Number(preferredRowIndex)
  if (!Number.isFinite(desiredIndex)) desiredIndex = rows.length
  desiredIndex = Math.max(0, desiredIndex)
  const rowAtIndex = rows[desiredIndex]
  if (rowAtIndex && String(rowAtIndex?.type || '') === 'row') return desiredIndex
  if (rows.length > 0 && desiredIndex < rows.length) return desiredIndex
  const insertAt = Math.max(0, Math.min(rows.length, desiredIndex))
  rows.splice(insertAt, 0, createRowBlock(columnCount))
  return insertAt
}

const insertElementIntoRow = (
  sectionIndex,
  rowIndex,
  elementType,
  explicitElementIndex = null,
  columnIndex = 0,
  rowColumnCount = 1,
  nestedRowElementIndex = -1
) => {
  if (!isRowElementType(elementType)) return
  const target = getRowTargetForElements(sectionIndex, rowIndex, nestedRowElementIndex)
  if (!target?.targetRow) return
  const sIdx = target.sectionIndex
  const rIdx = target.rowIndex
  const nestedIdx = target.nestedRowElementIndex
  const elements = target.targetRow.props.elements
  const element = createRowElementBlock(elementType, columnIndex, { columnCount: rowColumnCount })
  const hasExplicitIndex = explicitElementIndex !== null && explicitElementIndex !== undefined && Number.isFinite(Number(explicitElementIndex))
  const at = hasExplicitIndex ? Math.max(0, Math.min(elements.length, Number(explicitElementIndex))) : elements.length
  elements.splice(at, 0, element)
  onSelect(sIdx)
  selectedRowContext.value = { sectionIndex: sIdx, rowIndex: rIdx }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  selectedElementContext.value = nestedIdx >= 0
    ? { sectionIndex: sIdx, rowIndex: rIdx, elementIndex: at, nestedRowElementIndex: nestedIdx }
    : { sectionIndex: sIdx, rowIndex: rIdx, elementIndex: at, nestedRowElementIndex: -1 }
}

const insertRowIntoSection = (sectionIndex, columnCount = 1, explicitRowIndex = null) => {
  const sIdx = Number(sectionIndex)
  if (!Number.isFinite(sIdx) || sIdx < 0) return
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return
  ensureSectionBlockProps(section)
  const rows = section.props.rows
  const row = createRowBlock(columnCount)
  const hasExplicitIndex = explicitRowIndex !== null && explicitRowIndex !== undefined && Number.isFinite(Number(explicitRowIndex))
  const at = hasExplicitIndex
    ? Math.max(0, Math.min(rows.length, Number(explicitRowIndex)))
    : rows.length
  rows.splice(at, 0, row)
  onSelect(sIdx)
  selectedRowContext.value = { sectionIndex: sIdx, rowIndex: at }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
}

const insertRowFromPalette = (columnCount) => {
  if (!canInsertRows.value) return
  const sIdx = getActiveSectionIndexForRows()
  if (!Number.isFinite(sIdx) || sIdx < 0) return
  insertRowIntoSection(sIdx, columnCount)
}

const onRowPaletteDragStart = (columnCount, event) => {
  const dt = event?.dataTransfer
  if (!dt) return
  const payload = JSON.stringify({ columnCount: Math.max(1, Math.min(6, toRoundedNumber(columnCount, 1))) })
  dt.effectAllowed = 'copy'
  dt.setData(PAGE_ROW_INSERT_MIME, payload)
  dt.setData('text/plain', `${PAGE_ROW_INSERT_TEXT_PREFIX}${payload}`)
}

const onInsertRowFromDrop = ({ sectionIndex, rowIndex, columnCount }) => {
  insertRowIntoSection(sectionIndex, columnCount, rowIndex)
}

const onInsertRowElementFromDrop = ({ sectionIndex, rowIndex, elementIndex, elementType, columnIndex, columnCount, nestedRowElementIndex }) => {
  const nestedIdx = Number(nestedRowElementIndex)
  let targetRowIndex = Number(rowIndex)
  if (!Number.isFinite(nestedIdx) || nestedIdx < 0) {
    targetRowIndex = ensureSectionRowTargetForElementDrop(sectionIndex, rowIndex, columnCount)
  }
  insertElementIntoRow(sectionIndex, targetRowIndex, elementType, elementIndex, columnIndex, columnCount, nestedRowElementIndex)
}

const onAddRowAfter = ({ sectionIndex, rowIndex }) => {
  const section = editorDoc.value?.sections?.[Number(sectionIndex)]
  if (!section || String(section.type || '') !== 'section') return
  const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
  const base = rows[Number(rowIndex)]
  const nextColumns = Math.max(1, Math.min(6, toRoundedNumber(base?.props?.rowColumnCount, 1)))
  insertRowIntoSection(sectionIndex, nextColumns, Number(rowIndex) + 1)
}

const onAddRowElementAfter = ({ sectionIndex, rowIndex, elementIndex, anchorRect }) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  const eIdx = Number(elementIndex)
  if (!Number.isFinite(sIdx) || sIdx < 0) return
  if (!Number.isFinite(rIdx) || rIdx < 0) return
  onSelectRow({ sectionIndex: sIdx, rowIndex: rIdx })
  openContentSelectorMenu(anchorRect || null, null, {
    sectionIndex: sIdx,
    rowIndex: rIdx,
    elementIndex: Number.isFinite(eIdx) ? eIdx : -1,
  })
}

const duplicateRow = ({ sectionIndex, rowIndex }) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return
  ensureSectionBlockProps(section)
  const rows = section.props.rows
  if (!rows[rIdx]) return
  const clone = scopeNodeTreeToCurrentViewport(JSON.parse(JSON.stringify(rows[rIdx])))
  if (nodeContainsRequiredSystemElement(clone)) {
    alert('Move the required system element to another row before duplicating this row.')
    return
  }
  rows.splice(rIdx + 1, 0, clone)
  onSelect(sIdx)
  selectedRowContext.value = { sectionIndex: sIdx, rowIndex: rIdx + 1 }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
}

const duplicateRowElement = ({ sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1 }) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  const eIdx = Number(elementIndex)
  const nestedIdx = Number(nestedRowElementIndex ?? -1)
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return
  ensureSectionBlockProps(section)
  const target = getRowTargetForElements(sIdx, rIdx, nestedIdx)
  const row = target?.targetRow
  if (!row || !Array.isArray(row?.props?.elements) || !row.props.elements[eIdx]) return
  if (isRequiredSystemElement(row.props.elements[eIdx])) return
  const clone = scopeNodeTreeToCurrentViewport(JSON.parse(JSON.stringify(row.props.elements[eIdx])))
  ensureRowElementBlockProps(clone)
  row.props.elements.splice(eIdx + 1, 0, clone)
  onSelect(sIdx)
  selectedRowContext.value = { sectionIndex: sIdx, rowIndex: rIdx }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = {
    sectionIndex: sIdx,
    rowIndex: rIdx,
    elementIndex: eIdx + 1,
    nestedRowElementIndex: Number.isFinite(nestedIdx) && nestedIdx >= 0 ? nestedIdx : -1,
  }
}

const requestDeleteSelection = (kind, payload = {}) => {
  const normalizedKind = String(kind || '').trim().toLowerCase()
  const confirmation = {
    kind: normalizedKind,
    payload: { ...payload },
    blocked: false,
    title: 'Remove this item?',
    message: `This item will be hidden on the ${editorViewportMode.value} layout.`,
    viewportOnly: normalizedKind !== 'column',
  }

  if (normalizedKind === 'section') {
    const sectionIndex = Number(payload?.index ?? payload?.sectionIndex)
    const section = editorDoc.value?.sections?.[sectionIndex]
    if (!section) return
    confirmation.payload.index = sectionIndex
    confirmation.title = 'Remove this section?'
    if (nodeContainsRequiredSystemElement(section)) {
      confirmation.blocked = true
      confirmation.title = 'This section contains the Order Form'
      confirmation.message = 'Move the required Order Form to another section before removing this section.'
    }
  } else if (normalizedKind === 'row') {
    const sectionIndex = Number(payload?.sectionIndex)
    const rowIndex = Number(payload?.rowIndex)
    const row = editorDoc.value?.sections?.[sectionIndex]?.props?.rows?.[rowIndex]
    if (!row) return
    confirmation.title = 'Remove this row?'
    if (nodeContainsRequiredSystemElement(row)) {
      confirmation.blocked = true
      confirmation.title = 'This row contains the Order Form'
      confirmation.message = 'Move the required Order Form to another row before removing this row.'
    }
  } else if (normalizedKind === 'column') {
    const target = getRowTargetForElements(payload?.sectionIndex, payload?.rowIndex, payload?.nestedRowElementIndex)
    const row = target?.targetRow
    const columnIndex = Number(payload?.columnIndex)
    if (!row || !Number.isFinite(columnIndex)) return
    const columnCount = Math.max(1, Math.min(6, Number(row?.props?.rowColumnCount ?? 1) || 1))
    confirmation.title = 'Delete this column?'
    confirmation.message = 'The column and every element inside it will be removed from all device layouts.'
    if (columnCount <= 1) {
      confirmation.blocked = true
      confirmation.title = 'Keep at least one column'
      confirmation.message = 'A row must contain at least one column.'
    } else if (rowColumnContainsRequiredSystemElement(row, columnIndex)) {
      confirmation.blocked = true
      confirmation.title = 'This column contains the Order Form'
      confirmation.message = 'Move the required Order Form to another column before deleting this column.'
    }
  } else if (normalizedKind === 'element') {
    const target = getRowTargetForElements(payload?.sectionIndex, payload?.rowIndex, payload?.nestedRowElementIndex)
    const element = target?.targetRow?.props?.elements?.[Number(payload?.elementIndex)]
    if (!element) return
    confirmation.title = `Remove ${getEditorElementLabel(element)}?`
    if (isRequiredSystemElement(element)) {
      confirmation.blocked = true
      confirmation.title = `${getEditorElementLabel(element)} is required`
      confirmation.message = 'This checkout page must keep its Order Form element. You can move and restyle it instead.'
    }
  } else {
    return
  }

  deleteConfirmation.value = confirmation
}

const confirmDeleteSelection = () => {
  const confirmation = deleteConfirmation.value
  if (!confirmation || confirmation.blocked) return
  deleteConfirmation.value = null
  const payload = confirmation.payload || {}
  if (confirmation.kind === 'section') deleteBlock(payload.index)
  if (confirmation.kind === 'row') deleteRow(payload)
  if (confirmation.kind === 'column') deleteRowColumn(payload)
  if (confirmation.kind === 'element') deleteRowElement(payload)
  if (payload.fromDrawer) closeDrawer()
}

const deleteRow = ({ sectionIndex, rowIndex }) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return
  ensureSectionBlockProps(section)
  const rows = section.props.rows
  const targetRow = rows[rIdx]
  if (!targetRow) return
  if (nodeContainsRequiredSystemElement(targetRow)) return
  const { shouldRemoveCompletely } = hideNodeInCurrentViewport(targetRow)
  if (shouldRemoveCompletely) {
    rows.splice(rIdx, 1)
  }
  onSelect(sIdx)
  const visibleRowIndexes = rows.reduce((acc, row, index) => {
    if (String(row?.type || '') === 'row' && isNodeVisibleForViewport(row)) acc.push(index)
    return acc
  }, [])
  if (visibleRowIndexes.length > 0) {
    const nextVisibleIndex = visibleRowIndexes.find((index) => index >= rIdx)
    const fallbackIndex = visibleRowIndexes[visibleRowIndexes.length - 1]
    selectedRowContext.value = {
      sectionIndex: sIdx,
      rowIndex: Number.isFinite(nextVisibleIndex) ? nextVisibleIndex : fallbackIndex,
    }
    selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
    selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  } else {
    selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
    selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
    selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  }
}

const deleteRowElement = ({ sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1 }) => {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  const eIdx = Number(elementIndex)
  const nestedIdx = Number(nestedRowElementIndex ?? -1)
  const section = editorDoc.value?.sections?.[sIdx]
  if (!section || String(section.type || '') !== 'section') return
  ensureSectionBlockProps(section)
  const target = getRowTargetForElements(sIdx, rIdx, nestedIdx)
  const row = target?.targetRow
  if (!row) return
  if (!Array.isArray(row?.props?.elements)) return
  const targetElement = row.props.elements[eIdx]
  if (!targetElement) return
  if (isRequiredSystemElement(targetElement)) return
  const { shouldRemoveCompletely } = hideNodeInCurrentViewport(targetElement)
  if (shouldRemoveCompletely) {
    row.props.elements.splice(eIdx, 1)
  }
  onSelect(sIdx)
  selectedRowContext.value = { sectionIndex: sIdx, rowIndex: rIdx }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  const visibleElementIndexes = row.props.elements.reduce((acc, element, index) => {
    if (isNodeVisibleForViewport(element)) acc.push(index)
    return acc
  }, [])
  if (visibleElementIndexes.length > 0) {
    const nextVisibleIndex = visibleElementIndexes.find((index) => index >= eIdx)
    const fallbackIndex = visibleElementIndexes[visibleElementIndexes.length - 1]
    const nextIndex = Number.isFinite(nextVisibleIndex) ? nextVisibleIndex : fallbackIndex
    selectedElementContext.value = {
      sectionIndex: sIdx,
      rowIndex: rIdx,
      elementIndex: nextIndex,
      nestedRowElementIndex: Number.isFinite(nestedIdx) && nestedIdx >= 0 ? nestedIdx : -1,
    }
  } else {
    selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  }
}

const moveRowColumn = ({ sectionIndex, rowIndex, columnIndex, direction, nestedRowElementIndex = -1 }) => {
  const target = getRowTargetForElements(sectionIndex, rowIndex, nestedRowElementIndex)
  const row = target?.targetRow
  if (!row) return
  if (!row.props || typeof row.props !== 'object') row.props = {}
  if (!Array.isArray(row.props.elements)) row.props.elements = []
  const columnCount = Math.max(1, Math.min(6, Number(row?.props?.rowColumnCount ?? 1) || 1))
  const sourceIndex = Number(columnIndex)
  const targetIndex = sourceIndex + Number(direction)
  if (!Number.isFinite(sourceIndex) || sourceIndex < 0 || sourceIndex >= columnCount) return
  if (!Number.isFinite(targetIndex) || targetIndex < 0 || targetIndex >= columnCount) return

  if (!Array.isArray(row.props.rowColumnStyles)) row.props.rowColumnStyles = []
  while (row.props.rowColumnStyles.length < columnCount) {
    row.props.rowColumnStyles.push({ widthPercent: 100 / columnCount })
  }
  const sourceStyle = row.props.rowColumnStyles[sourceIndex]
  row.props.rowColumnStyles[sourceIndex] = row.props.rowColumnStyles[targetIndex]
  row.props.rowColumnStyles[targetIndex] = sourceStyle

  row.props.elements.forEach((element) => {
    if (!element?.props || typeof element.props !== 'object') element.props = {}
    const elementColumnIndex = getEditorRowElementColumnIndex(element, row)
    if (elementColumnIndex === sourceIndex) element.props.rowElementColumnIndex = targetIndex
    else if (elementColumnIndex === targetIndex) element.props.rowElementColumnIndex = sourceIndex
  })

  onSelect(target.sectionIndex)
  selectedRowContext.value = { sectionIndex: target.sectionIndex, rowIndex: target.rowIndex }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  selectedColumnContext.value = {
    sectionIndex: target.sectionIndex,
    rowIndex: target.rowIndex,
    columnIndex: targetIndex,
    nestedRowElementIndex: target.nestedRowElementIndex,
  }
}

const deleteRowColumn = ({ sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1 }) => {
  const target = getRowTargetForElements(sectionIndex, rowIndex, nestedRowElementIndex)
  const row = target?.targetRow
  if (!row) return
  if (!row.props || typeof row.props !== 'object') row.props = {}
  if (!Array.isArray(row.props.elements)) row.props.elements = []
  const columnCount = Math.max(1, Math.min(6, Number(row?.props?.rowColumnCount ?? 1) || 1))
  const removeIndex = Number(columnIndex)
  if (columnCount <= 1 || !Number.isFinite(removeIndex) || removeIndex < 0 || removeIndex >= columnCount) return
  if (rowColumnContainsRequiredSystemElement(row, removeIndex)) return

  row.props.elements = row.props.elements.filter((element) => getEditorRowElementColumnIndex(element, row) !== removeIndex)
  row.props.elements.forEach((element) => {
    if (!element?.props || typeof element.props !== 'object') element.props = {}
    const elementColumnIndex = getEditorRowElementColumnIndex(element, row)
    if (elementColumnIndex > removeIndex) element.props.rowElementColumnIndex = elementColumnIndex - 1
  })

  const nextColumnCount = columnCount - 1
  const styles = Array.isArray(row.props.rowColumnStyles) ? row.props.rowColumnStyles : []
  styles.splice(removeIndex, 1)
  const remainingWidths = styles.slice(0, nextColumnCount).map((entry) => Math.max(5, Number(entry?.widthPercent) || 0))
  const remainingTotal = remainingWidths.reduce((sum, width) => sum + width, 0)
  row.props.rowColumnStyles = Array.from({ length: nextColumnCount }, (_, index) => ({
    ...(styles[index] && typeof styles[index] === 'object' ? styles[index] : {}),
    widthPercent: remainingTotal > 0 ? (remainingWidths[index] / remainingTotal) * 100 : 100 / nextColumnCount,
  }))
  row.props.rowColumnCount = nextColumnCount

  onSelect(target.sectionIndex)
  selectedRowContext.value = { sectionIndex: target.sectionIndex, rowIndex: target.rowIndex }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  selectedColumnContext.value = {
    sectionIndex: target.sectionIndex,
    rowIndex: target.rowIndex,
    columnIndex: Math.min(removeIndex, nextColumnCount - 1),
    nestedRowElementIndex: target.nestedRowElementIndex,
  }
}

const onReorderRows = ({ fromSectionIndex, fromRowIndex, toSectionIndex, toRowIndex }) => {
  const fromS = Number(fromSectionIndex)
  const fromR = Number(fromRowIndex)
  const toS = Number(toSectionIndex)
  const toR = Number(toRowIndex)
  const fromSection = editorDoc.value?.sections?.[fromS]
  const toSection = editorDoc.value?.sections?.[toS]
  if (!fromSection || !toSection) return
  if (String(fromSection.type || '') !== 'section' || String(toSection.type || '') !== 'section') return
  ensureSectionBlockProps(fromSection)
  ensureSectionBlockProps(toSection)
  const fromRows = fromSection.props.rows
  const toRows = toSection.props.rows
  if (!fromRows[fromR]) return
  const [moved] = fromRows.splice(fromR, 1)
  let targetIndex = Math.max(0, Math.min(toRows.length, toR))
  if (fromS === toS && fromR < targetIndex) {
    targetIndex -= 1
  }
  toRows.splice(targetIndex, 0, moved)
  onSelect(toS)
  selectedRowContext.value = { sectionIndex: toS, rowIndex: targetIndex }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
}

const onReorderRowElements = ({
  fromSectionIndex,
  fromRowIndex,
  fromElementIndex,
  toSectionIndex,
  toRowIndex,
  toElementIndex,
  toColumnIndex,
  fromNestedRowElementIndex = -1,
  toNestedRowElementIndex = -1,
}) => {
  const fromS = Number(fromSectionIndex)
  const fromR = Number(fromRowIndex)
  const fromE = Number(fromElementIndex)
  const toS = Number(toSectionIndex)
  let toR = Number(toRowIndex)
  const toE = Number(toElementIndex)
  const toNestedIdxRaw = Number(toNestedRowElementIndex)
  if (!Number.isFinite(toNestedIdxRaw) || toNestedIdxRaw < 0) {
    const ensuredTargetRowIndex = ensureSectionRowTargetForElementDrop(toS, toR, 1)
    toR = ensuredTargetRowIndex
  }
  const fromTarget = getRowTargetForElements(fromS, fromR, fromNestedRowElementIndex)
  const toTarget = getRowTargetForElements(toS, toR, toNestedRowElementIndex)
  if (!fromTarget?.targetRow || !toTarget?.targetRow) return
  const fromRow = fromTarget.targetRow
  const toRow = toTarget.targetRow
  const toNestedIdx = Number(toTarget.nestedRowElementIndex)
  if (!Array.isArray(fromRow?.props?.elements) || !Array.isArray(toRow?.props?.elements)) return
  if (!fromRow.props.elements[fromE]) return
  if (isRequiredSystemElement(fromRow.props.elements[fromE]) && Number(toTarget.nestedRowElementIndex) >= 0) return
  const [moved] = fromRow.props.elements.splice(fromE, 1)
  if (!moved) return
  let targetIndex = Math.max(0, Math.min(toRow.props.elements.length, Number.isFinite(toE) ? toE : toRow.props.elements.length))
  if (
    fromS === toS &&
    fromR === toR &&
    Number(fromTarget.nestedRowElementIndex) === Number(toTarget.nestedRowElementIndex) &&
    fromE < targetIndex
  ) targetIndex -= 1
  if (!moved.props || typeof moved.props !== 'object') moved.props = {}
  moved.props.rowElementColumnIndex = Math.max(0, toRoundedNumber(toColumnIndex, Number(moved?.props?.rowElementColumnIndex || 0)))
  toRow.props.elements.splice(targetIndex, 0, moved)
  onSelect(toS)
  selectedRowContext.value = { sectionIndex: toS, rowIndex: toR }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = Number.isFinite(toNestedIdx) && toNestedIdx >= 0
    ? { sectionIndex: toS, rowIndex: toR, elementIndex: toNestedIdx }
    : { sectionIndex: toS, rowIndex: toR, elementIndex: targetIndex }
}

const onNestRowIntoRow = ({
  fromSectionIndex,
  fromRowIndex,
  toSectionIndex,
  toRowIndex,
  toElementIndex,
  toColumnIndex,
  toNestedRowElementIndex = -1,
}) => {
  const fromS = Number(fromSectionIndex)
  const fromR = Number(fromRowIndex)
  const toS = Number(toSectionIndex)
  const toRRaw = Number(toRowIndex)
  const toE = Number(toElementIndex)
  const toC = Number(toColumnIndex)
  const toNestedIdx = Number(toNestedRowElementIndex)
  if (!Number.isFinite(fromS) || !Number.isFinite(fromR) || !Number.isFinite(toS) || !Number.isFinite(toRRaw)) return
  if (fromS === toS && fromR === toRRaw) return
  const fromSection = editorDoc.value?.sections?.[fromS]
  const toSection = editorDoc.value?.sections?.[toS]
  if (!fromSection || !toSection) return
  if (String(fromSection.type || '') !== 'section' || String(toSection.type || '') !== 'section') return
  ensureSectionBlockProps(fromSection)
  ensureSectionBlockProps(toSection)
  const fromRows = fromSection.props.rows
  const toRows = toSection.props.rows
  if (!fromRows[fromR]) return
  const [movedRow] = fromRows.splice(fromR, 1)
  if (!movedRow) return
  let toR = toRRaw
  if (fromS === toS && fromR < toR) toR -= 1
  const hostRow = toRows[toR]
  if (!hostRow || String(hostRow.type || '') !== 'row') {
    fromRows.splice(Math.max(0, Math.min(fromRows.length, fromR)), 0, movedRow)
    return
  }
  let targetRow = hostRow
  if (Number.isFinite(toNestedIdx) && toNestedIdx >= 0) {
    const nestedRowElement = hostRow?.props?.elements?.[toNestedIdx]
    if (!nestedRowElement || String(nestedRowElement?.type || '') !== 'row') {
      fromRows.splice(Math.max(0, Math.min(fromRows.length, fromR)), 0, movedRow)
      return
    }
    ensureRowElementBlockProps(nestedRowElement)
    targetRow = nestedRowElement
  }
  if (!Array.isArray(targetRow?.props?.elements)) targetRow.props.elements = []
  ensureRowElementBlockProps(movedRow)
  movedRow.props.rowElementColumnIndex = Math.max(0, toRoundedNumber(toC, Number(movedRow?.props?.rowElementColumnIndex || 0)))
  const insertAt = Math.max(0, Math.min(targetRow.props.elements.length, Number.isFinite(toE) ? toE : targetRow.props.elements.length))
  targetRow.props.elements.splice(insertAt, 0, movedRow)
  onSelect(toS)
  selectedRowContext.value = { sectionIndex: toS, rowIndex: toR }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = Number.isFinite(toNestedIdx) && toNestedIdx >= 0
    ? { sectionIndex: toS, rowIndex: toR, elementIndex: toNestedIdx }
    : { sectionIndex: toS, rowIndex: toR, elementIndex: insertAt }
}

const deleteDrawerSelection = () => {
  if (selectedElement.value) {
    requestDeleteSelection('element', { ...selectedElementContext.value, fromDrawer: true })
    return
  }
  if (selectedColumn.value) {
    requestDeleteSelection('column', { ...selectedColumnContext.value, fromDrawer: true })
    return
  }
  if (selectedRow.value) {
    requestDeleteSelection('row', { ...selectedRowContext.value, fromDrawer: true })
    return
  }
  if (selectedBlock.value && selectedIdx.value >= 0) requestDeleteSelection('section', { index: selectedIdx.value, fromDrawer: true })
}

const closeDrawer = () => {
  showSettings.value = false
  showStructureMenu.value = false
  showAddPalette.value = false
  selectedIdx.value = -1
  selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  selectedMemberLoginPart.value = ''
  showSectionBackgroundColorPicker.value = false
  showRowBackgroundColorPicker.value = false
  showColumnBackgroundColorPicker.value = false
  showElementBackgroundColorPicker.value = false
  closeElementSettingsMenus()
  contentSelectorMenuOpen.value = false
  contentSelectorRowTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
}

const onSectionBackgroundColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const normalized = parseBackgroundColor(raw)
  if (normalized) sectionBackgroundColorModel.value = normalized
}

const onSectionBackgroundColorTextBlur = (event) => {
  sectionBackgroundColorModel.value = normalizeBackgroundColor(String(event?.target?.value || '').trim(), '#FFFFFF')
}

const onRowBackgroundColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const normalized = parseBackgroundColor(raw)
  if (normalized) rowBackgroundColorModel.value = normalized
}

const onRowBackgroundColorTextBlur = (event) => {
  rowBackgroundColorModel.value = normalizeBackgroundColor(String(event?.target?.value || '').trim(), '#FFFFFF')
}

const onColumnBackgroundColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const normalized = parseBackgroundColor(raw)
  if (normalized) columnBackgroundColorModel.value = normalized
}

const onColumnBackgroundColorTextBlur = (event) => {
  columnBackgroundColorModel.value = normalizeBackgroundColor(String(event?.target?.value || '').trim(), 'transparent')
}

const onElementBackgroundColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const normalized = parseBackgroundColor(raw)
  if (normalized) elementBackgroundColorModel.value = normalized
}

const onElementBackgroundColorTextBlur = (event) => {
  elementBackgroundColorModel.value = normalizeBackgroundColor(String(event?.target?.value || '').trim(), 'transparent')
}

const onElementTextColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    selectedElementTextColorModel.value = withHash
  }
}

const onElementTextColorTextBlur = (event) => {
  selectedElementTextColorModel.value = String(event?.target?.value || '').trim()
}

const onButtonBackgroundColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    selectedButtonBackgroundColorModel.value = withHash
  }
}

const onButtonBackgroundColorTextBlur = (event) => {
  selectedButtonBackgroundColorModel.value = String(event?.target?.value || '').trim()
}

const onButtonTextColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    selectedButtonTextColorModel.value = withHash
  }
}

const onButtonTextColorTextBlur = (event) => {
  selectedButtonTextColorModel.value = String(event?.target?.value || '').trim()
}

const onLoginSubmitButtonColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    selectedLoginSubmitButtonColorModel.value = withHash
  }
}

const onLoginSubmitButtonColorTextBlur = (event) => {
  selectedLoginSubmitButtonColorModel.value = String(event?.target?.value || '').trim()
}

const onLoginForgotPasswordColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    selectedLoginForgotPasswordColorModel.value = withHash
  }
}

const onLoginForgotPasswordColorTextBlur = (event) => {
  selectedLoginForgotPasswordColorModel.value = String(event?.target?.value || '').trim()
}

const onLoginRememberCheckboxColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    selectedLoginRememberCheckboxColorModel.value = withHash
  }
}

const onLoginRememberCheckboxColorTextBlur = (event) => {
  selectedLoginRememberCheckboxColorModel.value = String(event?.target?.value || '').trim()
}

const buttonActionFeatureOptions = computed(() => [])

const activeSettingsBlock = computed(() => {
  const idx = Number(activeSettingsContext.value?.index)
  if (!Number.isFinite(idx) || idx < 0) return null
  const sections = Array.isArray(editorDoc.value?.sections) ? editorDoc.value.sections : []
  const section = sections[idx] || null
  if (!section) return null
  if (String(activeSettingsContext.value?.scope || '') === 'row-element') {
    if (String(section?.type || '') !== 'section') return null
    const rowIndex = Number(activeSettingsContext.value?.rowIndex)
    const elementIndex = Number(activeSettingsContext.value?.elementIndex)
    const nestedRowElementIndex = Number(activeSettingsContext.value?.nestedRowElementIndex ?? -1)
    if (!Number.isFinite(rowIndex) || rowIndex < 0) return null
    if (!Number.isFinite(elementIndex) || elementIndex < 0) return null
    const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
    const row = rows[rowIndex]
    if (!row || String(row.type || '') !== 'row') return null
    const elements = Array.isArray(row?.props?.elements) ? row.props.elements : []
    if (Number.isFinite(nestedRowElementIndex) && nestedRowElementIndex >= 0) {
      const nestedRow = elements[nestedRowElementIndex]
      if (!nestedRow || String(nestedRow?.type || '') !== 'row') return null
      const nestedElements = Array.isArray(nestedRow?.props?.elements) ? nestedRow.props.elements : []
      return nestedElements[elementIndex] || null
    }
    return elements[elementIndex] || null
  }
  return section
})

const normalizeTextAlign = (value, fallback = 'center') => {
  const v = String(value || '').trim().toLowerCase()
  if (v === 'left' || v === 'center' || v === 'right') return v
  return fallback
}
const normalizeHexColor = (value, fallback = '#18181B') => {
  const raw = String(value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toUpperCase()
  return fallback
}
function normalizeVideoType(value, fallback = 'upload') {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'custom_embed') return 'custom_embed'
  if (normalized === 'youtube') return 'youtube'
  if (normalized === 'vimeo') return 'vimeo'
  if (normalized === 'wistia') return 'wistia'
  if (normalized === 'upload') return 'upload'
  if (normalized === 'platforma_media') return 'platforma_media'
  return fallback
}
function normalizeVideoSourceUrl(value) {
  const raw = String(value || '').trim()
  if (!raw || /^undefined(?:\/|$)/i.test(raw) || /^null(?:\/|$)/i.test(raw)) return ''
  const normalizeCreatorProxyPath = (input) => String(input || '')
    .replace(/\/api\/media\/stream-hls\/playlist\//i, '/api/stream-hls/playlist/')
    .replace(/\/api\/media\/video-thumbnail\//i, '/api/video-thumbnail/')

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw)
      parsed.pathname = normalizeCreatorProxyPath(parsed.pathname)
      return parsed.toString()
    } catch (_error) {
      return normalizeCreatorProxyPath(raw)
    }
  }

  return normalizeCreatorProxyPath(raw)
}
function isLikelyHlsVideoSource(value) {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return false
  return raw.includes('/stream-hls/') || raw.includes('.m3u8')
}
function extractVideoFileIdFromSourceUrl(value) {
  const raw = normalizeVideoSourceUrl(value)
  if (!raw) return ''
  const directMatch = raw.match(/\/(?:stream-hls\/playlist|media\/stream-hls\/playlist|media\/video-thumbnail|video-thumbnail)\/([^/?#]+)/i)
  if (directMatch?.[1]) return sanitizeMediaFileId(decodeURIComponent(directMatch[1]))
  const uuidMatch = raw.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)
  return sanitizeMediaFileId(uuidMatch?.[1] || '')
}
function resolveVideoFileIdCandidate(...values) {
  for (const value of values) {
    const direct = sanitizeMediaFileId(value)
    if (direct) return direct
  }
  for (const value of values) {
    const fromUrl = extractVideoFileIdFromSourceUrl(value)
    if (fromUrl) return fromUrl
  }
  return ''
}
function buildVideoPlaybackUrlFromFileId(fileId) {
  const normalized = sanitizeMediaFileId(fileId)
  if (!normalized) return ''
  return `/api/stream-hls/playlist/${encodeURIComponent(normalized)}`
}
function buildVideoThumbnailProxyUrlFromFileId(fileId) {
  const normalized = sanitizeMediaFileId(fileId)
  if (!normalized) return ''
  if (apiProxy) return `${apiProxy}/api/video-thumbnail/${encodeURIComponent(normalized)}`
  return `/api/video-thumbnail/${encodeURIComponent(normalized)}`
}
function isVideoExternalProviderType(type) {
  const normalized = normalizeVideoType(type, '')
  return normalized === 'youtube' || normalized === 'vimeo' || normalized === 'wistia'
}
function extractYouTubeId(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const direct = raw.match(/^[a-zA-Z0-9_-]{11}$/)
  if (direct) return direct[0]
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/i,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/i,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/i,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/i,
  ]
  for (const pattern of patterns) {
    const match = raw.match(pattern)
    if (match?.[1]) return match[1]
  }
  return ''
}
function extractVimeoId(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const direct = raw.match(/^\d+$/)
  if (direct) return direct[0]
  const match = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/i)
  return match?.[1] || ''
}
function extractWistiaId(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const medias = raw.match(/wistia\.(?:com|net)\/(?:medias|embed\/medias)\/([a-zA-Z0-9]+)/i)
  if (medias?.[1]) return medias[1]
  const query = raw.match(/[?&]wvideo=([a-zA-Z0-9]+)/i)
  if (query?.[1]) return query[1]
  const direct = raw.match(/^[a-zA-Z0-9]+$/)
  return direct?.[0] || ''
}
function buildVideoProviderEmbedUrl(type, value) {
  const normalizedType = normalizeVideoType(type, '')
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (normalizedType === 'youtube') {
    const id = extractYouTubeId(raw)
    return id ? `https://www.youtube.com/embed/${id}` : ''
  }
  if (normalizedType === 'vimeo') {
    const id = extractVimeoId(raw)
    return id ? `https://player.vimeo.com/video/${id}` : ''
  }
  if (normalizedType === 'wistia') {
    const id = extractWistiaId(raw)
    return id ? `https://fast.wistia.net/embed/iframe/${id}` : ''
  }
  return ''
}
function inferVideoTypeFromLegacyProps(props = {}) {
  const explicit = normalizeVideoType(props.videoType || props.video_type || '', '')
  if (explicit) return explicit
  const embedCode = String(props.videoEmbedCode || props.video_embed_code || '').trim()
  if (embedCode) return 'custom_embed'
  const externalUrl = String(props.videoExternalUrl || props.video_external_url || '').trim()
  if (extractYouTubeId(externalUrl)) return 'youtube'
  if (extractVimeoId(externalUrl)) return 'vimeo'
  if (extractWistiaId(externalUrl)) return 'wistia'
  const fileId = String(props.videoFileId || props.video_file_id || '').trim()
  if (fileId) return 'platforma_media'
  return 'upload'
}
const parseBackgroundColor = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (raw.toLowerCase() === 'transparent') return 'transparent'
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toUpperCase()
  const rgbMatch = raw.match(/^rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)$/i)
  if (rgbMatch) {
    const r = Math.max(0, Math.min(255, Number(rgbMatch[1])))
    const g = Math.max(0, Math.min(255, Number(rgbMatch[2])))
    const b = Math.max(0, Math.min(255, Number(rgbMatch[3])))
    return `rgb(${r}, ${g}, ${b})`
  }
  const rgbaMatch = raw.match(/^rgba\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([01](?:\.\d+)?)\s*\)$/i)
  if (rgbaMatch) {
    const r = Math.max(0, Math.min(255, Number(rgbaMatch[1])))
    const g = Math.max(0, Math.min(255, Number(rgbaMatch[2])))
    const b = Math.max(0, Math.min(255, Number(rgbaMatch[3])))
    const a = Math.max(0, Math.min(1, Number(rgbaMatch[4])))
    return `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(2))})`
  }
  return ''
}
const normalizeBackgroundColor = (value, fallback = '#FFFFFF') => {
  const normalized = parseBackgroundColor(value)
  if (normalized) return normalized
  return fallback
}
const toRoundedNumber = (value, fallback) => {
  const n = Number(value)
  return Number.isFinite(n) ? Math.round(n) : fallback
}
const getLoginPartTextDefaults = (partKey) => {
  if (partKey === 'left_headline') return { fontSize: 20, fontWeight: 400, lineHeight: 1.4, textColor: '#FFFFFF', textAlign: 'center' }
  if (partKey === 'right_heading') return { fontSize: 24, fontWeight: 700, lineHeight: 1.2, textColor: '#18181B', textAlign: 'left' }
  if (partKey === 'join_prompt') return { fontSize: 14, fontWeight: 400, lineHeight: 1.5, textColor: '#6B7280', textAlign: 'left' }
  if (partKey === 'remember_forgot') return { fontSize: 14, fontWeight: 500, lineHeight: 1.5, textColor: '#4B5563', textAlign: 'left' }
  if (partKey === 'submit_button') return { fontSize: 15, fontWeight: 700, lineHeight: 1.3, textColor: '#FFFFFF', textAlign: 'center' }
  return { fontSize: 16, fontWeight: 400, lineHeight: 1.6, textColor: '#18181B', textAlign: 'left' }
}
const normalizeTextSettings = (value, fallback = {}) => {
  const base = (value && typeof value === 'object') ? value : {}
  const merged = { ...fallback, ...base }
  const linkAddress = String(
    merged.linkAddress ??
    merged.textLinkAddress ??
    merged.text_link_address ??
    fallback.linkAddress ??
    fallback.textLinkAddress ??
    ''
  ).trim()
  const openInNewTab = (merged.openInNewTab ?? merged.textOpenInNewTab ?? merged.text_open_in_new_tab ?? fallback.openInNewTab ?? true) !== false
  return {
    fontSize: Math.max(10, toRoundedNumber(merged.fontSize, 16)),
    fontWeight: Math.max(100, Math.min(900, Math.round(toRoundedNumber(merged.fontWeight, 400) / 100) * 100)),
    lineHeight: Math.max(0.8, Math.min(3, Number.isFinite(Number(merged.lineHeight)) ? Number(merged.lineHeight) : 1.6)),
    textColor: normalizeHexColor(merged.textColor, fallback.textColor || '#18181B'),
    textAlign: normalizeTextAlign(merged.textAlign, fallback.textAlign || 'left'),
    linkAddress,
    openInNewTab,
  }
}

const activeTextSettings = computed(() => {
  const ctx = activeSettingsContext.value
  const block = activeSettingsBlock.value
  if (!ctx || !block) return normalizeTextSettings({}, { fontSize: 16, fontWeight: 400, lineHeight: 1.6, textColor: '#18181B', textAlign: 'left', linkAddress: '', openInNewTab: true })
  if (ctx.scope === 'login-part') {
    const partKey = String(ctx.partKey || '').trim()
    const existing = block?.props?.loginPartSettings?.[partKey] || {}
    return normalizeTextSettings(existing, getLoginPartTextDefaults(partKey))
  }
  if (String(block.type || '') === 'header') {
    const explicitAlignRaw = String(block?.props?.textAlign || block?.props?.text_align || '').trim()
    const inheritedAlign = normalizeTextAlign(
      explicitAlignRaw || block?.props?.elementAlign,
      'left'
    )
    const textSettingsSeed = {
      ...(block?.props?.textSettings && typeof block.props.textSettings === 'object'
        ? block.props.textSettings
        : {}),
    }
    if (explicitAlignRaw) {
      textSettingsSeed.textAlign = normalizeTextAlign(explicitAlignRaw, inheritedAlign)
    }
    return normalizeTextSettings(textSettingsSeed, {
      fontSize: Math.max(10, toRoundedNumber(block?.props?.size, 32)),
      fontWeight: 700,
      lineHeight: 1.2,
      textColor: '#18181B',
      textAlign: inheritedAlign,
      linkAddress: '',
      openInNewTab: true,
    })
  }
  const inheritedAlign = normalizeTextAlign(
    String(block?.props?.textAlign || block?.props?.text_align || '').trim() || block?.props?.elementAlign,
    'left'
  )
  const explicitAlignRaw = String(block?.props?.textAlign || block?.props?.text_align || '').trim()
  const textSettingsSeed = {
    ...(block?.props?.textSettings && typeof block.props.textSettings === 'object'
      ? block.props.textSettings
      : {}),
  }
  if (explicitAlignRaw) {
    textSettingsSeed.textAlign = normalizeTextAlign(explicitAlignRaw, inheritedAlign)
  }
  return normalizeTextSettings(textSettingsSeed, {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.6,
    textColor: '#18181B',
    textAlign: inheritedAlign,
    linkAddress: '',
    openInNewTab: true,
  })
})

const activeImageSettings = computed(() => {
  const ctx = activeSettingsContext.value
  const block = activeSettingsBlock.value
  if (!ctx || !block) return { imageWidth: 80, imageAlign: 'center', imageLinkAddress: '', imageOpenInNewTab: false }
  if (ctx.scope === 'login-part') {
    const partKey = String(ctx.partKey || '').trim()
    const existing = block?.props?.loginPartSettings?.[partKey] || {}
    return {
      imageWidth: Math.min(100, Math.max(20, toRoundedNumber(existing.imageWidth, partKey === 'left_panel' ? 100 : 80))),
      imageAlign: normalizeTextAlign(existing.imageAlign, partKey === 'left_panel' ? 'center' : 'center'),
      imageLinkAddress: String(existing.imageLinkAddress || existing.image_link_address || '').trim(),
      imageOpenInNewTab: (existing.imageOpenInNewTab ?? existing.image_open_in_new_tab) === true,
    }
  }
  return {
    imageWidth: Math.min(100, Math.max(20, toRoundedNumber(block?.props?.imageWidth, 80))),
    imageAlign: normalizeTextAlign(block?.props?.imageAlign, 'center'),
    imageLinkAddress: String(block?.props?.imageLinkAddress || block?.props?.image_link_address || '').trim(),
    imageOpenInNewTab: (block?.props?.imageOpenInNewTab ?? block?.props?.image_open_in_new_tab) === true,
  }
})

const activeButtonSettings = computed(() => {
  const ctx = activeSettingsContext.value
  const block = activeSettingsBlock.value
  if (!ctx || !block) {
    return {
      buttonBackgroundColor: '#2D8CFF',
      buttonTextColor: '#FFFFFF',
      buttonTextSize: 100,
      buttonFontSize: 18,
      buttonActionType: 'go_to',
      buttonGoToType: 'custom_link',
      buttonFeatureSlug: '',
      buttonLinkAddress: '',
      buttonOpenInNewTab: true,
      buttonFileId: null,
      buttonFileUrl: '',
      buttonFileName: '',
      buttonFileMimeType: '',
      buttonFileAccessMode: 'view',
      buttonWidth: 36,
      buttonPaddingX: 24,
      buttonPaddingY: 14,
      buttonAlign: 'center',
    }
  }
  if (ctx.scope === 'login-part') {
    const partKey = String(ctx.partKey || '').trim()
    const defaults = getLoginPartTextDefaults(partKey)
    const existing = block?.props?.loginPartSettings?.[partKey] || {}
    return {
      buttonBackgroundColor: '#2D8CFF',
      buttonTextColor: normalizeHexColor(existing.textColor, defaults.textColor),
      buttonTextSize: 100,
      buttonFontSize: Math.max(10, toRoundedNumber(existing.fontSize, defaults.fontSize)),
      buttonActionType: 'go_to',
      buttonGoToType: 'custom_link',
      buttonFeatureSlug: '',
      buttonLinkAddress: '',
      buttonOpenInNewTab: true,
      buttonFileId: null,
      buttonFileUrl: '',
        buttonFileName: '',
        buttonFileMimeType: '',
        buttonFileAccessMode: 'view',
        buttonWidth: 36,
      buttonPaddingX: 24,
      buttonPaddingY: 14,
      buttonAlign: normalizeTextAlign(existing.textAlign, defaults.textAlign),
    }
  }
  return {
    buttonBackgroundColor: normalizeHexColor(block?.props?.buttonBackgroundColor || block?.props?.button_background_color, '#2D8BFF'),
    buttonTextColor: normalizeHexColor(block?.props?.buttonTextColor || block?.props?.button_text_color, '#FFFFFF'),
    buttonTextSize: Math.max(60, Math.min(160, toRoundedNumber(block?.props?.buttonTextSize ?? block?.props?.button_text_size, 100))),
    buttonFontSize: Math.max(10, Math.min(56, toRoundedNumber(block?.props?.buttonFontSize ?? block?.props?.button_font_size, 18))),
    buttonActionType: (() => {
      const token = String(block?.props?.buttonActionType || block?.props?.button_action_type || block?.props?.actionType || 'go_to').trim().toLowerCase()
      if (token === 'file_access') return 'file_access'
      if (token === 'start_quiz') return 'start_quiz'
      if (token === 'open_pay_click_pop') return 'open_pay_click_pop'
      return 'go_to'
    })(),
    buttonGoToType: String(block?.props?.buttonGoToType || block?.props?.button_go_to_type || 'custom_link').trim().toLowerCase() === 'feature_instance' ? 'feature_instance' : 'custom_link',
    buttonFeatureSlug: String(block?.props?.buttonFeatureSlug || block?.props?.button_feature_slug || '').trim(),
    buttonLinkAddress: String(block?.props?.buttonLinkAddress || block?.props?.button_link_address || '').trim(),
    buttonOpenInNewTab: (block?.props?.buttonOpenInNewTab ?? block?.props?.button_open_in_new_tab) !== false,
    buttonFileId: block?.props?.buttonFileId || block?.props?.button_file_id || null,
    buttonFileUrl: String(block?.props?.buttonFileUrl || block?.props?.button_file_url || '').trim(),
    buttonFileName: String(block?.props?.buttonFileName || block?.props?.button_file_name || '').trim(),
    buttonFileMimeType: String(block?.props?.buttonFileMimeType || block?.props?.button_file_mime_type || '').trim(),
    buttonFileAccessMode: String(block?.props?.buttonFileAccessMode || block?.props?.button_file_access_mode || '').trim().toLowerCase() === 'download' ? 'download' : 'view',
    buttonWidth: Math.max(20, Math.min(100, toRoundedNumber(block?.props?.buttonWidth ?? block?.props?.button_width, 36))),
    buttonPaddingX: Math.max(4, Math.min(80, toRoundedNumber(block?.props?.buttonPaddingX ?? block?.props?.button_padding_x, 24))),
    buttonPaddingY: Math.max(4, Math.min(56, toRoundedNumber(block?.props?.buttonPaddingY ?? block?.props?.button_padding_y, 14))),
    buttonAlign: normalizeTextAlign(block?.props?.buttonAlign || block?.props?.button_align, 'center'),
  }
})

const activeButtonFilePreview = computed(() => {
  const ctx = activeSettingsContext.value
  const block = activeSettingsBlock.value
  if (!ctx || !block || ctx.scope === 'login-part') {
    return { fileId: null, fileName: '', fileMimeType: '', fileUrl: '' }
  }
  return {
    fileId: block?.props?.buttonFileId || block?.props?.button_file_id || null,
    fileName: String(block?.props?.buttonFileName || block?.props?.button_file_name || '').trim(),
    fileMimeType: String(block?.props?.buttonFileMimeType || block?.props?.button_file_mime_type || '').trim(),
    fileUrl: String(block?.props?.buttonFileUrl || block?.props?.button_file_url || '').trim(),
  }
})

const activeVideoSettings = computed(() => {
  const block = activeSettingsBlock.value
  if (!block || String(block.type || '') !== 'video') {
    return {
      videoType: 'upload',
      videoFileId: null,
      videoUrl: '',
      videoPlaybackUrl: '',
      videoExternalUrl: '',
      videoEmbedCode: '',
      videoFileName: '',
      videoFileMimeType: '',
      videoThumbnailFileId: null,
      videoThumbnailUrl: '',
      videoAutoPreviewUrl: '',
      videoPosterUrl: '',
      videoBorderRadius: 16,
      videoControlColor: '#2D8CFF',
    }
  }
  const videoType = inferVideoTypeFromLegacyProps(block?.props || {})
  const videoFileId = resolveVideoFileIdCandidate(
    block?.props?.videoFileId,
    block?.props?.video_file_id,
    block?.props?.videoPlaybackUrl,
    block?.props?.video_playback_url,
    block?.props?.videoUrl,
    block?.props?.video_url,
    block?.props?.videoPosterUrl,
    block?.props?.video_poster_url
  ) || null
  const videoThumbnailFileIdRaw = sanitizeMediaFileId(
    block?.props?.videoThumbnailFileId ||
    block?.props?.video_thumbnail_file_id ||
    block?.props?.thumbnailFileId ||
    block?.props?.thumbnail_file_id
  ) || null
  const videoThumbnailFileId = videoThumbnailFileIdRaw && videoThumbnailFileIdRaw !== videoFileId
    ? videoThumbnailFileIdRaw
    : null
  return {
    videoType,
    videoFileId,
    videoUrl: String(block?.props?.videoUrl || block?.props?.video_url || '').trim(),
    videoPlaybackUrl: String(block?.props?.videoPlaybackUrl || block?.props?.video_playback_url || '').trim(),
    videoExternalUrl: String(block?.props?.videoExternalUrl || block?.props?.video_external_url || '').trim(),
    videoEmbedCode: String(block?.props?.videoEmbedCode || block?.props?.video_embed_code || '').trim(),
    videoFileName: String(block?.props?.videoFileName || block?.props?.video_file_name || '').trim(),
    videoFileMimeType: String(block?.props?.videoFileMimeType || block?.props?.video_file_mime_type || '').trim(),
    videoThumbnailFileId,
    videoThumbnailUrl: String(block?.props?.videoThumbnailUrl || block?.props?.video_thumbnail_url || '').trim(),
    videoAutoPreviewUrl: String(block?.props?.videoAutoPreviewUrl || block?.props?.video_auto_preview_url || '').trim(),
    videoPosterUrl: String(block?.props?.videoPosterUrl || block?.props?.video_poster_url || '').trim(),
    videoBorderRadius: Math.max(0, Math.min(120, toRoundedNumber(block?.props?.videoBorderRadius ?? block?.props?.video_border_radius, 16))),
    videoControlColor: normalizeHexColor(block?.props?.videoControlColor || block?.props?.video_control_color, '#2D8CFF'),
  }
})

const sanitizeMediaFileId = (value) => {
  const normalized = String(value ?? '').trim()
  if (!normalized || normalized === 'null' || normalized === 'undefined') return ''
  return normalized
}

const resolveStoredMediaUrl = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized || normalized === 'null' || normalized === 'undefined') return ''
  return normalized
}

const resolveImageUrl = (fileId, url, fallback = '') => {
  const fid = sanitizeMediaFileId(fileId)
  if (fid) return `${apiProxy}/api/image/${encodeURIComponent(fid)}`
  const u = resolveStoredMediaUrl(url)
  if (!u) return fallback
  if (u.startsWith('@/assets/media/images/platforma-logo-white.svg')) return loginLeftLogoDefault
  if (u.startsWith('@/assets/media/images/platforma-logo-in-color.svg')) return loginRightLogoDefault
  if (u.startsWith('@/assets/media/images/bg_hold_reg.gif')) return loginBgDefault
  if (u.startsWith('@/assets/media/images/platforma-creator-icons-onboarding-page.png')) return onboardingLogoDefault
  if (u.startsWith('@/assets/media/images/business-group.jpg')) return homeBusinessGroupDefault
  if (u.startsWith('@/assets/media/images/platforma-businessman-rv.jpeg')) return homeBusinessmanDefault
  if (u.startsWith('@/assets/media/images/online-learning-courses-video-thumbnail.jpg')) return pageTemplateCourseDefault
  if (u.startsWith('@/assets/media/images/webinar.png')) return pageTemplateWebinarDefault
  if (u.startsWith('@/assets/media/images/skin-care-2-default.png')) return pageTemplateSkincareDefault
  if (u.startsWith('@/assets/media/images/business-group-hero.jpeg')) return pageTemplateBusinessHeroDefault
  if (u.startsWith('@/assets/media/images/business-group-hero2.png')) return pageTemplateBusinessHeroAltDefault
  if (u.startsWith('@/assets/media/images/businessman2.jpg')) return pageTemplateBusinessPortraitDefault
  if (u.startsWith('@/assets/media/images/online-course.png')) return pageTemplateCourseDashboardDefault
  if (u.startsWith('@/assets/media/images/skin-care-1-default.png')) return pageTemplateSkincareAltDefault
  if (u.startsWith('@/assets/media/images/cart-templates/founder-studio.png')) return cartTemplateFounderStudio
  if (u.startsWith('@/assets/media/images/cart-templates/fitness-focus.png')) return cartTemplateFitnessFocus
  if (u.startsWith('@/assets/media/images/cart-templates/botanical-luxe.png')) return cartTemplateBotanicalLuxe
  return u || fallback
}

const activeImagePreviewUrl = computed(() => {
  const ctx = activeSettingsContext.value
  const block = activeSettingsBlock.value
  if (!ctx || !block) return ''
  if (ctx.scope === 'login-part') {
    const partKey = String(ctx.partKey || '').trim()
    if (partKey === 'left_panel') return resolveImageUrl('', block?.props?.backgroundImageUrl, '')
    if (partKey === 'left_logo') return resolveImageUrl('', block?.props?.leftLogoUrl, '')
    if (partKey === 'right_logo') return resolveImageUrl('', block?.props?.rightLogoUrl, '')
    return ''
  }
  return resolveImageUrl(
    block?.props?.imageFileId || block?.props?.image_file_id || block?.props?.fileId || block?.props?.file_id,
    block?.props?.imageUrl || block?.props?.image_url,
    ''
  )
})

const activeVideoPreviewUrl = computed(() => {
  const settings = activeVideoSettings.value
  const type = normalizeVideoType(settings.videoType, 'upload')
  if (type === 'custom_embed' || isVideoExternalProviderType(type)) return ''
  const preview = resolveStoredMediaUrl(settings.videoAutoPreviewUrl)
  if (preview) return preview
  const poster = resolveStoredMediaUrl(settings.videoPosterUrl)
  if (poster) return poster
  const customThumb = resolveStoredMediaUrl(settings.videoThumbnailUrl)
  if (customThumb) return customThumb
  const fileId = sanitizeMediaFileId(settings.videoFileId)
  if (fileId) return buildVideoThumbnailProxyUrlFromFileId(fileId)
  return ''
})

const activeVideoThumbnailPreviewUrl = computed(() => {
  const block = activeSettingsBlock.value
  const settings = activeVideoSettings.value
  if (!block || String(block.type || '') !== 'video') return ''
  const videoFileId = sanitizeMediaFileId(settings.videoFileId)
  const thumbnailFileId = sanitizeMediaFileId(
    block?.props?.videoThumbnailFileId ||
    block?.props?.video_thumbnail_file_id ||
    block?.props?.thumbnailFileId ||
    block?.props?.thumbnail_file_id
  )
  const safeThumbnailFileId = thumbnailFileId && thumbnailFileId !== videoFileId ? thumbnailFileId : ''
  return resolveImageUrl(
    safeThumbnailFileId,
    block?.props?.videoThumbnailUrl || block?.props?.video_thumbnail_url || block?.props?.thumbnailUrl || block?.props?.thumbnail_url,
    ''
  )
})

const activeVideoUploadItem = computed(() => {
  const uploads = Array.isArray(mediaUploadStore.uploads) ? mediaUploadStore.uploads : []
  const candidate = uploads
    .filter((upload) => String(upload?.origin || '').trim().toLowerCase() === 'creator-page-editor-video')
    .sort((a, b) => Number(b?.startTime || 0) - Number(a?.startTime || 0))[0]
  return candidate || null
})

const activeVideoElementUploadProgress = computed(() => {
  const value = Number(activeVideoUploadItem.value?.progress || 0)
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(100, Math.round(value)))
})

const activeVideoElementUploading = computed(() =>
  String(activeVideoUploadItem.value?.status || '').trim().toLowerCase() === 'uploading'
)

const activeVideoElementProcessing = computed(() =>
  String(activeVideoUploadItem.value?.status || '').trim().toLowerCase() === 'processing'
)

const activeVideoElementHasSource = computed(() =>
  {
    const type = normalizeVideoType(activeVideoSettings.value.videoType, 'upload')
    if (type === 'custom_embed') {
      return Boolean(sanitizeVideoEmbedHtml(activeVideoSettings.value.videoEmbedCode))
    }
    if (isVideoExternalProviderType(type)) {
      return Boolean(buildVideoProviderEmbedUrl(type, activeVideoSettings.value.videoExternalUrl))
    }
    return Boolean(
      sanitizeMediaFileId(activeVideoSettings.value.videoFileId) ||
      resolveStoredMediaUrl(activeVideoSettings.value.videoUrl)
    )
  }
)

const activeVideoElementReady = computed(() =>
  activeVideoElementHasSource.value &&
  !activeVideoElementUploading.value &&
  !activeVideoElementProcessing.value
)

const activeVideoUploadActionsDisabled = computed(() =>
  activeVideoElementUploading.value || activeVideoElementProcessing.value
)

const activeVideoHasCustomThumbnail = computed(() =>
  Boolean(
    sanitizeMediaFileId(activeVideoSettings.value.videoThumbnailFileId) ||
    resolveStoredMediaUrl(activeVideoSettings.value.videoThumbnailUrl)
  )
)

const selectedVideoMediaButtonLabel = computed(() => {
  return activeVideoElementHasSource.value ? 'Replace From Media Content' : 'Select From Media Content'
})

const selectedVideoThumbnailUploadButtonLabel = computed(() =>
  activeVideoHasCustomThumbnail.value ? 'Change Custom Thumbnail' : 'Upload Custom Thumbnail'
)

const selectedVideoFileName = computed(() =>
  String(activeVideoSettings.value.videoFileName || '').trim()
)

const selectedVideoFileMimeType = computed(() =>
  String(activeVideoSettings.value.videoFileMimeType || '').trim().toLowerCase()
)
const selectedVideoType = computed(() =>
  normalizeVideoType(activeVideoSettings.value.videoType, 'upload')
)
const selectedVideoTypeModel = computed({
  get: () => (selectedVideoType.value === 'upload' || selectedVideoType.value === 'platforma_media')
    ? 'media_content'
    : selectedVideoType.value,
  set: (value) => {
    void onVideoTypeSelection(value === 'media_content' ? 'platforma_media' : value)
  },
})
const selectedVideoEmbedCodeModel = computed(() =>
  String(activeVideoSettings.value.videoEmbedCode || '').trim()
)
const selectedVideoExternalUrlModel = computed(() =>
  String(activeVideoSettings.value.videoExternalUrl || '').trim()
)
const selectedVideoExternalUrlLabel = computed(() => {
  const type = selectedVideoType.value
  if (type === 'youtube') return 'YouTube URL'
  if (type === 'vimeo') return 'Vimeo URL'
  if (type === 'wistia') return 'Wistia URL'
  return 'Video URL'
})
const selectedVideoExternalUrlPlaceholder = computed(() => {
  const type = selectedVideoType.value
  if (type === 'youtube') return 'https://www.youtube.com/watch?v=...'
  if (type === 'vimeo') return 'https://vimeo.com/...'
  if (type === 'wistia') return 'https://fast.wistia.net/embed/medias/...'
  return 'https://example.com/video'
})
const selectedVideoPreviewEmbedHtml = computed(() => {
  if (selectedVideoType.value !== 'custom_embed') return ''
  return sanitizeVideoEmbedHtml(activeVideoSettings.value.videoEmbedCode)
})
const selectedVideoPreviewIframeUrl = computed(() => {
  if (!isVideoExternalProviderType(selectedVideoType.value)) return ''
  return buildVideoProviderEmbedUrl(selectedVideoType.value, activeVideoSettings.value.videoExternalUrl)
})

const selectedVideoShowReadySpinner = computed(() => {
  if (selectedVideoPreviewEmbedHtml.value || selectedVideoPreviewIframeUrl.value) return false
  if (activeVideoElementReady.value) return false
  if (activeVideoElementUploading.value || activeVideoElementProcessing.value) return true
  return activeVideoElementHasSource.value
})

const selectedVideoShowNavigateAwayMessage = computed(() => {
  if (selectedVideoPreviewEmbedHtml.value || selectedVideoPreviewIframeUrl.value) return false
  if (activeVideoElementReady.value) return false
  return activeVideoElementHasSource.value
})

const selectedVideoSpinnerMessage = computed(() => {
  if (selectedVideoPreviewEmbedHtml.value || selectedVideoPreviewIframeUrl.value) return ''
  if (activeVideoElementUploading.value) return `Uploading video... ${activeVideoElementUploadProgress.value}%`
  if (activeVideoElementProcessing.value) return 'Video uploaded. Processing HLS stream...'
  if (activeVideoElementHasSource.value) return 'Preparing video playback...'
  return ''
})

const videoPreviewImageErrored = ref(false)
const thumbnailPreviewImageErrored = ref(false)
let videoPreviewRetryTimer = null
let thumbnailPreviewRetryTimer = null

watch(
  [() => activeVideoPreviewUrl.value, () => activeVideoThumbnailPreviewUrl.value],
  () => {
    videoPreviewImageErrored.value = false
    thumbnailPreviewImageErrored.value = false
  }
)

const selectedVideoResolvedPreviewUrl = computed(() => {
  if (activeVideoElementUploading.value || activeVideoElementProcessing.value) return chatVideoProcessingPoster
  const preview = String(activeVideoPreviewUrl.value || '').trim()
  if (preview && !videoPreviewImageErrored.value) return preview
  return ''
})

const selectedVideoResolvedThumbnailPreviewUrl = computed(() => {
  const preview = String(activeVideoThumbnailPreviewUrl.value || '').trim()
  if (preview && !thumbnailPreviewImageErrored.value) return preview
  return ''
})

const selectedVideoControlColorModel = computed({
  get: () => normalizeHexColor(activeVideoSettings.value.videoControlColor, '#2D8CFF'),
  set: (value) => {
    onUpdateVideoSettings({ videoControlColor: normalizeHexColor(value, '#2D8CFF') })
  },
})

const selectedVideoBorderRadiusModel = computed({
  get: () => Math.max(0, Math.min(120, toRoundedNumber(activeVideoSettings.value.videoBorderRadius, 16))),
  set: (value) => {
    const nextValue = Math.max(0, Math.min(120, toRoundedNumber(value, 16)))
    onUpdateVideoSettings({ videoBorderRadius: nextValue })
  },
})

const onVideoPreviewImageError = () => {
  if (activeVideoElementUploading.value || activeVideoElementProcessing.value) return
  videoPreviewImageErrored.value = true
  if (videoPreviewRetryTimer) clearTimeout(videoPreviewRetryTimer)
  videoPreviewRetryTimer = setTimeout(() => {
    videoPreviewImageErrored.value = false
    videoPreviewRetryTimer = null
  }, 3000)
}

const onVideoThumbnailPreviewImageError = () => {
  thumbnailPreviewImageErrored.value = true
  if (thumbnailPreviewRetryTimer) clearTimeout(thumbnailPreviewRetryTimer)
  thumbnailPreviewRetryTimer = setTimeout(() => {
    thumbnailPreviewImageErrored.value = false
    thumbnailPreviewRetryTimer = null
  }, 3000)
}

const onVideoControlColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    onUpdateVideoSettings({ videoControlColor: withHash })
  }
}

const onVideoControlColorTextBlur = (event) => {
  onUpdateVideoSettings({ videoControlColor: event?.target?.value })
}

const onVideoMediaFolderChanged = (folderId) => {
  videoMediaCurrentFolderId.value = String(folderId || '').trim()
}
const openVideoMediaPicker = (requestedType = '') => {
  videoMediaRequestedType.value = normalizeVideoType(requestedType || selectedVideoType.value, 'platforma_media')
  videoMediaPickerOpen.value = true
}
const closeVideoMediaPicker = () => {
  videoMediaPickerOpen.value = false
}
const onImageMediaFolderChanged = (folderId) => {
  imageMediaCurrentFolderId.value = String(folderId || '').trim()
}
const captureImageSourceTarget = () => {
  const context = { ...(activeSettingsContext.value || {}) }
  const activeBlock = activeSettingsBlock.value
  if (activeBlock && (context.scope === 'login-part' || String(activeBlock?.type || '').trim().toLowerCase() === 'image')) {
    return { block: activeBlock, context }
  }

  const selected = selectedElement.value
  if (selected && String(selected?.type || '').trim().toLowerCase() === 'image') {
    return {
      block: selected,
      context: {
        scope: 'row-element',
        index: Number(selectedElementContext.value?.sectionIndex),
        rowIndex: Number(selectedElementContext.value?.rowIndex),
        elementIndex: Number(selectedElementContext.value?.elementIndex),
        nestedRowElementIndex: Number(selectedElementContext.value?.nestedRowElementIndex ?? -1),
        partKey: '',
        settingsType: 'image',
      },
    }
  }

  const selectedSection = selectedBlock.value
  if (selectedSection && String(selectedSection?.type || '').trim().toLowerCase() === 'image') {
    return {
      block: selectedSection,
      context: { scope: 'block', index: Number(selectedIdx.value), partKey: '', settingsType: 'image' },
    }
  }
  return null
}
const openImageMediaPicker = () => {
  pendingImageSourceTarget.value = captureImageSourceTarget()
  imageMediaPickerOpen.value = true
}
const closeImageMediaPicker = () => {
  imageMediaPickerOpen.value = false
  pendingImageSourceTarget.value = null
}
const applyImageSourceToActiveSettings = ({ fileId = '', url = '', target = null } = {}) => {
  const capturedTarget = target || pendingImageSourceTarget.value || captureImageSourceTarget()
  const block = capturedTarget?.block || null
  const ctx = capturedTarget?.context || null
  const normalizedFileId = sanitizeMediaFileId(fileId)
  const normalizedUrl = resolveStoredMediaUrl(url)
  if (!block || !ctx || (!normalizedFileId && !normalizedUrl)) return false
  if (ctx.scope === 'login-part') {
    const partKey = String(ctx.partKey || '').trim()
    if (partKey === 'left_panel') block.props.backgroundImageUrl = normalizedUrl
    if (partKey === 'left_logo') block.props.leftLogoUrl = normalizedUrl
    if (partKey === 'right_logo') block.props.rightLogoUrl = normalizedUrl
    const settings = ensureLoginPartSettings(block, partKey)
    settings.imageFileId = normalizedFileId || null
    settings.image_file_id = normalizedFileId || null
    return true
  }
  if (!block.props) block.props = {}
  block.props.imageFileId = normalizedFileId || null
  block.props.image_file_id = normalizedFileId || null
  block.props.imageUrl = normalizedUrl
  block.props.image_url = normalizedUrl
  return true
}
const onImageMediaSelected = (payload) => {
  const file = payload?.file || {}
  const fileId = sanitizeMediaFileId(file?.file_id || file?.fileId || file?.media_file_id || file?.mediaFileId)
  if (!fileId) {
    closeImageMediaPicker()
    return
  }
  const target = pendingImageSourceTarget.value
  applyImageSourceToActiveSettings({
    fileId,
    url: `${apiProxy}/api/image/${encodeURIComponent(fileId)}`,
    target,
  })
  closeImageMediaPicker()
}
const onVideoMediaSelected = (payload) => {
  const file = payload?.file || {}
  const fileId = sanitizeMediaFileId(file?.file_id || file?.fileId || file?.media_file_id || file?.mediaFileId)
  if (!fileId) {
    closeVideoMediaPicker()
    return
  }
  const uploadedType = normalizeVideoType(payload?.requestedType || videoMediaRequestedType.value || selectedVideoType.value, 'platforma_media')
  const fallbackPlaylistUrl = `${apiProxy}/api/stream-hls/playlist/${encodeURIComponent(fileId)}`
  const selectedVideoSource = String(
    file?.hls_playlist_url ||
    file?.hlsPlaylistUrl ||
    file?.hls_url ||
    file?.hlsUrl ||
    file?.secure_file_url ||
    file?.secureFileUrl ||
    file?.file_url ||
    file?.fileUrl ||
    fallbackPlaylistUrl
  ).trim()
  const nextFileName = String(file?.file_name || file?.fileName || '').trim()
  const nextFileMimeType = String(file?.mime_type || file?.mimeType || '').trim()
  const nextPreviewUrl = String(
    file?.secure_thumbnail_url ||
    file?.secureThumbnailUrl ||
    file?.thumbnail_url ||
    file?.thumbnailUrl ||
    ''
  ).trim()
  const nextPlaybackUrl = buildVideoPlaybackUrlFromFileId(fileId)
  const nextVideoUrl = nextPlaybackUrl || normalizeVideoSourceUrl(selectedVideoSource)
  const nextPosterUrl = nextPreviewUrl || buildVideoThumbnailProxyUrlFromFileId(fileId)
  onUpdateVideoSettings({
    videoType: uploadedType,
    videoFileId: fileId,
    videoUrl: nextVideoUrl,
    videoPlaybackUrl: nextPlaybackUrl,
    videoExternalUrl: '',
    videoEmbedCode: '',
    videoFileName: nextFileName,
    videoFileMimeType: nextFileMimeType,
    videoAutoPreviewUrl: nextPreviewUrl,
    videoPosterUrl: nextPosterUrl,
    videoThumbnailFileId: null,
    videoThumbnailUrl: '',
    thumbnailFileId: null,
    thumbnailUrl: '',
  })
  closeVideoMediaPicker()
}
const onVideoTypeSelection = (value) => {
  const normalized = normalizeVideoType(value, 'upload')
  onUpdateVideoSettings({ videoType: normalized })
  if (normalized === 'upload' || normalized === 'platforma_media') {
    openVideoMediaPicker(normalized)
  }
}

const closeElementSettingsMenus = () => {
  textSettingsMenuOpen.value = false
  imageSettingsMenuOpen.value = false
  buttonSettingsMenuOpen.value = false
  videoSettingsMenuOpen.value = false
  closeVideoMediaPicker()
  closeImageMediaPicker()
  showVideoControlColorPicker.value = false
  showButtonBackgroundColorPicker.value = false
  showButtonTextColorPicker.value = false
  showLoginSubmitButtonColorPicker.value = false
  showLoginForgotPasswordColorPicker.value = false
  showLoginRememberCheckboxColorPicker.value = false
}

const canDragContentElement = (key) => isRowElementType(key)

const onContentElementDragStart = (key, event) => {
  const normalized = String(key || '').trim()
  if (!canDragContentElement(normalized)) return
  const dt = event?.dataTransfer
  if (!dt) return
  const payload = JSON.stringify({ type: normalized })
  dt.effectAllowed = 'copy'
  dt.setData(PAGE_ROW_ELEMENT_INSERT_MIME, payload)
  dt.setData('text/plain', `${PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX}${payload}`)
}

const onContentElementDragEnd = () => {}

const openContentSelectorMenu = (anchorRect, insertIndex = null, rowTarget = null) => {
  pendingInsertIndex.value = Number.isFinite(Number(insertIndex)) ? Math.max(0, Number(insertIndex) - 1) : null
  showSettings.value = false
  showAddPalette.value = true
  addPaletteCategory.value = 'elements'
  selectedMemberLoginPart.value = ''
  contentSelectorAnchorRect.value = anchorRect || null
  contentSelectorMenuTargetIndex.value = null
  contentSelectorRowTarget.value = rowTarget && Number.isFinite(Number(rowTarget.sectionIndex)) && Number.isFinite(Number(rowTarget.rowIndex))
    ? {
      sectionIndex: Number(rowTarget.sectionIndex),
      rowIndex: Number(rowTarget.rowIndex),
      elementIndex: Number.isFinite(Number(rowTarget.elementIndex)) ? Number(rowTarget.elementIndex) : -1,
    }
    : { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
  contentSelectorMenuOpen.value = false
  closeElementSettingsMenus()
}

const closeContentSelectorMenu = () => {
  contentSelectorMenuOpen.value = false
  contentSelectorMenuTargetIndex.value = null
  contentSelectorRowTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
}

const onSelectContentElement = (key) => {
  const elementType = String(key || '').trim().toLowerCase()
  if (!isRowElementType(elementType)) return
  const rowTarget = getActiveRowContextForElements()
  const sIdx = Number(rowTarget.sectionIndex)
  const rIdx = Number(rowTarget.rowIndex)
  if (!Number.isFinite(sIdx) || sIdx < 0 || !Number.isFinite(rIdx) || rIdx < 0) return
  const explicitElementIndex = Number.isFinite(Number(rowTarget.elementIndex)) && Number(rowTarget.elementIndex) >= 0
    ? Number(rowTarget.elementIndex) + 1
    : null
  insertElementIntoRow(sIdx, rIdx, elementType, explicitElementIndex)
  closeContentSelectorMenu()
  showAddPalette.value = false
}

const onInsertFromDrop = ({ type }) => {
  const normalized = String(type || '').trim()
  if (!canDragContentElement(normalized)) return
  const rowTarget = getActiveRowContextForElements()
  if (!Number.isFinite(Number(rowTarget.sectionIndex)) || !Number.isFinite(Number(rowTarget.rowIndex))) return
  insertElementIntoRow(rowTarget.sectionIndex, rowTarget.rowIndex, normalized)
}

const duplicateBlock = (index) => {
  const idx = Number(index)
  if (!Number.isFinite(idx) || idx < 0 || !Array.isArray(editorDoc.value?.sections)) return
  const original = editorDoc.value.sections[idx]
  if (!original) return
  const cloned = scopeNodeTreeToCurrentViewport(JSON.parse(JSON.stringify(original)))
  if (nodeContainsRequiredSystemElement(cloned)) {
    alert('Move the required system element to another section before duplicating this section.')
    return
  }
  const insertAt = idx + 1
  editorDoc.value.sections.splice(insertAt, 0, cloned)
  selectedIdx.value = insertAt
  selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
}

const onReorderBlocks = ({ from, to }) => {
  const fromIndex = Number(from)
  const toIndexRaw = Number(to)
  const list = Array.isArray(editorDoc.value?.sections) ? editorDoc.value.sections : null
  if (!list) return
  if (!Number.isFinite(fromIndex) || fromIndex < 0 || fromIndex >= list.length) return
  if (!Number.isFinite(toIndexRaw)) return
  const toIndex = Math.max(0, Math.min(list.length, toIndexRaw))
  const [moved] = list.splice(fromIndex, 1)
  if (!moved) return
  const normalizedTo = toIndex > fromIndex ? toIndex - 1 : toIndex
  list.splice(normalizedTo, 0, moved)
  selectedIdx.value = normalizedTo
  selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
  selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
}

const ensureLoginPartSettings = (block, partKey) => {
  if (!block?.props) block.props = {}
  if (!block.props.loginPartSettings || typeof block.props.loginPartSettings !== 'object') {
    block.props.loginPartSettings = {}
  }
  if (!block.props.loginPartSettings[partKey] || typeof block.props.loginPartSettings[partKey] !== 'object') {
    block.props.loginPartSettings[partKey] = {}
  }
  return block.props.loginPartSettings[partKey]
}

const onOpenSettingsMenu = (payload = {}) => {
  const index = Number(payload?.index)
  if (!Number.isFinite(index) || index < 0) return
  const partKey = String(payload?.partKey || '').trim()
  const scope = String(payload?.scope || 'block').trim()
  const rowIndex = Number(payload?.rowIndex)
  const columnIndexRaw = Number(payload?.columnIndex)
  const columnIndex = Number.isFinite(columnIndexRaw) ? columnIndexRaw : -1
  const nestedRowElementIndexRaw = Number(payload?.nestedRowElementIndex)
  const nestedRowElementIndex = Number.isFinite(nestedRowElementIndexRaw) ? nestedRowElementIndexRaw : -1
  const elementIndex = Number(payload?.elementIndex)
  const settingsType = String(payload?.settingsType || 'text').trim().toLowerCase()
  onSelect(index)
  if (scope === 'section-row' && Number.isFinite(rowIndex) && rowIndex >= 0) {
    selectedRowContext.value = { sectionIndex: index, rowIndex }
    selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
    selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  }
  if (scope === 'row-column' && Number.isFinite(rowIndex) && rowIndex >= 0 && Number.isFinite(columnIndex) && columnIndex >= 0) {
    selectedRowContext.value = { sectionIndex: index, rowIndex }
    selectedColumnContext.value = { sectionIndex: index, rowIndex, columnIndex, nestedRowElementIndex }
    selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  }
  if (scope === 'row-element' && Number.isFinite(rowIndex) && rowIndex >= 0 && Number.isFinite(elementIndex) && elementIndex >= 0) {
    selectedRowContext.value = { sectionIndex: index, rowIndex }
    selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
    selectedElementContext.value = { sectionIndex: index, rowIndex, elementIndex, nestedRowElementIndex }
  }
  if (scope === 'login-part' && partKey) {
    selectedMemberLoginPart.value = partKey
  }
  activeSettingsContext.value = { scope, index, rowIndex, columnIndex, elementIndex, nestedRowElementIndex, partKey, settingsType }
  closeContentSelectorMenu()
  closeElementSettingsMenus()
  showSettings.value = false
  showStructureMenu.value = false
  showAddPalette.value = false
}

const onUpdateTextSettings = (next = {}) => {
  const ctx = activeSettingsContext.value
  const block = activeSettingsBlock.value
  if (!ctx || !block) return
  const normalized = normalizeTextSettings(next, activeTextSettings.value)
  if (ctx.scope === 'login-part') {
    const partKey = String(ctx.partKey || '').trim()
    if (!partKey) return
    const target = ensureLoginPartSettings(block, partKey)
    Object.assign(target, normalized)
    return
  }
  if (!block.props) block.props = {}
  block.props.textSettings = { ...normalized }
  if (String(block.type || '') === 'header') {
    block.props.size = normalized.fontSize
  }
}

const onUpdateImageSettings = (next = {}) => {
  const ctx = activeSettingsContext.value
  const block = activeSettingsBlock.value
  if (!ctx || !block) return
  const imageWidth = Math.max(20, Math.min(100, toRoundedNumber(next.imageWidth, activeImageSettings.value.imageWidth)))
  const imageAlign = normalizeTextAlign(next.imageAlign, activeImageSettings.value.imageAlign)
  const imageLinkAddress = String(
    next.imageLinkAddress ??
    next.image_link_address ??
    activeImageSettings.value.imageLinkAddress ??
    ''
  ).trim()
  const imageOpenInNewTab = (next.imageOpenInNewTab ?? next.image_open_in_new_tab ?? activeImageSettings.value.imageOpenInNewTab) === true
  if (ctx.scope === 'login-part') {
    const partKey = String(ctx.partKey || '').trim()
    if (!partKey) return
    const target = ensureLoginPartSettings(block, partKey)
    target.imageWidth = imageWidth
    target.imageAlign = imageAlign
    target.imageLinkAddress = imageLinkAddress
    target.imageOpenInNewTab = imageOpenInNewTab
    return
  }
  if (!block.props) block.props = {}
  block.props.imageWidth = imageWidth
  block.props.imageAlign = imageAlign
  block.props.imageLinkAddress = imageLinkAddress
  block.props.image_link_address = imageLinkAddress
  block.props.imageOpenInNewTab = imageOpenInNewTab
  block.props.image_open_in_new_tab = imageOpenInNewTab
}

const onUpdateButtonSettings = (next = {}) => {
  const ctx = activeSettingsContext.value
  const block = activeSettingsBlock.value
  if (!ctx || !block) return
  if (ctx.scope === 'login-part') {
    const partKey = String(ctx.partKey || '').trim()
    const target = ensureLoginPartSettings(block, partKey)
    const defaults = getLoginPartTextDefaults(partKey)
    target.fontSize = Math.max(10, toRoundedNumber(next.buttonFontSize, activeButtonSettings.value.buttonFontSize))
    target.fontWeight = Math.max(100, Math.min(900, Math.round(toRoundedNumber(target.fontWeight, defaults.fontWeight) / 100) * 100))
    target.textColor = normalizeHexColor(next.buttonTextColor, activeButtonSettings.value.buttonTextColor || '#FFFFFF')
    target.textAlign = normalizeTextAlign(next.buttonAlign, activeButtonSettings.value.buttonAlign || 'center')
    if (partKey === 'submit_button' && typeof next.buttonText === 'string' && next.buttonText.trim()) {
      block.props.submitButtonText = next.buttonText.trim()
    }
    return
  }
  if (!block.props) block.props = {}
  const current = block.props
  const nextBackgroundColor = normalizeHexColor(
    next.buttonBackgroundColor ?? next.backgroundColor ?? current.buttonBackgroundColor ?? current.button_background_color,
    '#2D8BFF'
  )
  const nextTextColor = normalizeHexColor(
    next.buttonTextColor ?? next.textColor ?? current.buttonTextColor ?? current.button_text_color,
    '#FFFFFF'
  )
  const nextTextSize = Math.max(60, Math.min(160, toRoundedNumber(
    next.buttonTextSize ?? next.textSize ?? current.buttonTextSize ?? current.button_text_size,
    100
  )))
  const nextFontSize = Math.max(10, Math.min(56, toRoundedNumber(
    next.buttonFontSize ?? next.fontSize ?? current.buttonFontSize ?? current.button_font_size,
    18
  )))
  const nextLinkAddress = String(
    next.buttonLinkAddress ?? next.linkAddress ?? current.buttonLinkAddress ?? current.button_link_address ?? ''
  ).trim()
  const nextActionType = (() => {
    const token = String(
    next.buttonActionType ?? next.actionType ?? current.buttonActionType ?? current.button_action_type ?? current.actionType ?? 'go_to'
    ).trim().toLowerCase()
    if (token === 'file_access') return 'file_access'
    if (token === 'start_quiz') return 'start_quiz'
    if (token === 'open_pay_click_pop') return 'open_pay_click_pop'
    return 'go_to'
  })()
  const nextGoToType = String(
    next.buttonGoToType ?? next.goToType ?? current.buttonGoToType ?? current.button_go_to_type ?? 'custom_link'
  ).trim().toLowerCase() === 'feature_instance' ? 'feature_instance' : 'custom_link'
  const nextFeatureSlug = String(
    next.buttonFeatureSlug ?? next.featureSlug ?? current.buttonFeatureSlug ?? current.button_feature_slug ?? ''
  ).trim()
  const nextOpenInNewTab = (next.buttonOpenInNewTab ?? next.openInNewTab ?? current.buttonOpenInNewTab ?? current.button_open_in_new_tab) !== false
  const nextFileId = next.buttonFileId ?? next.fileId ?? current.buttonFileId ?? current.button_file_id ?? null
  const nextFileUrl = String(
    next.buttonFileUrl ?? next.fileUrl ?? current.buttonFileUrl ?? current.button_file_url ?? ''
  ).trim()
  const nextFileName = String(
    next.buttonFileName ?? next.fileName ?? current.buttonFileName ?? current.button_file_name ?? ''
  ).trim()
  const nextFileMimeType = String(
    next.buttonFileMimeType ?? next.fileMimeType ?? current.buttonFileMimeType ?? current.button_file_mime_type ?? ''
  ).trim()
  const nextFileAccessMode = String(
    next.buttonFileAccessMode ?? next.fileAccessMode ?? current.buttonFileAccessMode ?? current.button_file_access_mode ?? 'view'
  ).trim().toLowerCase() === 'download' ? 'download' : 'view'
  const nextWidth = Math.max(20, Math.min(100, toRoundedNumber(
    next.buttonWidth ?? next.width ?? current.buttonWidth ?? current.button_width,
    56
  )))
  const nextPaddingX = Math.max(4, Math.min(80, toRoundedNumber(
    next.buttonPaddingX ?? next.paddingX ?? current.buttonPaddingX ?? current.button_padding_x,
    24
  )))
  const nextPaddingY = Math.max(4, Math.min(56, toRoundedNumber(
    next.buttonPaddingY ?? next.paddingY ?? current.buttonPaddingY ?? current.button_padding_y,
    14
  )))
  const nextAlign = normalizeTextAlign(
    next.buttonAlign ?? next.align ?? current.buttonAlign ?? current.button_align,
    'center'
  )
  const nextText = String(
    next.buttonText ?? next.text ?? current.buttonText ?? current.button_text ?? current.content ?? 'Button Element'
  ).trim() || 'Button Element'
  Object.assign(block.props, {
    buttonText: nextText,
    button_text: nextText,
    content: nextText,
    buttonBackgroundColor: nextBackgroundColor,
    button_background_color: nextBackgroundColor,
    buttonTextColor: nextTextColor,
    button_text_color: nextTextColor,
    buttonTextSize: nextTextSize,
    button_text_size: nextTextSize,
    buttonFontSize: nextFontSize,
    button_font_size: nextFontSize,
    buttonActionType: nextActionType,
    button_action_type: nextActionType,
    actionType: nextActionType,
    buttonGoToType: nextGoToType,
    button_go_to_type: nextGoToType,
    buttonFeatureSlug: nextFeatureSlug,
    button_feature_slug: nextFeatureSlug,
    buttonLinkAddress: nextLinkAddress,
    button_link_address: nextLinkAddress,
    buttonOpenInNewTab: nextOpenInNewTab,
    button_open_in_new_tab: nextOpenInNewTab,
    buttonFileId: nextFileId,
    button_file_id: nextFileId,
    buttonFileUrl: nextFileUrl,
    button_file_url: nextFileUrl,
    buttonFileName: nextFileName,
    button_file_name: nextFileName,
    buttonFileMimeType: nextFileMimeType,
    button_file_mime_type: nextFileMimeType,
    buttonFileAccessMode: nextFileAccessMode,
    button_file_access_mode: nextFileAccessMode,
    buttonWidth: nextWidth,
    button_width: nextWidth,
    buttonPaddingX: nextPaddingX,
    button_padding_x: nextPaddingX,
    buttonPaddingY: nextPaddingY,
    button_padding_y: nextPaddingY,
    buttonAlign: nextAlign,
    button_align: nextAlign,
  })
}

const onUpdateVideoSettings = (next = {}) => {
  const block = activeSettingsBlock.value
  if (!block || String(block.type || '') !== 'video') return
  if (!block.props) block.props = {}
  const current = block.props || {}
  const nextVideoType = normalizeVideoType(
    next.videoType ?? next.video_type ?? current.videoType ?? current.video_type ?? inferVideoTypeFromLegacyProps(current),
    'upload'
  )
  const nextVideoFileIdRaw = next.videoFileId ?? next.video_file_id ?? current.videoFileId ?? current.video_file_id ?? null
  const nextVideoThumbnailFileIdRaw = next.videoThumbnailFileId ?? next.video_thumbnail_file_id ?? current.videoThumbnailFileId ?? current.video_thumbnail_file_id ?? null
  const nextVideoUrl = String(next.videoUrl ?? next.video_url ?? current.videoUrl ?? current.video_url ?? '').trim()
  const nextVideoExternalUrl = String(next.videoExternalUrl ?? next.video_external_url ?? current.videoExternalUrl ?? current.video_external_url ?? '').trim()
  const nextVideoEmbedCode = String(next.videoEmbedCode ?? next.video_embed_code ?? current.videoEmbedCode ?? current.video_embed_code ?? '').trim()
  const nextVideoFileName = String(next.videoFileName ?? next.video_file_name ?? current.videoFileName ?? current.video_file_name ?? '').trim()
  const nextVideoFileMimeType = String(next.videoFileMimeType ?? next.video_file_mime_type ?? current.videoFileMimeType ?? current.video_file_mime_type ?? '').trim()
  const nextVideoThumbnailUrl = String(next.videoThumbnailUrl ?? next.video_thumbnail_url ?? current.videoThumbnailUrl ?? current.video_thumbnail_url ?? '').trim()
  const nextVideoAutoPreviewUrl = String(next.videoAutoPreviewUrl ?? next.video_auto_preview_url ?? current.videoAutoPreviewUrl ?? current.video_auto_preview_url ?? '').trim()
  const nextVideoPlaybackUrlRaw = normalizeVideoSourceUrl(next.videoPlaybackUrl ?? next.video_playback_url ?? current.videoPlaybackUrl ?? current.video_playback_url ?? '')
  const nextVideoPosterUrlRaw = normalizeVideoSourceUrl(next.videoPosterUrl ?? next.video_poster_url ?? current.videoPosterUrl ?? current.video_poster_url ?? '')
  const nextVideoBorderRadius = Math.max(0, Math.min(120, toRoundedNumber(next.videoBorderRadius ?? next.video_border_radius ?? current.videoBorderRadius ?? current.video_border_radius, 16)))
  const nextControlColor = normalizeHexColor(next.videoControlColor ?? next.video_control_color ?? current.videoControlColor ?? current.video_control_color, '#2D8CFF')
  const nextVideoFileId = resolveVideoFileIdCandidate(
    nextVideoFileIdRaw,
    nextVideoPlaybackUrlRaw,
    nextVideoUrl,
    nextVideoPosterUrlRaw
  ) || null
  const rawThumbnailFileId = sanitizeMediaFileId(nextVideoThumbnailFileIdRaw) || null
  const nextVideoThumbnailFileId = rawThumbnailFileId && rawThumbnailFileId !== nextVideoFileId ? rawThumbnailFileId : null
  const normalizedVideoUrl = normalizeVideoSourceUrl(nextVideoUrl)
  const nextVideoPlaybackUrl = nextVideoFileId
    ? buildVideoPlaybackUrlFromFileId(nextVideoFileId)
    : (isLikelyHlsVideoSource(nextVideoPlaybackUrlRaw)
      ? nextVideoPlaybackUrlRaw
      : (isLikelyHlsVideoSource(normalizedVideoUrl) ? normalizedVideoUrl : ''))
  const nextVideoPosterUrl = (nextVideoThumbnailFileId ? resolveImageUrl(nextVideoThumbnailFileId, '') : '') ||
    nextVideoThumbnailUrl ||
    nextVideoPosterUrlRaw ||
    nextVideoAutoPreviewUrl ||
    (nextVideoFileId ? buildVideoThumbnailProxyUrlFromFileId(nextVideoFileId) : '')
  Object.assign(block.props, next || {}, {
    videoType: nextVideoType,
    video_type: nextVideoType,
    videoFileId: nextVideoFileId,
    video_file_id: nextVideoFileId,
    videoUrl: normalizedVideoUrl,
    video_url: normalizedVideoUrl,
    videoPlaybackUrl: nextVideoPlaybackUrl,
    video_playback_url: nextVideoPlaybackUrl,
    videoExternalUrl: nextVideoExternalUrl,
    video_external_url: nextVideoExternalUrl,
    videoEmbedCode: nextVideoEmbedCode,
    video_embed_code: nextVideoEmbedCode,
    videoFileName: nextVideoFileName,
    video_file_name: nextVideoFileName,
    videoFileMimeType: nextVideoFileMimeType,
    video_file_mime_type: nextVideoFileMimeType,
    videoThumbnailFileId: nextVideoThumbnailFileId,
    video_thumbnail_file_id: nextVideoThumbnailFileId,
    thumbnailFileId: nextVideoThumbnailFileId,
    thumbnail_file_id: nextVideoThumbnailFileId,
    videoThumbnailUrl: nextVideoThumbnailUrl,
    video_thumbnail_url: nextVideoThumbnailUrl,
    thumbnailUrl: nextVideoThumbnailUrl,
    thumbnail_url: nextVideoThumbnailUrl,
    videoAutoPreviewUrl: nextVideoAutoPreviewUrl,
    video_auto_preview_url: nextVideoAutoPreviewUrl,
    videoPosterUrl: nextVideoPosterUrl,
    video_poster_url: nextVideoPosterUrl,
    videoBorderRadius: nextVideoBorderRadius,
    video_border_radius: nextVideoBorderRadius,
    videoControlColor: nextControlColor,
    video_control_color: nextControlColor,
  })
}

const buildUploadedMediaUrl = (uploaded) => {
  const nestedFile = Array.isArray(uploaded?.files) ? (uploaded.files[0] || {}) : {}
  const fileId = sanitizeMediaFileId(
    uploaded?.fileId ||
    uploaded?.file_id ||
    uploaded?.mediaFileId ||
    uploaded?.media_file_id ||
    nestedFile?.fileId ||
    nestedFile?.file_id ||
    nestedFile?.mediaFileId ||
    nestedFile?.media_file_id
  )
  const permanent = resolveStoredMediaUrl(
    uploaded?.permanentUrl ||
    uploaded?.fileUrl ||
    nestedFile?.permanentUrl ||
    nestedFile?.fileUrl
  )
  if (fileId) return { fileId, url: `${apiProxy}/api/image/${encodeURIComponent(fileId)}` }
  if (permanent) return { fileId: '', url: permanent }
  return { fileId: '', url: '' }
}

const onSectionBackgroundUploadSelected = async (event) => {
  const file = event?.target?.files?.[0]
  if (!file || !String(file.type || '').startsWith('image/')) return
  const uploaded = await mediaUploadStore.uploadSingleFile(file, null, { origin: 'creator-page-editor-section-background' })
  event.target.value = ''
  if (!uploaded || !selectedBlock.value || String(selectedBlock.value?.type || '') !== 'section') return
  const { fileId, url } = buildUploadedMediaUrl(uploaded)
  if (!selectedBlock.value.props || typeof selectedBlock.value.props !== 'object') selectedBlock.value.props = {}
  selectedBlock.value.props.sectionBackgroundImageFileId = fileId || null
  selectedBlock.value.props.sectionBackgroundImageUrl = url
}

const onRowBackgroundUploadSelected = async (event) => {
  const file = event?.target?.files?.[0]
  if (!file || !String(file.type || '').startsWith('image/')) return
  const uploaded = await mediaUploadStore.uploadSingleFile(file, null, { origin: 'creator-page-editor-row-background' })
  event.target.value = ''
  if (!uploaded || !selectedRow.value) return
  const { fileId, url } = buildUploadedMediaUrl(uploaded)
  if (!selectedRow.value.props || typeof selectedRow.value.props !== 'object') selectedRow.value.props = {}
  selectedRow.value.props.rowBackgroundImageFileId = fileId || null
  selectedRow.value.props.rowBackgroundImageUrl = url
}

const onColumnBackgroundUploadSelected = async (event) => {
  const file = event?.target?.files?.[0]
  if (!file || !String(file.type || '').startsWith('image/')) return
  const uploaded = await mediaUploadStore.uploadSingleFile(file, null, { origin: 'creator-page-editor-column-background' })
  event.target.value = ''
  if (!uploaded || !selectedColumn.value?.row) return
  const { fileId, url } = buildUploadedMediaUrl(uploaded)
  const style = ensureRowColumnStyleEntry(selectedColumn.value.row, selectedColumn.value.columnIndex)
  style.backgroundImageFileId = fileId || null
  style.backgroundImageUrl = url
}

const onButtonFileUploadRequest = () => {
  pageButtonFileUploadInputRef.value?.click?.()
}

const onPageButtonFileUploadSelected = async (event) => {
  const file = event?.target?.files?.[0]
  if (!file) return
  const uploaded = await mediaUploadStore.uploadSingleFile(file, null, { origin: 'creator-page-editor-button-file' })
  event.target.value = ''
  if (!uploaded) return
  const block = activeSettingsBlock.value
  if (!block || String(block.type || '') !== 'button') return
  if (!block.props) block.props = {}
  const nextFileId = uploaded?.fileId || uploaded?.file_id || null
  const nextFileUrl = String(uploaded?.permanentUrl || uploaded?.fileUrl || '').trim()
  const nextFileName = String(file.name || '').trim()
  const nextFileMimeType = String(file.type || '').trim()
  block.props.buttonActionType = 'file_access'
  block.props.button_action_type = 'file_access'
  block.props.buttonGoToType = String(block.props.buttonGoToType || block.props.button_go_to_type || 'custom_link').trim().toLowerCase() === 'feature_instance'
    ? 'feature_instance'
    : 'custom_link'
  block.props.button_go_to_type = block.props.buttonGoToType
  block.props.buttonFileId = nextFileId
  block.props.button_file_id = nextFileId
  block.props.buttonFileUrl = nextFileUrl
  block.props.button_file_url = nextFileUrl
  block.props.buttonFileName = nextFileName
  block.props.button_file_name = nextFileName
  block.props.buttonFileMimeType = nextFileMimeType
  block.props.button_file_mime_type = nextFileMimeType
}

const onVideoUploadRequest = () => {
  pageVideoUploadInputRef.value?.click?.()
}

const onPageVideoUploadSelected = async (event) => {
  const file = event?.target?.files?.[0]
  if (!file || !String(file.type || '').startsWith('video/')) return
  const uploaded = await mediaUploadStore.uploadSingleFile(file, null, { origin: 'creator-page-editor-video' })
  event.target.value = ''
  if (!uploaded) return
  const block = activeSettingsBlock.value
  if (!block || String(block.type || '') !== 'video') return
  if (!block.props) block.props = {}
  const nextVideoFileId = sanitizeMediaFileId(uploaded?.fileId || uploaded?.file_id) || null
  const uploadedVideoSource = String(uploaded?.hlsPlaylistUrl || uploaded?.permanentUrl || uploaded?.fileUrl || '').trim()
  const nextVideoFileName = String(file.name || '').trim()
  const nextVideoFileMimeType = String(file.type || '').trim()
  const nextAutoPreviewUrl = String(uploaded?.thumbnailUrl || '').trim()
  const nextPlaybackUrl = nextVideoFileId
    ? buildVideoPlaybackUrlFromFileId(nextVideoFileId)
    : (isLikelyHlsVideoSource(uploadedVideoSource) ? normalizeVideoSourceUrl(uploadedVideoSource) : '')
  const nextVideoUrl = nextPlaybackUrl || normalizeVideoSourceUrl(uploadedVideoSource)
  const nextPosterUrl = nextAutoPreviewUrl || (nextVideoFileId ? buildVideoThumbnailProxyUrlFromFileId(nextVideoFileId) : '')
  block.props.videoType = 'upload'
  block.props.video_type = 'upload'
  block.props.videoFileId = nextVideoFileId
  block.props.video_file_id = nextVideoFileId
  block.props.videoUrl = nextVideoUrl
  block.props.video_url = block.props.videoUrl
  block.props.videoPlaybackUrl = nextPlaybackUrl
  block.props.video_playback_url = nextPlaybackUrl
  block.props.videoExternalUrl = ''
  block.props.video_external_url = ''
  block.props.videoEmbedCode = ''
  block.props.video_embed_code = ''
  block.props.videoFileName = nextVideoFileName
  block.props.video_file_name = nextVideoFileName
  block.props.videoFileMimeType = nextVideoFileMimeType
  block.props.video_file_mime_type = nextVideoFileMimeType
  block.props.videoThumbnailFileId = null
  block.props.video_thumbnail_file_id = null
  block.props.thumbnailFileId = null
  block.props.thumbnail_file_id = null
  block.props.videoThumbnailUrl = ''
  block.props.video_thumbnail_url = ''
  block.props.thumbnailUrl = ''
  block.props.thumbnail_url = ''
  block.props.videoAutoPreviewUrl = nextAutoPreviewUrl
  block.props.video_auto_preview_url = nextAutoPreviewUrl
  block.props.videoPosterUrl = nextPosterUrl
  block.props.video_poster_url = nextPosterUrl
}

const onVideoThumbnailUploadRequest = () => {
  pageVideoThumbnailUploadInputRef.value?.click?.()
}

const onPageVideoThumbnailUploadSelected = async (event) => {
  const file = event?.target?.files?.[0]
  if (!file || !String(file.type || '').startsWith('image/')) return
  const uploaded = await mediaUploadStore.uploadSingleFile(file, null, { origin: 'creator-page-editor-video-thumbnail' })
  event.target.value = ''
  if (!uploaded) return
  const block = activeSettingsBlock.value
  if (!block || String(block.type || '') !== 'video') return
  if (!block.props) block.props = {}
  const { fileId, url } = buildUploadedMediaUrl(uploaded)
  block.props.videoThumbnailFileId = fileId || null
  block.props.video_thumbnail_file_id = fileId || null
  block.props.videoThumbnailUrl = url
  block.props.video_thumbnail_url = url
  block.props.videoPosterUrl = url
  block.props.video_poster_url = url
}

function slugify(s) {
  return String(s || 'untitled')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

// If GET /content returns 404, auto-create a stub row via POST /api/pages, swap to the new id, then refetch.
async function ensurePageExistsIfMissing(resp) {
  if (resp.status !== 404) return resp

  const stub = editorDoc.value || {
    version: 1,
    title: 'Untitled Page',
    sections: [],
    seo: { title: 'Untitled Page', description: '' }
  }

  const payload = {
    title: stub.title || 'Untitled Page',
    slug: slugify(stub.title || 'untitled')
  }

  const createRes = await fetch(`${apiProxy}/api/pages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
  })
  const created = await createRes.json()
  if (!createRes.ok || !created?.success || !created?.id) {
    throw new Error(created?.message || 'Failed to auto-create page')
  }

  // Update route to the newly created id and refetch content
  pageIdRef.value = String(created.id)
  if (!props.embedded) {
    await router.replace({ name: route.name, params: { pageId: pageIdRef.value } })
  }

  return await fetch(`${apiProxy}/api/pages/${pageIdRef.value}/content`, {
    credentials: 'include'
  })
}

function addBulletItem() {
  const element = selectedElement.value
  if (!element?.props) return
  const items = Array.isArray(element.props.items) ? element.props.items : []
  element.props.items = [
    ...items,
    { id: `bullet-${Date.now()}-${items.length + 1}`, text: `Add another benefit or key point` },
  ]
}

function removeBulletItem(index) {
  const element = selectedElement.value
  if (!element?.props || !Array.isArray(element.props.items) || element.props.items.length <= 1) return
  element.props.items = element.props.items.filter((_item, itemIndex) => itemIndex !== index)
}

function toggleShareNetwork(network, enabled) {
  const element = selectedElement.value
  if (!element?.props) return
  const normalized = String(network || '').trim().toLowerCase()
  const current = Array.isArray(element.props.shareNetworks) ? element.props.shareNetworks : []
  if (enabled) {
    element.props.shareNetworks = Array.from(new Set([...current, normalized])).filter(Boolean)
    return
  }
  element.props.shareNetworks = current.filter((entry) => String(entry) !== normalized)
}

function addOptinField() {
  const element = selectedElement.value
  if (!element?.props) return
  const fields = Array.isArray(element.props.optinFields) ? element.props.optinFields : []
  element.props.optinFields = [
    ...fields,
    { key: 'custom', type: 'text', label: 'New field', placeholder: 'Enter a value', showLabel: true, required: false },
  ]
}

function removeOptinField(index) {
  const element = selectedElement.value
  if (!element?.props || !Array.isArray(element.props.optinFields)) return
  const field = element.props.optinFields[index]
  if (String(field?.key || '').trim().toLowerCase() === 'email') return
  element.props.optinFields = element.props.optinFields.filter((_entry, fieldIndex) => fieldIndex !== index)
}

function syncOptinFieldType(field) {
  if (!field || typeof field !== 'object') return
  const key = String(field.key || 'custom').trim()
  if (key === 'email') {
    field.type = 'email'
    field.label = String(field.label || 'Email address')
    field.placeholder = String(field.placeholder || 'you@example.com')
    field.required = true
  } else if (key === 'phone') {
    field.type = 'tel'
    field.label = String(field.label || 'Phone number')
  } else {
    field.type = 'text'
  }
}

async function loadEmailAudience() {
  try {
    const response = await fetch(`${apiProxy}/api/creator-integrations/email-audiences`, { credentials: 'include' })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok || !payload?.success) throw new Error(payload?.message || 'Unable to load email integration')
    const audience = payload?.audience && typeof payload.audience === 'object' ? payload.audience : {}
    emailAudience.value = {
      selectedProvider: String(audience.selectedProvider || '').trim(),
      providers: Array.isArray(audience.providers) ? audience.providers : [],
    }
  } catch (error) {
    console.warn('[SitePageEditor] Failed to load email integration audience', error)
    emailAudience.value = { selectedProvider: '', providers: [] }
  }
}

async function loadCheckoutBumpProducts() {
  if (!props.cartProductId || checkoutBumpProductsLoading.value) return
  checkoutBumpProductsLoading.value = true
  try {
    const response = await fetch(`${apiProxy}/api/creator-payments/creator/cart/products?page=1&pageSize=100`, {
      credentials: 'include',
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok || !payload?.success) throw new Error(payload?.message || 'Unable to load products')
    const products = Array.isArray(payload.products) ? payload.products : []
    checkoutCurrentProduct.value = products.find((product) => Number(product?.id || 0) === Number(props.cartProductId || 0)) || null
    const normalizedProducts = products
      .filter((product) => Number(product?.id || 0) !== Number(props.cartProductId || 0))
      .filter((product) => String(product?.status || '') !== 'disabled')
      .map((product) => ({
        id: Number(product.id || 0),
        publicId: String(product.productPublicId || ''),
        name: String(product.productName || 'Untitled product'),
        paymentType: String(product.paymentType || 'one_time'),
        priceCents: Number(product.priceCents || 0),
        currencyCode: String(product.currencyCode || 'USD'),
        productKind: String(product.productKind || 'digital'),
      }))
      .filter((product) => product.publicId && ['one_time', 'monthly', 'yearly', 'free_forever'].includes(product.paymentType))
    checkoutCatalogProducts.value = normalizedProducts.filter((product) => {
      const currentCurrency = String(checkoutCurrentProduct.value?.currencyCode || 'USD')
      const currentKind = String(checkoutCurrentProduct.value?.productKind || '')
      return product.currencyCode === currentCurrency && (!currentKind || product.productKind === currentKind)
    })
    checkoutBumpProducts.value = normalizedProducts.filter((product) => ['one_time', 'free_forever'].includes(product.paymentType))
    syncCheckoutPrimaryProductOption()
  } catch (error) {
    console.warn('[SitePageEditor] Failed to load order-bump products', error)
    checkoutBumpProducts.value = []
    checkoutCatalogProducts.value = []
  } finally {
    checkoutBumpProductsLoading.value = false
  }
}

function formatCheckoutProductPrice(product) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: product?.currencyCode || 'USD' }).format(Number(product?.priceCents || 0) / 100)
  } catch (_error) {
    return `$${(Number(product?.priceCents || 0) / 100).toFixed(2)}`
  }
}

function onCheckoutCardBackgroundColorChange(value) {
  const element = selectedElement.value
  if (!element?.props) return
  element.props.cardBackgroundColor = normalizeBackgroundColor(value, '#FFFFFF')
  element.props.cardTransparent = false
}

function onCheckoutOrderFlowChange(value) {
  const element = selectedElement.value
  if (!element?.props) return
  const mode = String(value || '') === 'two_step' ? 'two_step' : 'one_step'
  element.props.checkoutMode = mode
  if (mode === 'one_step') {
    element.props.orderBumpEnabled = false
    element.props.editorPreviewStep = 'contact'
  }
}

function addCheckoutProductOption() {
  const element = selectedElement.value
  if (!element?.props) return
  const options = Array.isArray(element.props.productSelectorOptions) ? element.props.productSelectorOptions : []
  if (options.length >= 12) return
  element.props.productSelectorOptions = [...options, { id: `offer-${Date.now()}-${options.length + 1}`, productPublicId: '', title: 'Choose a product', subtitle: '', badge: '', paymentType: 'one_time', priceCents: 0, currencyCode: String(checkoutCurrentProduct.value?.currencyCode || 'USD') }]
}

function updateCheckoutProductOption(option, productPublicId) {
  if (!option) return
  const product = checkoutCatalogProducts.value.find((entry) => entry.publicId === String(productPublicId || ''))
  if (!product) return
  Object.assign(option, { productPublicId: product.publicId, title: product.name, subtitle: '', badge: '', paymentType: product.paymentType, priceCents: product.priceCents, currencyCode: product.currencyCode })
}

function syncCheckoutPrimaryProductOption() {
  const current = checkoutCurrentProduct.value
  if (!current?.productPublicId || !editorDoc.value) return
  const seen = new Set()
  const visit = (value) => {
    if (!value || typeof value !== 'object' || seen.has(value)) return false
    seen.add(value)
    if (String(value.type || '').trim().toLowerCase() === 'checkout-order-form') {
      if (!value.props) value.props = {}
      const options = Array.isArray(value.props.productSelectorOptions) && value.props.productSelectorOptions.length
        ? value.props.productSelectorOptions
        : [{ id: 'primary-offer' }]
      options[0] = {
        ...options[0],
        id: String(options[0]?.id || 'primary-offer'),
        productPublicId: String(current.productPublicId),
        title: String(current.productName || 'Current product'),
        subtitle: '',
        badge: '',
        paymentType: String(current.paymentType || 'one_time'),
        priceCents: Number(current.priceCents || 0),
        currencyCode: String(current.currencyCode || 'USD'),
      }
      value.props.productSelectorOptions = options
      return true
    }
    for (const child of Array.isArray(value) ? value : Object.values(value)) {
      if (visit(child)) return true
    }
    return false
  }
  visit(editorDoc.value)
}

function removeCheckoutProductOption(index) {
  const element = selectedElement.value
  if (!element?.props || !Array.isArray(element.props.productSelectorOptions) || element.props.productSelectorOptions.length <= 1) return
  element.props.productSelectorOptions = element.props.productSelectorOptions.filter((_option, optionIndex) => optionIndex !== index)
}

function onOrderBumpToggle(event) {
  const element = selectedElement.value
  if (!element?.props) return
  const enabled = Boolean(event?.target?.checked)
  element.props.orderBumpEnabled = enabled
  if (enabled) {
    element.props.checkoutMode = 'two_step'
    element.props.editorPreviewStep = 'payment'
  }
}

onMounted(async () => {
  syncEditorWindowSize()
  window.addEventListener('resize', syncEditorWindowSize, { passive: true })
  window.visualViewport?.addEventListener('resize', syncEditorWindowSize, { passive: true })
  window.addEventListener('keydown', onEditorHistoryKeydown)
  loadCheckoutBumpProducts()
  loadEmailAudience()
  loadSavedPageLibrary()
  initialChromeVisible.value = true
  if (initialChromeTimer) clearTimeout(initialChromeTimer)
  initialChromeTimer = setTimeout(() => {
    initialChromeVisible.value = false
  }, 2200)
  try {
    const storedViewportMode = localStorage.getItem(EDITOR_VIEWPORT_STORAGE_KEY)
    if (storedViewportMode) {
      setEditorViewportMode(storedViewportMode)
    }
  } catch (err) {
    console.debug('[SitePageEditor] Failed to load persisted viewport mode', err)
  }
  // no-op: floating menus disabled for drawer-first workflow

  // ---- 1) Template handoff (from picker) ----
  const templateKey = `pageTemplate:${pageIdRef.value}`;
  const pendingKey  = `pageTemplate:pending`; // id-agnostic safety net

  const parseJSON = (raw, where) => {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.warn(`[SitePageEditor] Invalid JSON in ${where}`, err);
      return null;
    }
  };

  const rawIdTemplate  = sessionStorage.getItem(templateKey);
  const rawPendingTmpl = sessionStorage.getItem(pendingKey);

  const templateFromSession =
    parseJSON(rawIdTemplate, templateKey) ??
    parseJSON(rawPendingTmpl, pendingKey);

  if (templateFromSession) {
    editorDoc.value = mapDocForEditorCanvas(templateFromSession);
    ensureSeo();
    ensureSections();
    ensureRequiredSystemElements();
  }

  // ---- 2) Load content; if 404, auto-create then retry ----
  try {
    let res = await fetch(`${apiProxy}/api/pages/${pageIdRef.value}/content`, {
      credentials: 'include'
    });

    if (res.status === 401) {
      alert('Session expired. Please log in again.');
      return;
    }

    if (res.status === 404) {
      // Page row not found yet - create it, then refetch
      res = await ensurePageExistsIfMissing(res);
    }

    if (!res.ok) {
      throw new Error(`Content load failed: ${res.status}`);
    }

    const data = await res.json();
    if (data?.success) {
      currentEditorPageSlug.value = normalizeMemberPageSlug(data?.slug || '')
      const serverDoc = data.draft || data.published || null;

      // Prefer server doc; if missing, keep session template (or seed a minimal doc)
      editorDoc.value = mapDocForEditorCanvas(
        serverDoc ||
        editorDoc.value ||
        templateFromSession || {
          version: 1,
          title: 'Untitled Page',
          sections: [],
          seo: { title: '', description: '' }
        }
      );

      ensureSeo();
      ensureSections();
      ensureRequiredSystemElements();

      // Optional: if we just seeded from template, autosave once so refreshes are stable
      if (!serverDoc && templateFromSession) {
        try {
          await fetch(`${apiProxy}/api/pages/${pageIdRef.value}/content`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ data: mapDocForPersistence(editorDoc.value) })
          });
        } catch (err) {
          console.warn('[SitePageEditor] Autosave after template seed failed', err);
        }
      }
    }
  } catch (e) {
    console.warn('[SitePageEditor] Content fetch failed, keeping session/template', e);
  }

  // ---- 3) Clear consumed keys so we don't reapply on refresh ----
  try {
    sessionStorage.removeItem(templateKey);
    sessionStorage.removeItem(pendingKey);
  } catch (err) {
    console.debug('[SitePageEditor] sessionStorage cleanup skipped', err);
  }
  if (editorDoc.value) {
    lastSavedDocSignature = getPersistedDocumentSnapshot().signature
    hasUnsavedChanges.value = false
    autosaveError.value = ''
  }
  initializeEditorHistory()
  autosaveReady = true
});

watch(editorDoc, () => {
  if (!props.autosave || !autosaveReady || !editorDoc.value) return
  const { signature } = getPersistedDocumentSnapshot()
  if (signature === lastSavedDocSignature) {
    hasUnsavedChanges.value = false
    if (autosaveTimer) {
      clearTimeout(autosaveTimer)
      autosaveTimer = null
    }
    return
  }
  hasUnsavedChanges.value = true
  autosaveError.value = ''
  if (autosaveTimer) clearTimeout(autosaveTimer)
  autosaveTimer = setTimeout(() => {
    autosaveTimer = null
    saveDraft({ quiet: true })
  }, 900)
}, { deep: true, flush: 'post' })

watch(editorDoc, () => {
  scheduleEditorHistorySnapshot()
}, { deep: true, flush: 'post' })


async function preview() {
  if (!editorDoc.value || previewing.value) return

  const previewWindow = window.open('about:blank', '_blank')
  if (!previewWindow) {
    alert('Please allow pop-ups for Platforma so the member preview can open in a new tab.')
    return
  }
  try {
    previewWindow.opener = null
    previewWindow.document.title = 'Preparing private preview…'
    previewWindow.document.body.innerHTML = '<p style="font: 15px system-ui; padding: 24px; color: #52525b">Preparing your private member-page preview…</p>'
  } catch (_error) {
    // The blank tab can still be navigated even if its document is unavailable.
  }

  previewing.value = true
  try {
    if (autosaveTimer) {
      clearTimeout(autosaveTimer)
      autosaveTimer = null
    }
    const saved = await saveDraft({ quiet: false })
    if (!saved) throw new Error('The latest draft could not be saved for preview.')

    const response = await fetch(`${apiProxy}/api/pages/${pageIdRef.value}/preview`, {
      method: 'POST',
      credentials: 'include',
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok || !payload?.success || !payload?.previewUrl) {
      throw new Error(payload?.message || 'The private preview link could not be created.')
    }
    previewWindow.location.replace(payload.previewUrl)
  } catch (error) {
    try { previewWindow.close() } catch (_closeError) { /* no-op */ }
    console.error('[Preview] error', error)
    alert(error?.message || 'Failed to open the member preview.')
  } finally {
    previewing.value = false
  }
}

async function exitEmbeddedEditor() {
  if (props.autosave && editorDoc.value) {
    if (autosaveTimer) {
      clearTimeout(autosaveTimer)
      autosaveTimer = null
    }
    const saved = await saveDraft({ quiet: false })
    if (!saved) return
  }
  emit('exit-embedded')
}

async function saveDraft({ quiet = false } = {}) {
  if (!editorDoc.value) return
  const snapshot = getPersistedDocumentSnapshot()
  if (snapshot.signature === lastSavedDocSignature) {
    hasUnsavedChanges.value = false
    autosaveError.value = ''
    return true
  }
  if (saving.value) {
    autosaveQueued = props.autosave
    return
  }
  saving.value = true
  autosaveError.value = ''
  try {

    // 1) DB title first (canonical)
    await saveMetaIfChanged();

    // 2) draft blob
    const res = await fetch(`${apiProxy}/api/pages/${pageIdRef.value}/content`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ data: snapshot.data })
    })
    const out = await res.json()
    if (!res.ok || !out.success) throw new Error(out.message || 'Page not found or not owned by user.')
    lastSavedDocSignature = snapshot.signature
    hasUnsavedChanges.value = false
    autosaveError.value = ''
    emit('draft-saved', { pageId: pageIdRef.value })
    // show "Saved" chip for 5s
    justSaved.value = true
    if (savedTimer) clearTimeout(savedTimer)
    savedTimer = setTimeout(() => { justSaved.value = false }, 5000)
    return true
  } catch (e) {
    hasUnsavedChanges.value = true
    autosaveError.value = String(e?.message || 'Failed to save draft.')
    console.error('[SaveDraft] error', e)
    if (!quiet) alert(e.message || 'Failed to save draft.')
    return false
  } finally {
    saving.value = false
    if (autosaveQueued) {
      autosaveQueued = false
      if (autosaveTimer) clearTimeout(autosaveTimer)
      autosaveTimer = setTimeout(() => {
        autosaveTimer = null
        saveDraft({ quiet: true })
      }, 250)
    }
  }
}

async function publishNow() {
  if (!editorDoc.value) return
  if (props.externalPublish) {
    if (autosaveTimer) {
      clearTimeout(autosaveTimer)
      autosaveTimer = null
    }
    const saved = await saveDraft({ quiet: false })
    if (!saved) return
    emit('publish-request')
    return
  }
  publishing.value = true
  try {

    // 1) DB title first (canonical used by serializer during publish)
    await saveMetaIfChanged();

    // 2) publish (will read DB title and push normalized)

    const res = await fetch(`${apiProxy}/api/pages/${pageIdRef.value}/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ data: mapDocForPersistence(editorDoc.value) })
    })
    const out = await res.json()
    if (!res.ok || !out.success) throw new Error(out.message || 'Publish failed')

    // Show the publish banner for 6s
    justPublished.value = true
    if (publishedTimer) clearTimeout(publishedTimer)
    publishedTimer = setTimeout(() => { justPublished.value = false }, 6000)
  } catch (e) {
    console.error('[Publish] error', e)
    alert(e.message || 'Failed to publish.')
  } finally {
    publishing.value = false
  }
}

// helper to ensure shape exists
const ensureSeo = () => {
  if (!editorDoc.value) return
  if (!editorDoc.value.seo || typeof editorDoc.value.seo !== 'object') {
    editorDoc.value.seo = { title: '', description: '' }
  } else {
    if (typeof editorDoc.value.seo.title !== 'string') editorDoc.value.seo.title = ''
    if (typeof editorDoc.value.seo.description !== 'string') editorDoc.value.seo.description = ''
  }
}

const normalizeMemberAuthElementTypesForRowElements = (elements, desiredType) => {
  const list = Array.isArray(elements) ? elements : []
  list.forEach((element) => {
    const type = normalizeElementType(element?.type)
    if (isMemberAuthFormType(type)) {
      element.type = desiredType
      return
    }
    if (type === 'row') {
      normalizeMemberAuthElementTypesForRowElements(element?.props?.elements, desiredType)
    }
  })
}

const syncMemberAuthFormTypesForCurrentPage = () => {
  const desiredType = isJoinPageContext.value
    ? MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE
    : 'member-login-form'
  const sections = Array.isArray(editorDoc.value?.sections) ? editorDoc.value.sections : []
  sections.forEach((section) => {
    if (String(section?.type || '') !== 'section') return
    const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
    rows.forEach((row) => {
      if (String(row?.type || '') !== 'row') return
      normalizeMemberAuthElementTypesForRowElements(row?.props?.elements, desiredType)
    })
  })
}

const ensureSections = () => {
  if (!editorDoc.value) return
  if (!Array.isArray(editorDoc.value.sections)) editorDoc.value.sections = []
  if (shouldMigrateLegacyMemberApprovalTemplateDoc(editorDoc.value) || shouldRefreshDefaultMemberApprovalTemplateDoc(editorDoc.value)) {
    editorDoc.value.sections = buildMemberApprovalTemplateSections(editorDoc.value)
  } else if (shouldMigrateLegacyGetAccessSuccessTemplateDoc(editorDoc.value) || shouldRefreshDefaultMemberGetAccessSuccessTemplateDoc(editorDoc.value)) {
    editorDoc.value.sections = buildMemberGetAccessSuccessTemplateSections(editorDoc.value)
  } else if (shouldMigrateLegacyGetAccessTemplateDoc(editorDoc.value) || shouldRefreshDefaultMemberGetAccessTemplateDoc(editorDoc.value)) {
    editorDoc.value.sections = buildMemberGetAccessTemplateSections(editorDoc.value)
  } else if (shouldMigrateLegacyPricingTemplateDoc(editorDoc.value) || shouldRefreshDefaultMemberPricingTemplateDoc(editorDoc.value)) {
    editorDoc.value.sections = buildMemberPricingTemplateSections(editorDoc.value)
  } else if (shouldMigrateLegacyHomeTemplateDoc(editorDoc.value) || shouldRefreshDefaultMemberHomeTemplateDoc(editorDoc.value)) {
    editorDoc.value.sections = buildMemberHomeTemplateSections(editorDoc.value)
  }
  editorDoc.value.sections = editorDoc.value.sections.map((block) => {
    if (String(block?.type || '') === 'member-login') {
      return buildMemberLoginTemplateSection(block)
    }
    if (
      String(block?.type || '') === 'section' &&
      String(block?.props?.sectionTemplateType || '') === MEMBER_LOGIN_TEMPLATE_SECTION &&
      !isCurrentMemberLoginTemplateSection(block)
    ) {
      return buildMemberLoginTemplateSection(buildLegacyMemberLoginBlockFromTemplateSection(block))
    }
    return block
  })
  if (editorDoc.value.sections.length === 0) {
    editorDoc.value.sections.push({
        type: 'section',
        props: {
          sectionWidthPercent: 100,
          sectionHeight: 120,
          sectionHeightUnit: 'px',
          sectionPadding: 0,
          sectionPaddingX: 24,
          sectionPaddingY: 24,
          sectionMargin: 14,
          sectionAlign: 'left',
          sectionBackgroundColor: '#FFFFFF',
          sectionBackgroundImageFileId: null,
          sectionBackgroundImageUrl: '',
          sectionBackgroundSize: 'cover',
          sectionBackgroundPosition: 'center',
          sectionBackgroundRepeat: 'no-repeat',
          sectionPlaceholderText: 'This page has no content yet.',
          rows: [],
        },
      })
  }
  syncMemberAuthFormTypesForCurrentPage()
  editorDoc.value.sections.forEach((block) => {
    ensureSectionBlockProps(block)
  })
}

const ensureRequiredSystemElements = () => {
  const descriptors = Array.isArray(props.requiredSystemElements)
    ? props.requiredSystemElements.filter((element) => String(element?.type || '').trim())
    : []
  if (!descriptors.length || !Array.isArray(editorDoc.value?.sections)) return

  const existingTypes = new Set()
  const visitRow = (row) => {
    if (!row || String(row?.type || '').trim().toLowerCase() !== 'row') return
    const elements = Array.isArray(row?.props?.elements) ? row.props.elements : []
    elements.forEach((element) => {
      const type = String(element?.type || '').trim().toLowerCase()
      if (type) existingTypes.add(type)
      if (type === 'row') visitRow(element)
    })
  }
  editorDoc.value.sections.forEach((section) => {
    const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
    rows.forEach(visitRow)
  })

  const targetSection = [...editorDoc.value.sections]
    .reverse()
    .find((section) => String(section?.type || '').trim().toLowerCase() === 'section')
  if (!targetSection) return
  ensureSectionBlockProps(targetSection)
  if (!targetSection.props.rows.length) targetSection.props.rows.push(createRowBlock(1))
  const targetRow = targetSection.props.rows[targetSection.props.rows.length - 1]
  ensureRowBlockProps(targetRow)

  descriptors.forEach((descriptor) => {
    const type = String(descriptor?.type || '').trim().toLowerCase()
    if (!type || existingTypes.has(type)) return
    const element = deepClone(descriptor)
    element.type = type
    if (!element.props || typeof element.props !== 'object') element.props = {}
    element.props.systemManaged = true
    ensureRowElementBlockProps(element)
    targetRow.props.elements.push(element)
    existingTypes.add(type)
  })
}

const deleteBlock = (i) => {
  if (!editorDoc.value?.sections || i == null) return
  const index = Number(i)
  if (!Number.isFinite(index) || index < 0) return
  const block = editorDoc.value.sections[index]
  if (!block || typeof block !== 'object') return
  if (nodeContainsRequiredSystemElement(block)) return

  const wasSelected = selectedIdx.value === index
  const { shouldRemoveCompletely } = hideNodeInCurrentViewport(block)
  if (shouldRemoveCompletely) {
    editorDoc.value.sections.splice(index, 1)
  }

  // fix selection indices & drawer state
  if (wasSelected) {
    selectedIdx.value = -1
    selectedRowContext.value = { sectionIndex: -1, rowIndex: -1 }
    selectedColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1 }
    selectedElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1 }
    selectedMemberLoginPart.value = ''
    showAddPalette.value = false
    showSettings.value = false
    closeElementSettingsMenus()
    closeContentSelectorMenu()
  } else if (shouldRemoveCompletely && selectedIdx.value > index) {
    selectedIdx.value -= 1
    if (Number(selectedRowContext.value.sectionIndex) > index) {
      selectedRowContext.value = {
        sectionIndex: Number(selectedRowContext.value.sectionIndex) - 1,
        rowIndex: Number(selectedRowContext.value.rowIndex),
      }
      if (Number(selectedElementContext.value.sectionIndex) > index) {
        selectedElementContext.value = {
          sectionIndex: Number(selectedElementContext.value.sectionIndex) - 1,
          rowIndex: Number(selectedElementContext.value.rowIndex),
          elementIndex: Number(selectedElementContext.value.elementIndex),
        }
      }
    }
    if (Number(selectedColumnContext.value.sectionIndex) > index) {
      selectedColumnContext.value = {
        sectionIndex: Number(selectedColumnContext.value.sectionIndex) - 1,
        rowIndex: Number(selectedColumnContext.value.rowIndex),
        columnIndex: Number(selectedColumnContext.value.columnIndex),
      }
    }
  }
}

async function saveMetaIfChanged() {
  if (!editorDoc.value) return;

  // Only send what we actually want to update. For now, title only.
  const metaPayload = {};
  if (typeof editorDoc.value.title === 'string' && editorDoc.value.title.trim().length) {
    metaPayload.title = editorDoc.value.title.trim();
  }

  if (!Object.keys(metaPayload).length) return;

  try {
    const r = await fetch(`${apiProxy}/api/pages/${pageIdRef.value}/meta`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(metaPayload),
    });
    const out = await r.json();
    if (!r.ok || !out.success) return;
  } catch (e) {
    // no-op: metadata save failures are surfaced by draft/publish paths
  }
}



onUnmounted(() => {
  window.removeEventListener('resize', syncEditorWindowSize)
  window.visualViewport?.removeEventListener('resize', syncEditorWindowSize)
  window.removeEventListener('keydown', onEditorHistoryKeydown)
  if (savedTimer) clearTimeout(savedTimer) // cleanup
  if (publishedTimer) clearTimeout(publishedTimer)
  if (initialChromeTimer) clearTimeout(initialChromeTimer)
  if (editorChromeHideTimer) clearTimeout(editorChromeHideTimer)
  if (videoPreviewRetryTimer) clearTimeout(videoPreviewRetryTimer)
  if (thumbnailPreviewRetryTimer) clearTimeout(thumbnailPreviewRetryTimer)
  if (autosaveTimer) clearTimeout(autosaveTimer)
  if (editorHistoryTimer) clearTimeout(editorHistoryTimer)
  // no-op: floating menus disabled for drawer-first workflow
})
</script>

<style scoped>
.styled-tooltip {
  position: relative;
}

.styled-tooltip::before,
.styled-tooltip::after {
  pointer-events: none;
  position: absolute;
  left: 50%;
  z-index: 200;
  opacity: 0;
  transition: opacity 150ms ease, transform 150ms ease;
}

.styled-tooltip::before {
  top: calc(100% + 5px);
  width: 8px;
  height: 8px;
  content: '';
  background: #18181b;
  border-left: 1px solid rgb(255 255 255 / 0.1);
  border-top: 1px solid rgb(255 255 255 / 0.1);
  transform: translate(-50%, -3px) rotate(45deg);
}

.styled-tooltip::after {
  top: calc(100% + 9px);
  max-width: 240px;
  content: attr(data-tooltip);
  transform: translate(-50%, -3px);
  border: 1px solid rgb(255 255 255 / 0.1);
  border-radius: 8px;
  background: #18181b;
  padding: 6px 9px;
  color: #fff;
  box-shadow: 0 10px 26px rgb(0 0 0 / 0.3);
  font-size: 11px;
  font-weight: 650;
  line-height: 1.25;
  white-space: nowrap;
}

.styled-tooltip:hover::before,
.styled-tooltip:hover::after,
.styled-tooltip:focus-visible::before,
.styled-tooltip:focus-visible::after {
  opacity: 1;
}

.styled-tooltip:hover::before,
.styled-tooltip:focus-visible::before {
  transform: translate(-50%, 0) rotate(45deg);
}

.styled-tooltip:hover::after,
.styled-tooltip:focus-visible::after {
  transform: translate(-50%, 0);
}

.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
}

.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.55) transparent;
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.5);
}

.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}

.no-inline-gutter {
  scrollbar-gutter: auto;
}

/* Modern range styling */
.range-modern {
  -webkit-appearance: none;
  appearance: none;
  height: 10px;
  border-radius: 9999px;
  background: #e5e7eb; /* will be overridden by inline gradient */
  outline: none;
}

/* WebKit thumb */
.range-modern::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: white;
  border: 1px solid #d4d4d8; /* zinc-300 */
  box-shadow:
    0 1px 2px rgba(0,0,0,0.08),
    0 4px 12px rgba(0,0,0,0.08),
    inset 0 0 0 2px rgba(59,130,246,0.08); /* subtle brand ring */
  cursor: pointer;
}

/* Firefox track + thumb */
.range-modern::-moz-range-track {
  height: 10px;
  border-radius: 9999px;
  background: transparent; /* gradient applied inline */
}
.range-modern::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: white;
  border: 1px solid #d4d4d8;
  box-shadow:
    0 1px 2px rgba(0,0,0,0.08),
    0 4px 12px rgba(0,0,0,0.08),
    inset 0 0 0 2px rgba(59,130,246,0.08);
  cursor: pointer;
}

/* Better focus */
.range-modern:focus-visible {
  box-shadow: 0 0 0 4px rgba(59,130,246,0.25);
  border-radius: 9999px;
}

.saved-element-preview-frame {
  position: relative;
  height: 190px;
  overflow: hidden;
  border-bottom: 1px solid #e4e4e7;
  background:
    linear-gradient(45deg, #f4f4f5 25%, transparent 25%),
    linear-gradient(-45deg, #f4f4f5 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f4f4f5 75%),
    linear-gradient(-45deg, transparent 75%, #f4f4f5 75%),
    #fafafa;
  background-position: 0 0, 0 6px, 6px -6px, -6px 0;
  background-size: 12px 12px;
}

.saved-element-preview-canvas {
  position: absolute;
  left: 50%;
  top: 0;
  width: 900px;
  min-height: 520px;
  transform: translateX(-50%) scale(0.36);
  transform-origin: top center;
  background: white;
}
</style>





